import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import NBS_CODES from "./nbs-codes";
import { LayoutDashboard, FilePlus, Briefcase, Users, Clock, Building2, ShieldCheck, LogOut, Sun, Moon, Key, TrendingUp, Trash2, RefreshCw, CheckCircle, AlertTriangle, FileText, DollarSign, Hash, Receipt, BarChart2, Download, AlertCircle, ClipboardList, X, Ban, FileDown, MoreHorizontal, Copy, MessageSquare, Paperclip, Send, Check, Loader2, MapPin, Menu, Info, Globe, Lock, Eye, EyeOff, Settings, Upload, Handshake, Pencil } from "lucide-react";
import PoliticaPrivacidade from "./PoliticaPrivacidade";
import { incidenciaDaNbs } from "./nbsIncidencia";

const API = process.env.REACT_APP_API || "https://nfse-inatec.com.br/api";

// ── Empresa atual, compartilhada entre as telas ──────────────────────────────
// Antes cada tela escolhia a primeira empresa da lista ao abrir. Quem estava
// trabalhando na terceira mudava de aba e voltava para a primeira sem perceber —
// e serviços, pessoas e notas acabavam indo parar na empresa errada.
const CHAVE_EMPRESA_ATUAL = "nfse_empresa_atual";
const lerEmpresaAtual = () => { try { return localStorage.getItem(CHAVE_EMPRESA_ATUAL) || ""; } catch { return ""; } };
const gravarEmpresaAtual = (id) => { try { if (id) localStorage.setItem(CHAVE_EMPRESA_ATUAL, id); } catch {} };
// A guardada só vale se ainda existir na lista (empresa excluída, ou outra conta
// em acesso delegado); senão cai na primeira, como era.
const empresaInicial = (lista, guardada) =>
  (lista || []).some(e => e.id === guardada) ? guardada : ((lista && lista[0]?.id) || "");

// Cinco lugares do sistema usam `animation: "spin 1s linear infinite"` nos
// ícones de carregando — mas o projeto não tem arquivo CSS e os keyframes nunca
// existiram, então nenhuma bolinha girava: o ícone só ficava parado na tela.
// Injetamos uma vez, aqui, porque todo o estilo daqui é inline.
if (typeof document !== "undefined" && !document.getElementById("nfse-keyframes")) {
  const st = document.createElement("style");
  st.id = "nfse-keyframes";
  st.textContent = "@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}";
  document.head.appendChild(st);
}

// ── CNPJ alfanumérico (IN RFB 2.229/2024 — vigência jul/2026) ────────────────
// Remove apenas pontuação de formatação (. / - espaços); preserva letras A-Z
const limparCNPJ = v => v.replace(/[.\-\/\s]/g, "").toUpperCase().slice(0, 14);
// Aplica máscara XX.XXX.XXX/XXXX-XX por posição (aceita A-Z e 0-9)
const fmtCNPJAlfa = n => {
  const c = n.slice(0, 14);
  if (c.length <= 2)  return c;
  if (c.length <= 5)  return c.slice(0,2)+'.'+c.slice(2);
  if (c.length <= 8)  return c.slice(0,2)+'.'+c.slice(2,5)+'.'+c.slice(5);
  if (c.length <= 12) return c.slice(0,2)+'.'+c.slice(2,5)+'.'+c.slice(5,8)+'/'+c.slice(8);
  return c.slice(0,2)+'.'+c.slice(2,5)+'.'+c.slice(5,8)+'/'+c.slice(8,12)+'-'+c.slice(12);
};
// Dígitos verificadores do CNPJ. Sem isto, um CNPJ digitado errado só aparecia
// na primeira emissão — e com uma mensagem genérica mandando conferir o
// certificado. Na regra alfanumérica (IN RFB 2.229/2024) cada caractere vale
// o código ASCII menos 48, o que mantém 0-9 com o valor de sempre.
const validarCNPJ = (valor) => {
  const c = limparCNPJ(valor || "");
  if (c.length !== 14 || !/^[0-9A-Z]{12}[0-9]{2}$/.test(c)) return false;
  if (c === c[0].repeat(14)) return false;
  const peso = (base, pesos) => {
    const soma = base.split("").reduce((s, ch, i) => s + (ch.charCodeAt(0) - 48) * pesos[i], 0);
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const d1 = peso(c.slice(0, 12), [5,4,3,2,9,8,7,6,5,4,3,2]);
  const d2 = peso(c.slice(0, 13), [6,5,4,3,2,9,8,7,6,5,4,3,2]);
  return c.slice(12) === `${d1}${d2}`;
};

// ── Moedas: tabela do BACEN, NÃO a ISO 4217 ─────────────────────────────────
// O campo tpMoeda da DPS é "código que identifica a moeda conforme tabela do
// BACEN" (TSCodMoeda, no XSD). Esta lista estava em ISO 4217, que usa números
// diferentes para quase tudo — GBP seria 826 na ISO e é 540 no BACEN, o iene
// 392 contra 470, o real 986 contra 790. Passava porque o schema só exige três
// dígitos; a NT 009 (item 2.12) passa a validar o código contra a tabela, e aí
// tudo que não fosse dólar ou euro seria rejeitado.
// Códigos conferidos na tabela Siscomex/BACEN em duas fontes independentes.
// ── Consulta de CNPJ com provedores de reserva ──────────────────────────────
// A BrasilAPI é só um proxy do minhareceita.org, e quando o banco de lá cai
// (aconteceu em 30/09, 06/10 e por três dias a partir de 08/10) a consulta
// inteira morre. Como era a única fonte, o cadastro parava junto. Agora, se ela
// falhar por indisponibilidade, a consulta cai sozinha para o próximo provedor.
//
// Cada um devolve um formato diferente; `mapear` normaliza todos para o mesmo
// conjunto de chaves, que é o que as telas consomem.
const TIMEOUT_CNPJ_MS = 8000;

const _soDigitos = (v) => String(v || "").replace(/\D/g, "");

const CNPJ_PROVEDORES = [
  {
    nome: "BrasilAPI",
    url: (c) => `https://brasilapi.com.br/api/cnpj/v1/${c}`,
    mapear: (d) => ({
      razao_social: d.razao_social || "",
      email: d.email || "",
      telefone: _soDigitos(d.ddd_telefone_1),
      logradouro: d.logradouro || "",
      numero: d.numero || "",
      bairro: d.bairro || "",
      municipio: d.municipio || "",
      uf: d.uf || "",
      cep: _soDigitos(d.cep),
      codigo_municipio_ibge: String(d.codigo_municipio_ibge || d.municipio_ibge || ""),
      codigo_natureza_juridica: d.codigo_natureza_juridica != null ? String(d.codigo_natureza_juridica) : "",
    }),
  },
  {
    nome: "cnpj.ws",
    url: (c) => `https://publica.cnpj.ws/cnpj/${c}`,
    mapear: (d) => {
      const e = d.estabelecimento || {};
      const lgr = [e.tipo_logradouro, e.logradouro].filter(Boolean).join(" ");
      return {
        razao_social: d.razao_social || "",
        email: e.email || "",
        telefone: _soDigitos((e.ddd1 || "") + (e.telefone1 || "")),
        logradouro: lgr,
        numero: e.numero || "",
        bairro: e.bairro || "",
        municipio: e.cidade?.nome || "",
        uf: e.estado?.sigla || "",
        cep: _soDigitos(e.cep),
        codigo_municipio_ibge: String(e.cidade?.ibge_id || ""),
        codigo_natureza_juridica: String(d.natureza_juridica?.id || ""),
      };
    },
  },
  {
    nome: "opencnpj",
    url: (c) => `https://api.opencnpj.org/${c}`,
    mapear: (d) => {
      const tel = (d.telefones || []).find(t => !t.is_fax) || (d.telefones || [])[0] || {};
      return {
        razao_social: d.razao_social || "",
        email: d.email || "",
        telefone: _soDigitos((tel.ddd || "") + (tel.numero || "")),
        logradouro: [d.tipo_logradouro, d.logradouro].filter(Boolean).join(" "),
        numero: d.numero || "",
        bairro: d.bairro || "",
        municipio: d.municipio || "",
        uf: d.uf || "",
        cep: _soDigitos(d.cep),
        // O "codigo_municipio" daqui é o SIAFI (ex: 8165), não o IBGE que a
        // nota exige (4208401). Fica vazio de propósito: quem resolve é o CEP.
        codigo_municipio_ibge: "",
        // Só vem a descrição da natureza jurídica, sem o código.
        codigo_natureza_juridica: "",
      };
    },
  },
];

// Lança Error("nao_encontrado") quando todos responderam que o CNPJ não existe,
// e Error("indisponivel") quando nenhum conseguiu responder.
async function consultarCNPJ(cnpj) {
  let algumRespondeu = false;
  for (const p of CNPJ_PROVEDORES) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), TIMEOUT_CNPJ_MS);
      let r;
      try { r = await fetch(p.url(cnpj), { signal: ctrl.signal }); }
      finally { clearTimeout(t); }
      // 404 significa que ESTE provedor não tem o CNPJ. As bases são atualizadas
      // em datas diferentes, então um CNPJ recém-aberto pode faltar num e existir
      // no outro — por isso continua tentando em vez de desistir aqui.
      if (r.status === 404) { algumRespondeu = true; continue; }
      if (!r.ok) continue;
      const dados = p.mapear(await r.json());
      if (!dados.razao_social) { algumRespondeu = true; continue; }
      return { ...dados, _provedor: p.nome };
    } catch { /* indisponível ou tempo esgotado: tenta o próximo */ }
  }
  throw new Error(algumRespondeu ? "nao_encontrado" : "indisponivel");
}

const MOEDAS = [
  {codigo:"220",sigla:"USD",nome:"Dólar dos Estados Unidos"},
  {codigo:"978",sigla:"EUR",nome:"Euro"},
  {codigo:"540",sigla:"GBP",nome:"Libra Esterlina"},
  {codigo:"470",sigla:"JPY",nome:"Iene Japonês"},
  {codigo:"790",sigla:"BRL",nome:"Real Brasileiro"},
  {codigo:"706",sigla:"ARS",nome:"Peso Argentino"},
  {codigo:"715",sigla:"CLP",nome:"Peso Chileno"},
  {codigo:"165",sigla:"CAD",nome:"Dólar Canadense"},
  {codigo:"425",sigla:"CHF",nome:"Franco Suíço"},
  {codigo:"150",sigla:"AUD",nome:"Dólar Australiano"},
  {codigo:"741",sigla:"MXN",nome:"Peso Mexicano"},
  {codigo:"745",sigla:"UYU",nome:"Peso Uruguaio"},
  {codigo:"660",sigla:"PEN",nome:"Sol Peruano"},
  {codigo:"720",sigla:"COP",nome:"Peso Colombiano"},
  {codigo:"795",sigla:"CNY",nome:"Yuan Chinês (Renminbi)"},
  {codigo:"860",sigla:"INR",nome:"Rúpia Indiana"},
  {codigo:"930",sigla:"KRW",nome:"Won Coreano"},
  {codigo:"195",sigla:"SGD",nome:"Dólar de Singapura"},
  {codigo:"205",sigla:"HKD",nome:"Dólar de Hong Kong"},
  {codigo:"145",sigla:"AED",nome:"Dirhem dos Emirados Árabes"},
  {codigo:"100",sigla:"KWD",nome:"Dinar Kuwaitiano"},
  {codigo:"800",sigla:"QAR",nome:"Rial Qatariano"},
  {codigo:"820",sigla:"SAR",nome:"Rial Saudita"},
  {codigo:"880",sigla:"ILS",nome:"Novo Shekel Israelense"},
  {codigo:"055",sigla:"DKK",nome:"Coroa Dinamarquesa"},
  {codigo:"065",sigla:"NOK",nome:"Coroa Norueguesa"},
  {codigo:"070",sigla:"SEK",nome:"Coroa Sueca"},
  {codigo:"245",sigla:"NZD",nome:"Dólar Neozelandês"},
  {codigo:"785",sigla:"ZAR",nome:"Rand Sul-Africano"},
  {codigo:"015",sigla:"THB",nome:"Baht Tailandês"},
  {codigo:"975",sigla:"PLN",nome:"Zloty Polonês"},
  {codigo:"345",sigla:"HUF",nome:"Forint Húngaro"},
  {codigo:"075",sigla:"CZK",nome:"Coroa Tcheca"},
  {codigo:"030",sigla:"BOB",nome:"Boliviano"},
  {codigo:"450",sigla:"PYG",nome:"Guarani Paraguaio"},
  {codigo:"026",sigla:"VEF",nome:"Bolívar Venezuelano"},
  {codigo:"495",sigla:"HNL",nome:"Lempira Hondurenho"},
  {codigo:"770",sigla:"GTQ",nome:"Quetzal Guatemalteco"},
  {codigo:"040",sigla:"CRC",nome:"Colón Costa-Riquenho"},
  {codigo:"730",sigla:"DOP",nome:"Peso Dominicano"},
];

function MoedaInput({ value, onChange, S, T }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const filtered = query.length >= 1
    ? MOEDAS.filter(m =>
        m.nome.toLowerCase().includes(query.toLowerCase()) ||
        m.sigla.toLowerCase().includes(query.toLowerCase()) ||
        m.codigo.includes(query)
      ).slice(0, 10)
    : [];
  useEffect(() => { setQuery(value || ""); }, [value]);
  const handleFocus = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    setOpen(true);
  };
  return (
    <div style={{ position:"relative" }}>
      <input ref={inputRef} style={S.input} placeholder="Digite a moeda (ex: Dólar, USD, 220...)" value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); onChange(""); if(inputRef.current) setRect(inputRef.current.getBoundingClientRect()); }}
        onFocus={handleFocus} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && filtered.length > 0 && rect && createPortal(
        <div style={{ position:"fixed", top: rect.bottom + 2, left: rect.left, width: rect.width,
          background: T?.cardBg || "#1e293b", border:"1px solid #334155", borderRadius:6,
          zIndex:99999, maxHeight:240, overflowY:"auto", boxShadow:"0 8px 24px #000a" }}>
          {filtered.map(m => (
            <div key={m.codigo} onMouseDown={() => { onChange(m.codigo); setQuery(`${m.sigla} – ${m.nome}`); setOpen(false); }}
              style={{ padding:"8px 12px", cursor:"pointer", fontSize:13, display:"flex", gap:10, alignItems:"center",
                borderBottom:"1px solid #33415540" }}>
              <span style={{ fontFamily:"monospace", fontWeight:700, color:"#38bdf8", fontSize:12, minWidth:32 }}>{m.codigo}</span>
              <span style={{ fontWeight:600, color:"#a78bfa", minWidth:36 }}>{m.sigla}</span>
              <span style={{ color:"#fff" }}>{m.nome}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

const PAISES = [
  {iso:"AF",nome:"Afeganistão"},{iso:"ZA",nome:"África do Sul"},{iso:"AL",nome:"Albânia"},
  {iso:"DE",nome:"Alemanha"},{iso:"AD",nome:"Andorra"},{iso:"AO",nome:"Angola"},
  {iso:"AG",nome:"Antígua e Barbuda"},{iso:"SA",nome:"Arábia Saudita"},{iso:"DZ",nome:"Argélia"},
  {iso:"AR",nome:"Argentina"},{iso:"AM",nome:"Armênia"},{iso:"AU",nome:"Austrália"},
  {iso:"AT",nome:"Áustria"},{iso:"AZ",nome:"Azerbaijão"},{iso:"BS",nome:"Bahamas"},
  {iso:"BD",nome:"Bangladesh"},{iso:"BB",nome:"Barbados"},{iso:"BH",nome:"Bahrein"},
  {iso:"BE",nome:"Bélgica"},{iso:"BZ",nome:"Belize"},{iso:"BJ",nome:"Benim"},
  {iso:"BY",nome:"Bielorrússia"},{iso:"BO",nome:"Bolívia"},{iso:"BA",nome:"Bósnia e Herzegovina"},
  {iso:"BW",nome:"Botsuana"},{iso:"BR",nome:"Brasil"},{iso:"BN",nome:"Brunei"},
  {iso:"BG",nome:"Bulgária"},{iso:"BF",nome:"Burkina Faso"},{iso:"BI",nome:"Burundi"},
  {iso:"BT",nome:"Butão"},{iso:"CV",nome:"Cabo Verde"},{iso:"CM",nome:"Camarões"},
  {iso:"KH",nome:"Camboja"},{iso:"CA",nome:"Canadá"},{iso:"QA",nome:"Catar"},
  {iso:"KZ",nome:"Cazaquistão"},{iso:"TD",nome:"Chade"},{iso:"CL",nome:"Chile"},
  {iso:"CN",nome:"China"},{iso:"CY",nome:"Chipre"},{iso:"CO",nome:"Colômbia"},
  {iso:"KM",nome:"Comores"},{iso:"CG",nome:"Congo"},{iso:"KP",nome:"Coreia do Norte"},
  {iso:"KR",nome:"Coreia do Sul"},{iso:"CI",nome:"Costa do Marfim"},{iso:"CR",nome:"Costa Rica"},
  {iso:"HR",nome:"Croácia"},{iso:"CU",nome:"Cuba"},{iso:"DK",nome:"Dinamarca"},
  {iso:"DJ",nome:"Djibuti"},{iso:"EG",nome:"Egito"},{iso:"SV",nome:"El Salvador"},
  {iso:"AE",nome:"Emirados Árabes Unidos"},{iso:"EC",nome:"Equador"},{iso:"ER",nome:"Eritreia"},
  {iso:"SK",nome:"Eslováquia"},{iso:"SI",nome:"Eslovênia"},{iso:"ES",nome:"Espanha"},
  {iso:"US",nome:"Estados Unidos"},{iso:"EE",nome:"Estônia"},{iso:"ET",nome:"Etiópia"},
  {iso:"FJ",nome:"Fiji"},{iso:"PH",nome:"Filipinas"},{iso:"FI",nome:"Finlândia"},
  {iso:"FR",nome:"França"},{iso:"GA",nome:"Gabão"},{iso:"GM",nome:"Gâmbia"},
  {iso:"GH",nome:"Gana"},{iso:"GE",nome:"Geórgia"},{iso:"GD",nome:"Granada"},
  {iso:"GR",nome:"Grécia"},{iso:"GT",nome:"Guatemala"},{iso:"GN",nome:"Guiné"},
  {iso:"GW",nome:"Guiné-Bissau"},{iso:"GQ",nome:"Guiné Equatorial"},{iso:"GY",nome:"Guiana"},
  {iso:"HT",nome:"Haiti"},{iso:"HN",nome:"Honduras"},{iso:"HU",nome:"Hungria"},
  {iso:"YE",nome:"Iêmen"},{iso:"IN",nome:"Índia"},{iso:"ID",nome:"Indonésia"},
  {iso:"IR",nome:"Irã"},{iso:"IQ",nome:"Iraque"},{iso:"IE",nome:"Irlanda"},
  {iso:"IS",nome:"Islândia"},{iso:"IL",nome:"Israel"},{iso:"IT",nome:"Itália"},
  {iso:"JM",nome:"Jamaica"},{iso:"JP",nome:"Japão"},{iso:"JO",nome:"Jordânia"},
  {iso:"KI",nome:"Kiribati"},{iso:"KW",nome:"Kuwait"},{iso:"LA",nome:"Laos"},
  {iso:"LS",nome:"Lesoto"},{iso:"LV",nome:"Letônia"},{iso:"LB",nome:"Líbano"},
  {iso:"LR",nome:"Libéria"},{iso:"LY",nome:"Líbia"},{iso:"LI",nome:"Liechtenstein"},
  {iso:"LT",nome:"Lituânia"},{iso:"LU",nome:"Luxemburgo"},{iso:"MK",nome:"Macedônia do Norte"},
  {iso:"MG",nome:"Madagascar"},{iso:"MY",nome:"Malásia"},{iso:"MW",nome:"Malawi"},
  {iso:"MV",nome:"Maldivas"},{iso:"ML",nome:"Mali"},{iso:"MT",nome:"Malta"},
  {iso:"MA",nome:"Marrocos"},{iso:"MR",nome:"Mauritânia"},{iso:"MU",nome:"Maurícia"},
  {iso:"MX",nome:"México"},{iso:"FM",nome:"Micronésia"},{iso:"MZ",nome:"Moçambique"},
  {iso:"MD",nome:"Moldova"},{iso:"MC",nome:"Mônaco"},{iso:"MN",nome:"Mongólia"},
  {iso:"ME",nome:"Montenegro"},{iso:"MM",nome:"Myanmar"},{iso:"NA",nome:"Namíbia"},
  {iso:"NR",nome:"Nauru"},{iso:"NP",nome:"Nepal"},{iso:"NI",nome:"Nicarágua"},
  {iso:"NE",nome:"Níger"},{iso:"NG",nome:"Nigéria"},{iso:"NO",nome:"Noruega"},
  {iso:"NZ",nome:"Nova Zelândia"},{iso:"OM",nome:"Omã"},{iso:"NL",nome:"Países Baixos"},
  {iso:"PW",nome:"Palau"},{iso:"PA",nome:"Panamá"},{iso:"PG",nome:"Papua Nova Guiné"},
  {iso:"PK",nome:"Paquistão"},{iso:"PY",nome:"Paraguai"},{iso:"PE",nome:"Peru"},
  {iso:"PL",nome:"Polônia"},{iso:"PT",nome:"Portugal"},{iso:"GB",nome:"Reino Unido"},
  {iso:"CF",nome:"República Centro-Africana"},{iso:"CZ",nome:"República Tcheca"},
  {iso:"CD",nome:"Rep. Democrática do Congo"},{iso:"DO",nome:"República Dominicana"},
  {iso:"RO",nome:"Romênia"},{iso:"RW",nome:"Ruanda"},{iso:"RU",nome:"Rússia"},
  {iso:"WS",nome:"Samoa"},{iso:"SM",nome:"San Marino"},{iso:"ST",nome:"São Tomé e Príncipe"},
  {iso:"LC",nome:"Santa Lúcia"},{iso:"KN",nome:"São Cristóvão e Neves"},
  {iso:"VC",nome:"São Vicente e Granadinas"},{iso:"SC",nome:"Seicheles"},
  {iso:"SN",nome:"Senegal"},{iso:"SL",nome:"Serra Leoa"},{iso:"RS",nome:"Sérvia"},
  {iso:"SG",nome:"Singapura"},{iso:"SY",nome:"Síria"},{iso:"SO",nome:"Somália"},
  {iso:"LK",nome:"Sri Lanka"},{iso:"SD",nome:"Sudão"},{iso:"SS",nome:"Sudão do Sul"},
  {iso:"SE",nome:"Suécia"},{iso:"CH",nome:"Suíça"},{iso:"SR",nome:"Suriname"},
  {iso:"TH",nome:"Tailândia"},{iso:"TW",nome:"Taiwan"},{iso:"TZ",nome:"Tanzânia"},
  {iso:"TJ",nome:"Tajiquistão"},{iso:"TL",nome:"Timor-Leste"},{iso:"TG",nome:"Togo"},
  {iso:"TO",nome:"Tonga"},{iso:"TT",nome:"Trinidad e Tobago"},{iso:"TN",nome:"Tunísia"},
  {iso:"TM",nome:"Turcomenistão"},{iso:"TR",nome:"Turquia"},{iso:"TV",nome:"Tuvalu"},
  {iso:"UA",nome:"Ucrânia"},{iso:"UG",nome:"Uganda"},{iso:"UY",nome:"Uruguai"},
  {iso:"UZ",nome:"Uzbequistão"},{iso:"VU",nome:"Vanuatu"},{iso:"VA",nome:"Vaticano"},
  {iso:"VE",nome:"Venezuela"},{iso:"VN",nome:"Vietnã"},{iso:"ZM",nome:"Zâmbia"},
  {iso:"ZW",nome:"Zimbábue"},
];

function PaisInput({ value, onChange, S, T }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const filtered = query.length >= 1
    ? PAISES.filter(p => p.nome.toLowerCase().includes(query.toLowerCase()) || p.iso.toLowerCase().startsWith(query.toLowerCase())).slice(0, 8)
    : [];
  useEffect(() => { if (!value) setQuery(""); }, [value]);
  const handleFocus = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    setOpen(true);
  };
  return (
    <div style={{ position:"relative" }}>
      <input ref={inputRef} style={S.input} placeholder="Digite o país..." value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); onChange("",""); if(inputRef.current) setRect(inputRef.current.getBoundingClientRect()); }}
        onFocus={handleFocus} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && filtered.length > 0 && rect && createPortal(
        <div style={{ position:"fixed", top: rect.bottom + 2, left: rect.left, width: rect.width,
          background: T?.cardBg || "#1e293b", border:"1px solid #334155", borderRadius:6,
          zIndex:99999, maxHeight:220, overflowY:"auto", boxShadow:"0 8px 24px #000a" }}>
          {filtered.map(p => (
            <div key={p.iso} onMouseDown={() => { onChange(p.iso, p.nome); setQuery(p.nome); setOpen(false); }}
              style={{ padding:"8px 12px", cursor:"pointer", fontSize:13, display:"flex", gap:10, alignItems:"center",
                borderBottom:"1px solid #33415540" }}>
              <span style={{ fontFamily:"monospace", fontWeight:700, color:"#38bdf8", fontSize:12, minWidth:28 }}>{p.iso}</span>
              <span style={{ color:"#fff" }}>{p.nome}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return isMobile;
}

const LC116 = {
  "1.01":"Análise e desenvolvimento de sistemas","1.02":"Programação","1.03":"Processamento de dados e congêneres",
  "1.04":"Elaboração de programas de computadores","1.05":"Licenciamento ou cessão de direito de uso de programas de computação",
  "1.06":"Assessoria e consultoria em informática","1.07":"Suporte técnico em informática",
  "1.08":"Planejamento, confecção, manutenção e atualização de páginas eletrônicas",
  "2.01":"Serviços de pesquisas e desenvolvimento de qualquer natureza",
  "3.02":"Cessão de direito de uso de marcas e de sinais de propaganda",
  "4.01":"Medicina e biomedicina","4.02":"Análises clínicas, patologia, eletricidade médica",
  "4.03":"Hospitais, clínicas, laboratórios","4.14":"Serviços farmacêuticos",
  "5.01":"Medicina veterinária e zootecnia",
  "6.01":"Barbearia, cabeleireiros, manicuros, pedicuros e congêneres",
  "7.01":"Engenharia, agronomia, agrimensura, arquitetura, geologia, urbanismo",
  "7.02":"Execução por administração, empreitada ou subempreitada de obras de construção civil",
  "7.10":"Limpeza e dragagem de rios, portos, canais, baías e outros",
  "8.01":"Ensino regular pré-escolar, fundamental, médio e superior",
  "8.02":"Instrução, treinamento, orientação pedagógica e educacional",
  "10.01":"Agenciamento, corretagem ou intermediação de câmbio",
  "10.02":"Agenciamento, corretagem ou intermediação de seguros",
  "10.03":"Agenciamento, corretagem ou intermediação de títulos em geral",
  "10.04":"Agenciamento, corretagem ou intermediação de contratos de arrendamento mercantil",
  "10.05":"Agenciamento, corretagem ou intermediação de bens móveis e imóveis",
  "10.09":"Representação de qualquer natureza, inclusive comercial",
  "11.01":"Guarda e estacionamento de veículos terrestres automotores",
  "11.02":"Vigilância, segurança ou monitoramento de bens e pessoas",
  "12.01":"Espetáculos teatrais","12.06":"Execução de música",
  "13.02":"Fonografia ou gravação de sons, inclusive trucagem, dublagem, mixagem e congêneres",
  "14.01":"Lubrificação, limpeza, lustração, revisão, carga e recarga de baterias",
  "14.05":"Restauração, recondicionamento, acondicionamento, pintura, beneficiamento de bens",
  "15.01":"Administração de fundos quaisquer","15.08":"Arrendamento mercantil (leasing)",
  "15.09":"Assessoria e consultoria em geral","15.10":"Auditoria","15.13":"Gestão por terceiros",
  "15.14":"Estudo, análise e avaliação de organizações e sistemas",
  "15.18":"Planejamento, organização e desenvolvimento de negócios",
  "17.01":"Assessoria ou consultoria de qualquer natureza",
  "17.02":"Análise, exame, pesquisa, coleta, compilação e fornecimento de dados",
  "17.03":"Planejamento, coordenação, programação ou organização técnica, financeira ou administrativa",
  "17.04":"Recrutamento, agenciamento, seleção e colocação de mão-de-obra",
  "17.05":"Fornecimento de mão-de-obra, mesmo em caráter temporário",
  "17.06":"Propaganda e publicidade","17.08":"Franquia (franchising)",
  "17.09":"Perícias, laudos, exames técnicos e análises técnicas",
  "17.10":"Planejamento, organização e administração de feiras e eventos",
  "17.11":"Organização de festas e recepções","17.12":"Administração em geral",
  "17.16":"Tributação, auditoria e consultoria fiscal ou tributária",
  "17.19":"Datilografia, digitação, estenografia, expediente, secretaria em geral",
  "17.20":"Recepção, atendimento ao cliente e serviços correlatos",
  "17.20.01":"Consultoria e assessoria econômica ou financeira",
  "17.21":"Contabilidade, inclusive serviços técnicos e auxiliares",
  "17.22":"Consultoria e assessoria econômica ou financeira",
  "17.23":"Estatística","17.24":"Captação de dados, inclusive preenchimento de formulários",
  "17.25":"Transplante de órgãos e tecidos humanos",
  "18.01":"Serviços de regulação de sinistros vinculados a contratos de seguros",
  "20.01":"Serviços portuários, ferroportuários e congêneres",
  "21.01":"Serviços de registros públicos, cartorários e notariais",
  "22.01":"Exploração de rodovia mediante cobrança de preço",
  "26.01":"Serviços de coleta, remessa ou entrega de correspondências",
};

const descLC116 = (codigo) => {
  if (!codigo) return "";
  if (LC116[codigo]) return LC116[codigo];
  // tenta prefixo (ex: "17.20.01" → "17.20")
  const partes = codigo.split(".");
  if (partes.length > 2) {
    const prefix = partes.slice(0,2).join(".");
    if (LC116[prefix]) return LC116[prefix];
  }
  return "";
};

const fmtCnpj = v => v ? v.replace(/\D/g,"").replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,"$1.$2.$3/$4-$5") : "";
  const fmtMoeda = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtCalc  = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(v || 0);
const fmtData = (d) => { if (!d) return "—"; const s = typeof d==="string" && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d+"T12:00:00" : d; return new Date(s).toLocaleDateString("pt-BR"); };
function fmtMoneyInput(val) {
  const digits = String(val).replace(/\D/g, "");
  if (!digits) return "";
  const num = parseInt(digits, 10) / 100;
  return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function parseBR(val) {
  if (!val && val !== 0) return 0;
  return parseFloat(String(val).replace(/\./g, "").replace(",", ".")) || 0;
}
function numToBR(num) {
  if (!num && num !== 0) return "";
  return parseFloat(num).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_DARK = {
  autorizada:  { label:"Autorizada",  bg:"#0d2f1a", color:"#4ade80", dot:"#22c55e" },
  processando: { label:"Processando", bg:"#1a1a0d", color:"#facc15", dot:"#eab308" },
  cancelada:   { label:"Cancelada",   bg:"#2f0d0d", color:"#f87171", dot:"#ef4444" },
  erro:        { label:"Erro",        bg:"#2f0d0d", color:"#f87171", dot:"#ef4444" },
};
const STATUS_LIGHT = {
  autorizada:  { label:"Autorizada",  bg:"#dcfce7", color:"#16a34a", dot:"#22c55e" },
  processando: { label:"Processando", bg:"#fefce8", color:"#ca8a04", dot:"#eab308" },
  cancelada:   { label:"Cancelada",   bg:"#fef2f2", color:"#dc2626", dot:"#ef4444" },
  erro:        { label:"Erro",        bg:"#fef2f2", color:"#dc2626", dot:"#ef4444" },
};

function StatusBadge({ status, dark = true }) {
  const cfg = dark ? STATUS_DARK : STATUS_LIGHT;
  const c = cfg[status] || cfg.erro;
  return <span style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600, background:c.bg, color:c.color }}>
    <span style={{ width:5, height:5, borderRadius:"50%", background:c.dot }} />{c.label}
  </span>;
}

/** Campo de senha com botão de mostrar/ocultar. */
function CampoSenha({ estilo, valor, onChange, onKeyDown, placeholder, autoComplete }) {
  const [visivel, setVisivel] = useState(false);
  return (
    <div style={{ position:"relative" }}>
      <input
        style={{ ...estilo, paddingRight:42 }}
        type={visivel ? "text" : "password"}
        value={valor}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        onClick={()=>setVisivel(v=>!v)}
        title={visivel ? "Ocultar senha" : "Mostrar senha"}
        aria-label={visivel ? "Ocultar senha" : "Mostrar senha"}
        style={{ position:"absolute", right:4, top:"50%", transform:"translateY(-50%)",
                 background:"transparent", border:"none", cursor:"pointer", padding:8,
                 display:"flex", alignItems:"center", color:"#94a3b8", lineHeight:0 }}>
        {visivel ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

function LoginView({ onLogin, avisoSessao = "" }) {
  // Detecta token de reset na URL
  const resetTokenUrl = new URLSearchParams(window.location.search).get("reset_token") || "";
  const [mode, setMode] = useState(resetTokenUrl ? "redefinir" : "login");
  const [form, setForm] = useState({ nome:"", email:"", senha:"", novaSenha:"", confirmarSenha:"" });
  const [resetToken, setResetToken] = useState(resetTokenUrl);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [aceitePolitica, setAceitePolitica] = useState(false);

  const irPara = (m) => { setErro(""); setSucesso(""); setMode(m); };

  const handle = async () => {
    setLoading(true); setErro(""); setSucesso("");
    try {
      if (mode === "registro") {
        if ((form.senha || "").length < 8) throw new Error("A senha deve ter pelo menos 8 caracteres.");
        if (!aceitePolitica) throw new Error("Confirme que leu a Política de Privacidade para continuar.");
        const r = await fetch(API + "/auth/registro", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ nome:form.nome, email:form.email, senha:form.senha, aceite_politica:true }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        onLogin(d.access_token, d.usuario);

      } else if (mode === "login") {
        const body = new URLSearchParams({ username: form.email, password: form.senha });
        const r = await fetch(API + "/auth/login", { method:"POST", body });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        onLogin(d.access_token, d.usuario);

      } else if (mode === "esqueci") {
        const r = await fetch(API + "/auth/esqueci-senha", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: form.email }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        setSucesso(d.mensagem);

      } else if (mode === "redefinir") {
        if (form.novaSenha !== form.confirmarSenha) throw new Error("As senhas não coincidem.");
        if (form.novaSenha.length < 8) throw new Error("A senha deve ter pelo menos 8 caracteres.");
        const r = await fetch(API + "/auth/redefinir-senha", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ token: resetToken, nova_senha: form.novaSenha }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        setSucesso(d.mensagem);
        // Limpa token da URL sem recarregar a página
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => irPara("login"), 2500);
      }
    } catch(e) { setErro(e.message); }
    setLoading(false);
  };

  const S = {
    wrap:  { minHeight:"100vh", background:"#f0f4f8", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'IBM Plex Sans',sans-serif" },
    card:  { background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:20, padding: window.innerWidth < 768 ? "28px 20px" : "48px 44px", width:"100%", maxWidth:420, boxShadow:"0 8px 32px rgba(0,0,0,0.08)" },
    input: { width:"100%", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#1e293b", outline:"none", boxSizing:"border-box" },
    btn:   { width:"100%", background:"linear-gradient(135deg,#0ea5e9,#0284c7)", color:"#fff", border:"none", borderRadius:8, padding:"12px", fontSize:14, fontWeight:600, cursor:"pointer" },
    label: { fontSize:12, fontWeight:600, color:"#64748b", display:"block", marginBottom:6 },
    erro:  { background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#dc2626", marginBottom:12 },
    ok:    { background:"#f0fdf4", border:"1px solid #86efac", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#16a34a", marginBottom:12 },
    link:  { color:"#0284c7", cursor:"pointer", textDecoration:"none" },
  };

  const titulos = {
    login:    "Entre na sua conta",
    registro: "Crie sua conta",
    esqueci:  "Recuperar senha",
    redefinir:"Criar nova senha",
  };

  return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={{ textAlign:"center", marginBottom:36 }}>
          <div style={{ width:52, height:52, background:"linear-gradient(135deg,#0ea5e9,#0284c7)", borderRadius:14, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, fontWeight:800, color:"#fff", margin:"0 auto 16px" }}>N</div>
          <h1 style={{ fontSize:22, fontWeight:800, color:"#0f172a", margin:"0 0 6px" }}>NFPro</h1>
          <p style={{ fontSize:13, color:"#64748b", margin:0 }}>{titulos[mode]}</p>
        </div>

        {avisoSessao && !erro && (
          <div style={{ display:"flex", gap:8, alignItems:"flex-start", background:"#fffbeb",
                        border:"1px solid #fde68a", borderRadius:8, padding:"10px 13px",
                        marginBottom:16, fontSize:12.5, color:"#92400e", lineHeight:1.5 }}>
            <Clock size={14} style={{ flexShrink:0, marginTop:1 }} />
            <span>{avisoSessao}</span>
          </div>
        )}
        {erro    && <div style={S.erro}><AlertTriangle size={13} style={{verticalAlign:"middle",marginRight:5}} />{erro}</div>}
        {sucesso && <div style={S.ok}><Check size={13} style={{verticalAlign:"middle",marginRight:5}} />{sucesso}</div>}

        {/* ── LOGIN ── */}
        {mode === "login" && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <div><label style={S.label}>E-MAIL <span style={{color:"#f97316",marginLeft:3}}>*</span></label><input style={S.input} type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} onKeyDown={e=>e.key==="Enter"&&handle()} /></div>
            <div>
              <label style={S.label}>SENHA <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
              <CampoSenha estilo={S.input} valor={form.senha} autoComplete="current-password"
                onChange={e=>setForm({...form,senha:e.target.value})}
                onKeyDown={e=>e.key==="Enter"&&handle()} />
              <div style={{ textAlign:"right", marginTop:6 }}>
                <span style={{...S.link, fontSize:12}} onClick={()=>irPara("esqueci")}>Esqueci minha senha</span>
              </div>
            </div>
            <button style={S.btn} onClick={handle} disabled={loading}>{loading ? "Aguarde..." : "Entrar"}</button>
          </div>
        )}

        {/* ── REGISTRO ── */}
        {mode === "registro" && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <div><label style={S.label}>NOME <span style={{color:"#f97316",marginLeft:3}}>*</span></label><input style={S.input} value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})} /></div>
            <div><label style={S.label}>E-MAIL <span style={{color:"#f97316",marginLeft:3}}>*</span></label><input style={S.input} type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></div>
            <div>
              <label style={S.label}>SENHA <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
              <CampoSenha estilo={S.input} valor={form.senha} autoComplete="new-password"
                placeholder="Mínimo 8 caracteres"
                onChange={e=>setForm({...form,senha:e.target.value})} />
            </div>

            {/* Prova do dever de informar (LGPD art. 9º). Não é consentimento:
                a base legal do cadastro é execução de contrato. */}
            <label style={{ display:"flex", alignItems:"flex-start", gap:9, cursor:"pointer",
                            fontSize:12.5, color:"#64748b", lineHeight:1.55 }}>
              <input type="checkbox" checked={aceitePolitica}
                onChange={e=>setAceitePolitica(e.target.checked)}
                style={{ marginTop:2, width:15, height:15, accentColor:"#0ea5e9",
                         cursor:"pointer", flexShrink:0 }} />
              <span>
                Li e estou ciente da{" "}
                <a href="/privacidade" target="_blank" rel="noopener noreferrer"
                   onClick={e=>e.stopPropagation()}
                   style={{ color:"#0ea5e9", textDecoration:"none" }}>
                  Política de Privacidade
                </a>.
              </span>
            </label>

            <button style={{ ...S.btn, opacity: aceitePolitica ? 1 : 0.55,
                             cursor: aceitePolitica ? "pointer" : "not-allowed" }}
              onClick={handle} disabled={loading || !aceitePolitica}>
              {loading ? "Aguarde..." : "Criar conta"}
            </button>
          </div>
        )}

        {/* ── ESQUECI SENHA ── */}
        {mode === "esqueci" && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <p style={{ fontSize:13, color:"#64748b", margin:"0 0 4px", lineHeight:1.6 }}>
              Informe o e-mail cadastrado e enviaremos um link para redefinir sua senha.
            </p>
            <div><label style={S.label}>E-MAIL <span style={{color:"#f97316",marginLeft:3}}>*</span></label><input style={S.input} type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} onKeyDown={e=>e.key==="Enter"&&handle()} /></div>
            <button style={S.btn} onClick={handle} disabled={loading}>{loading ? "Enviando..." : "Enviar link de recuperação"}</button>
          </div>
        )}

        {/* ── REDEFINIR SENHA ── */}
        {mode === "redefinir" && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <p style={{ fontSize:13, color:"#64748b", margin:"0 0 4px", lineHeight:1.6 }}>
              Escolha uma nova senha segura com pelo menos 8 caracteres.
            </p>
            <div>
              <label style={S.label}>NOVA SENHA <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
              <CampoSenha estilo={S.input} valor={form.novaSenha} autoComplete="new-password"
                placeholder="Mínimo 8 caracteres"
                onChange={e=>setForm({...form,novaSenha:e.target.value})} />
            </div>
            <div>
              <label style={S.label}>CONFIRMAR NOVA SENHA <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
              <CampoSenha estilo={S.input} valor={form.confirmarSenha} autoComplete="new-password"
                onChange={e=>setForm({...form,confirmarSenha:e.target.value})}
                onKeyDown={e=>e.key==="Enter"&&handle()} />
            </div>
            <button style={S.btn} onClick={handle} disabled={loading}>{loading ? "Salvando..." : "Salvar nova senha"}</button>
          </div>
        )}

        {/* ── Links de navegação ── */}
        <div style={{ textAlign:"center", marginTop:24, fontSize:13, color:"#475569" }}>
          {mode === "login" && <span>Sem conta? <span style={S.link} onClick={()=>irPara("registro")}>Cadastre-se</span></span>}
          {mode === "registro" && <span>Já tem conta? <span style={S.link} onClick={()=>irPara("login")}>Entrar</span></span>}
          {(mode === "esqueci" || mode === "redefinir") && <span><span style={S.link} onClick={()=>irPara("login")}>← Voltar ao login</span></span>}
        </div>

        {/* Dever de informar (LGPD art. 9º): a política precisa estar visível
            já no cadastro, antes de qualquer dado ser enviado. */}
        <div style={{ textAlign:"center", marginTop:18, paddingTop:16, borderTop:"1px solid #1e293b" }}>
          <a href="/privacidade" style={{ fontSize:12, color:"#64748b", textDecoration:"none" }}>
            Política de Privacidade
          </a>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ token, usuario, onNavigate, isMobile = false, T = {bg:"#080c10",sidebar:"#0c1117",border:"#1a2332",card:"#0c1117",cardInner:"#080c10",text:"#f1f5f9",textMuted:"#94a3b8",textFaint:"#475569",input:"#080c10",inputBorder:"#1a2332",inputText:"#e2e8f0",btnSecBg:"#1a2332",btnSecText:"#94a3b8",rowBorder:"#0f1923",tableBg:"#0c1117"} }) {
  const hoje = new Date();
  const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,"0")}`;
  const [notas, setNotas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [mesSel, setMesSel] = useState(mesAtual);
  // Sem isto o painel somava o faturamento de todas as empresas num número só,
  // e a lista misturava as notas de cada uma. Usa a mesma empresa das outras
  // telas, então trocar de aba não troca a empresa por baixo dos panos.
  const [empresaFiltro, setEmpresaFiltro] = useState(() => lerEmpresaAtual());

  useEffect(() => {
    const h = { Authorization: "Bearer " + token };
    fetch(API + "/nfse", { headers:h }).then(r=>r.json()).then(d=>setNotas(Array.isArray(d)?d:[]));
    fetch(API + "/empresas", { headers:h }).then(r=>r.json()).then(d=>{
      const lista = Array.isArray(d) ? d : [];
      setEmpresas(lista);
      // A empresa guardada pode não existir nesta conta (acesso delegado) ou ter
      // sido excluída; nesse caso cai na primeira, como as demais telas fazem.
      setEmpresaFiltro(f => lista.some(e => e.id === f) ? f : (lista.length === 1 ? lista[0].id : ""));
    });
  }, [token]);

  const nomeEmpresa = (id) => empresas.find(e => e.id === id)?.razao_social || "";

  // Gera opções dos últimos 24 meses
  const mesesOpcoes = Array.from({ length: 24 }, (_, i) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    const mesesNomes = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
    const label = `${mesesNomes[d.getMonth()]}/${d.getFullYear()}`;
    return { val, label };
  });

  // Filtra notas pela empresa escolhida e pelo mês (usa created_at = data real
  // de emissão). Empresa vazia = "Todas", que volta ao comportamento somado.
  const notasMes = notas.filter(n => {
    if (empresaFiltro && n.empresa_id !== empresaFiltro) return false;
    const ds = n.created_at;
    const d = new Date(ds);
    const nm = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    return nm === mesSel;
  });

  // Calcula stats do mês. Faturamento e ISS somam apenas notas autorizadas:
  // cancelada não gera receita, e nota com erro nunca chegou a existir para o
  // fisco — incluí-las inflava o total do mês.
  const notasAutorizadas = notasMes.filter(n => n.status === "autorizada");
  const totalNotas      = notasMes.length;
  const foraDoTotal     = totalNotas - notasAutorizadas.length;
  const totalFaturado   = notasAutorizadas.reduce((s,n) => s + (parseFloat(n.valor_servico)||0), 0);
  const totalIss        = notasAutorizadas.reduce((s,n) => s + (parseFloat(n.valor_iss)||0), 0);
  const totalEmpresas   = empresas.length;

  const cards = [
    { icon:FileText,   label:"Total de Notas",  value:totalNotas,              cor:"#0ea5e9" },
    { icon:DollarSign, label:"Total Faturado",  value:fmtMoeda(totalFaturado), cor:"#4ade80",
      // Sem isto, um total menor que a lista de notas parece erro de cálculo
      nota: foraDoTotal > 0
        ? (isMobile
            ? `${foraDoTotal} fora do total`
            : `${foraDoTotal} nota${foraDoTotal>1?"s":""} não autorizada${foraDoTotal>1?"s":""} fora do total`)
        : null },
    ...(totalIss > 0 ? [{ icon:Receipt, label:"ISS Total", value:fmtMoeda(totalIss), cor:"#f59e0b" }] : []),
    // Com uma empresa escolhida o card de contagem não diz nada sobre ela — o
    // nome já aparece no seletor logo acima.
    ...(empresaFiltro ? [] : [{ icon:Building2, label:"Empresas", value:totalEmpresas, cor:"#8b5cf6" }]),
  ];

  const S = {
    // No celular os cards eram altos demais e empurravam a lista de notas para
    // fora da tela. Compactados, os três cabem na mesma faixa de rolagem.
    // 3 cards cabem numa linha; com o de ISS são 4, e aí 2x2 fica melhor que
    // deixar um sozinho na segunda linha.
    grid: { display:"grid",
            gridTemplateColumns: isMobile ? (cards.length > 3 ? "repeat(2,1fr)" : `repeat(${cards.length},1fr)`)
                                          : `repeat(${cards.length},1fr)`,
            gap: isMobile ? 8 : 16, marginBottom: isMobile ? 18 : 28 },
    card: { background:T.card, border:"1px solid "+T.border, borderRadius:12,
            padding: isMobile ? "12px 8px" : "20px 22px", boxShadow:T.shadow,
            minWidth:0 },
  };

  return (
    <div>
      <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", justifyContent:"space-between", alignItems: isMobile ? "flex-start" : "center", marginBottom:24, gap: isMobile ? 12 : 0 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:700, color:T.text, margin:"0 0 4px" }}>Dashboard</h2>
          <p style={{ fontSize:13, color:T.textMuted, margin:0 }}>Visão geral por período</p>
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <div style={{ display:"flex", alignItems:"center", gap:4, background:T.card, border:"1px solid "+T.border, borderRadius:12, padding:"4px 6px" }}>
            <button onClick={()=>{ const idx=mesesOpcoes.findIndex(m=>m.val===mesSel); if(idx<mesesOpcoes.length-1) setMesSel(mesesOpcoes[idx+1].val); }}
              style={{ background:"none", border:"none", color:T.textMuted, cursor:"pointer", fontSize:18, padding:"2px 8px", borderRadius:8, lineHeight:1 }}>&#8249;</button>
            <span style={{ fontSize:14, fontWeight:700, color:T.text, minWidth:80, textAlign:"center" }}>
              {mesesOpcoes.find(m=>m.val===mesSel)?.label || mesSel}
            </span>
            <button onClick={()=>{ const idx=mesesOpcoes.findIndex(m=>m.val===mesSel); if(idx>0) setMesSel(mesesOpcoes[idx-1].val); }}
              disabled={mesesOpcoes.findIndex(m=>m.val===mesSel)===0}
              style={{ background:"none", border:"none", color: mesesOpcoes.findIndex(m=>m.val===mesSel)===0 ? T.textFaint : T.textMuted, cursor: mesesOpcoes.findIndex(m=>m.val===mesSel)===0 ? "default":"pointer", fontSize:18, padding:"2px 8px", borderRadius:8, lineHeight:1 }}>&#8250;</button>
          </div>
          <button onClick={()=>onNavigate("emitir")} style={{ background:"linear-gradient(135deg,#0ea5e9,#0284c7)", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer" }}>+ Nova NFS-e</button>
        </div>
      </div>
      {/* Só aparece para quem tem mais de uma empresa — com uma só não há o que separar */}
      {empresas.length > 1 && (
        <select
          style={{ width: isMobile ? "100%" : 300, background:T.input,
                   border:"1px solid " + (empresaFiltro ? "#38bdf880" : T.inputBorder),
                   borderRadius:8, padding:"10px 14px", fontSize:13, color:T.inputText,
                   outline:"none", boxSizing:"border-box", marginBottom:16 }}
          value={empresaFiltro}
          onChange={e => { setEmpresaFiltro(e.target.value); gravarEmpresaAtual(e.target.value); }}>
          <option value="">Todas as empresas ({empresas.length})</option>
          {empresas.map(e => <option key={e.id} value={e.id}>{e.razao_social}</option>)}
        </select>
      )}
      <div style={S.grid}>
        {cards.map((c,i)=>(
          <div key={i} style={S.card}>
            <c.icon size={isMobile ? 17 : 24} color={c.cor} style={{ marginBottom: isMobile ? 7 : 12 }} />
            <div style={{ fontSize: isMobile ? 10 : 12, color:T.textFaint, marginBottom: isMobile ? 3 : 6, lineHeight:1.3 }}>{c.label}</div>
            {/* Valores em reais estouram a largura num card de 1/3 de tela */}
            <div style={{ fontSize: isMobile ? 14 : 22, fontWeight:700, color:T.text, overflowWrap:"anywhere", lineHeight:1.25 }}>{c.value}</div>
            {c.nota && <div style={{ fontSize: isMobile ? 9.5 : 11, color:T.textFaint, marginTop: isMobile ? 4 : 6, lineHeight:1.35 }}>{c.nota}</div>}
          </div>
        ))}
      </div>
      <div style={{ background:T.tableBg, border:"1px solid "+T.border, borderRadius:12, overflow:"hidden", boxShadow:T.shadow }}>
        <div style={{ padding:"16px 24px", borderBottom:"1px solid "+T.border, display:"flex", justifyContent:"space-between" }}>
          <span style={{ fontSize:14, fontWeight:600, color:T.text }}>
            Últimas notas — {mesesOpcoes.find(m=>m.val===mesSel)?.label}
            {empresaFiltro && nomeEmpresa(empresaFiltro) &&
              <span style={{ color:T.textFaint, fontWeight:500 }}> · {nomeEmpresa(empresaFiltro)}</span>}
          </span>
          <button onClick={()=>onNavigate("historico")} style={{ background:"transparent", border:"1px solid "+T.border, borderRadius:6, padding:"4px 12px", fontSize:12, color:T.textFaint, cursor:"pointer" }}>Ver todas →</button>
        </div>
        {isMobile ? (
          <div>
            {notasMes.length===0 && <div style={{ padding:"40px", textAlign:"center", color:T.textFaint }}>Nenhuma nota em {mesesOpcoes.find(m=>m.val===mesSel)?.label}</div>}
            {notasMes.slice(0,5).map(n => (
              <div key={n.id} style={{ padding:"14px 16px", borderBottom:"1px solid "+T.rowBorder, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{n.tomador_razao_social || "Não informado"}</div>
                  {/* Sem empresa escolhida, é preciso dizer de quem é cada nota */}
                  {!empresaFiltro && empresas.length > 1 && nomeEmpresa(n.empresa_id) && (
                    <div style={{ fontSize:10.5, color:"#38bdf8", marginTop:2, display:"flex", alignItems:"center", gap:3 }}>
                      <Building2 size={10} />{nomeEmpresa(n.empresa_id)}
                    </div>
                  )}
                  <div style={{ fontSize:11, color:T.textFaint, marginTop:2 }}>{n.servico_descricao?.slice(0,35)}</div>
                  <div style={{ marginTop:4 }}><StatusBadge status={n.status} dark={T.isDark} /></div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"#4ade80" }}>{fmtMoeda(n.valor_servico)}</div>
                  <div style={{ fontSize:11, color:T.textFaint, marginTop:3 }}>{fmtData(n.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr>{["Tomador","Serviço","Valor","Status","Data"].map(h=><th key={h} style={{ padding:"12px 20px", fontSize:11, fontWeight:600, color:T.textFaint, textAlign:"left", borderBottom:"1px solid "+T.border, textTransform:"uppercase" }}>{h}</th>)}</tr></thead>
            <tbody>
              {notasMes.slice(0,5).map(n=>(
                <tr key={n.id}>
                  <td style={{ padding:"14px 20px", fontSize:13, color:T.text, fontWeight:500 }}>
                    {n.tomador_razao_social}
                    {/* Sem empresa escolhida, é preciso dizer de quem é cada nota */}
                    {!empresaFiltro && empresas.length > 1 && nomeEmpresa(n.empresa_id) && (
                      <div style={{ fontSize:10.5, color:"#38bdf8", marginTop:2, display:"flex", alignItems:"center", gap:3, fontWeight:500 }}>
                        <Building2 size={10} />{nomeEmpresa(n.empresa_id)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding:"14px 20px", fontSize:13, color:T.textMuted }}>{n.servico_descricao?.slice(0,30)}</td>
                  <td style={{ padding:"14px 20px", fontSize:13, color:"#4ade80", fontWeight:600 }}>{fmtMoeda(n.valor_servico)}</td>
                  <td style={{ padding:"14px 20px" }}><StatusBadge status={n.status} dark={T.isDark} /></td>
                  <td style={{ padding:"14px 20px", fontSize:13, color:T.textMuted }}>{fmtData(n.created_at)}</td>
                </tr>
              ))}
              {notasMes.length===0 && <tr><td colSpan={5} style={{ padding:"40px", textAlign:"center", color:T.textFaint }}>Nenhuma nota em {mesesOpcoes.find(m=>m.val===mesSel)?.label}</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ── Validação de CPF (dígitos verificadores) ─────────────────────────────────
function validarCPF(cpf) {
  const n = cpf.replace(/\D/g, "");
  if (n.length !== 11 || /^(\d)\1+$/.test(n)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += parseInt(n[i]) * (10 - i);
  let r = (s * 10) % 11; if (r >= 10) r = 0;
  if (r !== parseInt(n[9])) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += parseInt(n[i]) * (11 - i);
  r = (s * 10) % 11; if (r >= 10) r = 0;
  return r === parseInt(n[10]);
}

// ── Campo CPF/CNPJ unificado com auto-detecção e auto-preenchimento ──────────
function CpfCnpjInput({ S, onResult, token, onCadastrar, defaultValue = "", empresaId = "", T = {dropdownBg:"#0c1117",dropdownBorder:"#1a2332",dropdownText:"#e2e8f0",dropdownRowBorder:"#0f172a",textFaint:"#475569",cardInner:"#080c10"} }) {
  const [raw, setRaw] = useState("");
  const [tipo, setTipo] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [sugestoes, setSugestoes] = useState([]);
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);
  const timerRef = useRef(null);

  const fmtCPF = n => n.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");

  useEffect(() => {
    const handler = (e) => { if(ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!defaultValue) return;
    const limpo = limparCNPJ(defaultValue);
    const soDigitos = defaultValue.replace(/\D/g,"");
    if (/^\d+$/.test(limpo) && limpo.length === 11) {
      setRaw(fmtCPF(limpo)); setTipo("cpf");
      fetch(`${API}/pessoas?q=${encodeURIComponent(limpo)}${empresaId ? "&empresa_id="+empresaId : ""}`, { headers:{ Authorization:"Bearer "+token } })
        .then(r=>r.json()).then(lista => {
          if (!Array.isArray(lista)) return;
          const exato = lista.find(p => p.cpf.replace(/\D/g,"") === limpo);
          if (exato) {
            setAviso("banco");
            onResult({
              tipo:"cpf", doc: fmtCPF(limpo),
              razao_social: exato.nome, email: exato.email||"", fone: exato.fone||"",
              endereco: exato.endereco||"", numero: exato.numero||"", bairro: exato.bairro||"",
              municipio: exato.municipio||"", municipio_codigo: exato.municipio_codigo||"",
              uf: exato.uf||"", cep: exato.cep ? exato.cep.replace(/(\d{5})(\d{3})/, "$1-$2") : "",
            });
          }
        }).catch(()=>{});
    } else if (limpo.length === 14) {
      setRaw(fmtCNPJAlfa(limpo)); setTipo("cnpj");
    }
  }, [defaultValue]);

  const buscarNoBanco = async (texto) => {
    if (!texto || texto.length < 2) { setSugestoes([]); setAberto(false); return; }
    try {
      const r = await fetch(`${API}/pessoas?q=${encodeURIComponent(texto)}${empresaId ? "&empresa_id="+empresaId : ""}`, { headers: { Authorization: "Bearer " + token } });
      const d = await r.json();
      const lista = Array.isArray(d) ? d.slice(0, 8) : [];
      setSugestoes(lista);
      setAberto(lista.length > 0);
      // CPF completo (11 dígitos) e não encontrado no banco
      const nums = texto.replace(/\D/g,"");
      if (lista.length === 0 && nums.length === 11) {
        if (!validarCPF(nums)) {
          setAviso("cpf_invalido");
        } else {
          setAviso("nao_cadastrado");
        }
      }
    } catch { setSugestoes([]); }
  };

  const buscarCNPJ = async (cnpj) => {
    setBuscando(true); setAviso("");
    try {
      // Tenta BrasilAPI e, se estiver fora, os provedores de reserva.
      const d = await consultarCNPJ(cnpj);
      // Código IBGE do município: a nota exige e nem todo provedor devolve.
      // Quando não vier, o ViaCEP resolve a partir do CEP.
      let municipio_codigo = d.codigo_municipio_ibge || "";
      if (!municipio_codigo && d.cep.length === 8) {
        try {
          const rv = await fetch(`https://viacep.com.br/ws/${d.cep}/json/`);
          const dv = await rv.json();
          if (dv.ibge) municipio_codigo = String(dv.ibge);
        } catch { /* ignora se ViaCEP falhar */ }
      }
      onResult({
        tipo: "cnpj", doc: cnpj,
        razao_social: d.razao_social,
        email: d.email,
        fone: d.telefone ? d.telefone.replace(/(\d{2})(\d{4,5})(\d{4})/, "($1) $2-$3") : "",
        endereco: d.logradouro, numero: d.numero, bairro: d.bairro,
        municipio: d.municipio, uf: d.uf,
        cep: d.cep ? d.cep.replace(/(\d{5})(\d{3})/, "$1-$2") : "",
        municipio_codigo,
        // Natureza jurídica iniciada em 1 é Administração Pública. Serve para a
        // tela saber que a nota é compra governamental e exigir o tpOper.
        natureza_juridica: d.codigo_natureza_juridica,
      });
      setAviso("ok");
    } catch (ex) {
      // Falha de rede (sem resposta) também é indisponibilidade, não ausência.
      setAviso(ex?.message === "nao_encontrado" ? "erro" : "indisponivel");
      onResult({ tipo: "cnpj", doc: cnpj, razao_social: "", email: "", fone: "", endereco: "", numero: "", bairro: "", municipio: "", municipio_codigo: "", uf: "", cep: "" });
    }
    setBuscando(false);
  };

  const selecionarPessoa = (p) => {
    const fmt = fmtCPF(p.cpf);
    setRaw(fmt);
    setTipo("cpf");
    setAberto(false);
    setSugestoes([]);
    setAviso("banco");
    onResult({
      tipo: "cpf", doc: fmt,
      // O registro inteiro, para a emissão poder completar o cadastro depois
      pessoa: p,
      razao_social: p.nome, email: p.email || "", fone: p.fone || "",
      endereco: p.endereco || "", numero: p.numero || "", bairro: p.bairro || "",
      municipio: p.municipio || "", municipio_codigo: p.municipio_codigo || "",
      uf: p.uf || "", cep: p.cep ? p.cep.replace(/(\d{5})(\d{3})/, "$1-$2") : "",
    });
  };

  const handle = (e) => {
    const valor = e.target.value;
    // Remove só pontuação CNPJ para analisar o conteúdo
    const limpo = limparCNPJ(valor);
    // Detecta se é nome em vez de documento. Desde o CNPJ alfanumérico (2026) o
    // documento aceita letras, então uma palavra única como "VICENTE" passava a
    // ser tratada como CNPJ e recebia máscara ("VI.CEN.TE").
    // Regra decisiva: CNPJ — numérico ou alfanumérico — SEMPRE termina em dois
    // dígitos verificadores. Texto sem nenhum dígito não pode ser documento.
    const semDigito = !/\d/.test(valor);
    const ehNome = /[\s]/.test(valor)
                || /[À-ÿ]/.test(valor)
                || (semDigito && valor.trim().length >= 2)
                || (!/^[A-Z0-9.\-\/]*$/i.test(valor.trim()) && valor.trim().length > 0);

    if (ehNome) {
      setRaw(valor); setTipo(null); setAviso("");
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => buscarNoBanco(valor), 250);
      return;
    }

    // CPF: só dígitos e comprimento ≤ 11
    if (/^\d*$/.test(limpo) && limpo.length <= 11) {
      const nums = limpo.slice(0, 11);
      setRaw(fmtCPF(nums)); setTipo(nums.length > 0 ? "cpf" : null); setAviso("");
      if (nums.length >= 3) { clearTimeout(timerRef.current); timerRef.current = setTimeout(() => buscarNoBanco(nums), 250); }
      if (!nums.length) { onResult(null); setSugestoes([]); setAberto(false); }
      return;
    }

    // CNPJ numérico ou alfanumérico
    const cnpj = limpo.slice(0, 14);
    setRaw(fmtCNPJAlfa(cnpj)); setTipo(cnpj.length > 0 ? "cnpj" : null); setAviso("");
    if (cnpj.length === 14) {
      setSugestoes([]); setAberto(false);
      buscarCNPJ(cnpj);
    } else if (!cnpj.length) {
      onResult(null); setSugestoes([]); setAberto(false);
    }
  };

  const cor = aviso === "ok" || aviso === "banco" ? "#4ade80"
            : aviso === "indisponivel" ? "#fbbf24"
            : aviso === "erro" || aviso === "nao_cadastrado" || aviso === "cpf_invalido" ? "#f87171"
            : tipo === "cpf" ? "#4ade80" : tipo === "cnpj" ? "#38bdf8" : "#475569";
  const BadgeIcon = aviso === "banco" || aviso === "ok" || tipo === "cpf" || tipo === "cnpj" ? Check :
                   aviso === "erro" || aviso === "nao_cadastrado" ? AlertTriangle :
                   aviso === "indisponivel" ? AlertCircle :
                   aviso === "cpf_invalido" ? X :
                   buscando ? Loader2 : null;
  const badgeText = aviso === "banco" ? "Encontrado no cadastro" :
                aviso === "ok" ? "Dados preenchidos automaticamente" :
                aviso === "erro" ? "CNPJ não encontrado" :
                aviso === "indisponivel" ? "Consulta de CNPJ indisponível — preencha os dados manualmente" :
                aviso === "nao_cadastrado" ? "CPF não cadastrado" :
                aviso === "cpf_invalido" ? "CPF inválido" :
                buscando ? "Buscando..." :
                tipo === "cpf" ? "CPF detectado" :
                tipo === "cnpj" ? "CNPJ detectado" : null;
  const badge = badgeText ? <>{BadgeIcon && <BadgeIcon size={11} style={{verticalAlign:"middle",marginRight:3}} />}{badgeText}</> : null;

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <label style={S.label}>
        DOCUMENTO DO TOMADOR <span style={{color:"#f97316",marginLeft:3}}>*</span>
        {badge && <span style={{ color:cor, fontWeight:600, marginLeft:8, fontSize:11 }}>{badge}</span>}
      </label>
      <input
        style={{ ...S.input, borderColor: tipo ? cor+"80" : "#1a2332", transition:"border-color .2s" }}
        placeholder="CPF, CNPJ ou nome da pessoa..."
        value={raw}
        onChange={handle}
        onFocus={() => { if(sugestoes.length > 0) setAberto(true); }}
      />
      {aviso === "cpf_invalido" && (
        <div style={{ marginTop:6, padding:"8px 12px", background:"#2f0d0d", border:"1px solid #f87171", borderRadius:8, fontSize:12, color:"#fca5a5" }}>
          CPF inválido — verifique os números digitados.
        </div>
      )}
      {aviso === "nao_cadastrado" && onCadastrar && (
        <div style={{ marginTop:6, padding:"8px 12px", background:"#2f1a0d", border:"1px solid #f87171", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:12, color:"#fca5a5" }}>CPF não encontrado no cadastro</span>
          <button onMouseDown={() => onCadastrar(raw)} style={{ background:"#0ea5e9", border:"none", borderRadius:6, padding:"4px 12px", fontSize:12, color:"#fff", cursor:"pointer", fontWeight:600 }}>
            + Cadastrar
          </button>
        </div>
      )}
      {aberto && sugestoes.length > 0 && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, background:T.dropdownBg, border:"1px solid "+T.dropdownBorder, borderRadius:8, zIndex:300, maxHeight:240, overflowY:"auto", marginTop:2, boxShadow:"0 8px 32px rgba(0,0,0,0.12)" }}
          onMouseDown={e => e.stopPropagation()}>
          <div style={{ padding:"6px 12px", fontSize:11, color:T.textFaint, borderBottom:"1px solid "+T.dropdownBorder }}>Pessoas cadastradas</div>
          {sugestoes.map(p => (
            <div key={p.id} onMouseDown={() => selecionarPessoa(p)}
              style={{ padding:"10px 14px", cursor:"pointer", borderBottom:"1px solid "+T.dropdownRowBorder }}
              onMouseEnter={e => e.currentTarget.style.background="#0ea5e915"}
              onMouseLeave={e => e.currentTarget.style.background="transparent"}
            >
              <div style={{ fontSize:13, color:T.dropdownText, fontWeight:600 }}>{p.nome}</div>
              <div style={{ fontSize:11, color:T.textFaint, marginTop:2 }}>
                {fmtCPF(p.cpf)}{p.municipio && ` · ${p.municipio}${p.uf ? "/" + p.uf : ""}`}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Busca de município via API IBGE ──────────────────────────────────────────
function MunicipioInput({ value, onChange, label, S, erro = false, T = {dropdownBg:"#0c1117",dropdownBorder:"#1a2332",dropdownText:"#e2e8f0",dropdownRowBorder:"#0d1520",textFaint:"#475569"} }) {
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const timerRef = useRef(null);

  const display = value ? `${value.nome}${value.uf ? " / " + value.uf : ""} (${value.codigo})` : "";

  const buscar = (q) => {
    setQuery(q);
    if (value) onChange(null);
    clearTimeout(timerRef.current);
    if (q.length < 2) { setResultados([]); setAberto(false); return; }
    timerRef.current = setTimeout(async () => {
      setCarregando(true);
      try {
        const r = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome`);
        const todos = await r.json();
        const norm = s => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const filtrados = todos
          .filter(m => norm(m.nome).includes(norm(q)))
          .slice(0, 10)
          .map(m => ({ codigo: String(m.id), nome: m.nome, uf: m.microrregiao?.mesorregiao?.UF?.sigla || "" }));
        setResultados(filtrados);
        setAberto(filtrados.length > 0);
      } catch { setResultados([]); }
      setCarregando(false);
    }, 350);
  };

  const selecionar = (m) => { onChange(m); setQuery(m.nome + " / " + m.uf); setAberto(false); };

  return (
    <div style={{ position: "relative" }}>
      {label && <label style={S.label}>{label}</label>}
      <input
        style={{ ...S.input, borderColor: erro ? "#ef4444" : value ? "#38bdf880" : "#1a2332" }}
        placeholder="Digite o nome do município..."
        value={value ? display : query}
        onChange={e => buscar(e.target.value)}
        onFocus={() => { if (resultados.length > 0) setAberto(true); }}
        onBlur={() => setTimeout(() => setAberto(false), 200)}
        autoComplete="off"
      />
      {carregando && <span style={{ position:"absolute", right:10, top:34, fontSize:11, color:"#475569" }}>buscando...</span>}
      {aberto && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, background:T.dropdownBg, border:"1px solid "+T.dropdownBorder, borderRadius:8, zIndex:200, maxHeight:220, overflowY:"auto", marginTop:2, boxShadow:"0 8px 24px rgba(0,0,0,0.12)" }}>
          {resultados.map(m => (
            <div key={m.codigo} onMouseDown={() => selecionar(m)}
              style={{ padding:"9px 14px", fontSize:13, color:T.dropdownText, cursor:"pointer", borderBottom:"1px solid "+T.dropdownRowBorder, display:"flex", justifyContent:"space-between" }}
              onMouseEnter={e => e.currentTarget.style.background="#0ea5e915"}
              onMouseLeave={e => e.currentTarget.style.background="transparent"}>
              <span>{m.nome}</span>
              <span style={{ fontSize:11, color:T.textFaint }}>{m.uf} · {m.codigo}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Seletor mês/ano para competência ─────────────────────────────────────────
function CompetenciaInput({ value, onChange, S }) {
  const hoje = new Date();
  const [ano, setAno] = useState(value ? parseInt(value.split("-")[0]) : hoje.getFullYear());
  const [mes, setMes] = useState(value ? parseInt(value.split("-")[1]) : hoje.getMonth() + 1);
  const [aberto, setAberto] = useState(false);
  const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const mesesFull = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

  const confirmar = (m, a) => { onChange(`${a}-${String(m).padStart(2,"0")}`); setAberto(false); };

  return (
    <div style={{ position:"relative" }}>
      <label style={S.label}>COMPETÊNCIA</label>
      <div onClick={() => setAberto(o => !o)}
        style={{ ...S.input, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", userSelect:"none" }}>
        <span style={{ color: value ? "#e2e8f0" : "#475569" }}>
          {value ? `${mesesFull[mes-1]} / ${ano}` : "Selecione mês e ano"}
        </span>
        <span style={{ fontSize:10, color:"#475569" }}>▼</span>
      </div>
      {aberto && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"#0c1117", border:"1px solid #1a2332", borderRadius:8, zIndex:200, padding:16, marginTop:2 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
            <button onClick={() => setAno(a => a-1)} style={{ background:"#1a2332", border:"none", color:"#94a3b8", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:16 }}>‹</button>
            <span style={{ fontSize:14, fontWeight:700, color:"#e2e8f0" }}>{ano}</span>
            <button onClick={() => setAno(a => a+1)} style={{ background:"#1a2332", border:"none", color:"#94a3b8", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:16 }}>›</button>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6 }}>
            {meses.map((m,i) => {
              const sel = mes === i+1 && value;
              return (
                <div key={i} onClick={() => { setMes(i+1); confirmar(i+1, ano); }}
                  style={{ textAlign:"center", padding:"7px 4px", borderRadius:6, fontSize:12, fontWeight:sel?700:400, cursor:"pointer",
                    background: sel ? "linear-gradient(135deg,#0ea5e9,#0284c7)" : "#1a2332",
                    color: sel ? "#fff" : "#94a3b8" }}>
                  {m}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tela de emissão ───────────────────────────────────────────────────────────
// ============================================================
// NOVA ServicosView — substituir/adicionar antes do App()
// ============================================================

const SERVICOS_NAC = [
  {c:"01.01.01",d:"Análise e desenvolvimento de sistemas."},
  {c:"01.02.01",d:"Programação."},
  {c:"01.03.01",d:"Processamento de dados, textos, imagens, vídeos, páginas eletrônicas, aplicativos e sistemas de informação, entre outros formatos, e congêneres."},
  {c:"01.03.02",d:"Armazenamento ou hospedagem de dados, textos, imagens, vídeos, páginas eletrônicas, aplicativos e sistemas de informação, entre outros formatos, e congêneres."},
  {c:"01.04.01",d:"Elaboração de programas de computadores, inclusive de jogos eletrônicos, independentemente da arquitetura construtiva da máquina em que o programa será executado, incluindo tablets, smartphones e congêneres."},
  {c:"01.05.01",d:"Licenciamento ou cessão de direito de uso de programas de computação."},
  {c:"01.06.01",d:"Assessoria e consultoria em informática."},
  {c:"01.07.01",d:"Suporte técnico em informática, inclusive instalação, configuração e manutenção de programas de computação e bancos de dados."},
  {c:"01.08.01",d:"Planejamento, confecção, manutenção e atualização de páginas eletrônicas."},
  {c:"01.09.01",d:"Disponibilização, sem cessão definitiva, de conteúdos de áudio por meio da internet (exceto a distribuição de conteúdos pelas prestadoras de Serviço de Acesso Condicionado, de que trata a Lei nº 12.485, de 12 de setembro de 2011, sujeita ao ICMS)."},
  {c:"01.09.02",d:"Disponibilização, sem cessão definitiva, de conteúdos de vídeo, imagem e texto por meio da internet, respeitada a imunidade de livros, jornais e periódicos (exceto a distribuição de conteúdos pelas prestadoras de Serviço de Acesso Condicionado, de que trata a Lei nº 12.485, de 12 de setembro de 2011, sujeita ao ICMS)."},
  {c:"02.01.01",d:"Serviços de pesquisas e desenvolvimento de qualquer natureza."},
  {c:"03.02.01",d:"Cessão de direito de uso de marcas e de sinais de propaganda."},
  {c:"03.03.01",d:"Exploração de salões de festas, centro de convenções, stands e congêneres, para realização de eventos ou negócios de qualquer natureza."},
  {c:"03.03.02",d:"Exploração de escritórios virtuais e congêneres, para realização de eventos ou negócios de qualquer natureza."},
  {c:"03.03.03",d:"Exploração de quadras esportivas, estádios, ginásios, canchas e congêneres, para realização de eventos ou negócios de qualquer natureza."},
  {c:"03.03.04",d:"Exploração de auditórios, casas de espetáculos e congêneres, para realização de eventos ou negócios de qualquer natureza."},
  {c:"03.03.05",d:"Exploração de parques de diversões e congêneres, para realização de eventos ou negócios de qualquer natureza."},
  {c:"03.04.01",d:"Locação, sublocação, arrendamento, direito de passagem ou permissão de uso, compartilhado ou não, de ferrovia."},
  {c:"03.04.02",d:"Locação, sublocação, arrendamento, direito de passagem ou permissão de uso, compartilhado ou não, de rodovia."},
  {c:"03.04.03",d:"Locação, sublocação, arrendamento, direito de passagem ou permissão de uso, compartilhado ou não, de postes, cabos, dutos e condutos de qualquer natureza."},
  {c:"03.05.01",d:"Cessão de andaimes, palcos, coberturas e outras estruturas de uso temporário."},
  {c:"04.01.01",d:"Medicina."},
  {c:"04.01.02",d:"Biomedicina."},
  {c:"04.02.01",d:"Análises clínicas e congêneres."},
  {c:"04.02.02",d:"Patologia e congêneres."},
  {c:"04.02.03",d:"Eletricidade médica (eletroestimulação de nervos e musculos, cardioversão, etc) e congêneres."},
  {c:"04.02.04",d:"Radioterapia, quimioterapia e congêneres."},
  {c:"04.02.05",d:"Ultra-sonografia, ressonância magnética, radiologia, tomografia e congêneres."},
  {c:"04.03.01",d:"Hospitais e congêneres."},
  {c:"04.03.02",d:"Laboratórios e congêneres."},
  {c:"04.03.03",d:"Clínicas, sanatórios, manicômios, casas de saúde, prontos-socorros, ambulatórios e congêneres."},
  {c:"04.04.01",d:"Instrumentação cirúrgica."},
  {c:"04.05.01",d:"Acupuntura."},
  {c:"04.06.01",d:"Enfermagem, inclusive serviços auxiliares."},
  {c:"04.07.01",d:"Serviços farmacêuticos."},
  {c:"04.08.01",d:"Terapia ocupacional."},
  {c:"04.08.02",d:"Fisioterapia."},
  {c:"04.08.03",d:"Fonoaudiologia."},
  {c:"04.09.01",d:"Terapias de qualquer espécie destinadas ao tratamento físico, orgânico e mental."},
  {c:"04.10.01",d:"Nutrição."},
  {c:"04.11.01",d:"Obstetrícia."},
  {c:"04.12.01",d:"Odontologia."},
  {c:"04.13.01",d:"Ortóptica."},
  {c:"04.14.01",d:"Próteses sob encomenda."},
  {c:"04.15.01",d:"Psicanálise."},
  {c:"04.16.01",d:"Psicologia."},
  {c:"04.17.01",d:"Casas de repouso e congêneres."},
  {c:"04.17.02",d:"Casas de recuperação e congêneres."},
  {c:"04.17.03",d:"Creches e congêneres."},
  {c:"04.17.04",d:"Asilos e congêneres."},
  {c:"04.18.01",d:"Inseminação artificial, fertilização in vitro e congêneres."},
  {c:"04.19.01",d:"Bancos de sangue, leite, pele, olhos, óvulos, sêmen e congêneres."},
  {c:"04.20.01",d:"Coleta de sangue, leite, tecidos, sêmen, órgãos e materiais biológicos de qualquer espécie."},
  {c:"04.21.01",d:"Unidade de atendimento, assistência ou tratamento móvel e congêneres."},
  {c:"04.22.01",d:"Planos de medicina de grupo ou individual e convênios para prestação de assistência médica, hospitalar, odontológica e congêneres."},
  {c:"04.23.01",d:"Outros planos de saúde que se cumpram através de serviços de terceiros contratados, credenciados, cooperados ou apenas pagos pelo operador do plano mediante indicação do beneficiário."},
  {c:"05.01.01",d:"Medicina veterinária"},
  {c:"05.01.02",d:"Zootecnia."},
  {c:"05.02.01",d:"Hospitais e congêneres, na área veterinária."},
  {c:"05.02.02",d:"Clínicas, ambulatórios, prontos-socorros e congêneres, na área veterinária."},
  {c:"05.03.01",d:"Laboratórios de análise na área veterinária."},
  {c:"05.04.01",d:"Inseminação artificial, fertilização in vitro e congêneres."},
  {c:"05.05.01",d:"Bancos de sangue e de órgãos e congêneres."},
  {c:"05.06.01",d:"Coleta de sangue, leite, tecidos, sêmen, órgãos e materiais biológicos de qualquer espécie."},
  {c:"05.07.01",d:"Unidade de atendimento, assistência ou tratamento móvel e congêneres."},
  {c:"05.08.01",d:"Guarda, tratamento, amestramento, embelezamento, alojamento e congêneres."},
  {c:"05.09.01",d:"Planos de atendimento e assistência médico-veterinária."},
  {c:"06.01.01",d:"Barbearia, cabeleireiros, manicuros, pedicuros e congêneres."},
  {c:"06.02.01",d:"Esteticistas, tratamento de pele, depilação e congêneres."},
  {c:"06.03.01",d:"Banhos, duchas, sauna, massagens e congêneres."},
  {c:"06.04.01",d:"Ginástica, dança, esportes, natação, artes marciais e demais atividades físicas."},
  {c:"06.05.01",d:"Centros de emagrecimento, spa e congêneres."},
  {c:"06.06.01",d:"Aplicação de tatuagens, piercings e congêneres."},
  {c:"07.01.01",d:"Engenharia e congêneres."},
  {c:"07.01.02",d:"Agronomia e congêneres."},
  {c:"07.01.03",d:"Agrimensura e congêneres."},
  {c:"07.01.04",d:"Arquitetura, urbanismo e congêneres."},
  {c:"07.01.05",d:"Geologia e congêneres."},
  {c:"07.01.06",d:"Paisagismo e congêneres."},
  {c:"07.02.01",d:"Execução, por administração, de obras de construção civil, hidráulica ou elétrica e de outras obras semelhantes, inclusive sondagem, perfuração de poços, escavação, drenagem e irrigação, terraplanagem, pavimentação, concretagem e a instalação e montagem de produtos, peças e equipamentos (exceto o fornecimento de mercadorias produzidas pelo prestador de serviços fora do local da prestação dos serviços, que fica sujeito ao ICMS)."},
  {c:"07.02.02",d:"Execução, por empreitada ou subempreitada, de obras de construção civil, hidráulica ou elétrica e de outras obras semelhantes, inclusive sondagem, perfuração de poços, escavação, drenagem e irrigação, terraplanagem, pavimentação, concretagem e a instalação e montagem de produtos, peças e equipamentos (exceto o fornecimento de mercadorias produzidas pelo prestador de serviços fora do local da prestação dos serviços, que fica sujeito ao ICMS)."},
  {c:"07.03.01",d:"Elaboração de planos diretores, estudos de viabilidade, estudos organizacionais e outros, relacionados com obras e serviços de engenharia."},
  {c:"07.03.02",d:"Elaboração de anteprojetos, projetos básicos e projetos executivos para trabalhos de engenharia."},
  {c:"07.04.01",d:"Demolição."},
  {c:"07.05.01",d:"Reparação, conservação e reforma de edifícios e congêneres (exceto o fornecimento de mercadorias produzidas pelo prestador dos serviços, fora do local da prestação dos serviços, que fica sujeito ao ICMS)."},
  {c:"07.05.02",d:"Reparação, conservação e reforma de estradas, pontes, portos e congêneres (exceto o fornecimento de mercadorias produzidas pelo prestador dos serviços, fora do local da prestação dos serviços, que fica sujeito ao ICMS)."},
  {c:"07.06.01",d:"Colocação e instalação de tapetes, carpetes, cortinas e congêneres, com material fornecido pelo tomador do serviço."},
  {c:"07.06.02",d:"Colocação e instalação de assoalhos, revestimentos de parede, vidros, divisórias, placas de gesso e congêneres, com material fornecido pelo tomador do serviço."},
  {c:"07.07.01",d:"Recuperação, raspagem, polimento e lustração de pisos e congêneres."},
  {c:"07.08.01",d:"Calafetação."},
  {c:"07.09.01",d:"Varrição, coleta e remoção de lixo, rejeitos e outros resíduos quaisquer."},
  {c:"07.09.02",d:"Incineração, tratamento, reciclagem, separação e destinação final de lixo, rejeitos e outros resíduos quaisquer."},
  {c:"07.10.01",d:"Limpeza, manutenção e conservação de vias e logradouros públicos, parques, jardins e congêneres."},
  {c:"07.10.02",d:"Limpeza, manutenção e conservação de imóveis, chaminés, piscinas e congêneres."},
  {c:"07.11.01",d:"Decoração."},
  {c:"07.11.02",d:"Jardinagem, inclusive corte e poda de árvores."},
  {c:"07.12.01",d:"Controle e tratamento de efluentes de qualquer natureza e de agentes físicos, químicos e biológicos."},
  {c:"07.13.01",d:"Dedetização, desinfecção, desinsetização, imunização, higienização, desratização, pulverização e congêneres."},
  {c:"07.16.01",d:"Florestamento, reflorestamento, semeadura, adubação, reparação de solo, plantio, silagem, colheita, corte e descascamento de árvores, silvicultura, exploração florestal e dos serviços congêneres indissociáveis da formação, manutenção e colheita de florestas, para quaisquer fins e por quaisquer meios."},
  {c:"07.17.01",d:"Escoramento, contenção de encostas e serviços congêneres."},
  {c:"07.18.01",d:"Limpeza e dragagem de rios, portos, canais, baías, lagos, lagoas, represas, açudes e congêneres."},
  {c:"07.19.01",d:"Acompanhamento e fiscalização da execução de obras de engenharia, arquitetura e urbanismo."},
  {c:"07.20.01",d:"Aerofotogrametria (inclusive interpretação), cartografia, mapeamento e congêneres."},
  {c:"07.20.02",d:"Levantamentos batimétricos, geográficos, geodésicos, geológicos, geofísicos e congêneres."},
  {c:"07.20.03",d:"Levantamentos topográficos e congêneres."},
  {c:"07.21.01",d:"Pesquisa, perfuração, cimentação, mergulho, perfilagem, concretação, testemunhagem, pescaria, estimulação e outros serviços relacionados com a exploração e explotação de petróleo, gás natural e de outros recursos minerais."},
  {c:"07.22.01",d:"Nucleação e bombardeamento de nuvens e congêneres."},
  {c:"08.01.01",d:"Ensino regular pré-escolar, fundamental e médio."},
  {c:"08.01.02",d:"Ensino regular superior."},
  {c:"08.02.01",d:"Instrução, treinamento, orientação pedagógica e educacional, avaliação de conhecimentos de qualquer natureza."},
  {c:"09.01.01",d:"Hospedagem em hotéis, hotelaria marítima e congêneres (o valor da alimentação e gorjeta, quando incluído no preço da diária, fica sujeito ao Imposto Sobre Serviços)."},
  {c:"09.01.02",d:"Hospedagem em pensões, albergues, pousadas, hospedarias, ocupação por temporada com fornecimento de serviços e congêneres (o valor da alimentação e gorjeta, quando incluído no preço da diária, fica sujeito ao Imposto Sobre Serviços)."},
  {c:"09.01.03",d:"Hospedagem em motéis e congêneres (o valor da alimentação e gorjeta, quando incluído no preço da diária, fica sujeito ao Imposto Sobre Serviços)."},
  {c:"09.01.04",d:"Hospedagem em apart-service condominiais, flat, apart-hotéis, hotéis residência, residence-service, suite service e congêneres (o valor da alimentação e gorjeta, quando incluído no preço da diária, fica sujeito ao Imposto Sobre Serviços)."},
  {c:"09.02.01",d:"Agenciamento e intermediação de programas de turismo, passeios, viagens, excursões, hospedagens e congêneres."},
  {c:"09.02.02",d:"Organização, promoção e execução de programas de turismo, passeios, viagens, excursões, hospedagens e congêneres."},
  {c:"09.03.01",d:"Guias de turismo."},
  {c:"10.01.01",d:"Agenciamento, corretagem ou intermediação de câmbio."},
  {c:"10.01.02",d:"Agenciamento, corretagem ou intermediação de seguros."},
  {c:"10.01.03",d:"Agenciamento, corretagem ou intermediação de cartões de crédito."},
  {c:"10.01.04",d:"Agenciamento, corretagem ou intermediação de planos de saúde."},
  {c:"10.01.05",d:"Agenciamento, corretagem ou intermediação de planos de previdência privada."},
  {c:"10.02.01",d:"Agenciamento, corretagem ou intermediação de títulos em geral e valores mobiliários."},
  {c:"10.02.02",d:"Agenciamento, corretagem ou intermediação de contratos quaisquer."},
  {c:"10.03.01",d:"Agenciamento, corretagem ou intermediação de direitos de propriedade industrial, artística ou literária."},
  {c:"10.04.01",d:"Agenciamento, corretagem ou intermediação de contratos de arrendamento mercantil (leasing)."},
  {c:"10.04.02",d:"Agenciamento, corretagem ou intermediação de contratos de franquia (franchising)."},
  {c:"10.04.03",d:"Agenciamento, corretagem ou intermediação de faturização (factoring)."},
  {c:"10.05.01",d:"Agenciamento, corretagem ou intermediação de bens móveis ou imóveis, não abrangidos em outros itens ou subitens, por quaisquer meios."},
  {c:"10.05.02",d:"Agenciamento, corretagem ou intermediação de bens móveis ou imóveis realizados no âmbito de Bolsas de Mercadorias e Futuros, por quaisquer meios."},
  {c:"10.06.01",d:"Agenciamento marítimo."},
  {c:"10.07.01",d:"Agenciamento de notícias."},
  {c:"10.08.01",d:"Agenciamento de publicidade e propaganda, inclusive o agenciamento de veiculação por quaisquer meios."},
  {c:"10.09.01",d:"Representação de qualquer natureza, inclusive comercial."},
  {c:"10.10.01",d:"Distribuição de bens de terceiros."},
  {c:"11.01.01",d:"Guarda e estacionamento de veículos terrestres automotores."},
  {c:"11.01.02",d:"Guarda e estacionamento de aeronaves e de embarcações."},
  {c:"11.02.01",d:"Vigilância, segurança ou monitoramento de bens, pessoas e semoventes."},
  {c:"11.03.01",d:"Escolta, inclusive de veículos e cargas."},
  {c:"11.04.01",d:"Armazenamento, depósito, guarda de bens de qualquer espécie."},
  {c:"11.04.02",d:"Carga, descarga, arrumação de bens de qualquer espécie."},
  {c:"11.05.01",d:"Serviços relacionados ao monitoramento e rastreamento a distância, em qualquer via ou local, de veículos, cargas, pessoas e semoventes em circulação ou movimento, realizados por meio de telefonia móvel, transmissão de satélites, rádio ou qualquer outro meio, inclusive pelas empresas de Tecnologia da Informação Veicular, independentemente de o prestador de serviços ser proprietário ou não da infraestrutura de telecomunicações que utiliza."},
  {c:"12.01.01",d:"Espetáculos teatrais."},
  {c:"12.02.01",d:"Exibições cinematográficas."},
  {c:"12.03.01",d:"Espetáculos circenses."},
  {c:"12.04.01",d:"Programas de auditório."},
  {c:"12.05.01",d:"Parques de diversões, centros de lazer e congêneres."},
  {c:"12.06.01",d:"Boates, taxi-dancing e congêneres."},
  {c:"12.07.01",d:"Shows, ballet, danças, desfiles, bailes, óperas, concertos, recitais, festivais e congêneres."},
  {c:"12.08.01",d:"Feiras, exposições, congressos e congêneres."},
  {c:"12.09.01",d:"Bilhares."},
  {c:"12.09.02",d:"Boliches."},
  {c:"12.09.03",d:"Diversões eletrônicas ou não."},
  {c:"12.10.01",d:"Corridas e competições de animais."},
  {c:"12.11.01",d:"Competições esportivas ou de destreza física ou intelectual, com ou sem a participação do espectador."},
  {c:"12.12.01",d:"Execução de música."},
  {c:"12.13.01",d:"Produção, mediante ou sem encomenda prévia, de eventos, espetáculos, entrevistas, shows, ballet, danças, desfiles, bailes, teatros, óperas, concertos, recitais, festivais e congêneres."},
  {c:"12.14.01",d:"Fornecimento de música para ambientes fechados ou não, mediante transmissão por qualquer processo."},
  {c:"12.15.01",d:"Desfiles de blocos carnavalescos ou folclóricos, trios elétricos e congêneres."},
  {c:"12.16.01",d:"Exibição de filmes, entrevistas, musicais, espetáculos, shows, concertos, desfiles, óperas, competições esportivas, de destreza intelectual ou congêneres."},
  {c:"12.17.01",d:"Recreação e animação, inclusive em festas e eventos de qualquer natureza."},
  {c:"13.02.01",d:"Fonografia ou gravação de sons, inclusive trucagem, dublagem, mixagem e congêneres."},
  {c:"13.03.01",d:"Fotografia e cinematografia, inclusive revelação, ampliação, cópia, reprodução, trucagem e congêneres."},
  {c:"13.04.01",d:"Reprografia, microfilmagem e digitalização."},
  {c:"13.05.01",d:"Composição gráfica, inclusive confecção de impressos gráficos, fotocomposição, clicheria, zincografia, litografia e fotolitografia, exceto se destinados a posterior operação de comercialização ou industrialização, ainda que incorporados, de qualquer forma, a outra mercadoria que deva ser objeto de posterior circulação, tais como bulas, rótulos, etiquetas, caixas, cartuchos, embalagens e manuais técnicos e de instrução, quando ficarão sujeitos ao ICMS."},
  {c:"14.01.01",d:"Lubrificação, limpeza, lustração, revisão, carga e recarga, conserto, restauração, blindagem, manutenção e conservação de máquinas, veículos, aparelhos, equipamentos, motores, elevadores ou de qualquer objeto (exceto peças e partes empregadas, que ficam sujeitas ao ICMS)."},
  {c:"14.02.01",d:"Assistência técnica."},
  {c:"14.03.01",d:"Recondicionamento de motores (exceto peças e partes empregadas, que ficam sujeitas ao ICMS)."},
  {c:"14.04.01",d:"Recauchutagem ou regeneração de pneus."},
  {c:"14.05.01",d:"Restauração, recondicionamento, acondicionamento, pintura, beneficiamento, lavagem, secagem, tingimento, galvanoplastia, anodização, corte, recorte, plastificação, costura, acabamento, polimento e congêneres de objetos quaisquer."},
  {c:"14.06.01",d:"Instalação e montagem de aparelhos, máquinas e equipamentos, inclusive montagem industrial, prestados ao usuário final, exclusivamente com material por ele fornecido."},
  {c:"14.07.01",d:"Colocação de molduras e congêneres."},
  {c:"14.08.01",d:"Encadernação, gravação e douração de livros, revistas e congêneres."},
  {c:"14.09.01",d:"Alfaiataria e costura, quando o material for fornecido pelo usuário final, exceto aviamento."},
  {c:"14.10.01",d:"Tinturaria e lavanderia."},
  {c:"14.11.01",d:"Tapeçaria e reforma de estofamentos em geral."},
  {c:"14.12.01",d:"Funilaria e lanternagem."},
  {c:"14.13.01",d:"Carpintaria."},
  {c:"14.13.02",d:"Serralheria."},
  {c:"14.14.01",d:"Guincho intramunicipal."},
  {c:"14.14.02",d:"Guindaste e içamento."},
  {c:"15.01.01",d:"Administração de fundos quaisquer e congêneres."},
  {c:"15.01.02",d:"Administração de consórcio e congêneres."},
  {c:"15.01.03",d:"Administração de cartão de crédito ou débito e congêneres."},
  {c:"15.01.04",d:"Administração de carteira de clientes e congêneres."},
  {c:"15.01.05",d:"Administração de cheques pré-datados e congêneres."},
  {c:"15.02.01",d:"Abertura de conta-corrente no País, bem como a manutenção da referida conta ativa e inativa."},
  {c:"15.02.02",d:"Abertura de conta-corrente no exterior, bem como a manutenção da referida conta ativa e inativa."},
  {c:"15.02.03",d:"Abertura de conta de investimentos e aplicação no País, bem como a manutenção da referida conta ativa e inativa."},
  {c:"15.02.04",d:"Abertura de conta de investimentos e aplicação no exterior, bem como a manutenção da referida conta ativa e inativa."},
  {c:"15.02.05",d:"Abertura de caderneta de poupança no País, bem como a manutenção da referida conta ativa e inativa."},
  {c:"15.02.06",d:"Abertura de caderneta de poupança no exterior, bem como a manutenção da referida conta ativa e inativa."},
  {c:"15.02.07",d:"Abertura de contas em geral no País, não abrangida em outro subitem, bem como a manutenção das referidas contas ativas e inativas."},
  {c:"15.02.08",d:"Abertura de contas em geral no exterior, não abrangida em outro subitem, bem como a manutenção das referidas contas ativas e inativas."},
  {c:"15.03.01",d:"Locação de cofres particulares."},
  {c:"15.03.02",d:"Manutenção de cofres particulares."},
  {c:"15.03.03",d:"Locação de terminais eletrônicos."},
  {c:"15.03.04",d:"Manutenção de terminais eletrônicos."},
  {c:"15.03.05",d:"Locação de terminais de atendimento."},
  {c:"15.03.06",d:"Manutenção de terminais de atendimento."},
  {c:"15.03.07",d:"Locação de bens e equipamentos em geral."},
  {c:"15.03.08",d:"Manutenção de bens e equipamentos em geral."},
  {c:"15.04.01",d:"Fornecimento ou emissão de atestados em geral, inclusive atestado de idoneidade, atestado de capacidade financeira e congêneres."},
  {c:"15.05.01",d:"Cadastro, elaboração de ficha cadastral, renovação cadastral e congêneres."},
  {c:"15.05.02",d:"Inclusão no Cadastro de Emitentes de Cheques sem Fundos - CCF."},
  {c:"15.05.03",d:"Exclusão no Cadastro de Emitentes de Cheques sem Fundos - CCF."},
  {c:"15.05.04",d:"Inclusão em quaisquer outros bancos cadastrais."},
  {c:"15.05.05",d:"Exclusão em quaisquer outros bancos cadastrais."},
  {c:"15.06.01",d:"Emissão, reemissão e fornecimento de avisos, comprovantes e documentos em geral"},
  {c:"15.06.02",d:"Abono de firmas."},
  {c:"15.06.03",d:"Coleta e entrega de documentos, bens e valores."},
  {c:"15.06.04",d:"Comunicação com outra agência ou com a administração central."},
  {c:"15.06.05",d:"Licenciamento eletrônico de veículos."},
  {c:"15.06.06",d:"Transferência de veículos."},
  {c:"15.06.07",d:"Agenciamento fiduciário ou depositário."},
  {c:"15.06.08",d:"Devolução de bens em custódia."},
  {c:"15.07.01",d:"Acesso, movimentação, atendimento e consulta a contas em geral, por qualquer meio ou processo, inclusive por telefone, fac-símile, internet e telex."},
  {c:"15.07.02",d:"Acesso a terminais de atendimento, inclusive vinte e quatro horas."},
  {c:"15.07.03",d:"Acesso a outro banco e à rede compartilhada."},
  {c:"15.07.04",d:"Fornecimento de saldo, extrato e demais informações relativas a contas em geral, por qualquer meio ou processo."},
  {c:"15.08.01",d:"Emissão, reemissão, alteração, cessão, substituição, cancelamento e registro de contrato de crédito."},
  {c:"15.08.02",d:"Estudo, análise e avaliação de operações de crédito."},
  {c:"15.08.03",d:"Emissão, concessão, alteração ou contratação de aval, fiança, anuência e congêneres."},
  {c:"15.08.04",d:"Serviços relativos à abertura de crédito, para quaisquer fins."},
  {c:"15.09.01",d:"Arrendamento mercantil (leasing) de quaisquer bens, inclusive cessão de direitos e obrigações, substituição de garantia, alteração, cancelamento e registro de contrato, e demais serviços relacionados ao arrendamento mercantil (leasing)."},
  {c:"15.10.01",d:"Serviços relacionados a cobranças em geral, de títulos quaisquer, de contas ou carnês, de câmbio, de tributos e por conta de terceiros, inclusive os efetuados por meio eletrônico, automático ou por máquinas de atendimento."},
  {c:"15.10.02",d:"Serviços relacionados a recebimentos em geral, de títulos quaisquer, de contas ou carnês, de câmbio, de tributos e por conta de terceiros, inclusive os efetuados por meio eletrônico, automático ou por máquinas de atendimento."},
  {c:"15.10.03",d:"Serviços relacionados a pagamentos em geral, de títulos quaisquer, de contas ou carnês, de câmbio, de tributos e por conta de terceiros, inclusive os efetuados por meio eletrônico, automático ou por máquinas de atendimento."},
  {c:"15.10.04",d:"Serviços relacionados a fornecimento de posição de cobrança, recebimento ou pagamento."},
  {c:"15.10.05",d:"Serviços relacionados a emissão de carnês, fichas de compensação, impressos e documentos em geral."},
  {c:"15.11.01",d:"Devolução de títulos, protesto de títulos, sustação de protesto, manutenção de títulos, reapresentação de títulos, e demais serviços a eles relacionados."},
  {c:"15.12.01",d:"Custódia em geral, inclusive de títulos e valores mobiliários."},
  {c:"15.13.01",d:"Serviços relacionados a operações de câmbio em geral, edição, alteração, prorrogação, cancelamento e baixa de contrato de câmbio."},
  {c:"15.13.02",d:"Serviços relacionados a emissão de registro de exportação ou de crédito."},
  {c:"15.13.03",d:"Serviços relacionados a cobrança ou depósito no exterior."},
  {c:"15.13.04",d:"Serviços relacionados a emissão, fornecimento e cancelamento de cheques de viagem."},
  {c:"15.13.05",d:"Serviços relacionados a fornecimento, transferência, cancelamento e demais serviços relativos a carta de crédito de importação, exportação e garantias recebidas."},
  {c:"15.13.06",d:"Serviços relacionados a envio e recebimento de mensagens em geral relacionadas a operações de câmbio."},
  {c:"15.14.01",d:"Fornecimento, emissão, reemissão de cartão magnético, cartão de crédito, cartão de débito, cartão salário e congêneres."},
  {c:"15.14.02",d:"Renovação de cartão magnético, cartão de crédito, cartão de débito, cartão salário e congêneres."},
  {c:"15.14.03",d:"Manutenção de cartão magnético, cartão de crédito, cartão de débito, cartão salário e congêneres."},
  {c:"15.15.01",d:"Compensação de cheques e títulos quaisquer."},
  {c:"15.15.02",d:"Serviços relacionados a depósito, inclusive depósito identificado, a saque de contas quaisquer, por qualquer meio ou processo, inclusive em terminais eletrônicos e de atendimento."},
  {c:"15.16.01",d:"Emissão, reemissão, liquidação, alteração, cancelamento e baixa de ordens de pagamento, ordens de crédito e similares, por qualquer meio ou processo."},
  {c:"15.16.02",d:"Serviços relacionados à transferência de valores, dados, fundos, pagamentos e similares, inclusive entre contas em geral."},
  {c:"15.17.01",d:"Emissão e fornecimento de cheques quaisquer, avulso ou por talão."},
  {c:"15.17.02",d:"Devolução de cheques quaisquer, avulso ou por talão."},
  {c:"15.17.03",d:"Sustação, cancelamento e oposição de cheques quaisquer, avulso ou por talão."},
  {c:"15.18.01",d:"Serviços relacionados a crédito imobiliário, de avaliação e vistoria de imóvel ou obra."},
  {c:"15.18.02",d:"Serviços relacionados a crédito imobiliário, de análise técnica e jurídica."},
  {c:"15.18.03",d:"Serviços relacionados a crédito imobiliário, de emissão, reemissão, alteração, transferência e renegociação de contrato."},
  {c:"15.18.04",d:"Serviços relacionados a crédito imobiliário, de emissão e reemissão do termo de quitação."},
  {c:"15.18.05",d:"Demais serviços relacionados a crédito imobiliário."},
  {c:"16.01.01",d:"Serviços de transporte coletivo municipal rodoviário de passageiros."},
  {c:"16.01.02",d:"Serviços de transporte coletivo municipal metroviário de passageiros."},
  {c:"16.01.03",d:"Serviços de transporte coletivo municipal ferroviário de passageiros."},
  {c:"16.01.04",d:"Serviços de transporte coletivo municipal aquaviário de passageiros."},
  {c:"16.02.01",d:"Outros serviços de transporte de natureza municipal."},
  {c:"17.01.01",d:"Assessoria ou consultoria de qualquer natureza, não contida em outros itens desta lista."},
  {c:"17.01.02",d:"Análise, exame, pesquisa, coleta, compilação e fornecimento de dados e informações de qualquer natureza, inclusive cadastro e similares."},
  {c:"17.02.01",d:"Datilografia, digitação, estenografia e congêneres."},
  {c:"17.02.02",d:"Expediente, secretaria em geral, apoio e infra-estrutura administrativa e congêneres."},
  {c:"17.02.03",d:"Resposta audível e congêneres."},
  {c:"17.02.04",d:"Redação, edição, revisão e congêneres."},
  {c:"17.02.05",d:"Interpretação, tradução e congêneres."},
  {c:"17.03.01",d:"Planejamento, coordenação, programação ou organização técnica."},
  {c:"17.03.02",d:"Planejamento, coordenação, programação ou organização financeira."},
  {c:"17.03.03",d:"Planejamento, coordenação, programação ou organização administrativa."},
  {c:"17.04.01",d:"Recrutamento, agenciamento, seleção e colocação de mão-de-obra."},
  {c:"17.05.01",d:"Fornecimento de mão-de-obra, mesmo em caráter temporário, inclusive de empregados ou trabalhadores, avulsos ou temporários, contratados pelo prestador de serviço."},
  {c:"17.06.01",d:"Propaganda e publicidade, inclusive promoção de vendas, planejamento de campanhas ou sistemas de publicidade, elaboração de desenhos, textos e demais materiais publicitários."},
  {c:"17.08.01",d:"Franquia (franchising)."},
  {c:"17.09.01",d:"Perícias, laudos, exames técnicos e análises técnicas."},
  {c:"17.10.01",d:"Planejamento, organização e administração de feiras, exposições, e congêneres."},
  {c:"17.10.02",d:"Planejamento, organização e administração de congressos e congêneres."},
  {c:"17.11.01",d:"Organização de festas e recepções."},
  {c:"17.11.02",d:"Bufê (exceto o fornecimento de alimentação e bebidas, que fica sujeito ao ICMS)."},
  {c:"17.12.01",d:"Administração em geral, inclusive de bens e negócios de terceiros."},
  {c:"17.13.01",d:"Leilão e congêneres."},
  {c:"17.14.01",d:"Advocacia"},
  {c:"17.15.01",d:"Arbitragem de qualquer espécie, inclusive jurídica."},
  {c:"17.16.01",d:"Auditoria."},
  {c:"17.17.01",d:"Análise de Organização e Métodos."},
  {c:"17.18.01",d:"Atuária e cálculos técnicos de qualquer natureza."},
  {c:"17.19.01",d:"Contabilidade, inclusive serviços técnicos e auxiliares."},
  {c:"17.20.01",d:"Consultoria e assessoria econômica ou financeira."},
  {c:"17.21.01",d:"Estatística."},
  {c:"17.22.01",d:"Cobrança em geral."},
  {c:"17.23.01",d:"Assessoria, análise, avaliação, atendimento, consulta, cadastro, seleção, gerenciamento de informações, administração de contas a receber ou a pagar e em geral, relacionados a operações de faturização (factoring)."},
  {c:"17.24.01",d:"Apresentação de palestras, conferências, seminários e congêneres."},
  {c:"17.25.01",d:"Inserção de textos, desenhos e outros materiais de propaganda e publicidade, em qualquer meio (exceto em livros, jornais, periódicos e nas modalidades de serviços de radiodifusão sonora e de sons e imagens de recepção livre e gratuita)."},
  {c:"18.01.01",d:"Serviços de regulação de sinistros vinculados a contratos de seguros e congêneres."},
  {c:"18.01.02",d:"Serviços de inspeção e avaliação de riscos para cobertura de contratos de seguros e congêneres."},
  {c:"18.01.03",d:"Serviços de prevenção e gerência de riscos seguráveis e congêneres."},
  {c:"19.01.01",d:"Serviços de distribuição e venda de bilhetes e demais produtos de loteria, cartões, pules ou cupons de apostas, sorteios, prêmios, inclusive os decorrentes de títulos de capitalização e congêneres."},
  {c:"19.01.02",d:"Serviços de distribuição e venda de bingos e congêneres."},
  {c:"20.01.01",d:"Serviços portuários, ferroportuários, utilização de porto, movimentação de passageiros, reboque de embarcações, rebocador escoteiro, atracação, desatracação, serviços de praticagem, capatazia, armazenagem de qualquer natureza, serviços acessórios, movimentação de mercadorias, serviços de apoio marítimo, de movimentação ao largo, serviços de armadores, estiva, conferência, logística e congêneres."},
  {c:"20.02.01",d:"Serviços aeroportuários, utilização de aeroporto, movimentação de passageiros, armazenagem de qualquer natureza, capatazia, movimentação de aeronaves, serviços de apoio aeroportuários, serviços acessórios, movimentação de mercadorias, logística e congêneres."},
  {c:"20.03.01",d:"Serviços de terminais rodoviários, ferroviários, metroviários, movimentação de passageiros, mercadorias, inclusive suas operações, logística e congêneres."},
  {c:"21.01.01",d:"Serviços de registros públicos, cartorários e notariais."},
  {c:"22.01.01",d:"Serviços de exploração de rodovia mediante cobrança de preço ou pedágio dos usuários, envolvendo execução de serviços de conservação, manutenção, melhoramentos para adequação de capacidade e segurança de trânsito, operação, monitoração, assistência aos usuários e outros serviços definidos em contratos, atos de concessão ou de permissão ou em normas oficiais."},
  {c:"23.01.01",d:"Serviços de programação e comunicação visual e congêneres."},
  {c:"23.01.02",d:"Serviços de desenho industrial e congêneres."},
  {c:"24.01.01",d:"Serviços de chaveiros, confecção de carimbos e congêneres."},
  {c:"24.01.02",d:"Serviços de placas, sinalização visual, banners, adesivos e congêneres."},
  {c:"25.01.01",d:"Funerais, inclusive fornecimento de caixão, urna ou esquifes; aluguel de capela; transporte do corpo cadavérico; fornecimento de flores, coroas e outros paramentos; desembaraço de certidão de óbito; fornecimento de véu, essa e outros adornos; embalsamento, embelezamento, conservação ou restauração de cadáveres."},
  {c:"25.02.01",d:"Translado intramunicipal de corpos e partes de corpos cadavéricos."},
  {c:"25.02.02",d:"Cremação de corpos e partes de corpos cadavéricos."},
  {c:"25.03.01",d:"Planos ou convênio funerários."},
  {c:"25.04.01",d:"Manutenção e conservação de jazigos e cemitérios."},
  {c:"25.05.01",d:"Cessão de uso de espaços em cemitérios para sepultamento."},
  {c:"26.01.01",d:"Serviços de coleta, remessa ou entrega de correspondências, documentos, objetos, bens ou valores, inclusive pelos correios e suas agências franqueadas."},
  {c:"26.01.02",d:"Serviços de courrier e congêneres."},
  {c:"27.01.01",d:"Serviços de assistência social."},
  {c:"28.01.01",d:"Serviços de avaliação de bens e serviços de qualquer natureza."},
  {c:"29.01.01",d:"Serviços de biblioteconomia."},
  {c:"30.01.01",d:"Serviços de biologia e biotecnologia."},
  {c:"30.01.02",d:"Serviços de química."},
  {c:"31.01.01",d:"Serviços técnicos em edificações e congêneres."},
  {c:"31.01.02",d:"Serviços técnicos em eletrônica, eletrotécnica e congêneres."},
  {c:"31.01.03",d:"Serviços técnicos em mecânica e congêneres."},
  {c:"31.01.04",d:"Serviços técnicos em telecomunicações e congêneres."},
  {c:"32.01.01",d:"Serviços de desenhos técnicos."},
  {c:"33.01.01",d:"Serviços de desembaraço aduaneiro, comissários, despachantes e congêneres."},
  {c:"34.01.01",d:"Serviços de investigações particulares, detetives e congêneres."},
  {c:"35.01.01",d:"Serviços de reportagem e jornalismo."},
  {c:"35.01.02",d:"Serviços de assessoria de imprensa."},
  {c:"35.01.03",d:"Serviços de relações públicas."},
  {c:"36.01.01",d:"Serviços de meteorologia."},
  {c:"37.01.01",d:"Serviços de artistas, atletas, modelos e manequins."},
  {c:"38.01.01",d:"Serviços de museologia."},
  {c:"39.01.01",d:"Serviços de ourivesaria e lapidação (quando o material for fornecido pelo tomador do serviço)."},
  {c:"40.01.01",d:"Obras de arte sob encomenda."},
  {c:"99.01.01",d:"Serviços sem a incidência de ISSQN e ICMS"},
  // Operações sem ISSQN criadas pela Reforma do Consumo (NT 005, 007 e 009).
  // Obrigatórias a partir de 01/12/2026 para locação de imóveis e de bens
  // móveis — 01/01/2027 para optantes do Simples Nacional (Ato Conjunto
  // RFB/CGIBS nº 4/2026). Descrições conforme a tabela da NT 009.
  {c:"99.02.01",d:"Operações com Bens Imateriais Não Classificados em Itens Anteriores"},
  {c:"99.03.01",d:"Locação de Bens Imóveis"},
  {c:"99.03.02",d:"Cessão Onerosa de Bens Imóveis"},
  {c:"99.03.03",d:"Arrendamento de Bens Imóveis"},
  {c:"99.03.04",d:"Servidão, Cessão de Uso ou de Espaço de Bens Imóveis (quando não caracterizem operações tributáveis pelo ISSQN)"},
  {c:"99.03.05",d:"Permissão de Uso ou Direito de Passagem de Bens Imóveis (quando não caracterizem operações tributáveis pelo ISSQN)"},
  {c:"99.04.01",d:"Locação de Bens Móveis"},
  {c:"99.05.01",d:"Operações Condominiais (taxas e demais valores cobrados pelo condomínio edilício, desde que não caracterizem operações enquadradas em outros códigos de tributação nacional da NFS-e)"}
];

// Códigos que a SEFIN pode ainda não aceitar em produção. Enquanto não entram,
// a orientação corrente é emitir pelo 99.01.01. Avisar na tela evita a nota ser
// montada, assinada e só então recusada.
const CODIGOS_RTC_NOVOS = ["99.02.", "99.03.", "99.04.", "99.05."];
const ehCodigoRTCNovo = (c) => CODIGOS_RTC_NOVOS.some(p => String(c || "").startsWith(p));

function ServicoSearch({ value, onChange, S, T = {dropdownBg:"#0c1117",dropdownBorder:"#1a2332",dropdownText:"#e2e8f0",dropdownRowBorder:"#0f172a",cardInner:"#080c10",textFaint:"#475569"} }) {
  const servicoAtual = SERVICOS_NAC.find(s => s.c === value);
  const [query, setQuery] = useState(value || "");
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);

  const norm = (s) => s.replace(/\./g, "").replace(/^0+/, "").toLowerCase();

  const filtrados = query.length >= 1
    ? SERVICOS_NAC.filter(s =>
        norm(s.c).startsWith(norm(query)) ||
        s.c.startsWith(query) ||
        s.d.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 50)
    : [];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setAberto(false);
        if (!SERVICOS_NAC.find(s => s.c === query)) setQuery(value || "");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [query, value]);

  const selecionar = (e, s) => {
    e.preventDefault();
    e.stopPropagation();
    setQuery(s.c);
    setAberto(false);
    onChange(s.c, s.d);
  };

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <label style={S.label}>CÓDIGO DE TRIBUTAÇÃO NACIONAL</label>
      <input
        style={S.input}
        placeholder="Digite o código ex: 17.18 ou palavra-chave..."
        value={query}
        onChange={e => { setQuery(e.target.value); setAberto(true); if(!e.target.value) onChange("",""); }}
        onFocus={() => setAberto(true)}
      />
      {/* Descrição do serviço selecionado */}
      {servicoAtual && (
        <div style={{ marginTop:5, padding:"6px 10px", background:T.cardInner, borderRadius:6, border:"1px solid #0ea5e930", fontSize:11, color:"#38bdf8", lineHeight:1.4 }}>
          <Check size={11} style={{verticalAlign:"middle",marginRight:3}} /> {servicoAtual.d}
        </div>
      )}
      {servicoAtual && ehCodigoRTCNovo(servicoAtual.c) && (
        <div style={{ marginTop:5, padding:"7px 10px", background:"#fbbf2410", borderRadius:6, border:"1px solid #fbbf2440", fontSize:11, color:"#fbbf24", lineHeight:1.5 }}>
          <AlertCircle size={11} style={{verticalAlign:"middle",marginRight:3}} />
          Código da Reforma do Consumo, obrigatório a partir de 01/12/2026
          (01/01/2027 no Simples Nacional). A SEFIN pode ainda não aceitá-lo em
          produção — se a nota for recusada, emita pelo <strong>99.01.01</strong> até
          a liberação. Habilite o IBS/CBS abaixo.
        </div>
      )}
      {/* Dropdown */}
      {aberto && filtrados.length > 0 && (
        <div
          style={{ position:"absolute", top:"100%", left:0, right:0, background:T.dropdownBg, border:"1px solid "+T.dropdownBorder, borderRadius:8, zIndex:300, maxHeight:260, overflowY:"auto", marginTop:2, boxShadow:"0 8px 32px rgba(0,0,0,0.15)" }}
          onMouseDown={e => e.stopPropagation()}
        >
          {filtrados.map(s => (
            <div
              key={s.c}
              onMouseDown={e => selecionar(e, s)}
              style={{ padding:"10px 14px", fontSize:12, color:T.dropdownText, cursor:"pointer", borderBottom:"1px solid "+T.dropdownRowBorder, lineHeight:1.4 }}
              onMouseEnter={e => e.currentTarget.style.background="#0ea5e915"}
              onMouseLeave={e => e.currentTarget.style.background="transparent"}
            >
              <span style={{ color:"#38bdf8", fontWeight:600, fontFamily:"monospace" }}>{s.c}</span>
              <span style={{ color:T.textFaint, margin:"0 6px" }}>–</span>
              <span>{s.d}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Tabelas de códigos IBS/CBS ────────────────────────────────────────────────
// Tabela oficial de CST do IBS/CBS — planilha cClassTrib da SEFIN (2026-06-22).
const IBS_CST_CODES = [
  { c:"000", d:"Tributação integral" },
  { c:"010", d:"Tributação com alíquotas uniformes" },
  { c:"011", d:"Tributação com alíquotas uniformes reduzidas" },
  { c:"200", d:"Alíquota reduzida" },
  { c:"220", d:"Alíquota fixa" },
  { c:"221", d:"Alíquota fixa proporcional" },
  { c:"222", d:"Redução de base de cálculo" },
  { c:"400", d:"Isenção" },
  { c:"410", d:"Imunidade e não incidência" },
  { c:"510", d:"Diferimento" },
  { c:"515", d:"Diferimento com redução de alíquota" },
  { c:"550", d:"Suspensão" },
  { c:"620", d:"Tributação monofásica" },
  { c:"800", d:"Transferência de crédito" },
  { c:"810", d:"Ajuste de IBS na ZFM" },
  { c:"811", d:"Ajustes" },
  { c:"820", d:"Tributação em documento específico" },
  { c:"830", d:"Exclusão de base de cálculo" },
];

// Anexo C da SEFIN (v1.01) — cIndOp tem 6 dígitos: os 4 primeiros são o tipo
// de operação e os 2 últimos o critério que define o LOCAL da operação, que é
// o que determina qual município/estado recebe o IBS/CBS.
const IBS_CIND_OP_CODES = [
  { c:"020101", d:"Operação com bem imóvel, bem imaterial, inclusive direi… · Localidade do imóvel (Inc.II)" },
  { c:"020201", d:"Serviço prestado fisicamente sobre bem imóvel · Localidade do imóvel (Inc.II)" },
  { c:"020301", d:"Serviço de administração e intermediação de bem imóvel · Localidade do imóvel (Inc.II)" },
  { c:"030101", d:"Serviço prestado fisicamente sobre a pessoa ou fruído p… · Estabelecimento do fornecedor (Inc.III)" },
  { c:"030102", d:"Serviço prestado fisicamente sobre a pessoa ou fruído p… · Endereço do adquirente (Inc.III)" },
  { c:"030103", d:"Serviço prestado fisicamente sobre a pessoa ou fruído p… · Endereço do destinatário (Inc.III)" },
  { c:"030104", d:"Serviço prestado fisicamente sobre a pessoa ou fruído p… · Endereço diverso do fornecedor, adquirente… (Inc.III)" },
  { c:"040101", d:"Serviço de planejamento, organização e administração de… · Local do Evento (Inc.IV)" },
  { c:"050101", d:"Serviço prestado fisicamente sobre bem móvel material · Estabelecimento do fornecedor (Inc.V)" },
  { c:"050102", d:"Serviço prestado fisicamente sobre bem móvel material · Endereço do adquirente (Inc.V)" },
  { c:"050103", d:"Serviço prestado fisicamente sobre bem móvel material · Endereço do destinatário (Inc.V)" },
  { c:"050104", d:"Serviço prestado fisicamente sobre bem móvel material · Endereço diverso do fornecedor, adquirente… (Inc.V)" },
  { c:"050201", d:"Serviços portuários · Local da prestação (Inc.V)" },
  { c:"060101", d:"Serviço de transporte de passageiros · Local de início do transporte (Inc.VI)" },
  { c:"070101", d:"Serviço de transporte de carga · Endereço fornecido para entrega (Inc.VII)" },
  { c:"070102", d:"Serviço de transporte de carga · Local da retirada (Inc.VII)" },
  { c:"080101", d:"Serviço de exploração de via · Local da prestação, correspondente à extens… (Inc.VIII)" },
  { c:"100101", d:"Cessão de espaço para prestação de serviços publicitári… · Local do domicílio principal do adquirente (Inc.X)" },
  { c:"100102", d:"Cessão de espaço para prestação de serviços publicitári… · Local do domicílio do destinatário, nos cas… (Inc.X)" },
  { c:"100201", d:"Cessão de espaço para prestação de serviços publicitári… · Local do domicílio principal do destinatário (Inc.X)" },
  { c:"100301", d:"Demais serviços, em operações onerosas · Local do domicílio principal do adquirente (Inc.X)" },
  { c:"100302", d:"Demais serviços, em operações onerosas · Local do domicílio do destinatário, nos cas… (Inc.X)" },
  { c:"100401", d:"Demais serviços, em operações não onerosas · Local do domicílio principal do destinatário (Inc.X)" },
  { c:"100501", d:"Demais bens móveis imateriais, inclusive direitos, em o… · Local do domicílio principal do adquirente (Inc.X)" },
  { c:"100502", d:"Demais bens móveis imateriais, inclusive direitos, em o… · Local do domicílio do destinatário, nos cas… (Inc.X)" },
  { c:"100601", d:"Demais bens móveis imateriais, inclusive direitos, em o… · Local do domicílio principal do destinatário (Inc.X)" },
];

// Classificação Tributária: 6 dígitos onde os 3 PRIMEIROS são o próprio CST e os
// 3 últimos apontam o dispositivo da LC 214/2025. Por isso a lista é filtrada
// pelo CST escolhido — combinação divergente é rejeitada pela SEFIN.
const IBS_CLASS_TRIB_CODES = [
  { c:"000001", d:"Situações tributadas integralmente pelo IBS e CBS" },
  { c:"000002", d:"Exploração de via (Art. 11, VIII)" },
  { c:"000003", d:"Regime automotivo - projetos incentivados (art. 311) (Art. 311)" },
  { c:"000004", d:"Regime automotivo - projetos incentivados (art. 312) (Art. 312)" },
  { c:"000005", d:"Operação com EAC destinado à mistura com gasolina A, mas com saída do bio…" },
  { c:"010001", d:"Operações do FGTS não realizadas pela Caixa Econômica Federal" },
  { c:"010002", d:"Operações do serviço financeiro (Art. 233)" },
  { c:"011001", d:"Planos de assistência funerária (Art. 236)" },
  { c:"011002", d:"Planos de assistência à saúde (Art. 237)" },
  { c:"011003", d:"Intermediação de planos de assistência à saúde (Art. 240)" },
  { c:"011004", d:"Concursos e prognósticos (Art. 246)" },
  { c:"011005", d:"Planos de assistência à saúde de animais domésticos (Art. 243)" },
  { c:"200001", d:"Serviços de transporte de bens até as zonas de processamento de exportaçã…" },
  { c:"200002", d:"Fornecimento ou importação para produtor rural não contribuinte ou TAC" },
  { c:"200003", d:"Vendas de produtos destinados à alimentação humana (Anexo I)" },
  { c:"200004", d:"Fornecimento de dispositivos médicos (Anexo XII) (Art. 144, I)" },
  { c:"200005", d:"Fornecimento de dispositivos médicos para órgãos da administração pública…" },
  { c:"200006", d:"Situação de emergência de saúde pública reconhecida pelo Poder público" },
  { c:"200007", d:"Fornecimento dos dispositivos de acessibilidade próprios para pessoas com…" },
  { c:"200008", d:"Fornecimento dos dispositivos de acessibilidade próprios para pessoas com…" },
  { c:"200009", d:"Fornecimento dos medicamentos registrados na Anvisa (Art. 146)" },
  { c:"200010", d:"Fornecimento dos medicamentos registrados na Anvisa, adquiridos por órgão…" },
  { c:"200011", d:"Fornecimento das composições para nutrição enteral e parenteral quando ad…" },
  { c:"200012", d:"Situação de emergência de saúde pública reconhecida pelo Poder público" },
  { c:"200013", d:"Fornecimento de tampões higiênicos, absorventes higiênicos internos ou ex…" },
  { c:"200014", d:"Fornecimento dos produtos hortícolas, frutas e ovos (Anexo XV)" },
  { c:"200015", d:"Venda de automóveis de passageiros de fabricação nacional adquiridos por…" },
  { c:"200016", d:"Prestação de serviços de pesquisa e desenvolvimento por Instituição Cient…" },
  { c:"200017", d:"Operações relacionadas ao FGTS (Art. 212, § 3º, I)" },
  { c:"200018", d:"Operações de resseguro e retrocessão (Art. 223, § 4º)" },
  { c:"200019", d:"Importador dos serviços financeiros contribuinte (Art. 231, § 1º, II)" },
  { c:"200020", d:"Operação praticada por sociedades cooperativas optantes por regime especí…" },
  { c:"200021", d:"Serviços de transporte público coletivo de passageiros ferroviário e hidr…" },
  { c:"200022", d:"Operação originada fora da ZFM que destine bem material industrializado a…" },
  { c:"200023", d:"Operação realizada por indústria incentivada que destine bem material int…" },
  { c:"200024", d:"Operação originada fora das Áreas de Livre Comércio destinadas a contribu…" },
  { c:"200025", d:"Fornecimento dos serviços de educação relacionados ao Programa Universida…" },
  { c:"200026", d:"Locação de imóveis localizados nas zonas reabilitadas (Art. 158, parágrafo ú…)" },
  { c:"200027", d:"Operações de locação, cessão onerosa e arrendamento de bens imóveis" },
  { c:"200028", d:"Fornecimento dos serviços de educação (Anexo II) (Art. 129)" },
  { c:"200029", d:"Fornecimento dos serviços de saúde humana (Anexo III) (Art. 130)" },
  { c:"200030", d:"Venda dos dispositivos médicos (Anexo IV) (Art. 131)" },
  { c:"200031", d:"Fornecimento dos dispositivos de acessibilidade próprios para pessoas com…" },
  { c:"200032", d:"Fornecimento dos medicamentos registrados na Anvisa ou produzidos por far…" },
  { c:"200033", d:"Fornecimento das composições para nutrição enteral e parenteral (Anexo VI)" },
  { c:"200034", d:"Fornecimento dos alimentos destinados ao consumo humano (Anexo VII)" },
  { c:"200035", d:"Fornecimento dos produtos de higiene pessoal e limpeza (Anexo VIII)" },
  { c:"200036", d:"Fornecimento de produtos agropecuários, aquícolas, pesqueiros, florestais…" },
  { c:"200037", d:"Fornecimento de serviços ambientais de conservação ou recuperação da vege…" },
  { c:"200038", d:"Fornecimento dos insumos agropecuários e aquícolas (Anexo IX)" },
  { c:"200039", d:"Fornecimento dos bens e serviços relacionados com produções nacionais art…" },
  { c:"200040", d:"Fornecimento de serviços de comunicação institucional à administração púb…" },
  { c:"200041", d:"Fornecimento de serviço de educação desportiva (art. 141. I)" },
  { c:"200042", d:"Fornecimento de serviço de gestão e exploração do desporto (art. 141. II)" },
  { c:"200043", d:"Fornecimento à administração pública dos serviços e dos bens relativos à…" },
  { c:"200044", d:"Operações e prestações de serviços de segurança da informação e segurança…" },
  { c:"200045", d:"Operações relacionadas a projetos de reabilitação urbana de zonas históri…" },
  { c:"200046", d:"Operações com bens imóveis (Art. 261)" },
  { c:"200047", d:"Bares e Restaurantes (Art. 275)" },
  { c:"200048", d:"Hotelaria, Parques de Diversão e Parques Temáticos (Art. 281)" },
  { c:"200049", d:"Transporte coletivo de passageiros rodoviário, ferroviário e hidroviário" },
  { c:"200050", d:"Serviços de transporte aéreo regional coletivo de passageiros ou de carga" },
  { c:"200051", d:"Agências de Turismo (Art. 289, II)" },
  { c:"200052", d:"Prestação de serviços de profissões intelectuais (Art. 127, I a XVIII)" },
  { c:"200053", d:"Fornecimento de medicamentos registrados na Anvisa, quando classificados…" },
  { c:"200054", d:"Fornecimento de bem material pela cooperativa de produção agropecuária a…" },
  { c:"220001", d:"Incorporação imobiliária submetida ao regime especial de tributação" },
  { c:"220002", d:"Incorporação imobiliária submetida ao regime especial de tributação" },
  { c:"220003", d:"Alienação de imóvel decorrente de parcelamento do solo (Art. 486)" },
  { c:"221001", d:"Locação, cessão onerosa ou arrendamento de bem imóvel com alíquota sobre…" },
  { c:"221002", d:"Incorporação imobiliária submetida ao regime especial de tributação" },
  { c:"221003", d:"Incorporação imobiliária submetida ao regime especial de tributação" },
  { c:"221004", d:"Alienação de imóvel decorrente de parcelamento do solo (Art. 486)" },
  { c:"222001", d:"Transporte internacional de passageiros, caso os trechos de ida e volta s…" },
  { c:"400001", d:"Fornecimento de serviços de transporte público coletivo de passageiros ro…" },
  { c:"400002", d:"Fornecimento de serviços de transporte público coletivo de passageiros ro…" },
  { c:"410001", d:"Fornecimento de bonificações quando constem no documento fiscal e que não…" },
  { c:"410002", d:"Transferências entre estabelecimentos pertencentes ao mesmo contribuinte" },
  { c:"410003", d:"Doações sem contraprestação em benefício do doador (Art. 6º, VIII)" },
  { c:"410004", d:"Exportações de bens e serviços (Art. 8º)" },
  { c:"410005", d:"Fornecimentos realizados pela União, pelos Estados, pelo Distrito Federal…" },
  { c:"410006", d:"Fornecimentos realizados por entidades religiosas e templos de qualquer c…" },
  { c:"410007", d:"Fornecimentos realizados por partidos políticos, entidades sindicais e in…" },
  { c:"410008", d:"Fornecimentos de livros, jornais, periódicos e do papel destinado a sua i…" },
  { c:"410009", d:"Fornecimentos de fonogramas e videofonogramas musicais produzidos no Bras…" },
  { c:"410010", d:"Fornecimentos de serviço de comunicação nas modalidades de radiodifusão s…" },
  { c:"410011", d:"Fornecimentos de ouro, quando definido em lei como ativo financeiro ou in…" },
  { c:"410012", d:"Fornecimento de condomínio edilício não optante pelo regime regular" },
  { c:"410013", d:"Exportações de combustíveis (Art. 98)" },
  { c:"410014", d:"Fornecimento de produtor rural não contribuinte (Art. 164)" },
  { c:"410015", d:"Fornecimento por transportador autônomo não contribuinte (Art. 169)" },
  { c:"410016", d:"Fornecimento ou aquisição de resíduos sólidos (Art. 170)" },
  { c:"410017", d:"Aquisição de bem móvel com crédito presumido sob condição de revenda real…" },
  { c:"410018", d:"Operações relacionadas aos fundos garantidores e executores de políticas…" },
  { c:"410019", d:"Exclusão da gorjeta na base de cálculo no fornecimento de alimentação" },
  { c:"410020", d:"Exclusão do valor de intermediação na base de cálculo no fornecimento de…" },
  { c:"410021", d:"Contribuição de que trata o art. 149-A da Constituição Federal" },
  { c:"410022", d:"Consolidação da propriedade do bem pelo credor (Art. 200, I)" },
  { c:"410023", d:"Alienação de bens móveis ou imóveis que tenham sido objeto de garantia em…" },
  { c:"410024", d:"Consolidação da propriedade do bem pelo grupo de consórcio (Art. 204, § 3º, I)" },
  { c:"410025", d:"Alienação de bem que tenha sido objeto de garantia em que o prestador da…" },
  { c:"410026", d:"Doação com anulação de crédito (Art. 6º, § 2º II)" },
  { c:"410027", d:"Exportação de serviço ou de bem imaterial (Art. 80 II)" },
  { c:"410028", d:"Operações com bens imóveis realizadas por pessoas físicas não considerada…" },
  { c:"410029", d:"Operações acobertadas somente pelo ICMS (Art. 4º)" },
  { c:"410030", d:"Estorno de crédito por perecimento, deteriorização, roubo, furto ou extra…" },
  { c:"410031", d:"Fornecimento em período anterior ao início de vigência de incidências de…" },
  { c:"410032", d:"Tributos incidentes na operação que não integram a base de cálculo do IBS…" },
  { c:"410033", d:"Operações de Fundos de Investimento Imobiliário (FII) e Fundos de Investi…" },
  { c:"410034", d:"Operações de fundos de investimento (Art. 26, § 5º-A, III)" },
  { c:"410035", d:"Fornecimento realizado por nanoempreendedor (Art. 26, IV)" },
  { c:"410036", d:"Descontos incondicionais (Art. 12, §2º III)" },
  { c:"410037", d:"Importação os bens materiais sem incidência de IBS e CBS (Art. 66, I,III e VII)" },
  { c:"410999", d:"Operações não onerosas sem previsão de tributação, não especificadas ante…" },
  { c:"510001", d:"Operações, sujeitas a diferimento, com energia elétrica, relativas à impo…" },
  { c:"515001", d:"Operações, sujeitas a diferimento, com insumos agropecuários e aquícolas…" },
  { c:"550001", d:"Exportações de bens materiais (Art. 82)" },
  { c:"550002", d:"Regime de Trânsito (Art. 84)" },
  { c:"550003", d:"Regimes de Depósito (art. 85) (Art. 85)" },
  { c:"550004", d:"Regimes de Depósito (art. 87) (Art. 87)" },
  { c:"550005", d:"Regimes de Depósito (art. 87, Parágrafo único) (Art. 87, parágrafo ún…)" },
  { c:"550006", d:"Regimes de Permanência Temporária (Art. 88)" },
  { c:"550007", d:"Regimes de Aperfeiçoamento (Art. 90)" },
  { c:"550008", d:"Importação de bens para o Regime de Repetro-Temporário (Art. 93, I)" },
  { c:"550009", d:"GNL-Temporário (Art. 93, II)" },
  { c:"550010", d:"Repetro-Permanente (Art. 93, III)" },
  { c:"550011", d:"Repetro-Industrialização (Art. 93, IV)" },
  { c:"550012", d:"Repetro-Nacional (Art. 93, V)" },
  { c:"550013", d:"Repetro-Entreposto (Art. 93, VI)" },
  { c:"550014", d:"Zona de Processamento de Exportação (Arts. 99, 100 e 102)" },
  { c:"550015", d:"Regime Tributário para Incentivo à Modernização e à Ampliação da Estrutur…" },
  { c:"550016", d:"Regime Especial de Incentivos para o Desenvolvimento da Infraestrutura" },
  { c:"550017", d:"Regime Tributário para Incentivo à Atividade Naval - Renaval (Art. 107, I)" },
  { c:"550018", d:"Desoneração da aquisição de bens de capital (Art. 109)" },
  { c:"550019", d:"Importação de bem material por indústria incentivada para utilização na Z…" },
  { c:"550020", d:"Áreas de livre comércio (Art. 461)" },
  { c:"550021", d:"Industrialização destinada a exportações (Art. 82, § 11)" },
  { c:"550022", d:"Regime Especial de Incentivos para a Produção de Hidrogênio de Baixa Emis…" },
  { c:"550023", d:"Operações com hidrocarbonetos líquidos derivados de petróleo não combustí…" },
  { c:"550024", d:"Regime Tributário para Incentivo à Atividade Naval - Renaval (Art. 107, I…" },
  { c:"550025", d:"Regime Tributário para Incentivo à Atividade Naval - Renaval (Art. 107, I…" },
  { c:"620001", d:"Tributação monofásica sobre combustíveis (Art. 172)" },
  { c:"620002", d:"Tributação monofásica com responsabilidade pela retenção sobre combustíve…" },
  { c:"620003", d:"Tributação monofásica com responsabilidade de retenção de tributos por te…" },
  { c:"620004", d:"Tributação monofásica sobre mistura de EAC com gasolina A em percentual s…" },
  { c:"620005", d:"Tributação monofásica sobre mistura de EAC com gasolina A em percentual i…" },
  { c:"620006", d:"Tributação monofásica sobre combustíveis cobrada anteriormente" },
  { c:"620007", d:"Perecimento, deteriorização, roubo, furto ou extravio no regime monofásico" },
  { c:"800001", d:"Fusão, cisão ou incorporação (Art. 55)" },
  { c:"800002", d:"Transferência de crédito do associado, inclusive as cooperativas singular…" },
  { c:"810001", d:"Crédito presumido de IBS sobre o valor apurado nos fornecimentos a partir…" },
  { c:"811001", d:"Anulação de Crédito por Saídas Imunes/Isentas (Art. 51)" },
  { c:"811002", d:"Débitos de notas fiscais não processadas na apuração (Art. 45)" },
  { c:"811003", d:"Desenquadramento do Simples Nacional (Art. 41)" },
  { c:"820001", d:"Documento com informações de fornecimento de serviços de planos de assist…" },
  { c:"820002", d:"Documento com informações de fornecimento de serviços de planos de assist…" },
  { c:"820003", d:"Documento com informações de fornecimento de serviços de planos de assist…" },
  { c:"820004", d:"Documento com informações de prestação de serviços de consursos de prognó…" },
  { c:"820005", d:"Documento com informações de alienação de bens imóveis (Art. 254, § 1º)" },
  { c:"820006", d:"Documento com informações de fornecimento de serviços de exploração de via" },
  { c:"820007", d:"Documento com informações de fornecimento de serviços financeiros" },
  { c:"820008", d:"Documento com informações de fornecimento de serviço continuado, mas com…" },
  { c:"820009", d:"Cobrança relativa a fornecimentos declarados em outro documento" },
  { c:"830001", d:"Documento com exclusão da BC da CBS e do IBS de energia elétrica fornecid…" },
];

// ── Autocomplete genérico para códigos IBS/CBS ─────────────────────────────
function IbsCbsCodigoInput({ value, onChange, codigos, label, sublabel, placeholder, maxLength, obrigatorio = false, S, T = {dropdownBg:"#0c1117",dropdownBorder:"#1a2332",dropdownText:"#e2e8f0",dropdownRowBorder:"#0f172a",cardInner:"#080c10",textFaint:"#475569"} }) {
  const atual = codigos.find(n => n.c === value);
  const [query, setQuery] = useState(value || "");
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);
  const blurTimer = useRef(null);

  const digits = query.replace(/\D/g,"");
  const filtrados = query.length >= 1
    ? codigos.filter(n =>
        (digits && n.c.startsWith(digits)) ||
        n.d.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 30)
    : [];

  const fechar = () => setAberto(false);

  const selecionar = (e, n) => {
    e.preventDefault(); e.stopPropagation();
    clearTimeout(blurTimer.current);
    setQuery(n.c); setAberto(false); onChange(n.c);
  };

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <label style={S.label}>{label}{obrigatorio && " *"}</label>
      <input
        style={S.input}
        placeholder={placeholder}
        maxLength={/\D/.test(query) ? undefined : maxLength}
        value={query}
        onChange={e => { const v = e.target.value; const d = v.replace(/\D/g,""); setQuery(v); setAberto(v.length > 0); onChange(d); }}
        onFocus={() => { if (query.length > 0) setAberto(true); }}
        onBlur={() => { blurTimer.current = setTimeout(fechar, 150); }}
        onKeyDown={e => { if (e.key === "Escape") fechar(); }}
      />
      {sublabel && <div style={{ fontSize:10, color:T.textFaint, marginTop:2 }}>{sublabel}</div>}
      {atual && !aberto && (
        <div style={{ marginTop:4, padding:"5px 10px", background:T.cardInner, borderRadius:6, border:"1px solid #0ea5e930", fontSize:11, color:"#38bdf8", lineHeight:1.4 }}>
          <Check size={11} style={{verticalAlign:"middle",marginRight:3}} /> {atual.d}
        </div>
      )}
      {aberto && filtrados.length > 0 && (
        <div
          style={{ position:"absolute", top:"100%", left:0, right:0, background:T.dropdownBg, border:"1px solid "+T.dropdownBorder, borderRadius:8, zIndex:300, maxHeight:220, overflowY:"auto", marginTop:2, boxShadow:"0 8px 32px rgba(0,0,0,0.18)" }}
          onMouseDown={e => { e.preventDefault(); clearTimeout(blurTimer.current); }}
        >
          {filtrados.map(n => (
            <div
              key={n.c}
              onMouseDown={e => selecionar(e, n)}
              style={{ padding:"9px 14px", fontSize:12, color:T.dropdownText, cursor:"pointer", borderBottom:"1px solid "+T.dropdownRowBorder, lineHeight:1.4 }}
              onMouseEnter={e => e.currentTarget.style.background="#0ea5e915"}
              onMouseLeave={e => e.currentTarget.style.background="transparent"}
            >
              <span style={{ color:"#38bdf8", fontWeight:700, fontFamily:"monospace" }}>{n.c}</span>
              <span style={{ color:T.textFaint, margin:"0 6px" }}>–</span>
              <span>{n.d}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Autocomplete de Código NBS ────────────────────────────────────────────────
function NbsSearch({ value, onChange, S, T = {dropdownBg:"#0c1117",dropdownBorder:"#1a2332",dropdownText:"#e2e8f0",dropdownRowBorder:"#0f172a",cardInner:"#080c10",textFaint:"#475569"} }) {
  const atual = NBS_CODES.find(n => n.c === value);
  const [query, setQuery] = useState(value || "");
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);

  const nbsDigits = query.replace(/\D/g,"");
  const filtrados = query.length >= 2
    ? NBS_CODES.filter(n =>
        (nbsDigits.length > 0 && n.c.startsWith(nbsDigits)) ||
        (nbsDigits.length === 0 && n.d.toLowerCase().includes(query.toLowerCase()))
      ).slice(0, 60)
    : [];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setAberto(false);
        // Se não é código válido na lista, mantém como texto livre
        if (!NBS_CODES.find(n => n.c === query.replace(/\D/g,""))) {
          onChange(query.replace(/\D/g,"").slice(0,9));
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [query, value]);

  const selecionar = (e, n) => {
    e.preventDefault(); e.stopPropagation();
    setQuery(n.c); setAberto(false); onChange(n.c);
  };

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <label style={S.label}>CÓDIGO NBS <span style={{ color:T.textFaint, fontWeight:400, marginLeft:4 }}>(opcional)</span></label>
      <input
        style={S.input}
        placeholder="Digite o código ou palavra-chave..."
        value={query}
        onChange={e => { setQuery(e.target.value); setAberto(true); if (!e.target.value) onChange(""); }}
        onFocus={() => { if (query.length >= 2) setAberto(true); }}
      />
      {atual && (
        <div style={{ marginTop:5, padding:"6px 10px", background:T.cardInner, borderRadius:6, border:"1px solid #0ea5e930", fontSize:11, color:"#38bdf8", lineHeight:1.4 }}>
          <Check size={11} style={{verticalAlign:"middle",marginRight:3}} /> {atual.d}
        </div>
      )}
      {aberto && filtrados.length > 0 && (
        <div
          style={{ position:"absolute", top:"100%", left:0, right:0, background:T.dropdownBg, border:"1px solid "+T.dropdownBorder, borderRadius:8, zIndex:300, maxHeight:260, overflowY:"auto", marginTop:2, boxShadow:"0 8px 32px rgba(0,0,0,0.15)" }}
          onMouseDown={e => e.stopPropagation()}
        >
          {filtrados.map(n => (
            <div
              key={n.c}
              onMouseDown={e => selecionar(e, n)}
              style={{ padding:"10px 14px", fontSize:12, color:T.dropdownText, cursor:"pointer", borderBottom:"1px solid "+T.dropdownRowBorder, lineHeight:1.4 }}
              onMouseEnter={e => e.currentTarget.style.background="#0ea5e915"}
              onMouseLeave={e => e.currentTarget.style.background="transparent"}
            >
              <span style={{ color:"#38bdf8", fontWeight:600, fontFamily:"monospace" }}>{n.c}</span>
              <span style={{ color:T.textFaint, margin:"0 6px" }}>–</span>
              <span>{n.d}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Importar cadastro a partir do XML de uma nota já emitida ────────────────
// O XML de qualquer NFS-e carrega a DPS, e nela está tudo o que o cadastro
// contribuiu na emissão: código do serviço, NBS, alíquota, regime tributário,
// PIS/COFINS, IBS/CBS e o tomador. Em vez de redigitar, lê-se de lá.
function ImportarXMLModal({ token, empresaId, onClose, onImportado, T, S, isMobile }) {
  const [modo, setModo] = useState("arquivo");     // "arquivo" | "chave"
  const [xml, setXml] = useState("");
  const [nomeArquivo, setNomeArquivo] = useState("");
  const [chave, setChave] = useState("");
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [criarServico, setCriarServico] = useState(true);
  const [criarTomador, setCriarTomador] = useState(true);

  const h = { "Content-Type":"application/json", Authorization:"Bearer "+token };

  const chamar = async (flags = {}) => {
    setErro(""); setCarregando(true);
    try {
      const r = await fetch(`${API}/importar-xml`, { method:"POST", headers:h,
        body: JSON.stringify({ empresa_id: empresaId, xml: xml || null,
                               chave: chave || null, ...flags }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErro(d.detail || `Erro ${r.status}`); return null; }
      return d;
    } catch (e) { setErro("Não foi possível falar com o servidor: " + e.message); return null; }
    finally { setCarregando(false); }
  };

  const analisar = async () => {
    const d = await chamar();
    if (!d) return;
    setPrevia(d);
    // Nada a criar quando já existe — evita o usuário confirmar e nada acontecer.
    setCriarServico(!d.servico?.ja_cadastrado);
    setCriarTomador(!!d.tomador?.cadastravel && !d.tomador?.ja_cadastrado);
  };

  const confirmar = async () => {
    const d = await chamar({ criar_servico: criarServico, criar_tomador: criarTomador });
    if (!d) return;
    const feito = [];
    if (d.criados?.servico_id) feito.push("serviço");
    if (d.criados?.pessoa_id) feito.push("tomador");
    alert(feito.length ? `Importado: ${feito.join(" e ")}.` : "Nada foi criado — já estava tudo cadastrado.");
    onImportado();
  };

  const lerArquivo = (f) => {
    if (!f) return;
    setNomeArquivo(f.name); setPrevia(null); setErro("");
    const fr = new FileReader();
    fr.onload = () => setXml(String(fr.result || ""));
    fr.onerror = () => setErro("Não foi possível ler o arquivo.");
    fr.readAsText(f, "utf-8");
  };

  const sv = previa?.servico, tm = previa?.tomador;
  const linha = (rot, val) => (
    <div style={{ display:"flex", gap:10, fontSize:12.5, padding:"3px 0" }}>
      <span style={{ color:T.textFaint, minWidth:112, flexShrink:0 }}>{rot}</span>
      <span style={{ color:T.text, wordBreak:"break-word" }}>{val || "—"}</span>
    </div>
  );

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom: isMobile ? 64 : 0, background:"rgba(0,0,0,0.75)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:16 }}
      onClick={() => { if (!carregando) onClose(); }}>
      <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:16, width:"100%", maxWidth:560, maxHeight:"88vh", overflowY:"auto", boxShadow:T.shadowHover }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding:"20px 24px 0" }}>
          <h3 style={{ margin:"0 0 4px", color:T.text, fontSize:16, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}>
            <Upload size={16} color="#38bdf8" /> Importar de XML
          </h3>
          <p style={{ margin:"0 0 16px", fontSize:12.5, color:T.textMuted, lineHeight:1.5 }}>
            Envie o XML de uma NFS-e que esta empresa já emitiu. Dele saem o serviço
            (código, tributação, IBS/CBS) e o tomador, sem digitar cadastro.
          </p>
        </div>

        {!previa && (
          <div style={{ padding:"0 24px" }}>
            <div style={{ display:"flex", gap:8, marginBottom:14 }}>
              {[["arquivo","Enviar arquivo"],["chave","Informar a chave"]].map(([v,rot]) => (
                <button key={v} onClick={()=>{ setModo(v); setErro(""); }}
                  style={{ flex:1, background: modo===v ? "#38bdf815" : "transparent",
                           border:"1px solid " + (modo===v ? "#38bdf880" : T.border),
                           color: modo===v ? "#38bdf8" : T.textFaint,
                           borderRadius:8, padding:"8px 12px", fontSize:12.5, fontWeight:600, cursor:"pointer" }}>
                  {rot}
                </button>
              ))}
            </div>

            {modo === "arquivo" ? (
              <div>
                <input type="file" accept=".xml,text/xml,application/xml"
                  onChange={e=>lerArquivo(e.target.files?.[0])}
                  style={{ ...S.input, padding:"8px 12px" }} />
                {nomeArquivo && xml && (
                  <div style={{ fontSize:11, color:"#4ade80", marginTop:6 }}>
                    <Check size={11} style={{verticalAlign:"middle",marginRight:3}} />
                    {nomeArquivo} ({(xml.length/1024).toFixed(1)} KB)
                  </div>
                )}
              </div>
            ) : (
              <div>
                <input value={chave} onChange={e=>{ setChave(e.target.value.replace(/\D/g,"").slice(0,50)); setPrevia(null); }}
                  placeholder="Chave de acesso (50 dígitos)" style={{ ...S.input, fontFamily:"monospace", fontSize:12 }} />
                <div style={{ fontSize:11, color:T.textFaint, marginTop:5, lineHeight:1.5 }}>
                  O sistema baixa o XML na SEFIN com o certificado desta empresa.
                  {chave && chave.length !== 50 && <> · <span style={{ color:"#fbbf24" }}>{chave.length}/50 dígitos</span></>}
                </div>
              </div>
            )}
          </div>
        )}

        {previa && (
          <div style={{ padding:"0 24px" }}>
            <div style={{ fontSize:10.5, color:T.textFaint, textTransform:"uppercase", fontWeight:700, letterSpacing:.5, margin:"4px 0 6px" }}>
              Nota lida
            </div>
            {linha("NFS-e nº", previa.nota?.numero_nfse)}
            {linha("Emitida em", previa.nota?.data_emissao ? fmtData(previa.nota.data_emissao) : null)}
            {linha("Valor", fmtMoeda(previa.nota?.valor_servico))}

            <div style={{ marginTop:16, padding:"12px 14px", borderRadius:10,
                          border:"1px solid " + (sv?.ja_cadastrado ? T.border : "#4ade8040"),
                          background: sv?.ja_cadastrado ? "transparent" : "#4ade8008" }}>
              <label style={{ display:"flex", alignItems:"center", gap:8, cursor: sv?.ja_cadastrado ? "default" : "pointer", marginBottom:8 }}>
                <input type="checkbox" checked={criarServico} disabled={sv?.ja_cadastrado}
                  onChange={e=>setCriarServico(e.target.checked)} />
                <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Serviço</span>
                {sv?.ja_cadastrado && (
                  <span style={{ fontSize:10.5, color:"#fbbf24", background:"#fbbf2415", border:"1px solid #fbbf2430", borderRadius:20, padding:"2px 8px" }}>
                    já cadastrado como “{sv.ja_cadastrado_como}”
                  </span>
                )}
              </label>
              {linha("Descrição", sv?.descricao)}
              {linha("LC 116 / NBS", [sv?.codigo_lc116, sv?.codigo_nbs].filter(Boolean).join(" · "))}
              {linha("Município", sv?.municipio_incidencia_nome)}
              {linha("ISS", sv?.aliquota_iss != null ? `${sv.aliquota_iss}%` : "definida pelo município")}
              {linha("ISS retido", sv?.iss_retido ? "sim" : "não")}
              {linha("Simples Nac.", { 1:"1 – Não optante", 2:"2 – MEI", 3:"3 – ME/EPP" }[sv?.op_simp_nac] || sv?.op_simp_nac)}
              {linha("PIS/COFINS", `CST ${sv?.cst_pis_cofins} · tpRet ${sv?.tp_ret_pis_cofins_csll}`)}
              {linha("IBS/CBS", sv?.ibscbs_habilitado
                ? `habilitado · cIndOp ${sv.ibscbs_c_ind_op || "—"} · CST ${sv.ibscbs_cst || "—"}`
                : "não habilitado nesta nota")}
            </div>

            <div style={{ marginTop:12, padding:"12px 14px", borderRadius:10,
                          border:"1px solid " + (tm?.cadastravel && !tm?.ja_cadastrado ? "#4ade8040" : T.border),
                          background: tm?.cadastravel && !tm?.ja_cadastrado ? "#4ade8008" : "transparent" }}>
              <label style={{ display:"flex", alignItems:"center", gap:8, cursor: tm?.cadastravel && !tm?.ja_cadastrado ? "pointer" : "default", marginBottom:8 }}>
                <input type="checkbox" checked={criarTomador}
                  disabled={!tm?.cadastravel || tm?.ja_cadastrado}
                  onChange={e=>setCriarTomador(e.target.checked)} />
                <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Tomador</span>
                {tm?.ja_cadastrado && (
                  <span style={{ fontSize:10.5, color:"#fbbf24", background:"#fbbf2415", border:"1px solid #fbbf2430", borderRadius:20, padding:"2px 8px" }}>
                    já cadastrado
                  </span>
                )}
              </label>
              {!tm ? (
                <div style={{ fontSize:12, color:T.textFaint }}>
                  A nota não traz tomador identificado (ou é do exterior) — nada a cadastrar.
                </div>
              ) : (<>
                {linha("Nome", tm.nome)}
                {linha(tm.cpf ? "CPF" : "CNPJ", tm.cpf || tm.cnpj)}
                {linha("Endereço", [tm.endereco, tm.numero, tm.bairro].filter(Boolean).join(", "))}
                {linha("CEP", tm.cep)}
                {!tm.cadastravel && (
                  <div style={{ fontSize:11, color:T.textFaint, marginTop:6, lineHeight:1.5 }}>
                    O cadastro de Pessoas guarda só CPF. Tomador com CNPJ não precisa de
                    cadastro — na emissão os dados vêm da consulta do CNPJ.
                  </div>
                )}
              </>)}
            </div>
          </div>
        )}

        {erro && (
          <div style={{ margin:"14px 24px 0", padding:"11px 13px", background:T.dangerBg, borderRadius:8, fontSize:12.5, color:T.danger, lineHeight:1.5 }}>
            {erro}
          </div>
        )}

        <div style={{ padding:"18px 24px 20px", display:"flex", gap:9, justifyContent:"flex-end", flexWrap:"wrap" }}>
          <button onClick={onClose} disabled={carregando}
            style={{ background:"transparent", border:"1px solid "+T.border, borderRadius:8, padding:"9px 16px", fontSize:12.5, color:T.textFaint, cursor: carregando ? "default" : "pointer" }}>
            {previa ? "Cancelar" : "Fechar"}
          </button>
          {previa && (
            <button onClick={()=>{ setPrevia(null); setErro(""); }} disabled={carregando}
              style={{ background:"transparent", border:"1px solid "+T.border, borderRadius:8, padding:"9px 16px", fontSize:12.5, color:T.textMuted, cursor:"pointer" }}>
              Trocar arquivo
            </button>
          )}
          <button onClick={previa ? confirmar : analisar}
            disabled={carregando || (!previa && !xml && chave.length !== 50)
                      || (previa && !criarServico && !criarTomador)}
            style={{ background: (carregando || (!previa && !xml && chave.length !== 50) || (previa && !criarServico && !criarTomador))
                       ? T.btnSecBg : "linear-gradient(135deg,#0ea5e9,#0284c7)",
                     color: (carregando || (!previa && !xml && chave.length !== 50) || (previa && !criarServico && !criarTomador)) ? T.textFaint : "#fff",
                     border:"none", borderRadius:8, padding:"9px 18px", fontSize:12.5, fontWeight:600,
                     cursor: carregando ? "default" : "pointer" }}>
            {carregando ? "Lendo..." : previa ? "Criar cadastro" : "Ler XML"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ServicosView({ token, isMobile = false, T = {bg:"#080c10",card:"#0c1117",border:"#1a2332",text:"#f1f5f9",textMuted:"#94a3b8",input:"#080c10",inputBorder:"#1a2332",inputText:"#e2e8f0"} }) {
  const [empresas, setEmpresas] = useState([]);
  const [empresaFiltro, setEmpresaFiltro] = useState("");
  const [servicos, setServicos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [munIncid, setMunIncid] = useState(null);
  const [erroModal, setErroModal] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [import_, setImport_] = useState(null);   // { aberto: true }
  const vazio = {
    empresa_id:"", nome:"", descricao:"", informacoes_complementares:"", codigo_lc116:"",
    codigo_nbs:"", municipio_incidencia_codigo:"", municipio_incidencia_nome:"", municipio_fixo:true,
    aliquota_iss:"", iss_retido:false, situacao_tributaria:1,
    op_simp_nac:3, reg_ap_trib_sn:1, reg_esp_trib:0,
    cst_pis_cofins:"00", tp_ret_pis_cofins_csll:1,
    p_aliq_pis:"0.65", p_aliq_cofins:"3.00",
    modo_tot_trib:2, aliquota_simples:"",
    perc_trib_fed:"0", perc_trib_est:"0", perc_trib_mun:"",
    // Tributação Municipal – campos especiais
    nao_tributacao_iss:"nao", tipo_nao_tributacao:"imunidade", tipo_imunidade:"1",
    exig_susp:"nao", tipo_susp:"1", num_processo_susp:"",
    tem_beneficio_mun:"nao", codigo_bm:"",
    tem_deducao_bc:"nao",
    // IBS/CBS (Reforma Tributária do Consumo)
    ibscbs_habilitado:"nao",
    ibscbs_c_ind_op:"", ibscbs_ind_dest:"0",
    ibscbs_cst:"", ibscbs_c_class_trib:"",
    ibscbs_c_cib:"", ibscbs_tp_oper:"", ibscbs_tp_ente_gov:"", ibscbs_ind_final:"",
    // Exportação de Serviço
    pais_resultado:"", modo_prestacao:"1", vinculo_partes:"1",
    moeda_exportacao:"",
    mecanismo_prestador:"9", mecanismo_tomador:"9",
    vinculo_movimentacao_bens:"1", compartilhar_mdic:"nao",
  };
  const [form, setForm] = useState(vazio);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const [ibptCarregando, setIbptCarregando] = useState(false);
  const [ibptPreenchido, setIbptPreenchido] = useState(false);
  const [ibptErro, setIbptErro] = useState("");
  const [ibptOrigem, setIbptOrigem] = useState("");

  // NBS com incidência no domicílio do adquirente: o município é do tomador,
  // decidido em cada nota — o campo do cadastro fica bloqueado.
  const nbsSegueTomador = incidenciaDaNbs(form.codigo_nbs)?.local === "A";

  const buscarIBPT = async (codigo) => {
    if (!codigo) return;
    const empresaSel = empresas.find(e => e.id === (form.empresa_id || empresaFiltro));
    const uf = (empresaSel?.uf || "").trim().toUpperCase();
    const cnpj = (empresaSel?.cnpj || "").replace(/\D/g, "");
    setIbptPreenchido(false);
    setIbptErro("");
    setIbptOrigem("");

    // A tabela do IBPT é por estado. Sem a UF a consulta traria os percentuais
    // de outro estado sem ninguém perceber — melhor não consultar e avisar.
    if (!uf) {
      setIbptErro("A empresa está sem UF no cadastro, e o IBPT consulta por estado. "
                + "Preencha a UF da empresa ou informe os percentuais manualmente.");
      return;
    }

    setIbptCarregando(true);
    const avisoFalha = "Não foi possível consultar o IBPT agora — o serviço deles está fora do ar "
                     + "com alguma frequência. Preencha os percentuais manualmente.";
    try {
      // O NBS é mais específico que o LC116; o backend usa quando disponível
      const params = new URLSearchParams({ codigo, uf, cnpj, nbs: form.codigo_nbs || "" });
      const r = await fetch(`${API}/ibpt/servico?${params}`, {
        headers: { Authorization: "Bearer " + token }
      });
      if (!r.ok) {
        setIbptErro(avisoFalha);
      } else {
        const d = await r.json();
        if (d.nao_encontrado) {
          setIbptErro(`O IBPT não retornou percentuais para o código ${codigo} em ${uf}. `
                    + "Confira o código ou preencha manualmente.");
        } else {
          setIbptOrigem(d.origem === "tabela"
            ? `tabela IBPT ${d.versao || ""} (${uf})`.replace("  ", " ")
            : "consulta ao IBPT");
          if (d.vencida)
            setIbptErro(`Os percentuais vieram da tabela IBPT, mas a vigência dela terminou em `
                      + `${(d.vigencia_fim||"").split("-").reverse().join("/")}. `
                      + "Vale importar a versão nova.");
          setForm(f => ({
            ...f,
            perc_trib_fed: d.perc_trib_fed != null ? String(d.perc_trib_fed) : f.perc_trib_fed,
            perc_trib_est: d.perc_trib_est != null ? String(d.perc_trib_est) : f.perc_trib_est,
            perc_trib_mun: d.perc_trib_mun != null ? String(d.perc_trib_mun) : f.perc_trib_mun,
          }));
          setIbptPreenchido(true);
        }
      }
    } catch (_) {
      setIbptErro(avisoFalha);
    }
    setIbptCarregando(false);
  };

  const S = {
    input: { width:"100%", background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8, padding:"10px 14px", fontSize:13, color:T.inputText, outline:"none", boxSizing:"border-box" },
    label: { fontSize:12, fontWeight:600, color:T.textMuted, display:"block", marginBottom:6 }
  };

  const carregar = () => {
    const url = empresaFiltro ? `${API}/servicos?empresa_id=${empresaFiltro}` : `${API}/servicos`;
    fetch(url, { headers:{ Authorization:"Bearer "+token } }).then(r=>r.json()).then(d=>setServicos(Array.isArray(d)?d:[]));
  };

  useEffect(() => {
    fetch(API+"/empresas",{headers:{Authorization:"Bearer "+token}}).then(r=>r.json()).then(d=>{
      if(Array.isArray(d)){ setEmpresas(d); if(d.length>0) setEmpresaFiltro(empresaInicial(d, lerEmpresaAtual())); }
    });
  },[token]);

  useEffect(() => { if(empresaFiltro) carregar(); },[empresaFiltro]);

  const abrirNovo = () => {
    setEditando(null);
    setForm({...vazio, empresa_id: empresaFiltro});
    setMunIncid(null);
    setErroModal("");
    setShowModal(true);
  };

  // Mapeamento de um serviço salvo para o formulário. Fica numa função só
  // porque é usado na edição e na duplicação — duas cópias disto ficariam
  // dessincronizadas a cada campo novo do cadastro.
  const formDoServico = (s) => ({
      empresa_id: s.empresa_id, nome: s.nome, descricao: s.descricao,
      codigo_lc116: s.codigo_lc116, codigo_nbs: s.codigo_nbs||"",
      municipio_incidencia_codigo: s.municipio_incidencia_codigo||"",
      municipio_incidencia_nome: s.municipio_incidencia_nome||"",
      municipio_fixo: s.municipio_fixo !== false,
      aliquota_iss: s.aliquota_iss != null ? String(s.aliquota_iss) : "", iss_retido: s.iss_retido,
      situacao_tributaria: s.situacao_tributaria,
      op_simp_nac: s.op_simp_nac, reg_ap_trib_sn: s.reg_ap_trib_sn, reg_esp_trib: s.reg_esp_trib,
      cst_pis_cofins: s.cst_pis_cofins || "07",
      tp_ret_pis_cofins_csll: s.tp_ret_pis_cofins_csll || 1,
      p_aliq_pis: String(s.p_aliq_pis ?? "0.65"),
      p_aliq_cofins: String(s.p_aliq_cofins ?? "3.00"),
      modo_tot_trib: s.modo_tot_trib || 2,
      aliquota_simples: s.aliquota_simples != null ? String(s.aliquota_simples) : "",
      perc_trib_fed: String(s.perc_trib_fed || 0),
      perc_trib_est: String(s.perc_trib_est || 0),
      perc_trib_mun: s.perc_trib_mun != null ? String(s.perc_trib_mun) : "",
      nao_tributacao_iss: s.nao_tributacao_iss ? "sim" : "nao",
      tipo_nao_tributacao: s.tipo_nao_tributacao || "imunidade",
      tipo_imunidade: s.tipo_imunidade != null ? String(s.tipo_imunidade) : "1",
      exig_susp: s.exig_susp ? "sim" : "nao",
      tipo_susp: s.tipo_susp != null ? String(s.tipo_susp) : "1",
      num_processo_susp: s.num_processo_susp || "",
      tem_beneficio_mun: s.tem_beneficio_mun ? "sim" : "nao",
      codigo_bm: s.codigo_bm || "",
      tem_deducao_bc: s.tem_deducao_bc ? "sim" : "nao",
      // IBS/CBS (Reforma Tributária do Consumo)
      ibscbs_habilitado: s.ibscbs_habilitado ? "sim" : "nao",
      ibscbs_c_ind_op: s.ibscbs_c_ind_op || "",
      ibscbs_ind_dest: s.ibscbs_ind_dest || "0",
      ibscbs_cst: s.ibscbs_cst || "",
      ibscbs_c_class_trib: s.ibscbs_c_class_trib || "",
      ibscbs_c_cib: s.ibscbs_c_cib || "",
      ibscbs_tp_oper: s.ibscbs_tp_oper || "",
      ibscbs_tp_ente_gov: s.ibscbs_tp_ente_gov || "",
      ibscbs_ind_final: s.ibscbs_ind_final || "",
      // Exportação de Serviço
      pais_resultado: s.pais_resultado || "",
      modo_prestacao: s.modo_prestacao != null ? String(s.modo_prestacao) : "1",
      vinculo_partes: s.vinculo_partes != null ? String(s.vinculo_partes) : "2",
      moeda_exportacao: s.moeda_exportacao || "",
      mecanismo_prestador: s.mecanismo_prestador != null ? String(s.mecanismo_prestador) : "9",
      mecanismo_tomador: s.mecanismo_tomador != null ? String(s.mecanismo_tomador) : "9",
      vinculo_movimentacao_bens: s.vinculo_movimentacao_bens != null ? String(s.vinculo_movimentacao_bens) : "1",
      compartilhar_mdic: s.compartilhar_mdic ? "sim" : "nao",
  });

  // `duplicar` abre o mesmo formulário sem id de edição: ao salvar, o sistema
  // cria um serviço novo em vez de sobrescrever o original.
  const abrirComServico = (s, duplicar = false) => {
    setEditando(duplicar ? null : s.id);
    setForm(duplicar
      ? { ...formDoServico(s), nome: `${s.nome} (cópia)` }
      : formDoServico(s));
    setMunIncid(s.municipio_incidencia_codigo ? { codigo: s.municipio_incidencia_codigo, nome: s.municipio_incidencia_nome } : null);
    setErroModal("");
    setShowModal(true);
  };

  const abrirEditar   = (s) => abrirComServico(s, false);
  const abrirDuplicar = (s) => abrirComServico(s, true);

  const salvar = async () => {
    setErroModal(""); setSalvando(true);
    try {
      const payload = {
        ...form,
        aliquota_iss: form.aliquota_iss !== "" ? (parseFloat(form.aliquota_iss) ?? null) : null,
        situacao_tributaria: parseInt(form.situacao_tributaria)||1,
        // Com NBS de domicílio do adquirente o município não é do cadastro: vai
        // vazio (a emissão usa o do tomador, ou o da empresa como reserva) e o
        // "fixo" fica ligado para a emissão não exigir escolha manual.
        municipio_incidencia_codigo: nbsSegueTomador ? "" : (munIncid ? munIncid.codigo : form.municipio_incidencia_codigo),
        municipio_incidencia_nome:   nbsSegueTomador ? "" : (munIncid ? munIncid.nome : form.municipio_incidencia_nome),
        municipio_fixo: nbsSegueTomador ? true : form.municipio_fixo !== false,
        p_aliq_pis: parseFloat(form.p_aliq_pis)||0.65,
        p_aliq_cofins: parseFloat(form.p_aliq_cofins)||3.00,
        modo_tot_trib: parseInt(form.modo_tot_trib)||2,
        aliquota_simples: form.aliquota_simples !== "" ? parseFloat(form.aliquota_simples)||null : null,
        perc_trib_fed: parseFloat(form.perc_trib_fed)||0,
        perc_trib_est: parseFloat(form.perc_trib_est)||0,
        perc_trib_mun: form.perc_trib_mun !== "" ? parseFloat(form.perc_trib_mun)||null : null,
        nao_tributacao_iss: form.nao_tributacao_iss === "sim",
        tipo_nao_tributacao: form.tipo_nao_tributacao || "imunidade",
        tipo_imunidade: (form.nao_tributacao_iss === "sim" && form.tipo_nao_tributacao === "imunidade") ? (parseInt(form.tipo_imunidade)||1) : null,
        exig_susp: form.exig_susp === "sim",
        tipo_susp: form.exig_susp === "sim" ? (parseInt(form.tipo_susp)||1) : null,
        num_processo_susp: form.exig_susp === "sim" ? (form.num_processo_susp||null) : null,
        tem_beneficio_mun: form.tem_beneficio_mun === "sim",
        codigo_bm: form.tem_beneficio_mun === "sim" ? (form.codigo_bm||null) : null,
        tem_deducao_bc: form.tem_deducao_bc === "sim",
        // Exportação de Serviço
        pais_resultado: form.nao_tributacao_iss==="sim" && form.tipo_nao_tributacao==="exportacao" ? (form.pais_resultado||null) : null,
        modo_prestacao: form.nao_tributacao_iss==="sim" && form.tipo_nao_tributacao==="exportacao" ? (parseInt(form.modo_prestacao)||1) : null,
        vinculo_partes: form.nao_tributacao_iss==="sim" && form.tipo_nao_tributacao==="exportacao" ? (parseInt(form.vinculo_partes)||2) : null,
        moeda_exportacao: form.nao_tributacao_iss==="sim" && form.tipo_nao_tributacao==="exportacao" ? (form.moeda_exportacao||null) : null,
        valor_moeda_estrangeira: null,
        mecanismo_prestador: form.nao_tributacao_iss==="sim" && form.tipo_nao_tributacao==="exportacao" ? (parseInt(form.mecanismo_prestador)||9) : null,
        mecanismo_tomador: form.nao_tributacao_iss==="sim" && form.tipo_nao_tributacao==="exportacao" ? (parseInt(form.mecanismo_tomador)||9) : null,
        vinculo_movimentacao_bens: form.nao_tributacao_iss==="sim" && form.tipo_nao_tributacao==="exportacao" ? (parseInt(form.vinculo_movimentacao_bens)||1) : null,
        compartilhar_mdic: form.nao_tributacao_iss==="sim" && form.tipo_nao_tributacao==="exportacao" ? form.compartilhar_mdic==="sim" : false,
        // IBS/CBS (Reforma Tributária do Consumo)
        ibscbs_habilitado: form.ibscbs_habilitado === "sim",
        ibscbs_c_ind_op: form.ibscbs_habilitado === "sim" ? (form.ibscbs_c_ind_op || null) : null,
        ibscbs_ind_dest: form.ibscbs_habilitado === "sim" ? (form.ibscbs_ind_dest || "0") : "0",
        ibscbs_cst: form.ibscbs_habilitado === "sim" ? (form.ibscbs_cst || null) : null,
        ibscbs_c_class_trib: form.ibscbs_habilitado === "sim" ? (form.ibscbs_c_class_trib || null) : null,
        ibscbs_c_cib: form.ibscbs_habilitado === "sim" ? (form.ibscbs_c_cib || null) : null,
        ibscbs_tp_oper: form.ibscbs_habilitado === "sim" ? (form.ibscbs_tp_oper || null) : null,
        ibscbs_tp_ente_gov: form.ibscbs_habilitado === "sim" ? (form.ibscbs_tp_ente_gov || null) : null,
        ibscbs_ind_final: form.ibscbs_habilitado === "sim" ? (form.ibscbs_ind_final || null) : null,
      };
      const url = editando ? `${API}/servicos/${editando}` : `${API}/servicos`;
      const method = editando ? "PUT" : "POST";
      const r = await fetch(url,{ method, headers:{"Content-Type":"application/json","Authorization":"Bearer "+token}, body:JSON.stringify(payload) });
      const d = await r.json();
      if (!r.ok) {
        const msg = Array.isArray(d.detail)
          ? d.detail.map(e => `${e.loc?.slice(-1)[0] || ""}: ${e.msg}`).join("; ")
          : (d.detail || JSON.stringify(d));
        throw new Error(msg);
      }
      setShowModal(false); carregar();
    } catch(e) {
      setErroModal(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (id) => {
    if(!window.confirm("Excluir este serviço?")) return;
    await fetch(`${API}/servicos/${id}`,{ method:"DELETE", headers:{Authorization:"Bearer "+token} });
    carregar();
  };

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:20, fontWeight:700, color:T.text, margin:"0 0 12px" }}>Catálogo de Serviços</h2>
        <div style={{ display:"flex", gap:10 }}>
          <select style={{ ...S.input, flex:1, minWidth:0 }} value={empresaFiltro} onChange={e=>{ setEmpresaFiltro(e.target.value); gravarEmpresaAtual(e.target.value); }}>
            {empresas.map(e=><option key={e.id} value={e.id}>{e.razao_social}</option>)}
          </select>
          {/* Cadastrar do zero é redigitar o que já está numa nota emitida. */}
          <button onClick={()=>{ setImport_({ aberto:true }); }} title="Criar serviço e tomador a partir do XML de uma nota já emitida"
            style={{ background:"transparent", color:"#38bdf8", border:"1px solid #38bdf850", borderRadius:8, padding:"9px 14px", fontSize:13, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0, display:"flex", alignItems:"center", gap:6 }}>
            <Upload size={13} /> Importar de XML
          </button>
          <button onClick={abrirNovo} style={{ background:"linear-gradient(135deg,#0ea5e9,#0284c7)", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>+ Novo</button>
        </div>
      </div>

      {import_?.aberto && (
        <ImportarXMLModal token={token} empresaId={empresaFiltro} T={T} S={S} isMobile={isMobile}
          onClose={()=>setImport_(null)}
          onImportado={()=>{ setImport_(null); carregar(); }} />
      )}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:14 }}>
        {servicos.map(s=>(
          <div key={s.id} style={{ background:T.card, border:"1px solid "+T.border, borderRadius:12, padding:20, boxShadow:T.shadow, transition:"box-shadow .2s" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:T.text }}>{s.nome}</div>
                <div style={{ fontSize:11, color:T.textFaint, marginTop:2 }}>LC 116: {s.codigo_lc116} {s.codigo_nbs && `· NBS: ${s.codigo_nbs}`}</div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={()=>abrirEditar(s)} style={{ background:T.btnSecBg, border:"none", borderRadius:6, padding:"5px 10px", fontSize:12, color:T.btnSecText, cursor:"pointer" }}>Editar</button>
                <button onClick={()=>abrirDuplicar(s)} title="Criar um serviço novo a partir deste"
                  style={{ display:"flex", alignItems:"center", gap:4, background:"#0ea5e915", border:"1px solid #0ea5e940", borderRadius:6, padding:"5px 10px", fontSize:12, color:"#38bdf8", cursor:"pointer", fontWeight:600 }}>
                  <Copy size={12} /> Duplicar
                </button>
                <button onClick={()=>excluir(s.id)} style={{ background:T.dangerBg, border:"none", borderRadius:6, padding:"5px 10px", fontSize:12, color:T.danger, cursor:"pointer" }}>Excluir</button>
              </div>
            </div>
            <div style={{ fontSize:12, color:T.textFaint, marginBottom:12, lineHeight:1.5 }}>{s.descricao}</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, padding:"3px 10px", borderRadius:20, background:"#0ea5e915", color:"#38bdf8" }}>ISS {s.aliquota_iss != null ? s.aliquota_iss+"%" : "—"}</span>
              {s.iss_retido && <span style={{ fontSize:11, padding:"3px 10px", borderRadius:20, background:"#1a1a0d", color:"#facc15" }}>Retido</span>}
              {incidenciaDaNbs(s.codigo_nbs)?.local === "A"
                ? <span style={{ fontSize:11, padding:"3px 10px", borderRadius:20, background:"#0ea5e912", border:"1px solid #38bdf850", color:"#38bdf8", display:"inline-flex", alignItems:"center", gap:3 }}><MapPin size={10} />Domicílio do tomador</span>
                : s.municipio_incidencia_nome && <span style={{ fontSize:11, padding:"3px 10px", borderRadius:20, background:T.card, border:"1px solid "+T.border, color:T.textFaint, display:"inline-flex", alignItems:"center", gap:3 }}><MapPin size={10} />{s.municipio_incidencia_nome}</span>}
            </div>
          </div>
        ))}
        {servicos.length===0 && (
          <div style={{ gridColumn:"1/-1", padding:60, textAlign:"center", color:T.textFaint }}>
            <div style={{ marginBottom:12, color:T.textFaint }}><Menu size={40} /></div>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:8 }}>Nenhum serviço cadastrado</div>
            <div style={{ fontSize:13 }}>Clique em "+ Novo Serviço" para cadastrar</div>
          </div>
        )}
      </div>

      {showModal && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom: isMobile ? 64 : 0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100, padding: isMobile ? "12px" : 20 }} onMouseDown={()=>setShowModal(false)}>
          <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:16, width:"100%", maxWidth:620, padding: isMobile ? "20px 16px" : 28, maxHeight:"100%", overflowY:"auto", boxShadow:T.shadowHover }} onMouseDown={e=>e.stopPropagation()}>
            <h3 style={{ color:T.text, marginTop:0, marginBottom:20 }}>{editando ? "Editar Serviço" : "Novo Serviço"}</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

              <div>
                <label style={S.label}>EMPRESA <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                <select style={S.input} value={form.empresa_id} onChange={e=>set("empresa_id",e.target.value)}>
                  {empresas.map(e=><option key={e.id} value={e.id}>{e.razao_social}</option>)}
                </select>
              </div>

              <div>
                <label style={S.label}>NOME DO SERVIÇO (exibição interna) <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                <input style={S.input} placeholder="Ex: Consulta Médica, Honorário Contábil Mensal..." value={form.nome} onChange={e=>set("nome",e.target.value)} />
              </div>

              <div>
                <label style={S.label}>DESCRIÇÃO NA NOTA <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                <textarea style={{...S.input, resize:"vertical", minHeight:70}} placeholder="Descrição que aparecerá na nota fiscal" value={form.descricao} onChange={e=>set("descricao",e.target.value)} />
              </div>
              <div>
                <label style={S.label}>INFORMAÇÕES COMPLEMENTARES <span style={{color:"#475569",fontWeight:400}}>(opcional)</span></label>
                <textarea style={{...S.input, resize:"vertical", minHeight:60}} placeholder="Observações adicionais que aparecem na nota fiscal" value={form.informacoes_complementares||""} onChange={e=>set("informacoes_complementares",e.target.value)} />
              </div>

              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16 }}>
                <div>
                  <ServicoSearch
                    value={form.codigo_lc116}
                    onChange={(cod, desc) => {
                      set("codigo_lc116", cod);
                      if(desc && !form.descricao) set("descricao", desc);
                      if(cod) buscarIBPT(cod);
                    }}
                    S={S} T={T}
                  />
                </div>
                <div>
                  <NbsSearch value={form.codigo_nbs} onChange={v=>{
                    set("codigo_nbs", v);
                    // Anexo VIII: a NBS define o cIndOp. Sugere quando o campo está
                    // vazio, sem sobrescrever uma escolha já feita.
                    const r = incidenciaDaNbs(v);
                    if (r?.indop && !form.ibscbs_c_ind_op) set("ibscbs_c_ind_op", r.indop);
                  }} S={S} T={T} />
                  {(() => {
                    const r = incidenciaDaNbs(form.codigo_nbs);
                    if (!r) return null;
                    return (
                      <div style={{ fontSize:11, color:T.textFaint, marginTop:4, lineHeight:1.5 }}>
                        cIndOp <strong style={{ color:T.textMuted }}>{r.indop}</strong>
                        {r.localNome && <> · incidência: <strong style={{ color: r.local === "A" ? "#38bdf8" : T.textMuted }}>{r.localNome}</strong></>}
                        {r.local === "A" && <> — o município da nota seguirá o tomador.</>}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {nbsSegueTomador ? (
                <>
                  {/* NBS de "domicílio do adquirente": o município é decidido pelo
                      tomador em cada nota. Um valor aqui só confundiria. */}
                  <label style={S.label}>MUNICÍPIO DE INCIDÊNCIA</label>
                  <div style={{ ...S.input, display:"flex", alignItems:"center", gap:7,
                                color:T.textFaint, background:T.btnSecBg, cursor:"not-allowed" }}>
                    <Lock size={13} style={{flexShrink:0}} />
                    <span style={{fontSize:12.5}}>Definido pelo tomador em cada nota</span>
                  </div>
                  <div style={{ fontSize:11, color:T.textFaint, marginTop:-8, lineHeight:1.5 }}>
                    Pela NBS informada, a incidência é no domicílio do adquirente: na emissão, o
                    município vem do CNPJ do tomador. Se o tomador não for informado, ou for do
                    exterior, a nota usa o município da própria empresa.
                  </div>
                </>
              ) : (
              <MunicipioInput value={munIncid} onChange={v=>{ setMunIncid(v); if(v){ set("municipio_incidencia_codigo",v.codigo); set("municipio_incidencia_nome",v.nome); }}}
                label={form.municipio_fixo === false ? "MUNICÍPIO DE INCIDÊNCIA (padrão — pode ser trocado na emissão)" : "MUNICÍPIO DE INCIDÊNCIA *"} S={S} T={T} />
              )}

              {/* Serviços prestados sempre na mesma cidade ficam fixos aqui; os que
                  variam passam a pedir município e alíquota em cada emissão. */}
              {!nbsSegueTomador && (
              <label style={{ display:"flex", alignItems:"flex-start", gap:9, cursor:"pointer",
                              marginTop:-4, fontSize:12.5, color:T.textMuted, lineHeight:1.55 }}>
                <input type="checkbox" checked={form.municipio_fixo !== false}
                  onChange={e=>set("municipio_fixo", e.target.checked)}
                  style={{ marginTop:2, width:15, height:15, accentColor:"#0ea5e9", cursor:"pointer", flexShrink:0 }} />
                <span>
                  <strong style={{color:T.text}}>Este serviço é sempre no mesmo município</strong><br/>
                  {form.municipio_fixo !== false
                    ? "A emissão usará sempre o município acima."
                    : "Ao emitir, será pedido o município e a alíquota do ISS de cada nota."}
                </span>
              </label>
              )}

              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16 }}>
                <div>
                  {/* Não optante: a alíquota é do município, não do cadastro. Informá-la
                      na nota gera rejeição E0617, então o campo fica desabilitado. */}
                  {Number(form.op_simp_nac) === 1 ? (
                    <>
                      <label style={S.label}>ALÍQUOTA ISS (%)</label>
                      <div style={{ ...S.input, display:"flex", alignItems:"center", gap:7,
                                    color:T.textFaint, background:T.btnSecBg, cursor:"not-allowed" }}>
                        <Info size={13} style={{flexShrink:0}} />
                        <span style={{fontSize:12.5}}>Calculada pelo município</span>
                      </div>
                      <div style={{ fontSize:11, color:T.textFaint, marginTop:4, lineHeight:1.5 }}>
                        Empresa não optante do Simples Nacional — a alíquota vem do município
                        e não é enviada na nota.
                      </div>
                    </>
                  ) : (
                    <>
                      <label style={S.label}>ALÍQUOTA ISS (%) <span style={{color:T.textFaint,fontWeight:400,marginLeft:4}}>(opcional — para preview)</span></label>
                      <input style={S.input} type="number" step="0.5" min="0" max="5" placeholder="ex: 2.00" value={form.aliquota_iss} onChange={e=>set("aliquota_iss",e.target.value)} />
                    </>
                  )}
                </div>
                <div>
                  <label style={S.label}>SITUAÇÃO TRIBUTÁRIA</label>
                  <select style={S.input} value={form.situacao_tributaria} onChange={e=>set("situacao_tributaria",parseInt(e.target.value))}>
                    <option value={1}>1 – Tributado no município</option>
                    <option value={2}>2 – Imune</option>
                    <option value={3}>3 – Exportação</option>
                    <option value={4}>4 – Não incidência</option>
                  </select>
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap:16 }}>
                {/* Domínio oficial TSOpSimpNac: só 1, 2 e 3. Tem que bater com o
                    cadastro do CNPJ na Receita, senão a nota é rejeitada (E0160). */}
                <div>
                  <label style={S.label}>SIMPLES NACIONAL</label>
                  <select style={S.input} value={form.op_simp_nac} onChange={e=>set("op_simp_nac",parseInt(e.target.value))}>
                    <option value={1}>1 – Não optante</option>
                    <option value={2}>2 – Optante – MEI</option>
                    <option value={3}>3 – Optante – ME/EPP</option>
                  </select>
                </div>
                {/* TSRegimeApuracaoSimpNac: só se aplica a optante ME/EPP e indica
                    onde os tributos são apurados, não regime de competência/caixa. */}
                <div>
                  <label style={S.label}>REGIME APURAÇÃO SN</label>
                  <select style={S.input} value={form.reg_ap_trib_sn} onChange={e=>set("reg_ap_trib_sn",parseInt(e.target.value))}
                    disabled={Number(form.op_simp_nac) !== 3}>
                    <option value={1}>1 – Federais e ISSQN pelo Simples</option>
                    <option value={2}>2 – Federais pelo Simples, ISSQN por fora</option>
                    <option value={3}>3 – Federais e ISSQN por fora do Simples</option>
                  </select>
                  {Number(form.op_simp_nac) !== 3 && (
                    <div style={{ fontSize:11, color:T.textFaint, marginTop:4, lineHeight:1.5 }}>
                      Só se aplica a optante ME/EPP.
                    </div>
                  )}
                </div>
                <div>
                  <label style={S.label}>REGIME ESPECIAL</label>
                  <select style={S.input} value={form.reg_esp_trib} onChange={e=>set("reg_esp_trib",parseInt(e.target.value))}>
                    {/* Domínio TSRegEspTrib do XSD. Os rótulos antigos não
                        batiam com ele — "6" aparecia como "ME EPP Simples"
                        quando na verdade significa Sociedade de Profissionais. */}
                    <option value={0}>0 – Nenhum</option>
                    <option value={1}>1 – Ato Cooperado (Cooperativa)</option>
                    <option value={2}>2 – Estimativa</option>
                    <option value={3}>3 – Microempresa Municipal</option>
                    <option value={4}>4 – Notário ou Registrador</option>
                    <option value={5}>5 – Profissional Autônomo</option>
                    <option value={6}>6 – Sociedade de Profissionais</option>
                    <option value={9}>9 – Outros</option>
                  </select>
                  <div style={{ fontSize:11, color:T.textMuted, marginTop:5, lineHeight:1.45 }}>
                    Regime especial concedido pelo município. Não tem relação com o
                    Simples Nacional — optante do Simples sem regime especial usa <strong>0</strong>.
                  </div>
                </div>
              </div>

              <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
                <input type="checkbox" checked={form.iss_retido} onChange={e=>set("iss_retido",e.target.checked)} style={{ width:18, height:18, accentColor:"#0ea5e9" }} />
                <span style={{ fontSize:13, color:T.inputText }}>Há retenção do ISSQN pelo Tomador ou Intermediário?</span>
              </label>

              {form.iss_retido && (
                <div style={{ background:T.cardInner, border:"1px solid #fbbf2440", borderRadius:8, padding:14, display:"flex", flexDirection:"column", gap:10 }}>
                  <div style={{ fontSize:12, color:"#fbbf24", display:"flex", alignItems:"center", gap:5 }}><AlertTriangle size={12} />Para ME/EPP com apuração pelo Simples Nacional, a alíquota mínima permitida é 1,8%.</div>
                  <div style={{ maxWidth:200 }}>
                    <label style={S.label}>ALÍQUOTA DE RETENÇÃO (%) <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                    <input style={S.input} type="number" step="0.0001" min="0" max="5" placeholder="ex: 2.0000"
                      value={form.aliquota_iss} onChange={e=>set("aliquota_iss",e.target.value)} />
                  </div>
                </div>
              )}

              {/* Tributação Federal */}
              <div style={{ fontSize:11, fontWeight:700, color:"#a78bfa", textTransform:"uppercase", letterSpacing:1, marginTop:4 }}>Tributação Federal</div>

              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16 }}>
                <div>
                  <label style={S.label}>SITUAÇÃO TRIBUTÁRIA PIS/COFINS</label>
                  <select style={S.input} value={form.cst_pis_cofins} onChange={e=>{
                    const cst = e.target.value;
                    setForm(f => ({
                      ...f,
                      cst_pis_cofins: cst,
                      // Preenche alíquotas padrão conforme regime
                      p_aliq_pis:    cst === "01" ? "0.65" : cst === "02" ? "1.65" : f.p_aliq_pis,
                      p_aliq_cofins: cst === "01" ? "3.00" : cst === "02" ? "7.60" : f.p_aliq_cofins,
                    }));
                  }}>
                    <option value="00">00 – Nenhum</option>
                    <option value="01">01 – Tributável Alíquota Básica</option>
                    <option value="02">02 – Tributável Alíquota Diferenciada</option>
                    <option value="03">03 – Tributável por Unidade de Medida</option>
                    <option value="04">04 – Monofásica – Alíquota Zero</option>
                    <option value="05">05 – Por Substituição Tributária</option>
                    <option value="06">06 – Alíquota Zero</option>
                    <option value="07">07 – Isenta da Contribuição</option>
                    <option value="08">08 – Sem Incidência</option>
                    <option value="09">09 – Com Suspensão</option>
                    <option value="49">49 – Outras Operações de Saída</option>
                    <option value="99">99 – Outras Operações</option>
                  </select>
                </div>
                {form.cst_pis_cofins !== "00" && (
                  <div>
                    <label style={S.label}>TIPO DE RETENÇÃO PIS/COFINS/CSLL</label>
                    <select style={S.input} value={form.tp_ret_pis_cofins_csll} onChange={e=>set("tp_ret_pis_cofins_csll",parseInt(e.target.value))}>
                      <option value={4}>4 – Sem Retenção (PIS/COFINS/CSLL Não Retidos)</option>
                      <option value={1}>1 – PIS e COFINS Retidos</option>
                      <option value={2}>2 – Apenas COFINS Retido</option>
                      <option value={3}>3 – Apenas PIS Retido</option>
                      <option value={5}>5 – PIS, COFINS e CSLL Retidos (todos)</option>
                      <option value={6}>6 – COFINS e CSLL Retidos (PIS Não Retido)</option>
                      <option value={7}>7 – Apenas CSLL Retido (PIS/COFINS Não Retidos)</option>
                      <option value={8}>8 – PIS e CSLL Retidos (COFINS Não Retido)</option>
                    </select>
                  </div>
                )}
              </div>

              {(form.cst_pis_cofins === "01" || form.cst_pis_cofins === "02") && (
                <>
                  <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr", gap:16 }}>
                    <div>
                      <label style={S.label}>ALÍQUOTA PIS (%)</label>
                      <input style={S.input} type="number" step="0.0001" min="0" placeholder={form.cst_pis_cofins === "02" ? "1.6500" : "0.6500"} value={form.p_aliq_pis} onChange={e=>set("p_aliq_pis",e.target.value)} />
                    </div>
                    <div>
                      <label style={S.label}>ALÍQUOTA COFINS (%)</label>
                      <input style={S.input} type="number" step="0.0001" min="0" placeholder={form.cst_pis_cofins === "02" ? "7.6000" : "3.0000"} value={form.p_aliq_cofins} onChange={e=>set("p_aliq_cofins",e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              {/* Valor Aproximado dos Tributos */}
              <div style={{ fontSize:11, fontWeight:700, color:"#38bdf8", textTransform:"uppercase", letterSpacing:1, marginTop:4 }}>Valor Aproximado dos Tributos</div>

              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {[
                  { v:1, label:"Preencher os valores monetários em cada NFS-e emitida" },
                  { v:2, label:"Configurar os valores percentuais correspondentes" },
                  { v:3, label:"Informar alíquota do Simples Nacional" },
                ].map(opt => (
                  <label key={opt.v} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
                    <input type="radio" name="modo_tot_trib" checked={form.modo_tot_trib===opt.v} onChange={()=>set("modo_tot_trib",opt.v)}
                      style={{ width:15, height:15, accentColor:"#38bdf8" }} />
                    <span style={{ fontSize:13, color:T.inputText }}>{opt.label}</span>
                  </label>
                ))}
              </div>

              {form.modo_tot_trib === 2 && (
                <>
                  {ibptCarregando && (
                    <div style={{display:"flex",alignItems:"center",gap:8,background:"#0ea5e920",border:"1px solid #38bdf8",borderRadius:8,padding:"8px 14px",marginBottom:4}}>
                      <Loader2 size={16} style={{color:"#38bdf8",animation:"spin 1s linear infinite"}} />
                      <span style={{color:"#38bdf8",fontWeight:600,fontSize:13}}>Buscando alíquotas no IBPT, aguarde…</span>
                    </div>
                  )}
                  {ibptPreenchido && !ibptCarregando && (
                    <div style={{display:"flex",alignItems:"center",gap:6,color:"#22c55e",fontSize:12,fontWeight:500,marginBottom:4}}>
                      <Check size={13} /><span>Preenchido automaticamente — {ibptOrigem || "IBPT"}</span>
                    </div>
                  )}
                  {/* Antes a falha era engolida em silêncio: o serviço era salvo
                      com os percentuais zerados sem ninguém perceber. */}
                  {ibptErro && !ibptCarregando && (
                    <div style={{display:"flex",alignItems:"flex-start",gap:8,background:"#f9731618",border:"1px solid #fb923c66",borderRadius:8,padding:"9px 14px",marginBottom:4}}>
                      <AlertTriangle size={15} style={{color:"#fb923c",flexShrink:0,marginTop:1}} />
                      <span style={{color:"#fb923c",fontWeight:500,fontSize:12.5,lineHeight:1.5}}>{ibptErro}</span>
                    </div>
                  )}
                  <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap:16 }}>
                    <div>
                      <label style={S.label}>% FEDERAL</label>
                      <input style={S.input} type="number" step="0.01" min="0" placeholder="0,00" value={form.perc_trib_fed} onChange={e=>set("perc_trib_fed",e.target.value)} />
                    </div>
                    <div>
                      <label style={S.label}>% ESTADUAL</label>
                      <input style={S.input} type="number" step="0.01" min="0" placeholder="0,00" value={form.perc_trib_est} onChange={e=>set("perc_trib_est",e.target.value)} />
                    </div>
                    <div>
                      <label style={S.label}>% MUNICIPAL <span style={{color:T.textFaint,fontWeight:400}}>(vazio = ISS)</span></label>
                      <input style={S.input} type="number" step="0.01" min="0" placeholder={form.aliquota_iss ? `auto (${form.aliquota_iss}%)` : "ex: 2.00"} value={form.perc_trib_mun} onChange={e=>set("perc_trib_mun",e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              {form.modo_tot_trib === 3 && (
                <div style={{ maxWidth:220 }}>
                  <label style={S.label}>ALÍQUOTA SIMPLES NACIONAL (%)</label>
                  <input style={S.input} type="number" step="0.01" min="0" placeholder="Ex: 18.83" value={form.aliquota_simples} onChange={e=>set("aliquota_simples",e.target.value)} />
                </div>
              )}

              {form.modo_tot_trib === 1 && (
                <div style={{ fontSize:12, color:"#38bdf8", background:"#0ea5e910", border:"1px solid #0ea5e930", borderRadius:8, padding:"10px 14px", lineHeight:1.6 }}>
                  <Info size={13} style={{verticalAlign:"middle",marginRight:5}} />Os valores monetários de tributos (R$ Federal, Estadual e Municipal) serão informados individualmente em cada emissão de nota.
                </div>
              )}

              {/* ── Tributação Municipal – Campos Especiais ── */}
              <div style={{ fontSize:11, fontWeight:700, color:"#fbbf24", letterSpacing:1, textTransform:"uppercase", marginTop:4 }}>Tributação Municipal – Campos Especiais</div>

              {/* Imunidade / Exportação / Não Incidência */}
              <div style={{ background:T.cardInner, border:"1px solid "+T.border, borderRadius:8, padding:14, display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ fontSize:12, fontWeight:600, color:T.textMuted }}>O serviço é caso de imunidade, exportação ou não incidência do ISSQN?</div>
                <div style={{ display:"flex", gap:20 }}>
                  {[["nao","Não"],["sim","Sim"]].map(([v,l])=>(
                    <label key={v} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:T.text, cursor:"pointer" }}>
                      <input type="radio" name="srv_nao_trib" value={v} checked={form.nao_tributacao_iss===v} onChange={()=>set("nao_tributacao_iss",v)} /> {l}
                    </label>
                  ))}
                </div>
                {form.nao_tributacao_iss==="sim" && (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    <div>
                      <label style={S.label}>TIPO</label>
                      <select style={S.input} value={form.tipo_nao_tributacao} onChange={e=>set("tipo_nao_tributacao",e.target.value)}>
                        <option value="imunidade">Imunidade</option>
                        <option value="exportacao">Exportação de Serviço</option>
                        <option value="nao_incidencia">Não Incidência</option>
                      </select>
                    </div>
                    {form.tipo_nao_tributacao==="imunidade" && (
                      <div>
                        <label style={S.label}>TIPO DE IMUNIDADE</label>
                        <select style={S.input} value={form.tipo_imunidade} onChange={e=>set("tipo_imunidade",e.target.value)}>
                          <option value="1">CF/88 Art.150,VI,'a' – Entes públicos</option>
                          <option value="2">CF/88 Art.150,VI,'b' – Templos de culto</option>
                          <option value="3">CF/88 Art.150,VI,'c' – Partidos / sindicatos / educação</option>
                          <option value="4">CF/88 Art.150,VI,'d' – Livros, jornais, periódicos</option>
                          <option value="5">CF/88 Art.150,VI,'e' – Fonogramas musicais</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
                {/* ── Campos de Exportação ── */}
                {form.nao_tributacao_iss==="sim" && form.tipo_nao_tributacao==="exportacao" && (
                  <div style={{ marginTop:4, display:"flex", flexDirection:"column", gap:12 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:"#38bdf8", letterSpacing:1, textTransform:"uppercase" }}>Informações para Comércio Exterior</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <div>
                        <label style={S.label}>PAÍS DO RESULTADO DO SERVIÇO</label>
                        <select style={S.input} value={form.pais_resultado} onChange={e=>set("pais_resultado",e.target.value)}>
                          <option value="">Selecione...</option>
                          {[["AF","Afeganistão"],["DE","Alemanha"],["AO","Angola"],["AR","Argentina"],["AU","Austrália"],["AT","Áustria"],["BE","Bélgica"],["BO","Bolívia"],["CA","Canadá"],["CL","Chile"],["CN","China"],["CO","Colômbia"],["KR","Coreia do Sul"],["CU","Cuba"],["DK","Dinamarca"],["EC","Equador"],["AE","Emirados Árabes"],["ES","Espanha"],["US","Estados Unidos"],["PH","Filipinas"],["FI","Finlândia"],["FR","França"],["GH","Gana"],["IN","Índia"],["ID","Indonésia"],["IE","Irlanda"],["IL","Israel"],["IT","Itália"],["JP","Japão"],["MX","México"],["MZ","Moçambique"],["NG","Nigéria"],["NO","Noruega"],["NZ","Nova Zelândia"],["NL","Países Baixos"],["PK","Paquistão"],["PY","Paraguai"],["PE","Peru"],["PL","Polônia"],["PT","Portugal"],["GB","Reino Unido"],["DO","Rep. Dominicana"],["RU","Rússia"],["CH","Suíça"],["SE","Suécia"],["TH","Tailândia"],["TW","Taiwan"],["TR","Turquia"],["UA","Ucrânia"],["UY","Uruguai"],["VE","Venezuela"],["VN","Vietnã"],["ZA","África do Sul"]].map(([c,n])=>(
                            <option key={c} value={c}>{n}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={S.label}>MODO DE PRESTAÇÃO</label>
                        <select style={S.input} value={form.modo_prestacao} onChange={e=>set("modo_prestacao",e.target.value)}>
                          <option value="1">1 – Prestação transfronteiriça</option>
                          <option value="2">2 – Consumo no exterior</option>
                          <option value="3">3 – Presença comercial no exterior</option>
                          <option value="4">4 – Movimento de pessoas físicas</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <div>
                        <label style={S.label}>VÍNCULO ENTRE AS PARTES</label>
                        <select style={S.input} value={form.vinculo_partes} onChange={e=>set("vinculo_partes",e.target.value)}>
                          <option value="1">Sem vínculo com o tomador/Prestador</option>
                          <option value="2">Controlada</option>
                          <option value="3">Controladora</option>
                          <option value="4">Coligada</option>
                          <option value="5">Matriz</option>
                          <option value="6">Filial</option>
                          <option value="7">Outras vinculações</option>
                        </select>
                      </div>
                      <div>
                        <label style={S.label}>MOEDA</label>
                        <select style={S.input} value={form.moeda_exportacao} onChange={e=>set("moeda_exportacao",e.target.value)}>
                          <option value="">Selecione...</option>
                          {[["USD","USD – Dólar americano"],["EUR","EUR – Euro"],["GBP","GBP – Libra esterlina"],["JPY","JPY – Iene japonês"],["CHF","CHF – Franco suíço"],["CAD","CAD – Dólar canadense"],["AUD","AUD – Dólar australiano"],["ARS","ARS – Peso argentino"],["CLP","CLP – Peso chileno"],["MXN","MXN – Peso mexicano"],["CNY","CNY – Yuan chinês"],["RUB","RUB – Rublo russo"],["INR","INR – Rúpia indiana"],["ZAR","ZAR – Rand sul-africano"],["UYU","UYU – Peso uruguaio"]].map(([c,n])=>(
                            <option key={c} value={c}>{n}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <div>
                        <label style={S.label}>MECANISMO DE APOIO – PRESTADOR</label>
                        <select style={S.input} value={form.mecanismo_prestador} onChange={e=>set("mecanismo_prestador",e.target.value)}>
                          <option value="9">Sem mecanismo de apoio</option>
                          <option value="1">PROEX Equalização</option>
                          <option value="2">PROEX Financiamento</option>
                          <option value="3">BNDES-Exim Pós-embarque</option>
                          <option value="4">BNDES-Exim Pré-embarque</option>
                          <option value="5">Garantias às Exportações</option>
                          <option value="6">Seguro de Crédito à Exportação</option>
                          <option value="7">ACC/ACE</option>
                          <option value="8">Outros</option>
                        </select>
                      </div>
                      <div>
                        <label style={S.label}>MECANISMO DE APOIO – TOMADOR</label>
                        <select style={S.input} value={form.mecanismo_tomador} onChange={e=>set("mecanismo_tomador",e.target.value)}>
                          <option value="9">Sem mecanismo de apoio</option>
                          <option value="1">PROEX Equalização</option>
                          <option value="2">PROEX Financiamento</option>
                          <option value="3">BNDES-Exim Pós-embarque</option>
                          <option value="4">BNDES-Exim Pré-embarque</option>
                          <option value="5">Garantias às Exportações</option>
                          <option value="6">Seguro de Crédito à Exportação</option>
                          <option value="7">ACC/ACE</option>
                          <option value="8">Outros</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <div>
                        <label style={S.label}>VÍNCULO COM MOVIMENTAÇÃO TEMPORÁRIA DE BENS?</label>
                        <select style={S.input} value={form.vinculo_movimentacao_bens} onChange={e=>set("vinculo_movimentacao_bens",e.target.value)}>
                          <option value="1">Não</option>
                          <option value="2">Vinculada – Declaração de Importação</option>
                          <option value="3">Vinculada – Declaração de Exportação</option>
                        </select>
                      </div>
                      <div>
                        <label style={S.label}>COMPARTILHAR NFS-e COM MDIC?</label>
                        <select style={S.input} value={form.compartilhar_mdic} onChange={e=>set("compartilhar_mdic",e.target.value)}>
                          <option value="nao">Não</option>
                          <option value="sim">Sim</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Exigibilidade Suspensa */}
              <div style={{ background:T.cardInner, border:"1px solid "+T.border, borderRadius:8, padding:14, display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ fontSize:12, fontWeight:600, color:T.textMuted }}>A exigibilidade do recolhimento do ISSQN está suspensa?</div>
                <div style={{ display:"flex", gap:20 }}>
                  {[["nao","Não"],["sim","Sim"]].map(([v,l])=>(
                    <label key={v} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:T.text, cursor:"pointer" }}>
                      <input type="radio" name="srv_exig" value={v} checked={form.exig_susp===v} onChange={()=>set("exig_susp",v)} /> {l}
                    </label>
                  ))}
                </div>
                {form.exig_susp==="sim" && (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    <div>
                      <label style={S.label}>TIPO DE SUSPENSÃO</label>
                      <select style={S.input} value={form.tipo_susp} onChange={e=>set("tipo_susp",e.target.value)}>
                        <option value="1">Decisão Judicial</option>
                        <option value="2">Processo Administrativo</option>
                      </select>
                    </div>
                    <div>
                      <label style={S.label}>NÚMERO DO PROCESSO</label>
                      <input style={S.input} placeholder="Ex: 0001234-56.2024.8.16.0001" value={form.num_processo_susp} onChange={e=>set("num_processo_susp",e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              {/* Benefício Municipal */}
              <div style={{ background:T.cardInner, border:"1px solid "+T.border, borderRadius:8, padding:14, display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ fontSize:12, fontWeight:600, color:T.textMuted }}>O serviço está amparado por benefício municipal fiscal?</div>
                <div style={{ display:"flex", gap:20 }}>
                  {[["nao","Não"],["sim","Sim"]].map(([v,l])=>(
                    <label key={v} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:T.text, cursor:"pointer" }}>
                      <input type="radio" name="srv_bm" value={v} checked={form.tem_beneficio_mun===v} onChange={()=>set("tem_beneficio_mun",v)} /> {l}
                    </label>
                  ))}
                </div>
                {form.tem_beneficio_mun==="sim" && (
                  <div>
                    <label style={S.label}>CÓDIGO DO BENEFÍCIO (nBM)</label>
                    <input style={S.input} placeholder="Ex: 41069020200013" value={form.codigo_bm} onChange={e=>set("codigo_bm",e.target.value)} />
                  </div>
                )}
              </div>

              {/* Dedução à Base de Cálculo */}
              <div style={{ background:T.cardInner, border:"1px solid "+T.border, borderRadius:8, padding:14, display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ fontSize:12, fontWeight:600, color:T.textMuted }}>Este serviço permite dedução/redução à base de cálculo do ISSQN?</div>
                <div style={{ display:"flex", gap:20 }}>
                  {[["nao","Não"],["sim","Sim"]].map(([v,l])=>(
                    <label key={v} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:T.text, cursor:"pointer" }}>
                      <input type="radio" name="srv_ded" value={v} checked={form.tem_deducao_bc===v} onChange={()=>set("tem_deducao_bc",v)} /> {l}
                    </label>
                  ))}
                </div>
              </div>

              {/* IBS/CBS — Reforma Tributária do Consumo */}
              <div style={{ background:T.cardInner, border:"1px solid #0ea5e940", borderRadius:8, padding:14, display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#38bdf8" }}>IBS/CBS — REFORMA TRIBUTÁRIA DO CONSUMO (v1.01)</div>
                  <div style={{ fontSize:10, background:"#0c2a3a", color:"#38bdf8", border:"1px solid #0ea5e940", borderRadius:4, padding:"1px 6px" }}>Obrigatório ago/2026 (imóveis)</div>
                </div>
                <div style={{ fontSize:12, fontWeight:600, color:T.textMuted }}>Habilitar informações IBS/CBS neste serviço?</div>
                <div style={{ display:"flex", gap:20 }}>
                  {[["nao","Não"],["sim","Sim"]].map(([v,l])=>(
                    <label key={v} style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, color:T.text, cursor:"pointer" }}>
                      <input type="radio" name="srv_ibs" value={v} checked={form.ibscbs_habilitado===v} onChange={()=>set("ibscbs_habilitado",v)} /> {l}
                    </label>
                  ))}
                </div>
                {form.ibscbs_habilitado === "sim" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:10, marginTop:4 }}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                      <IbsCbsCodigoInput
                        value={form.ibscbs_c_ind_op}
                        onChange={v=>set("ibscbs_c_ind_op",v)}
                        codigos={IBS_CIND_OP_CODES}
                        label="CÓD. INDICADOR DE OPERAÇÃO (cIndOp)"
                        sublabel="Digite código ou palavra-chave para filtrar"
                        placeholder="Ex: 020301 (6 dígitos)"
                        maxLength={6}
                        obrigatorio={true}
                        S={S} T={T}
                      />
                      <IbsCbsCodigoInput
                        value={form.ibscbs_cst}
                        onChange={v=>set("ibscbs_cst",v)}
                        codigos={IBS_CST_CODES}
                        label="CST IBS/CBS"
                        sublabel="Código de Situação Tributária"
                        placeholder="Ex: 000 (3 dígitos)"
                        maxLength={3}
                        obrigatorio={true}
                        S={S} T={T}
                      />
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                      {/* Os 3 primeiros dígitos do cClassTrib SÃO o CST. Filtrar a lista
                          pelo CST escolhido evita a combinação inválida, que a SEFIN rejeita. */}
                      <IbsCbsCodigoInput
                        value={form.ibscbs_c_class_trib}
                        onChange={v=>set("ibscbs_c_class_trib",v)}
                        codigos={
                          /^\d{3}$/.test(form.ibscbs_cst || "")
                            ? IBS_CLASS_TRIB_CODES.filter(x => x.c.startsWith(form.ibscbs_cst))
                            : IBS_CLASS_TRIB_CODES
                        }
                        label="CLASSIFICAÇÃO TRIBUTÁRIA (cClassTrib)"
                        sublabel={
                          /^\d{3}$/.test(form.ibscbs_cst || "")
                            ? `Mostrando apenas os do CST ${form.ibscbs_cst}`
                            : "Escolha o CST primeiro para filtrar a lista"
                        }
                        placeholder="Ex: 000001 (6 dígitos)"
                        maxLength={6}
                        obrigatorio={true}
                        S={S} T={T}
                      />
                      <div>
                        <label style={S.label}>DESTINATÁRIO (indDest)</label>
                        <select style={S.input} value={form.ibscbs_ind_dest} onChange={e=>set("ibscbs_ind_dest",e.target.value)}>
                          <option value="0">0 — Destinatário é o próprio tomador</option>
                          <option value="1">1 — Destinatário diferente do adquirente</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                      <div>
                        <label style={S.label}>CIB DO IMÓVEL (cCIB) — opcional</label>
                        <input style={S.input} placeholder="8 dígitos (Cadastro Imobiliário Brasileiro)" maxLength={8} value={form.ibscbs_c_cib} onChange={e=>set("ibscbs_c_cib",e.target.value.replace(/\D/g,""))} />
                      </div>
                      <div>
                        <label style={S.label}>TIPO DE OPERAÇÃO (tpOper) — opcional</label>
                        <select style={S.input} value={form.ibscbs_tp_oper} onChange={e=>set("ibscbs_tp_oper",e.target.value)}>
                          <option value="">— Não se aplica —</option>
                          <option value="1">1 — Fornecimento com pagamento posterior</option>
                          <option value="2">2 — Pagamento com fornecimento já realizado</option>
                          <option value="3">3 — Fornecimento com pagamento já realizado</option>
                          <option value="4">4 — Pagamento com fornecimento posterior</option>
                          <option value="5">5 — Fornecimento e pagamento concomitantes</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                      <div>
                        <label style={S.label}>ENTE GOVERNAMENTAL (tpEnteGov) — opcional</label>
                        <select style={S.input} value={form.ibscbs_tp_ente_gov} onChange={e=>set("ibscbs_tp_ente_gov",e.target.value)}>
                          <option value="">— Não se aplica —</option>
                          <option value="1">1 — União</option>
                          <option value="2">2 — Estado</option>
                          <option value="3">3 — Distrito Federal</option>
                          <option value="4">4 — Município</option>
                        </select>
                      </div>
                      <div>
                        <label style={S.label}>USO/CONSUMO PESSOAL (indFinal) — opcional</label>
                        <select style={S.input} value={form.ibscbs_ind_final} onChange={e=>set("ibscbs_ind_final",e.target.value)}>
                          <option value="">— Não informar —</option>
                          <option value="0">0 — Não (uso comercial/empresarial)</option>
                          <option value="1">1 — Sim (uso ou consumo pessoal)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {erroModal && (
                <div style={{ background:"#2f0d0d", border:"1px solid #ef444440", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#f87171" }}>
                  <AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {erroModal}
                </div>
              )}
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:8 }}>
                <button onClick={()=>setShowModal(false)} disabled={salvando} style={{ background:"transparent", border:"1px solid "+T.border, borderRadius:8, padding:"9px 18px", fontSize:13, color:T.textFaint, cursor:"pointer" }}>Cancelar</button>
                <button onClick={salvar} disabled={salvando} style={{ background: salvando ? T.btnSecBg : "linear-gradient(135deg,#0ea5e9,#0284c7)", color: salvando ? T.btnSecText : "#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor: salvando ? "not-allowed" : "pointer" }}>
                  {salvando ? "Salvando..." : "Salvar Serviço"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// NOVA EmitirView — simplificada (seleciona serviço do catálogo)
// ============================================================

// Nome de cada campo que a validação da emissão pode reprovar. "Corrija os
// campos em vermelho" sozinho não ajuda: o campo pode estar longe, fora da
// tela, e o banner de erro fica no topo. Com o nome dá para ir direto nele.
const ROTULOS_EMISSAO = {
  municipio_incidencia: "Município de incidência",
  aliquota_iss_nota: "Alíquota ISS",
  tomador_razao_social: "Razão social / nome do tomador",
  tomador_cep: "CEP do tomador",
  tomador_nif: "NIF do tomador",
  tomador_codigo_pais: "País do tomador",
  tomador_municipio_exterior: "Cidade do tomador no exterior",
  tomador_cep_exterior: "Código postal do tomador",
  tomador_estado_exterior: "Estado/província do tomador",
  v_ret_irrf: "IRRF", v_ret_csll: "CSLL", v_ret_cp: "Contribuição Previdenciária",
  desconto_incondicionado: "Desconto incondicionado",
  desconto_condicionado: "Desconto condicionado",
  valor_deducao_bc: "Dedução da base de cálculo",
  tp_oper_nota: "Tipo de operação",
  cno: "Código da obra (CNO)", obra_cib: "Código CIB", obra_forma: "Identificação da obra",
  obra_logradouro: "Logradouro da obra", obra_numero: "Número da obra",
  obra_bairro: "Bairro da obra", obra_cep: "CEP da obra",
  obra_cidade_ext: "Cidade da obra", obra_estado_ext: "Estado da obra",
  com_ext_md_prestacao: "Modo de prestação", com_ext_vinc_prest: "Vínculo da prestação",
  com_ext_tp_moeda: "Moeda", com_ext_v_serv_moeda: "Valor em moeda estrangeira",
  com_ext_mec_af_comex_p: "Mecanismo do prestador", com_ext_mec_af_comex_t: "Mecanismo do tomador",
  com_ext_mov_temp_bens: "Movimentação temporária de bens",
};

function EmitirView({ token, onSuccess, onIrParaEmpresas, isMobile = false, T = {bg:"#080c10",sidebar:"#0c1117",border:"#1a2332",card:"#0c1117",cardInner:"#080c10",text:"#f1f5f9",textMuted:"#94a3b8",textFaint:"#475569",input:"#080c10",inputBorder:"#1a2332",inputText:"#e2e8f0",btnSecBg:"#1a2332",btnSecText:"#94a3b8",rowBorder:"#0f1923",tableBg:"#0c1117"} }) {
  const [empresas, setEmpresas] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [empresaId, setEmpresaId] = useState("");
  const [servicoId, setServicoId] = useState("");
  // Municipio escolhido na nota (so usado quando o servico nao tem municipio fixo)
  const [munIncidNota, setMunIncidNota] = useState(null);
  // Pessoa (CPF) escolhida do cadastro, com o registro completo — usada para
  // gravar de volta o endereço que for preenchido só na emissão.
  const [tomadorPessoa, setTomadorPessoa] = useState(null);
  const [servicoSel, setServicoSel] = useState(null);
  const restaurandoRef = useRef(false);
  const emitindoRef = useRef(false);
  const [form, setForm] = useState({
    tomador_razao_social:"", tomador_cnpj:"", tomador_cpf:"",
    servico_descricao:"", informacoes_complementares:"", cno:"",
    aliquota_iss_nota:"",
    obra_forma:"cno", obra_cib:"", obra_cep:"", obra_logradouro:"", obra_numero:"",
    obra_complemento:"", obra_bairro:"", obra_cod_postal:"", obra_cidade_ext:"",
    obra_estado_ext:"", inscricao_imobiliaria_fiscal:"",
    tomador_email:"", tomador_fone:"", tomador_endereco:"", tomador_numero:"",
    tomador_bairro:"", tomador_municipio:"", tomador_municipio_codigo:"", tomador_cep:"", tomador_uf:"",
    tomador_natureza_juridica:"",
    // Tipo de operação e ente governamental, escolhidos por nota: dependem de
    // quem é o tomador, não do serviço.
    tp_oper_nota:"", tp_ente_gov_nota:"",
    valor_servico:"", quantidade:"1", data_emissao: new Date().toISOString().slice(0,10),
    tomador_nao_informado: false,
    tomador_localizacao: "brasil",
    tomador_nif: "", tomador_c_nao_nif: "",
    tomador_pais_nome: "", tomador_codigo_pais: "", tomador_municipio_exterior: "",
    tomador_cep_exterior: "", tomador_estado_exterior: "",
    com_ext_md_prestacao:"", com_ext_vinc_prest:"", com_ext_tp_moeda:"",
    com_ext_v_serv_moeda:"", com_ext_mec_af_comex_p:"01", com_ext_mec_af_comex_t:"01",
    com_ext_mov_temp_bens:"1", com_ext_mdic:"",
    tem_intermediario: false,
    intermediario_cnpj:"", intermediario_cpf:"", intermediario_razao_social:"",
    v_ret_irrf:"", v_ret_csll:"", v_ret_cp:"",
    // Descontos (variam por nota)
    desconto_incondicionado:"", desconto_condicionado:"",
    // Valor da dedução BC ISSQN (varia por nota; só aparece se serviço tem tem_deducao_bc=true)
    valor_deducao_bc:"",
    // Tributos aproximados monetários (modo 1 — variam por nota)
    v_trib_fed:"", v_trib_est:"", v_trib_mun:"",
  });
  const [docRestaurado, setDocRestaurado] = useState("");
  const [camposBloqueados, setCamposBloqueados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [erroQtd, setErroQtd] = useState("");
  const [errosCampos, setErrosCampos] = useState({});
  const erroRef = useRef(null);
  const [intermediarioBuscando, setIntermediarioBuscando] = useState(false);
  const [intermediarioErro, setIntermediarioErro] = useState("");
  const set = (k,v) => { setForm(f=>({...f,[k]:v})); setErrosCampos(e=>({...e,[k]:""})); };

  const buscarCnpjIntermediario = async (digits) => {
    if (digits.length !== 14) return;
    setIntermediarioBuscando(true); setIntermediarioErro("");
    try {
      // Mesma consulta com reserva usada no tomador e no cadastro de empresa.
      const d = await consultarCNPJ(digits);
      if (d.razao_social) set("intermediario_razao_social", d.razao_social);
    } catch (ex) {
      setIntermediarioErro(ex?.message === "nao_encontrado"
        ? "CNPJ não encontrado na Receita Federal"
        : "Consulta de CNPJ indisponível — digite a razão social manualmente");
    } finally {
      setIntermediarioBuscando(false);
    }
  };

  const S = {
    input: { width:"100%", background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8, padding: isMobile ? "14px" : "10px 14px", fontSize:13, color:T.inputText, outline:"none", boxSizing:"border-box" },
    label: { fontSize:12, fontWeight:600, color:T.textMuted, display:"block", marginBottom:6 }
  };
  const bloqStyle = (campo, base) => camposBloqueados.includes(campo)
    ? { ...base, background:T.lockedBg, border:"1px solid "+T.lockedBorder, color:T.lockedText, cursor:"not-allowed" }
    : base;
  // Borda vermelha + mensagem embaixo do campo. Os campos da obra ficaram de
  // fora disso por muito tempo: a validação reprovava o CEP e nada na tela
  // ficava vermelho, só o aviso genérico no topo.
  const errStyle = (campo) => ({ ...S.input, ...(errosCampos[campo] ? { borderColor:"#ef4444" } : {}) });
  const errMsg = (campo) => errosCampos[campo] ? (
    <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}>
      <AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos[campo]}
    </div>
  ) : null;

  useEffect(() => {
    const salvo = localStorage.getItem("nfse_emitir_rascunho");
    let parsed = null;
    if (salvo) {
      try { parsed = JSON.parse(salvo); } catch {}
      localStorage.removeItem("nfse_emitir_rascunho");
      if (parsed) {
        // Merge com o form atual (vazio) para garantir que todos os campos existam
        setForm(f => ({ ...f, ...parsed }));
        restaurandoRef.current = true;
      }
    }
    const docNovo = localStorage.getItem("nfse_emitir_doc_novo");
    if (docNovo) {
      localStorage.removeItem("nfse_emitir_doc_novo");
      setDocRestaurado(docNovo);
    } else if (parsed) {
      // Fallback: restaurar documento do rascunho se nfse_emitir_doc_novo não foi salvo
      const docFallback = ((parsed.tomador_cpf || parsed.tomador_cnpj) || "").replace(/\D/g, "");
      if (docFallback) setDocRestaurado(docFallback);
    }
  }, []);

  useEffect(() => {
    fetch(API+"/empresas",{headers:{Authorization:"Bearer "+token}}).then(r=>r.json()).then(d=>{
      if(Array.isArray(d)){
        setEmpresas(d);
        const salvoEmpresa = localStorage.getItem("nfse_emitir_empresa");
        if (salvoEmpresa) { localStorage.removeItem("nfse_emitir_empresa"); setEmpresaId(salvoEmpresa); gravarEmpresaAtual(salvoEmpresa); }
        else if(d.length>0) setEmpresaId(empresaInicial(d, lerEmpresaAtual()));
      }
    });
  },[token]);

  useEffect(() => {
    if(!empresaId) return;
    fetch(`${API}/servicos?empresa_id=${empresaId}`,{headers:{Authorization:"Bearer "+token}}).then(r=>r.json()).then(d=>{
      if(Array.isArray(d)){
        setServicos(d);
        const salvoServico = localStorage.getItem("nfse_emitir_servico");
        const salvoCodigo  = localStorage.getItem("nfse_emitir_servico_codigo");
        const salvoNome    = localStorage.getItem("nfse_emitir_servico_nome");
        if (salvoServico) {
          localStorage.removeItem("nfse_emitir_servico");
          setServicoId(salvoServico);
        } else if (salvoCodigo) {
          localStorage.removeItem("nfse_emitir_servico_codigo");
          localStorage.removeItem("nfse_emitir_servico_nome");
          // Tenta match exato por nome + código; fallback só por código
          const match =
            d.find(s => s.codigo_lc116 === salvoCodigo && s.nome === salvoNome) ||
            d.find(s => s.codigo_lc116 === salvoCodigo);
          if (match) setServicoId(match.id);
          else { setServicoId(""); setServicoSel(null); }
        } else {
          setServicoId(""); setServicoSel(null);
        }
      }
    });
  },[empresaId]);

  useEffect(() => {
    if(!servicoId) { setServicoSel(null); return; }
    const s = servicos.find(x=>x.id===servicoId);
    setServicoSel(s||null);
    // Servico com municipio fixo nao usa a escolha por nota
    if (!s || s.municipio_fixo !== false) { setMunIncidNota(null); set("aliquota_iss_nota",""); }
    // Município vindo de um "Copiar nota". Só se aplica quando o serviço deixa
    // escolher por nota; nos demais casos vale o município do próprio serviço.
    const munCopiado = localStorage.getItem("nfse_emitir_mun_incid");
    if (munCopiado) {
      localStorage.removeItem("nfse_emitir_mun_incid");
      if (s && s.municipio_fixo === false) {
        try { setMunIncidNota(JSON.parse(munCopiado)); } catch {}
      }
    }
    // Pré-preencher descrição e inf. complementares ao selecionar serviço
    // (exceto quando restaurando rascunho — preserva o que o usuário havia editado)
    if (restaurandoRef.current) { restaurandoRef.current = false; return; }
    if(s){
      set("servico_descricao", s.descricao||"");
      set("informacoes_complementares", s.informacoes_complementares||"");
    } else {
      set("servico_descricao", "");
      set("informacoes_complementares", "");
    }
  },[servicoId]);

  // ── Retenções x pessoa física ───────────────────────────────────────────────
  // Pessoa física não é substituto tributário: não pode reter tributo na fonte.
  // Se o serviço cadastrado tem retenção e a nota sai para CPF, as retenções são
  // zeradas automaticamente — emitir assim geraria imposto retido indevidamente.
  const tomadorEhCPF = !!(form.tomador_cpf || "").replace(/\D/g, "");

  // Sem certificado válido a assinatura da DPS é impossível. `certificado_validade`
  // vem nulo quando não há arquivo; quando há, traz a data de expiração.
  const empresaAtual = empresas.find(e => e.id === empresaId);
  const problemaCertificado = (() => {
    if (!empresaAtual) return null;
    const validade = empresaAtual.certificado_validade;
    if (!validade) {
      return {
        titulo: "Esta empresa está sem certificado digital.",
        texto: "Não é possível emitir NFS-e sem ele — a nota precisa ser assinada. "
             + "Envie o arquivo .pfx no cadastro da empresa.",
      };
    }
    const dias = Math.ceil((new Date(validade) - new Date()) / 86400000);
    if (dias < 0) {
      return {
        titulo: `O certificado desta empresa venceu em ${fmtData(validade)}.`,
        texto: "Certificado vencido não assina nota. Envie o novo arquivo .pfx no cadastro da empresa.",
      };
    }
    return null;
  })();

  // ── Município de incidência pela regra do IBS/CBS ─────────────────────────
  // O Anexo VIII da NFS-e Nacional diz, para cada NBS, onde a operação é
  // considerada realizada. Quando é "domicílio principal do adquirente", o
  // município da nota deve ser o do tomador — e ele já vem da consulta do CNPJ.
  const regraIncidencia = incidenciaDaNbs(servicoSel?.codigo_nbs);
  const tomadorTemMunicipio = !!(form.tomador_municipio_codigo && form.tomador_municipio)
    && !form.tomador_nao_informado && form.tomador_localizacao !== "exterior";
  const segueDomicilioTomador = regraIncidencia?.local === "A" && tomadorTemMunicipio;
  // Com essa regra o município é por nota mesmo que o serviço esteja como "fixo":
  // a lei manda seguir o tomador, e o cadastro não pode contrariar isso.
  const municipioPorNota = !!servicoSel && (servicoSel.municipio_fixo === false || segueDomicilioTomador);

  // A SEFIN exige o endereço completo do tomador (E0234) quando a operação é
  // localizada nele: domicílio do adquirente (100101/100301/100501) ou endereço
  // do adquirente (030102/050102). Vale para o cIndOp que a nota vai levar —
  // o escolhido no serviço ou, na falta, o sugerido pela NBS.
  const cIndOpDaNota = String(servicoSel?.ibscbs_c_ind_op || regraIncidencia?.indop || "");
  const exigeEnderecoTomador = !!servicoSel && !form.tomador_nao_informado
    && form.tomador_localizacao !== "exterior"
    && (regraIncidencia?.local === "A" || ["030102", "050102", "100101", "100301", "100501"].includes(cIndOpDaNota));

  useEffect(() => {
    if (!segueDomicilioTomador) return;
    const mun = {
      codigo: String(form.tomador_municipio_codigo),
      nome: form.tomador_municipio,
      uf: form.tomador_uf || "",
    };
    // Roda só quando serviço ou tomador mudam — uma troca manual do município
    // depois disso não dispara o efeito, então não é desfeita.
    setMunIncidNota(mun);
    set("municipio_incidencia", mun.codigo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segueDomicilioTomador, form.tomador_municipio_codigo, servicoSel?.id]);

  // Compra governamental: a natureza jurídica iniciada em 1 é Administração
  // Pública (órgãos, autarquias, fundações públicas). Nesses casos, e também
  // nos serviços LC116 abaixo, a SEFIN exige o tipo de operação (E0903).
  const LC116_EXIGE_TP_OPER = ["25.05", "15.09", "17.12", "10.05"];
  const tomadorEhOrgaoPublico = String(form.tomador_natureza_juridica || "").startsWith("1");
  const servicoExigeTpOper = LC116_EXIGE_TP_OPER.some(
    c => String(servicoSel?.codigo_lc116 || "").startsWith(c));
  const exigeTpOper = tomadorEhOrgaoPublico || servicoExigeTpOper;

  const retencoesDoServico = (() => {
    const r = [];
    if (servicoSel?.iss_retido) r.push("ISS retido na fonte");
    const tp = servicoSel?.tp_ret_pis_cofins_csll;
    if (tp && tp !== 4) r.push("Retenção de PIS/COFINS/CSLL");
    if (parseBR(form.v_ret_irrf) > 0) r.push("IRRF");
    if (parseBR(form.v_ret_csll) > 0) r.push("CSLL");
    if (parseBR(form.v_ret_cp)   > 0) r.push("Contribuição Previdenciária");
    return r;
  })();

  const retencoesRemovidas = tomadorEhCPF ? retencoesDoServico : [];

  // Endereço de obra no exterior só é válido quando a própria prestação ocorre
  // fora do Brasil — ou seja, em serviço marcado como exportação. Mesma regra do
  // Emissor Nacional: "Não é possível informar um endereço no exterior quando o
  // local da prestação do serviço for no Brasil".
  const prestacaoNoExterior = !!(
    servicoSel?.nao_tributacao_iss && servicoSel?.tipo_nao_tributacao === "exportacao"
  );

  // Completa o cadastro da pessoa com o que foi digitado na emissão. Só
  // preenche o que estava VAZIO no cadastro — nunca sobrescreve um dado que
  // já existia, para um ajuste pontual numa nota não alterar o registro.
  const completarCadastroPessoa = async () => {
    const p = tomadorPessoa;
    if (!p?.id || !tomadorEhCPF) return;

    const paraCadastro = {
      endereco: "tomador_endereco", numero: "tomador_numero", bairro: "tomador_bairro",
      municipio: "tomador_municipio", municipio_codigo: "tomador_municipio_codigo",
      uf: "tomador_uf", cep: "tomador_cep", email: "tomador_email", fone: "tomador_fone",
    };
    const novo = { ...p };
    let preenchidos = [];
    for (const [campo, campoForm] of Object.entries(paraCadastro)) {
      const noCadastro = String(p[campo] || "").trim();
      const digitado   = String(form[campoForm] || "").trim();
      if (!noCadastro && digitado) {
        novo[campo] = campo === "cep" ? digitado.replace(/\D/g, "") : digitado;
        preenchidos.push(campo);
      }
    }
    if (!preenchidos.length) return;

    try {
      const r = await fetch(`${API}/pessoas/${p.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({
          empresa_id: p.empresa_id || "", cpf: String(p.cpf || "").replace(/\D/g, ""), nome: p.nome,
          email: novo.email || "", fone: novo.fone || "",
          endereco: novo.endereco || "", numero: novo.numero || "", bairro: novo.bairro || "",
          municipio: novo.municipio || "", municipio_codigo: novo.municipio_codigo || "",
          uf: novo.uf || "", cep: novo.cep || "", observacao: p.observacao || "",
        }),
      });
      if (r.ok) setTomadorPessoa(await r.json());
    } catch { /* não pode impedir a emissão */ }
  };

  const handleDocumento = (result) => {
    if(!result){ setCamposBloqueados([]); setTomadorPessoa(null); return; }
    const b = [];
    setTomadorPessoa(result.tipo==="cpf" ? (result.pessoa || null) : null);
    if(result.tipo==="cpf"){
      set("tomador_cpf", result.doc); set("tomador_cnpj", "");
    } else {
      set("tomador_cnpj", result.doc); set("tomador_cpf", "");
    }
    // Preenche todos os campos disponíveis (CPF do banco ou CNPJ da Receita)
    if(result.razao_social){ set("tomador_razao_social", result.razao_social); b.push("tomador_razao_social"); }
    if(result.email){ set("tomador_email", result.email); b.push("tomador_email"); }
    if(result.fone){ set("tomador_fone", result.fone); b.push("tomador_fone"); }
    if(result.endereco){ set("tomador_endereco", result.endereco); b.push("tomador_endereco"); }
    if(result.numero){ set("tomador_numero", result.numero); b.push("tomador_numero"); }
    if(result.bairro){ set("tomador_bairro", result.bairro); b.push("tomador_bairro"); }
    if(result.municipio){ set("tomador_municipio", result.municipio); b.push("tomador_municipio"); }
    if(result.municipio_codigo){ set("tomador_municipio_codigo", result.municipio_codigo); }
    if(result.uf){ set("tomador_uf", result.uf); b.push("tomador_uf"); }
    if(result.cep){ set("tomador_cep", result.cep); b.push("tomador_cep"); }
    // Guardada para saber se a nota é compra governamental — nesse caso a SEFIN
    // exige o tipo de operação do grupo IBS/CBS (erro E0903).
    set("tomador_natureza_juridica", result.natureza_juridica || "");
    setCamposBloqueados(b);
  };

  const handleCepTomador = async (valor) => {
    const nums = valor.replace(/\D/g,"");
    const fmt = nums.length > 5 ? nums.replace(/(\d{5})(\d{1,3})/,"$1-$2") : nums;
    set("tomador_cep", fmt);
    if (nums.length === 8) {
      try {
        const r = await fetch(`https://viacep.com.br/ws/${nums}/json/`);
        const d = await r.json();
        if (!d.erro) {
          if (d.logradouro && !camposBloqueados.includes("tomador_endereco")) set("tomador_endereco", d.logradouro);
          if (d.bairro && !camposBloqueados.includes("tomador_bairro")) set("tomador_bairro", d.bairro);
          if (d.localidade && !camposBloqueados.includes("tomador_municipio")) set("tomador_municipio", d.localidade);
          if (d.uf && !camposBloqueados.includes("tomador_uf")) set("tomador_uf", d.uf);
          if (d.ibge) set("tomador_municipio_codigo", d.ibge);
        }
      } catch {}
    }
  };

  const validarEmissao = (valorTotal) => {
    const erros = {};
    const irrf  = parseBR(form.v_ret_irrf);
    const csll  = parseBR(form.v_ret_csll);
    const cp    = parseBR(form.v_ret_cp);
    const descInc = parseBR(form.desconto_incondicionado);
    const descCond = parseBR(form.desconto_condicionado);
    const dedBC   = parseBR(form.valor_deducao_bc);

    // E0700 — IRRF
    if (irrf < 0) erros.v_ret_irrf = "IRRF não pode ser negativo.";
    else if (irrf > 0 && irrf >= valorTotal) erros.v_ret_irrf = `IRRF (${numToBR(irrf)}) deve ser menor que o valor total do serviço (${numToBR(valorTotal)}).`;

    // E0701 — CSLL
    if (csll < 0) erros.v_ret_csll = "CSLL não pode ser negativa.";
    else if (csll > 0 && csll >= valorTotal) erros.v_ret_csll = `CSLL (${numToBR(csll)}) deve ser menor que o valor total do serviço (${numToBR(valorTotal)}).`;

    // E0702 — Contribuição Previdenciária
    if (cp < 0) erros.v_ret_cp = "Contribuição Previdenciária não pode ser negativa.";
    else if (cp > 0 && cp >= valorTotal) erros.v_ret_cp = `CP (${numToBR(cp)}) deve ser menor que o valor total do serviço (${numToBR(valorTotal)}).`;

    // Desconto incondicionado
    if (descInc < 0) erros.desconto_incondicionado = "Desconto não pode ser negativo.";
    else if (descInc >= valorTotal) erros.desconto_incondicionado = `Desconto (${numToBR(descInc)}) não pode ser maior ou igual ao valor do serviço.`;

    // Desconto condicionado
    if (descCond < 0) erros.desconto_condicionado = "Desconto não pode ser negativo.";
    else if (descCond >= valorTotal) erros.desconto_condicionado = `Desconto (${numToBR(descCond)}) não pode ser maior ou igual ao valor do serviço.`;

    // Dedução BC
    if (servicoSel?.tem_deducao_bc && dedBC < 0) erros.valor_deducao_bc = "Dedução não pode ser negativa.";
    else if (servicoSel?.tem_deducao_bc && dedBC >= valorTotal) erros.valor_deducao_bc = `Dedução (${numToBR(dedBC)}) não pode ser maior ou igual ao valor do serviço.`;

    // Alíquota de retenção ISS (quando serviço tem iss_retido e não é Lucro Presumido/Real)
    // op_simp_nac = 1 → Não optante: NFS-e nacional proíbe informar alíquota (E0617), não valida
    // Tomador pessoa física: a retenção é removida na emissão, então não faz
    // sentido exigir alíquota de retenção — senão a nota trava sem motivo.
    if (servicoSel?.iss_retido && servicoSel?.op_simp_nac !== 1 && !tomadorEhCPF) {
      const aliqRet = parseFloat(servicoSel.aliquota_iss);
      if (!aliqRet || aliqRet <= 0) {
        erros._aliquota_iss_retencao = "Alíquota de retenção ISS não pode ser zero. Edite o serviço e informe a alíquota.";
      } else if (String(servicoSel.op_simp_nac) === "3" && aliqRet < 1.8) {
        erros._aliquota_iss_retencao = `Alíquota ${aliqRet}% é inferior ao mínimo de 1,8% exigido para ME/EPP no Simples Nacional (E0621). Edite o serviço.`;
      }
    }

    // Compra governamental e alguns códigos LC116 exigem o tipo de operação.
    // Sem isso a SEFIN recusa com E0903, já depois de tudo assinado e enviado.
    if (exigeTpOper && !(form.tp_oper_nota || servicoSel?.ibscbs_tp_oper)) {
      erros.tp_oper_nota = tomadorEhOrgaoPublico
        ? "O tomador é órgão público: informe o tipo de operação."
        : "Este código de tributação exige o tipo de operação.";
    }

    // Tomador obrigatório quando não marcado como não informado
    if (!form.tomador_nao_informado && !form.tomador_razao_social.trim())
      erros.tomador_razao_social = "Nome/Razão Social do tomador é obrigatório.";

    // CEP deve ter 8 dígitos se preenchido
    const cepNums = (form.tomador_cep||"").replace(/\D/g,"");
    if (cepNums.length > 0 && cepNums.length !== 8)
      erros.tomador_cep = "CEP deve ter 8 dígitos.";

    // Quando a incidência é no domicílio ou endereço do tomador, a SEFIN exige o
    // endereço completo dele (E0234). Melhor barrar aqui do que depois de assinar
    // e transmitir a nota.
    if (exigeEnderecoTomador) {
      const faltam = [];
      if (!String(form.tomador_endereco||"").trim()) faltam.push("logradouro");
      if (!String(form.tomador_numero||"").trim())   faltam.push("número");
      if (!String(form.tomador_bairro||"").trim())   faltam.push("bairro");
      if (cepNums.length !== 8)                       faltam.push("CEP");
      if (!String(form.tomador_municipio_codigo||"").trim()) faltam.push("município");
      if (faltam.length) {
        erros.tomador_endereco =
          `Este serviço tem incidência no domicílio do tomador, e a SEFIN exige o endereço `
          + `completo dele. Faltando: ${faltam.join(", ")}. `
          + (tomadorEhCPF ? "Complete o cadastro da pessoa e selecione de novo."
                          : "Preencha os campos do endereço.");
      }
    }

    // Validações tomador exterior
    if (form.tomador_localizacao === "exterior") {
      if (!form.tomador_nif && !form.tomador_c_nao_nif)
        erros.tomador_nif = "Informe o NIF ou selecione o motivo de não informação.";
      if (!form.tomador_razao_social?.trim())
        erros.tomador_razao_social = "Nome/Razão Social é obrigatório.";
      if (!form.tomador_codigo_pais)
        erros.tomador_codigo_pais = "País é obrigatório.";
      if (!form.tomador_municipio_exterior?.trim())
        erros.tomador_municipio_exterior = "Cidade é obrigatória.";
      if (!form.tomador_cep_exterior?.trim())
        erros.tomador_cep_exterior = "Código de Endereçamento Postal é obrigatório.";
      if (!form.tomador_estado_exterior?.trim())
        erros.tomador_estado_exterior = "Estado/Província/Região é obrigatório.";
      // comExt obrigatórios
      if (!form.com_ext_md_prestacao) erros.com_ext_md_prestacao = "Modo de Prestação é obrigatório.";
      if (!form.com_ext_vinc_prest && form.com_ext_vinc_prest !== "0") erros.com_ext_vinc_prest = "Vínculo é obrigatório.";
      if (!form.com_ext_tp_moeda) erros.com_ext_tp_moeda = "Moeda é obrigatória.";
      if (!form.com_ext_v_serv_moeda || parseFloat(form.com_ext_v_serv_moeda) <= 0) erros.com_ext_v_serv_moeda = "Valor em moeda estrangeira é obrigatório.";
      if (!form.com_ext_mec_af_comex_p) erros.com_ext_mec_af_comex_p = "Mecanismo do Prestador é obrigatório.";
      if (!form.com_ext_mec_af_comex_t) erros.com_ext_mec_af_comex_t = "Mecanismo do Tomador é obrigatório.";
      if (!form.com_ext_mov_temp_bens) erros.com_ext_mov_temp_bens = "Movimentação temporária de bens é obrigatória.";
    }

    // Serviço sem município fixo: local e alíquota são definidos por nota
    if (municipioPorNota) {
      if (!munIncidNota?.codigo) {
        erros.municipio_incidencia = "Selecione o município onde o serviço foi prestado.";
      }
      // Não optante não informa alíquota — o município calcula. Só validamos
      // para optantes do Simples Nacional.
      if (Number(servicoSel.op_simp_nac) !== 1) {
        const informou = String(form.aliquota_iss_nota || "").trim() !== "";
        const aliq = parseFloat(form.aliquota_iss_nota);
        if (informou && (isNaN(aliq) || aliq < 0 || aliq > 100)) {
          erros.aliquota_iss_nota = "Alíquota deve ser um número entre 0 e 100.";
        } else if (servicoSel.iss_retido && !informou && !servicoSel.aliquota_iss) {
          // Com ISS retido a alíquota é obrigatória — sem ela a SEFIN rejeita (E0621)
          erros.aliquota_iss_nota = "Informe a alíquota: este serviço tem retenção de ISS.";
        }
      }
    }

    // CNO obrigatório para serviços de construção civil (07.02, 07.04–07.08, 07.17, 07.19, 14.14)
    const requerCno = ["07.02","07.04","07.05","07.06","07.07","07.08","07.17","07.19","14.14"]
      .some(p => servicoSel?.codigo_lc116?.startsWith(p));
    // O schema exige identificar a obra por UMA das formas — não necessariamente
    // pelo CNO. Validamos a que o usuário escolheu.
    if (requerCno) {
      const forma = form.obra_forma || "cno";
      if (forma === "cno") {
        const d = form.cno?.replace(/\D/g,"") || "";
        if (!d) erros.cno = "Informe o código da obra, ou escolha outra forma de identificação acima.";
        else if (d.length !== 12) erros.cno = "CNO deve ter 12 dígitos.";
      } else if (forma === "cib") {
        if ((form.obra_cib || "").trim().length !== 8) erros.obra_cib = "O CIB tem exatamente 8 caracteres.";
      } else {
        // Mesma regra do Emissor Nacional — pega o caso de rascunho salvo antes
        // de o serviço deixar de ser exportação.
        if (forma === "endereco_exterior" && !prestacaoNoExterior) {
          erros.obra_forma = "Não é possível informar um endereço no exterior quando o local da prestação do serviço for no Brasil.";
        }
        if (!form.obra_logradouro?.trim()) erros.obra_logradouro = "Informe o logradouro da obra.";
        if (!form.obra_numero?.trim())     erros.obra_numero = "Informe o número.";
        if (!form.obra_bairro?.trim())     erros.obra_bairro = "Informe o bairro.";
        if (forma === "endereco" && (form.obra_cep || "").replace(/\D/g,"").length !== 8)
          erros.obra_cep = "CEP deve ter 8 dígitos.";
        if (forma === "endereco_exterior") {
          if (!form.obra_cidade_ext?.trim()) erros.obra_cidade_ext = "Informe a cidade.";
          if (!form.obra_estado_ext?.trim()) erros.obra_estado_ext = "Informe o estado ou província.";
        }
      }
    }

    return erros;
  };

  const emitir = async () => {
    if(emitindoRef.current) return;
    const scrollErro = () => setTimeout(() => erroRef.current?.scrollIntoView({ behavior:"smooth", block:"center" }), 50);
    if(!servicoSel){ setErro("Selecione um serviço"); scrollErro(); return; }
    const qtdValidar = parseFloat(form.quantidade);
    if(!form.quantidade || isNaN(qtdValidar) || qtdValidar < 1){ setErro("Quantidade deve ser no mínimo 1"); scrollErro(); return; }
    const valorValidar = parseBR(form.valor_servico);
    if(!form.valor_servico || isNaN(valorValidar) || valorValidar <= 0){ setErro("Valor unitário deve ser maior que zero"); scrollErro(); return; }
    const qtd = parseFloat(form.quantidade)||1;
    const totalParaValidar = valorValidar * qtd;
    const errosValidacao = validarEmissao(totalParaValidar);
    if (Object.keys(errosValidacao).length > 0) {
      setErrosCampos(errosValidacao);
      // Mensagens compostas vão no topo: um campo vermelho não explica por que
      // o endereço passou a ser obrigatório.
      const chaves = Object.keys(errosValidacao).filter(k => ROTULOS_EMISSAO[k]);
      let resumo = "Corrija os campos em vermelho antes de emitir.";
      if (chaves.length === 1) {
        resumo = `${ROTULOS_EMISSAO[chaves[0]]}: ${errosValidacao[chaves[0]]}`;
      } else if (chaves.length > 1) {
        resumo = `Corrija antes de emitir: ${chaves.map(k => ROTULOS_EMISSAO[k]).join(", ")}.`;
      }
      setErro(errosValidacao.tomador_endereco || errosValidacao.tp_oper_nota || resumo);
      scrollErro();
      return;
    }
    setErrosCampos({});
    emitindoRef.current = true;
    setLoading(true); setErro("");
    // Antes de transmitir: o que foi digitado aqui para uma pessoa do cadastro
    // vai para o cadastro também. Fica salvo mesmo que a emissão falhe depois.
    await completarCadastroPessoa();
    try {
      const qtd = parseFloat(form.quantidade)||1;
      const valorUnit = parseBR(form.valor_servico)||0;
      const valorTotal = valorUnit * qtd;
      const payload = {
        empresa_id: empresaId,
        ...form,
        valor_servico: valorTotal,
        aliquota_iss: (servicoSel.op_simp_nac === 1) ? null
          : ((municipioPorNota && String(form.aliquota_iss_nota||"").trim() !== "")
              ? parseFloat(form.aliquota_iss_nota)
              : servicoSel.aliquota_iss),
        deducoes: 0,
        // CPF não retém na fonte — ver `retencoesRemovidas` acima
        iss_retido: tomadorEhCPF ? false : servicoSel.iss_retido,
        situacao_tributaria: servicoSel.situacao_tributaria,
        servico_codigo: servicoSel.codigo_lc116,
        servico_codigo_nbs: servicoSel.codigo_nbs || null,
        servico_nome: servicoSel.nome,
        servico_descricao: form.servico_descricao || servicoSel.descricao,
        // Servico sem municipio fixo usa o escolhido nesta nota
        municipio_incidencia: (municipioPorNota && munIncidNota)
          ? munIncidNota.codigo
          : (servicoSel.municipio_incidencia_codigo||""),
        competencia: (form.data_emissao||new Date().toISOString().slice(0,10)).slice(0,7),
        informacoes_complementares: form.informacoes_complementares||"",
        // Obra: envia a forma escolhida e só os campos dela — o backend descarta o
        // resto para não violar o xs:choice do schema.
        obra_forma: form.obra_forma || "cno",
        cno: form.cno || null,
        obra_cib: form.obra_cib || null,
        obra_cep: form.obra_cep || null,
        obra_logradouro: form.obra_logradouro || null,
        obra_numero: form.obra_numero || null,
        obra_complemento: form.obra_complemento || null,
        obra_bairro: form.obra_bairro || null,
        obra_cod_postal: form.obra_cod_postal || null,
        obra_cidade_ext: form.obra_cidade_ext || null,
        obra_estado_ext: form.obra_estado_ext || null,
        inscricao_imobiliaria_fiscal: form.inscricao_imobiliaria_fiscal || null,
        tomador_municipio_codigo: form.tomador_municipio_codigo||"",
        op_simp_nac: servicoSel.op_simp_nac,
        reg_ap_trib_sn: servicoSel.reg_ap_trib_sn,
        reg_esp_trib: servicoSel.reg_esp_trib,
        natureza_operacao: 1,
        // Intermediário: zerar campos se toggle desligado
        intermediario_cnpj: form.tem_intermediario ? (form.intermediario_cnpj||null) : null,
        intermediario_cpf:  form.tem_intermediario ? (form.intermediario_cpf||null)  : null,
        intermediario_razao_social: form.tem_intermediario ? (form.intermediario_razao_social||null) : null,
        // Tributação Federal (herdada do serviço)
        cst_pis_cofins: servicoSel.cst_pis_cofins || "07",
        tp_ret_pis_cofins_csll: tomadorEhCPF ? 4 : (servicoSel.tp_ret_pis_cofins_csll || 1),
        p_aliq_pis: servicoSel.p_aliq_pis || 0.65,
        p_aliq_cofins: servicoSel.p_aliq_cofins || 3.00,
        modo_tot_trib: servicoSel.modo_tot_trib || 2,
        aliquota_simples: servicoSel.aliquota_simples || null,
        perc_trib_fed: servicoSel.perc_trib_fed || 0,
        perc_trib_est: servicoSel.perc_trib_est || 0,
        perc_trib_mun: servicoSel.perc_trib_mun != null ? servicoSel.perc_trib_mun : null,
        // Tributos aproximados monetários (modo 1)
        v_trib_fed: servicoSel?.modo_tot_trib === 1 ? (parseFloat(form.v_trib_fed) || 0) : 0,
        v_trib_est: servicoSel?.modo_tot_trib === 1 ? (parseFloat(form.v_trib_est) || 0) : 0,
        v_trib_mun: servicoSel?.modo_tot_trib === 1 ? (parseFloat(form.v_trib_mun) || 0) : 0,
        // Retenções por nota — zeradas quando o tomador é pessoa física
        v_ret_irrf: tomadorEhCPF ? 0 : parseBR(form.v_ret_irrf),
        v_ret_csll: tomadorEhCPF ? 0 : parseBR(form.v_ret_csll),
        v_ret_cp:   tomadorEhCPF ? 0 : parseBR(form.v_ret_cp),
        // PIS/COFINS retidos — calculados automaticamente conforme tp_ret e alíquota do serviço
        v_ret_pis: (() => {
          if (tomadorEhCPF) return 0;
          const cst = servicoSel.cst_pis_cofins || "07";
          const tp  = servicoSel.tp_ret_pis_cofins_csll || 4;
          const pisRet = (cst === "01" || cst === "02") && [1,3,5,8].includes(tp);
          return pisRet ? Math.round(valorTotal * (parseFloat(servicoSel.p_aliq_pis)||0) / 100 * 100) / 100 : 0;
        })(),
        v_ret_cofins: (() => {
          if (tomadorEhCPF) return 0;
          const cst = servicoSel.cst_pis_cofins || "07";
          const tp  = servicoSel.tp_ret_pis_cofins_csll || 4;
          const cofinsRet = (cst === "01" || cst === "02") && [1,2,5,6].includes(tp);
          return cofinsRet ? Math.round(valorTotal * (parseFloat(servicoSel.p_aliq_cofins)||0) / 100 * 100) / 100 : 0;
        })(),
        // Descontos (por nota)
        desconto_incondicionado: parseBR(form.desconto_incondicionado),
        desconto_condicionado: parseBR(form.desconto_condicionado),
        // Dedução BC ISSQN — valor por nota, toggle vem do serviço
        tem_deducao_bc: !!servicoSel?.tem_deducao_bc,
        valor_deducao_bc: servicoSel?.tem_deducao_bc ? (parseFloat(form.valor_deducao_bc) || 0) : 0,
        // IBS/CBS — herdados do serviço cadastrado
        ibscbs_habilitado: !!servicoSel?.ibscbs_habilitado,
        ibscbs_c_ind_op: servicoSel?.ibscbs_c_ind_op || null,
        ibscbs_ind_dest: servicoSel?.ibscbs_ind_dest || "0",
        ibscbs_cst: servicoSel?.ibscbs_cst || null,
        ibscbs_c_class_trib: servicoSel?.ibscbs_c_class_trib || null,
        ibscbs_c_cib: servicoSel?.ibscbs_c_cib || null,
        // O que vale é a escolha da nota; o cadastro do serviço é só o padrão
        ibscbs_tp_oper: form.tp_oper_nota || servicoSel?.ibscbs_tp_oper || null,
        ibscbs_tp_ente_gov: form.tp_ente_gov_nota || servicoSel?.ibscbs_tp_ente_gov || null,
        ibscbs_ind_final: servicoSel?.ibscbs_ind_final || null,
        // Campos fixos herdados do serviço cadastrado
        nao_tributacao_iss: !!servicoSel?.nao_tributacao_iss,
        tipo_nao_tributacao: servicoSel?.tipo_nao_tributacao || "imunidade",
        pais_resultado: servicoSel?.pais_resultado || null,
        tipo_imunidade: servicoSel?.tipo_imunidade || null,
        exig_susp: !!servicoSel?.exig_susp,
        tipo_susp: servicoSel?.tipo_susp || null,
        num_processo_susp: servicoSel?.num_processo_susp || null,
        tem_beneficio_mun: !!servicoSel?.tem_beneficio_mun,
        codigo_bm: servicoSel?.codigo_bm || null,
        // Tomador exterior
        tomador_exterior: form.tomador_localizacao === "exterior",
        tomador_nif: form.tomador_nif || null,
        tomador_c_nao_nif: form.tomador_c_nao_nif ? parseInt(form.tomador_c_nao_nif) : null,
        tomador_pais_nome: form.tomador_pais_nome || null,
        tomador_codigo_pais: form.tomador_codigo_pais || null,
        tomador_municipio_exterior: form.tomador_municipio_exterior || null,
        tomador_cep_exterior: form.tomador_cep_exterior || null,
        tomador_estado_exterior: form.tomador_estado_exterior || null,
        com_ext_md_prestacao: parseInt(form.com_ext_md_prestacao||"1"),
        com_ext_vinc_prest: parseInt(form.com_ext_vinc_prest||"0"),
        com_ext_tp_moeda: parseInt(form.com_ext_tp_moeda||"220"),
        com_ext_v_serv_moeda: parseFloat((form.com_ext_v_serv_moeda||"0").replace(",",".")),
        com_ext_mec_af_comex_p: form.com_ext_mec_af_comex_p || "00",
        com_ext_mec_af_comex_t: form.com_ext_mec_af_comex_t || "00",
        com_ext_mov_temp_bens: parseInt(form.com_ext_mov_temp_bens||"0"),
        com_ext_mdic: parseInt(form.com_ext_mdic||"0"),
      };
      const r = await fetch(API+"/nfse/emitir",{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body:JSON.stringify(payload)
      });
      let d;
      try { d = await r.json(); } catch { const t = await r.text(); throw new Error(t || `Erro ${r.status}`); }
      if(!r.ok) throw new Error(d.detail||JSON.stringify(d));
      onSuccess();
    } catch(e){ setErro(e.message); scrollErro(); }
    emitindoRef.current = false;
    setLoading(false);
  };

  const qtd = parseFloat(form.quantidade)||1;
  const valorUnit = parseBR(form.valor_servico)||0;
  const valorTotal = valorUnit * qtd;
  const descontoInc = parseBR(form.desconto_incondicionado)||0;
  const descontoCond = parseBR(form.desconto_condicionado)||0;
  const deducaoBC = (servicoSel?.tem_deducao_bc) ? (parseBR(form.valor_deducao_bc)||0) : 0;
  const round2 = (v) => Math.round(v * 100) / 100;
  const aliq = (servicoSel?.nao_tributacao_iss) ? 0 : (servicoSel ? servicoSel.aliquota_iss : 0);
  const baseISS = Math.max(0, valorTotal - descontoInc - deducaoBC);
  const iss = round2(baseISS * (aliq/100));
  const retIRRF = round2(parseBR(form.v_ret_irrf)||0);
  const retCSLL = round2(parseBR(form.v_ret_csll)||0);
  const retCP   = round2(parseBR(form.v_ret_cp)||0);
  const cstPis  = servicoSel?.cst_pis_cofins || "07";
  const tpRet   = servicoSel?.tp_ret_pis_cofins_csll || 1;
  // Spec NFS-e nacional: 1=PIS+COFINS, 2=COFINS, 3=PIS, 4=Sem Ret., 5=Todos, 6=COFINS+CSLL, 7=CSLL, 8=PIS+CSLL
  const pisRetido    = (cstPis === "01" || cstPis === "02") && [1,3,5,8].includes(tpRet);
  const cofinsRetido = (cstPis === "01" || cstPis === "02") && [1,2,5,6].includes(tpRet);
  const retPIS    = pisRetido    ? round2(valorTotal * (parseFloat(servicoSel?.p_aliq_pis)   ||0) / 100) : 0;
  const retCOFINS = cofinsRetido ? round2(valorTotal * (parseFloat(servicoSel?.p_aliq_cofins)||0) / 100) : 0;
  const totalRetencoes = retIRRF + retCSLL + retCP + retPIS + retCOFINS;

  return (
    <div>
      <h2 style={{ fontSize:20, fontWeight:700, color:T.text, marginBottom:24 }}>Emitir NFS-e</h2>
      <div ref={erroRef} />
      {erro && <div style={{ background:"#2f0d0d", border:"1px solid #ef444440", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#f87171", marginBottom:16 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {erro}</div>}

      {/* O backend já recusa emitir sem certificado, mas só no fim — depois de
          preencher a nota inteira. O aviso aqui evita o trabalho perdido. */}
      {problemaCertificado && (
        <div style={{ background:"#2f0d0d", border:"1px solid #ef4444", borderRadius:10, padding:"14px 16px", marginBottom:16, display:"flex", alignItems:"flex-start", gap:10 }}>
          <AlertTriangle size={17} style={{ color:"#f87171", flexShrink:0, marginTop:1 }} />
          <div style={{ fontSize:13, color:"#fca5a5", lineHeight:1.6 }}>
            <strong style={{ color:"#f87171" }}>{problemaCertificado.titulo}</strong><br/>
            {problemaCertificado.texto}
            {onIrParaEmpresas && (
              <>
                {" "}
                <span onClick={onIrParaEmpresas}
                  style={{ color:"#38bdf8", cursor:"pointer", textDecoration:"underline", fontWeight:600 }}>
                  Ir para Empresas
                </span>
              </>
            )}
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 280px", gap:20 }}>
        <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:12, padding:28, display:"flex", flexDirection:"column", gap:18, boxShadow:T.shadow }}>

          {/* Empresa + Serviço */}
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16 }}>
            <div>
              <label style={S.label}>EMPRESA EMISSORA <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
              <select style={S.input} value={empresaId} onChange={e=>{ setEmpresaId(e.target.value); gravarEmpresaAtual(e.target.value); }}>
                {empresas.map(e=><option key={e.id} value={e.id}>{e.razao_social}</option>)}
                {empresas.length===0 && <option>Nenhuma empresa</option>}
              </select>
            </div>
            <div>
              <label style={S.label}>SERVIÇO <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
              <select style={{...S.input, color: servicoId ? T.inputText : T.textFaint}} value={servicoId} onChange={e=>setServicoId(e.target.value)}>
                <option value="">— Selecione um serviço —</option>
                {servicos.map(s=><option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
              {servicos.length===0 && <div style={{ fontSize:11, color:"#f87171", marginTop:6 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> Nenhum serviço cadastrado para esta empresa</div>}
            </div>
          </div>

          {/* Info do serviço selecionado */}
          {servicoSel && (
            <div style={{ background:T.cardInner, border:"1px solid #0ea5e930", borderRadius:8, padding:14 }}>
              <div style={{ fontSize:11, color:"#38bdf8", fontWeight:600, marginBottom:8 }}>SERVIÇO SELECIONADO</div>
              <div style={{ fontSize:13, color:T.inputText, marginBottom:4 }}>{servicoSel.descricao}</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <span style={{ fontSize:11, padding:"2px 8px", borderRadius:12, background:"#0ea5e915", color:"#38bdf8" }}>LC 116: {servicoSel.codigo_lc116}</span>
                {servicoSel.codigo_nbs && <span style={{ fontSize:11, padding:"2px 8px", borderRadius:12, background:"#0ea5e915", color:"#38bdf8" }}>NBS: {servicoSel.codigo_nbs}</span>}
                <span style={{ fontSize:11, padding:"2px 8px", borderRadius:12, background:T.btnSecBg, color:T.btnSecText }}>ISS {servicoSel.aliquota_iss != null ? servicoSel.aliquota_iss+"%" : "—"}</span>
                {servicoSel.municipio_incidencia_nome && <span style={{ fontSize:11, padding:"2px 8px", borderRadius:12, background:T.btnSecBg, color:T.btnSecText, display:"inline-flex", alignItems:"center", gap:3 }}><MapPin size={10} />{servicoSel.municipio_incidencia_nome}</span>}
              </div>
              {errosCampos._aliquota_iss_retencao && (
                <div style={{ marginTop:10, fontSize:11, color:"#f87171", background:"#2f0d0d", border:"1px solid #ef444440", borderRadius:6, padding:"8px 10px" }}>
                  <AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos._aliquota_iss_retencao}
                </div>
              )}
            </div>
          )}
          {/* Compra governamental: a SEFIN exige o tipo de operação. Fica aqui, e
              não no cadastro do serviço, porque depende do tomador de cada nota. */}
          {servicoSel && exigeTpOper && (
            <div style={{ background:"#0ea5e910", border:"1px solid #38bdf855", borderRadius:10, padding:"14px 16px", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:12 }}>
                <Info size={15} style={{ color:"#38bdf8", flexShrink:0, marginTop:1 }} />
                <span style={{ fontSize:12.5, color:T.text, lineHeight:1.55 }}>
                  {tomadorEhOrgaoPublico
                    ? <>O tomador é um <strong>órgão da Administração Pública</strong>, então esta é uma compra governamental.</>
                    : <>O código de tributação deste serviço está na lista que exige informar o tipo de operação.</>}
                  {" "}Sem estes campos a nota é recusada com o erro E0903.
                </span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12 }}>
                <div>
                  <label style={S.label}>TIPO DE OPERAÇÃO <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                  <select style={{...S.input, ...(errosCampos.tp_oper_nota ? {borderColor:"#ef4444"} : {})}}
                    value={form.tp_oper_nota || servicoSel.ibscbs_tp_oper || ""}
                    onChange={e=>set("tp_oper_nota", e.target.value)}>
                    <option value="">Selecione...</option>
                    <option value="1">1 — Fornecimento com pagamento posterior</option>
                    <option value="2">2 — Pagamento com fornecimento já realizado</option>
                    <option value="3">3 — Fornecimento com pagamento já realizado</option>
                    <option value="4">4 — Pagamento com fornecimento posterior</option>
                    <option value="5">5 — Fornecimento e pagamento concomitantes</option>
                  </select>
                </div>
                <div>
                  <label style={S.label}>ENTE GOVERNAMENTAL</label>
                  <select style={S.input}
                    value={form.tp_ente_gov_nota || servicoSel.ibscbs_tp_ente_gov || ""}
                    onChange={e=>set("tp_ente_gov_nota", e.target.value)}>
                    <option value="">— Não se aplica —</option>
                    <option value="1">1 — União</option>
                    <option value="2">2 — Estado</option>
                    <option value="3">3 — Distrito Federal</option>
                    <option value="4">4 — Município</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {servicoSel && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div>
              <label style={S.label}>DESCRIÇÃO DO SERVIÇO <span style={{color:"#4ade80",fontSize:10}}>(pré-preenchido — editável)</span></label>
              <textarea style={{...S.input, resize:"vertical", minHeight:72}} value={form.servico_descricao} onChange={e=>set("servico_descricao",e.target.value)} />
            </div>
            <div>
              <label style={S.label}>INFORMAÇÕES COMPLEMENTARES <span style={{color:"#475569",fontWeight:400}}>(opcional — editável)</span></label>
              <textarea style={{...S.input, resize:"vertical", minHeight:54}} placeholder="aparece na nota como observações" value={form.informacoes_complementares||""} onChange={e=>set("informacoes_complementares",e.target.value)} />
            </div>

            {/* Serviço sem município fixo: local e alíquota são definidos por nota.
                Cidade diferente costuma ter alíquota diferente, por isso os dois juntos. */}
            {municipioPorNota && (
            <div style={{ border:"1px solid "+T.border, borderRadius:10, padding:"14px 16px", background:T.cardInner }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#38bdf8", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>
                Local da prestação
              </div>
              {segueDomicilioTomador ? (
                <div style={{ display:"flex", alignItems:"flex-start", gap:8, background:"#0ea5e912", border:"1px solid #38bdf850", borderRadius:8, padding:"9px 12px", marginBottom:12 }}>
                  <Info size={14} style={{ color:"#38bdf8", flexShrink:0, marginTop:1 }} />
                  <div style={{ fontSize:12, color:T.text, lineHeight:1.55 }}>
                    <strong>Preenchido pelo domicílio do tomador.</strong> A NBS {servicoSel.codigo_nbs} deste
                    serviço tem incidência no <em>domicílio principal do adquirente</em> (cIndOp {regraIncidencia.indop}),
                    então o município da nota é o do tomador: <strong>{form.tomador_municipio}{form.tomador_uf ? ` / ${form.tomador_uf}` : ""}</strong>.
                    {servicoSel.municipio_fixo !== false && (
                      <> O município fixo do cadastro ({servicoSel.municipio_incidencia_nome || servicoSel.municipio_incidencia_codigo}) foi deixado de lado nesta nota por causa dessa regra.</>
                    )}
                    {" "}Pode trocar abaixo se precisar.
                  </div>
                </div>
              ) : (
                <div style={{ fontSize:12, color:T.textMuted, marginBottom:12, lineHeight:1.5 }}>
                  Este serviço não tem município fixo — informe onde ele foi prestado e a alíquota
                  de ISS do município.
                </div>
              )}
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap:12 }}>
                <div>
                  <MunicipioInput
                    value={munIncidNota}
                    onChange={v=>{ setMunIncidNota(v); if(v){ set("municipio_incidencia", v.codigo); } }}
                    erro={!!errosCampos.municipio_incidencia}
                    label="MUNICÍPIO DE INCIDÊNCIA *" S={S} T={T} />
                  {errosCampos.municipio_incidencia && (
                    <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}>
                      <AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.municipio_incidencia}
                    </div>
                  )}
                </div>
                <div>
                  {/* Não optante (Normal/Presumido) não informa alíquota: quem calcula
                      é o município. Enviar o valor gera rejeição E0617 na SEFIN. */}
                  {Number(servicoSel?.op_simp_nac) === 1 ? (
                    <>
                      <label style={S.label}>ALÍQUOTA ISS (%)</label>
                      <div style={{ ...S.input, display:"flex", alignItems:"center", gap:7,
                                    color:T.textFaint, background:T.btnSecBg, cursor:"not-allowed" }}>
                        <Info size={13} style={{flexShrink:0}} />
                        <span style={{fontSize:12.5}}>Calculada pelo município</span>
                      </div>
                      <div style={{ fontSize:11, color:T.textFaint, marginTop:4, lineHeight:1.5 }}>
                        A empresa não é optante do Simples Nacional — a alíquota é definida
                        pelo município de incidência e não deve ser informada.
                      </div>
                    </>
                  ) : (
                    <>
                      <label style={S.label}>
                        ALÍQUOTA ISS (%) {servicoSel?.iss_retido && <span style={{color:"#f97316",marginLeft:3}}>*</span>}
                      </label>
                      <input style={{...S.input, ...(errosCampos.aliquota_iss_nota ? {borderColor:"#ef4444"} : {})}}
                        placeholder={servicoSel?.aliquota_iss != null ? `padrão: ${servicoSel.aliquota_iss}` : "ex: 3.00"}
                        value={form.aliquota_iss_nota||""}
                        onChange={e=>set("aliquota_iss_nota", e.target.value.replace(",","."))} />
                      <div style={{ fontSize:11, color:T.textFaint, marginTop:4, lineHeight:1.5 }}>
                        Optante do Simples Nacional — informe a alíquota do município.
                      </div>
                      {errosCampos.aliquota_iss_nota && (
                        <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}>
                          <AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.aliquota_iss_nota}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            )}

            {["07.02","07.04","07.05","07.06","07.07","07.08","07.17","07.19","14.14"].some(p=>servicoSel?.codigo_lc116?.startsWith(p)) && (
            <div style={{ border:"1px solid "+T.border, borderRadius:10, padding:"14px 16px", background:T.cardInner }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#f59e0b", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>
                Informações da obra <span style={{color:"#f87171",fontSize:10,letterSpacing:0}}>OBRIGATÓRIO PARA ESTE SERVIÇO</span>
              </div>
              <div style={{ fontSize:12, color:T.textMuted, marginBottom:10, lineHeight:1.5 }}>
                A obra precisa ser identificada de <strong>uma</strong> destas formas. Se ela não tiver
                CNO, use o CIB ou informe o endereço.
              </div>

              {/* O schema aceita apenas UMA opção (xs:choice) — daí serem excludentes */}
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
                {[["cno","Código de obra (CNO)"],["cib","Código CIB"],
                  ["endereco","Endereço no Brasil"],["endereco_exterior","Endereço no exterior"]].map(([val,label]) => {
                  const bloqueado = val === "endereco_exterior" && !prestacaoNoExterior;
                  const ativo = (form.obra_forma||"cno") === val;
                  return (
                    <button key={val} type="button" disabled={bloqueado}
                      title={bloqueado ? "Disponível apenas quando o serviço é de exportação (prestação fora do Brasil)" : ""}
                      onClick={()=>setForm(f=>({...f, obra_forma:val, cno:"", obra_cib:"",
                        obra_cep:"", obra_logradouro:"", obra_numero:"", obra_complemento:"",
                        obra_bairro:"", obra_cod_postal:"", obra_cidade_ext:"", obra_estado_ext:""}))}
                      style={{ fontSize:12, fontWeight:600, padding:"6px 13px", borderRadius:20,
                               cursor: bloqueado ? "not-allowed" : "pointer",
                               opacity: bloqueado ? 0.45 : 1,
                               border:"1px solid "+(ativo ? "#f59e0b" : T.border),
                               background: ativo ? "#f59e0b" : T.btnSecBg,
                               color: ativo ? "#fff" : T.textMuted }}>
                      {label}
                    </button>
                  );
                })}
              </div>

              {!prestacaoNoExterior && (
                <div style={{ fontSize:11.5, color:T.textFaint, marginTop:-6, marginBottom:10, lineHeight:1.5 }}>
                  <Info size={11} style={{verticalAlign:"middle",marginRight:4}} />
                  Não é possível informar um endereço no exterior quando o local da prestação do serviço for no Brasil.
                </div>
              )}

              {(form.obra_forma||"cno")==="cno" && (
                <div>
                  <label style={S.label}>CÓDIGO DA OBRA — CNO OU CEI</label>
                  {/* O schema aceita até 30 (TSCodObra), mas CNO e CEI têm 12
                      dígitos. Deixar digitar mais só levava ao erro de validação
                      depois de o campo já estar preenchido errado. */}
                  <input style={{...S.input, ...(errosCampos.cno ? {borderColor:"#ef4444"} : {})}}
                    placeholder="12 dígitos (ex: 123456789012)" maxLength={12}
                    value={form.cno||""} onChange={e=>set("cno", e.target.value.replace(/\D/g,"").slice(0,12))} />
                  {errosCampos.cno && <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.cno}</div>}
                </div>
              )}

              {(form.obra_forma||"cno")==="cib" && (
                <div>
                  <label style={S.label}>CÓDIGO CIB — CADASTRO IMOBILIÁRIO BRASILEIRO</label>
                  <input style={errStyle("obra_cib")} placeholder="8 caracteres" maxLength={8}
                    value={form.obra_cib||""} onChange={e=>set("obra_cib", e.target.value.trim().slice(0,8))} />
                  {errMsg("obra_cib")}
                </div>
              )}

              {(form.obra_forma||"cno")==="endereco_exterior" && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                  <div><label style={S.label}>CÓDIGO POSTAL</label>
                    <input style={S.input} maxLength={11} value={form.obra_cod_postal||""} onChange={e=>set("obra_cod_postal",e.target.value)} /></div>
                  <div><label style={S.label}>CIDADE</label>
                    <input style={errStyle("obra_cidade_ext")} maxLength={60} value={form.obra_cidade_ext||""} onChange={e=>set("obra_cidade_ext",e.target.value)} />
                    {errMsg("obra_cidade_ext")}</div>
                  <div><label style={S.label}>ESTADO / PROVÍNCIA</label>
                    <input style={errStyle("obra_estado_ext")} maxLength={60} value={form.obra_estado_ext||""} onChange={e=>set("obra_estado_ext",e.target.value)} />
                    {errMsg("obra_estado_ext")}</div>
                </div>
              )}

              {(form.obra_forma||"cno")==="endereco" && (
                <div style={{ marginBottom:10 }}>
                  <label style={S.label}>CEP DA OBRA</label>
                  {/* Só 8 dígitos entram: antes dava para digitar um a mais e o
                      erro só aparecia no clique em Emitir, sem campo vermelho. */}
                  <input style={errStyle("obra_cep")} placeholder="00000-000" maxLength={9}
                    value={form.obra_cep||""}
                    onChange={e=>{
                      const d = e.target.value.replace(/\D/g,"").slice(0,8);
                      set("obra_cep", d.length > 5 ? `${d.slice(0,5)}-${d.slice(5)}` : d);
                    }} />
                  {errMsg("obra_cep")}
                </div>
              )}

              {["endereco","endereco_exterior"].includes(form.obra_forma||"") && (
                <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:10 }}>
                  <div><label style={S.label}>LOGRADOURO</label>
                    <input style={errStyle("obra_logradouro")} maxLength={255} value={form.obra_logradouro||""} onChange={e=>set("obra_logradouro",e.target.value)} />
                    {errMsg("obra_logradouro")}</div>
                  <div><label style={S.label}>NÚMERO</label>
                    <input style={errStyle("obra_numero")} maxLength={60} value={form.obra_numero||""} onChange={e=>set("obra_numero",e.target.value)} />
                    {errMsg("obra_numero")}</div>
                  <div><label style={S.label}>COMPLEMENTO <span style={{color:T.textFaint,fontWeight:400}}>(opcional)</span></label>
                    <input style={S.input} maxLength={156} value={form.obra_complemento||""} onChange={e=>set("obra_complemento",e.target.value)} /></div>
                  <div><label style={S.label}>BAIRRO</label>
                    <input style={errStyle("obra_bairro")} maxLength={60} value={form.obra_bairro||""} onChange={e=>set("obra_bairro",e.target.value)} />
                    {errMsg("obra_bairro")}</div>
                </div>
              )}

              {errosCampos.obra_forma && (
                <div style={{ marginTop:8, padding:"9px 12px", background:"#2f0d0d",
                              border:"1px solid #f87171", borderRadius:8, fontSize:12, color:"#fca5a5" }}>
                  <AlertTriangle size={12} style={{verticalAlign:"middle",marginRight:5}} />
                  {errosCampos.obra_forma}
                </div>
              )}

              <div style={{ marginTop:10 }}>
                <label style={S.label}>INSCRIÇÃO IMOBILIÁRIA FISCAL <span style={{color:T.textFaint,fontWeight:400}}>(opcional)</span></label>
                <input style={S.input} maxLength={30} placeholder="Código da Prefeitura para IPTU/identificação"
                  value={form.inscricao_imobiliaria_fiscal||""} onChange={e=>set("inscricao_imobiliaria_fiscal",e.target.value)} />
              </div>
            </div>
            )}
          </div>
          )}

          {/* Tomador */}
          <div style={{ fontSize:11, fontWeight:700, color:"#38bdf8", textTransform:"uppercase", letterSpacing:1 }}>Dados do Tomador</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {[["brasil",<>🇧🇷 Brasil</>],["exterior",<><Globe size={12} style={{verticalAlign:"middle",marginRight:3}} />Exterior</>],["nao_informado",<><X size={12} style={{verticalAlign:"middle",marginRight:3}} />Não informar</>]].map(([val, label]) => (
              <button key={val} type="button" onClick={() => {
                setForm(f => ({...f,
                  tomador_localizacao: val,
                  tomador_nao_informado: val === "nao_informado",
                  tomador_exterior: val === "exterior",
                  tomador_razao_social:"", tomador_cnpj:"", tomador_cpf:"",
                  tomador_nif:"", tomador_c_nao_nif:"", tomador_pais_nome:"", tomador_codigo_pais:"", tomador_municipio_exterior:"",
                  tomador_email:"", tomador_fone:"", tomador_endereco:"", tomador_numero:"",
                  tomador_bairro:"", tomador_municipio:"", tomador_municipio_codigo:"", tomador_cep:"", tomador_uf:""
                }));
                setCamposBloqueados([]);
              }} style={{
                padding:"6px 14px", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer",
                background: form.tomador_localizacao === val ? (val==="nao_informado"?"#f9731620":val==="exterior"?"#a78bfa20":"#38bdf820") : T.btnSecBg,
                border:"1px solid "+(form.tomador_localizacao === val ? (val==="nao_informado"?"#f97316":val==="exterior"?"#a78bfa":"#38bdf8") : T.border),
                color: form.tomador_localizacao === val ? (val==="nao_informado"?"#fb923c":val==="exterior"?"#a78bfa":"#38bdf8") : T.textMuted
              }}>{label}</button>
            ))}
          </div>

          {form.tomador_localizacao === "nao_informado" ? (
            <div style={{ background:"#f9731610", border:"1px solid #f9731640", borderRadius:8, padding:"12px 16px", fontSize:12, color:"#fb923c" }}>
              <Check size={11} style={{verticalAlign:"middle",marginRight:3}} /> Tomador não será incluído na nota fiscal. O campo &lt;toma&gt; ficará omitido no XML da DPS.
            </div>
          ) : form.tomador_localizacao === "exterior" ? (
            <>
              <div style={{ background:"#a78bfa15", border:"1px solid #a78bfa40", borderRadius:8, padding:"12px 16px" }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#a78bfa", marginBottom:10 }}><Globe size={13} style={{verticalAlign:"middle",marginRight:4}} /> TOMADOR ESTRANGEIRO</div>
                <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:12 }}>
                  <div>
                    <label style={{...S.label, fontSize:10}}>O NIF será informado?</label>
                    <div style={{ display:"flex", gap:8, marginTop:4 }}>
                      {[["sim","Sim"],["nao","Não"]].map(([v,l]) => (
                        <label key={v} style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, cursor:"pointer", color:T.text }}>
                          <input type="radio" name="nif_informado"
                            checked={v==="sim" ? !form.tomador_c_nao_nif : !!form.tomador_c_nao_nif}
                            onChange={()=>{
                              if(v==="nao") setForm(f=>({...f, tomador_nif:"", tomador_c_nao_nif:"1"}));
                              else setForm(f=>({...f, tomador_c_nao_nif:""}));
                            }} /> {l}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                {!form.tomador_c_nao_nif ? (
                  <div style={{ marginBottom:12 }}>
                    <label style={S.label}>NIF — NÚMERO DE IDENTIFICAÇÃO FISCAL</label>
                    <input style={S.input} placeholder="Número de identificação fiscal estrangeiro" value={form.tomador_nif||""} onChange={e=>set("tomador_nif",e.target.value)} />
                  </div>
                ) : (
                  <div style={{ marginBottom:12 }}>
                    <label style={S.label}>MOTIVO DA NÃO INFORMAÇÃO DO NIF</label>
                    <select style={S.input} value={form.tomador_c_nao_nif||""} onChange={e=>set("tomador_c_nao_nif",e.target.value)}>
                      <option value="">Selecione...</option>
                      <option value="1">Dispensado do NIF</option>
                      <option value="2">Não exigência do NIF</option>
                    </select>
                  </div>
                )}
                <div style={{ marginBottom:12 }}>
                  <label style={S.label}>NOME / RAZÃO SOCIAL</label>
                  <input style={S.input} value={form.tomador_razao_social||""} onChange={e=>set("tomador_razao_social",e.target.value)} />
                </div>
                <div style={{ marginBottom:12 }}>
                  <label style={S.label}>PAÍS *</label>
                  <PaisInput value={form.tomador_pais_nome||""} S={S} T={T}
                    onChange={(iso, nome) => { setForm(f => ({...f, tomador_codigo_pais: iso, tomador_pais_nome: nome})); setErrosCampos(e=>({...e,tomador_codigo_pais:""})); }} />
                  {form.tomador_codigo_pais && <div style={{ fontSize:10, color:"#38bdf8", marginTop:3 }}>Código ISO: <b>{form.tomador_codigo_pais}</b></div>}
                  {errosCampos.tomador_codigo_pais && <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.tomador_codigo_pais}</div>}
                </div>
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap:12, marginBottom:12 }}>
                  <div><label style={S.label}>ENDEREÇO</label><input style={S.input} value={form.tomador_endereco||""} onChange={e=>set("tomador_endereco",e.target.value)} /></div>
                  <div><label style={S.label}>NÚMERO</label><input style={S.input} value={form.tomador_numero||""} onChange={e=>set("tomador_numero",e.target.value)} /></div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap:12, marginBottom:12 }}>
                  <div><label style={S.label}>BAIRRO</label><input style={S.input} value={form.tomador_bairro||""} onChange={e=>set("tomador_bairro",e.target.value)} /></div>
                  <div>
                    <label style={S.label}>CIDADE *</label>
                    <input style={{...S.input,...(errosCampos.tomador_municipio_exterior?{borderColor:"#ef4444"}:{})}} value={form.tomador_municipio_exterior||""} onChange={e=>set("tomador_municipio_exterior",e.target.value)} />
                    {errosCampos.tomador_municipio_exterior && <div style={{fontSize:11,color:"#f87171",marginTop:4}}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.tomador_municipio_exterior}</div>}
                  </div>
                  <div>
                    <label style={S.label}>ESTADO/PROV/REGIÃO *</label>
                    <input style={{...S.input,...(errosCampos.tomador_estado_exterior?{borderColor:"#ef4444"}:{})}} value={form.tomador_estado_exterior||""} onChange={e=>set("tomador_estado_exterior",e.target.value)} />
                    {errosCampos.tomador_estado_exterior && <div style={{fontSize:11,color:"#f87171",marginTop:4}}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.tomador_estado_exterior}</div>}
                  </div>
                  <div>
                    <label style={S.label}>CÓD. POSTAL *</label>
                    <input style={{...S.input,...(errosCampos.tomador_cep_exterior?{borderColor:"#ef4444"}:{})}} value={form.tomador_cep_exterior||""} onChange={e=>set("tomador_cep_exterior",e.target.value)} />
                    {errosCampos.tomador_cep_exterior && <div style={{fontSize:11,color:"#f87171",marginTop:4}}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.tomador_cep_exterior}</div>}
                  </div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12, marginBottom:12 }}>
                  <div><label style={S.label}>E-MAIL</label><input style={S.input} type="email" value={form.tomador_email||""} onChange={e=>set("tomador_email",e.target.value)} /></div>
                  <div><label style={S.label}>TELEFONE</label><input style={S.input} value={form.tomador_fone||""} onChange={e=>set("tomador_fone",e.target.value)} /></div>
                </div>
                {/* comExt */}
                <div style={{ borderTop:"1px solid #a78bfa30", paddingTop:12, marginTop:4 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#a78bfa", marginBottom:4 }}>INFORMAÇÕES PARA COMÉRCIO EXTERIOR</div>
                  <div style={{ fontSize:11, color:"#94a3b8", background:"#a78bfa10", border:"1px solid #a78bfa30", borderRadius:6, padding:"8px 12px", marginBottom:12 }}>
                    Para a RFB, a prestação de serviço caracteriza-se como comércio exterior quando existe ao menos um dos seguintes itens no exterior: tomador, intermediário ou local da prestação do serviço.
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12, marginBottom:12 }}>
                    <div>
                      <label style={S.label}>MODO DE PRESTAÇÃO *</label>
                      <select style={{...S.input,...(errosCampos.com_ext_md_prestacao?{borderColor:"#ef4444"}:{})}} value={form.com_ext_md_prestacao||""} onChange={e=>set("com_ext_md_prestacao",e.target.value)}>
                        <option value="">Selecione...</option>
                        <option value="1">Transfronteiriço</option>
                        <option value="2">Consumo no Brasil</option>
                        <option value="3">Movimento Temporário de Pessoas Físicas</option>
                        <option value="4">Consumo no Exterior</option>
                      </select>
                      {errosCampos.com_ext_md_prestacao && <div style={{fontSize:11,color:"#f87171",marginTop:4}}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.com_ext_md_prestacao}</div>}
                    </div>
                    <div>
                      <label style={S.label}>VÍNCULO ENTRE AS PARTES NO NEGÓCIO *</label>
                      <select style={{...S.input,...(errosCampos.com_ext_vinc_prest?{borderColor:"#ef4444"}:{})}} value={form.com_ext_vinc_prest||""} onChange={e=>set("com_ext_vinc_prest",e.target.value)}>
                        <option value="">Selecione...</option>
                        <option value="0">Sem vínculo com o tomador/Prestador</option>
                        <option value="1">Controlada</option>
                        <option value="2">Controladora</option>
                        <option value="3">Coligada</option>
                        <option value="4">Matriz</option>
                        <option value="5">Filial ou sucursal</option>
                        <option value="6">Outro vínculo</option>
                        <option value="9">Desconhecido</option>
                      </select>
                      {errosCampos.com_ext_vinc_prest && <div style={{fontSize:11,color:"#f87171",marginTop:4}}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.com_ext_vinc_prest}</div>}
                    </div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12, marginBottom:12 }}>
                    <div>
                      <label style={S.label}>MOEDA *</label>
                      <MoedaInput value={form.com_ext_tp_moeda_display||""} S={S} T={T}
                        onChange={codigo => { setForm(f=>({...f, com_ext_tp_moeda: codigo, com_ext_tp_moeda_display: ""})); setErrosCampos(e=>({...e,com_ext_tp_moeda:""})); }} />
                      {form.com_ext_tp_moeda && <div style={{ fontSize:10, color:"#38bdf8", marginTop:3 }}>Código BACEN: <b>{form.com_ext_tp_moeda}</b></div>}
                      {errosCampos.com_ext_tp_moeda && <div style={{fontSize:11,color:"#f87171",marginTop:4}}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.com_ext_tp_moeda}</div>}
                    </div>
                    <div>
                      <label style={S.label}>VALOR DO SERVIÇO EM MOEDA ESTRANGEIRA *</label>
                      <input style={S.input} placeholder="Ex: 1500.89" value={form.com_ext_v_serv_moeda||""} onChange={e=>set("com_ext_v_serv_moeda",e.target.value.replace(/[^\d.,]/g,""))} />
                    </div>
                  </div>
                  <div style={{ marginBottom:12 }}>
                    <label style={S.label}>MECANISMO DE APOIO/FOMENTO UTILIZADO PELO PRESTADOR DO SERVIÇO *</label>
                    <select style={S.input} value={form.com_ext_mec_af_comex_p||""} onChange={e=>set("com_ext_mec_af_comex_p",e.target.value)}>
                      <option value="">Selecione...</option>
                      <option value="01">Nenhum</option>
                      <option value="02">ACC – Adiantamento sobre Contrato de Câmbio – Redução a Zero do IR e do IOF</option>
                      <option value="03">ACE – Adiantamento sobre Cambiais Entregues – Redução a Zero do IR e do IOF</option>
                      <option value="04">BNDES-Exim Pós-Embarque – Serviços</option>
                      <option value="05">BNDES-Exim Pré-Embarque – Serviços</option>
                      <option value="06">FGE – Fundo de Garantia à Exportação</option>
                      <option value="07">PROEX – Equalização</option>
                      <option value="08">PROEX – Financiamento</option>
                    </select>
                  </div>
                  <div style={{ marginBottom:12 }}>
                    <label style={S.label}>MECANISMO DE APOIO/FOMENTO UTILIZADO PELO TOMADOR DO SERVIÇO *</label>
                    <select style={S.input} value={form.com_ext_mec_af_comex_t||""} onChange={e=>set("com_ext_mec_af_comex_t",e.target.value)}>
                      <option value="">Selecione...</option>
                      <option value="01">Nenhum</option>
                      <option value="02">Adm. Pública e Repr. Internacional</option>
                      <option value="03">Alugueis e Arrend. Mercantil de maquinas, equip., embarc. e aeronaves</option>
                      <option value="04">Arrendamento Mercantil de aeronave para empresa de transporte aéreo público</option>
                      <option value="05">Comissão a agentes externos na exportação</option>
                      <option value="06">Despesas de armazenagem, mov. e transporte de carga no exterior</option>
                      <option value="07">Eventos FIFA (subsidiária)</option>
                      <option value="08">Eventos FIFA</option>
                      <option value="09">Fretes, arrendamentos de embarcações ou aeronaves e outros</option>
                      <option value="10">Material Aeronáutico</option>
                      <option value="11">Promoção de Bens no Exterior</option>
                      <option value="12">Promoção de Dest. Turísticos Brasileiros</option>
                      <option value="13">Promoção do Brasil no Exterior</option>
                      <option value="14">Promoção Serviços no Exterior</option>
                      <option value="15">RECINE</option>
                      <option value="16">RECOPA</option>
                      <option value="17">Registro e Manutenção de marcas, patentes e cultivares</option>
                      <option value="18">REICOMP</option>
                      <option value="19">REIDI</option>
                      <option value="20">REPENEC</option>
                      <option value="21">REPES</option>
                      <option value="22">RETAERO</option>
                      <option value="23">RETID</option>
                      <option value="24">Royalties, Assistência Técnica, Científica e Assemelhados</option>
                      <option value="25">Serviços de avaliação da conformidade vinculados aos Acordos da OMC</option>
                      <option value="26">ZPE</option>
                    </select>
                  </div>
                  <div style={{ marginBottom:12 }}>
                    <label style={S.label}>EXISTE VÍNCULO DA OPERAÇÃO À MOVIMENTAÇÃO TEMPORÁRIA DE BENS? *</label>
                    <select style={{...S.input,...(errosCampos.com_ext_mov_temp_bens?{borderColor:"#ef4444"}:{})}} value={form.com_ext_mov_temp_bens||""} onChange={e=>set("com_ext_mov_temp_bens",e.target.value)}>
                      <option value="">Selecione...</option>
                      <option value="1">Não</option>
                      <option value="2">Vinculada – Declaração de Importação</option>
                      <option value="3">Vinculada – Declaração de Exportação</option>
                    </select>
                    {errosCampos.com_ext_mov_temp_bens && <div style={{fontSize:11,color:"#f87171",marginTop:4}}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.com_ext_mov_temp_bens}</div>}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <CpfCnpjInput S={S} T={T} token={token} empresaId={empresaId} onResult={handleDocumento} defaultValue={docRestaurado} onCadastrar={(cpf) => {
                localStorage.setItem("nfse_precadastro_cpf", cpf);
                localStorage.setItem("nfse_emitir_rascunho", JSON.stringify(form));
                localStorage.setItem("nfse_emitir_empresa", empresaId);
                localStorage.setItem("nfse_emitir_servico", servicoId);
                // O município escolhido na nota não mora no `form` — é estado à
                // parte, e sem isto a ida ao cadastro de pessoas apagava o que já
                // tinha sido digitado aqui. Mesma chave usada pelo "Copiar nota".
                if (munIncidNota) localStorage.setItem("nfse_emitir_mun_incid", JSON.stringify(munIncidNota));
                onSuccess("pessoas");
              }} />

              <div>
                <label style={S.label}>RAZÃO SOCIAL / NOME DO TOMADOR</label>
                <input style={bloqStyle("tomador_razao_social", {...S.input, ...(errosCampos.tomador_razao_social ? {borderColor:"#ef4444"} : {})})} readOnly={camposBloqueados.includes("tomador_razao_social")} value={form.tomador_razao_social} onChange={e=>set("tomador_razao_social",e.target.value)} />
                {errosCampos.tomador_razao_social && <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.tomador_razao_social}</div>}
              </div>

              {/* Aviso antecipado: quando o serviço exige o endereço do tomador, quem
                  vem do cadastro de pessoas sem rua/número/bairro vai travar na emissão. */}
              {exigeEnderecoTomador && (
                (() => {
                  const cepOk = (form.tomador_cep||"").replace(/\D/g,"").length === 8;
                  const completo = [form.tomador_endereco, form.tomador_numero, form.tomador_bairro, form.tomador_municipio_codigo]
                    .every(v => String(v||"").trim()) && cepOk;
                  return (
                    <div style={{ display:"flex", alignItems:"flex-start", gap:8, borderRadius:8, padding:"9px 12px",
                                  background: completo ? "#0d2f1a" : "#2f0d0d",
                                  border: "1px solid " + (completo ? "#22c55e40" : "#ef4444") }}>
                      {completo ? <Check size={14} style={{ color:"#4ade80", flexShrink:0, marginTop:1 }} />
                                : <AlertTriangle size={14} style={{ color:"#f87171", flexShrink:0, marginTop:1 }} />}
                      <span style={{ fontSize:12, lineHeight:1.55, color: completo ? "#86efac" : "#fca5a5" }}>
                        {completo
                          ? "Endereço completo do tomador — exigido porque a incidência deste serviço é no domicílio dele."
                          : <>A incidência deste serviço é no <strong>domicílio do tomador</strong>, e a SEFIN exige o
                             endereço completo (logradouro, número, bairro, CEP e município). Sem isso a nota é
                             recusada com o erro E0234.{tomadorEhCPF && " Complete o cadastro em Pessoas (CPF) e selecione de novo."}</>}
                      </span>
                    </div>
                  );
                })()
              )}
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap:16 }}>
                <div><label style={S.label}>ENDEREÇO{exigeEnderecoTomador && <span style={{color:"#f97316",marginLeft:3}}>*</span>}</label><input style={bloqStyle("tomador_endereco",{...S.input, ...(errosCampos.tomador_endereco && !String(form.tomador_endereco||"").trim() ? {borderColor:"#ef4444"} : {})})} readOnly={camposBloqueados.includes("tomador_endereco")} value={form.tomador_endereco} onChange={e=>set("tomador_endereco",e.target.value)} /></div>
                <div><label style={S.label}>NÚMERO{exigeEnderecoTomador && <span style={{color:"#f97316",marginLeft:3}}>*</span>}</label><input style={bloqStyle("tomador_numero",{...S.input, ...(errosCampos.tomador_endereco && !String(form.tomador_numero||"").trim() ? {borderColor:"#ef4444"} : {})})} readOnly={camposBloqueados.includes("tomador_numero")} value={form.tomador_numero} onChange={e=>set("tomador_numero",e.target.value)} /></div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 80px", gap:16 }}>
                <div><label style={S.label}>BAIRRO{exigeEnderecoTomador && <span style={{color:"#f97316",marginLeft:3}}>*</span>}</label><input style={bloqStyle("tomador_bairro",{...S.input, ...(errosCampos.tomador_endereco && !String(form.tomador_bairro||"").trim() ? {borderColor:"#ef4444"} : {})})} readOnly={camposBloqueados.includes("tomador_bairro")} value={form.tomador_bairro} onChange={e=>set("tomador_bairro",e.target.value)} /></div>
                <div><label style={S.label}>MUNICÍPIO</label><input style={bloqStyle("tomador_municipio",S.input)} readOnly={camposBloqueados.includes("tomador_municipio")} value={form.tomador_municipio} onChange={e=>set("tomador_municipio",e.target.value)} /></div>
                <div>
                  <label style={S.label}>CEP</label>
                  <input style={bloqStyle("tomador_cep", {...S.input, ...(errosCampos.tomador_cep ? {borderColor:"#ef4444"} : {})})} placeholder="00000-000" readOnly={camposBloqueados.includes("tomador_cep")} value={form.tomador_cep} onChange={e=>handleCepTomador(e.target.value)} />
                  {errosCampos.tomador_cep && <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.tomador_cep}</div>}
                </div>
                <div><label style={S.label}>UF</label><input style={bloqStyle("tomador_uf",S.input)} maxLength={2} readOnly={camposBloqueados.includes("tomador_uf")} value={form.tomador_uf} onChange={e=>set("tomador_uf",e.target.value)} /></div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16 }}>
                <div><label style={S.label}>E-MAIL</label><input style={bloqStyle("tomador_email",S.input)} type="email" readOnly={camposBloqueados.includes("tomador_email")} value={form.tomador_email} onChange={e=>set("tomador_email",e.target.value)} /></div>
                <div><label style={S.label}>TELEFONE</label><input style={bloqStyle("tomador_fone",S.input)} placeholder="(00) 00000-0000" readOnly={camposBloqueados.includes("tomador_fone")} value={form.tomador_fone} onChange={e=>set("tomador_fone",e.target.value)} /></div>
              </div>
            </>
          )}

          {/* Aviso: retenções removidas por o tomador ser pessoa física */}
          {retencoesRemovidas.length > 0 && (
            <div style={{ display:"flex", gap:11, alignItems:"flex-start",
                          background: T.isDark ? "#2a1f0d" : "#fffbeb",
                          border:"1px solid "+(T.isDark ? "#f59e0b50" : "#fde68a"),
                          borderRadius:9, padding:"12px 15px" }}>
              <AlertTriangle size={16} style={{ color:"#f59e0b", flexShrink:0, marginTop:1 }} />
              <div>
                <div style={{ fontSize:13, fontWeight:700, color: T.isDark ? "#fcd34d" : "#92400e" }}>
                  Retenções removidas automaticamente
                </div>
                <div style={{ fontSize:12.5, color: T.isDark ? "#fcd34d" : "#92400e", marginTop:3, lineHeight:1.6 }}>
                  O tomador é pessoa física (CPF) e não pode reter tributos na fonte.
                  O serviço <strong>{servicoSel?.nome}</strong> está cadastrado com{" "}
                  <strong>{retencoesRemovidas.join(", ")}</strong>, que {retencoesRemovidas.length === 1 ? "será desconsiderada" : "serão desconsideradas"} nesta nota.
                </div>
                <div style={{ fontSize:11.5, color: T.isDark ? "#fcd34d" : "#92400e", opacity:0.8, marginTop:4 }}>
                  O cadastro do serviço permanece inalterado — a remoção vale só para esta emissão.
                </div>
              </div>
            </div>
          )}

          {/* Intermediário */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#a78bfa", textTransform:"uppercase", letterSpacing:1 }}>Intermediário</div>
            <button
              type="button"
              onClick={() => {
                const novo = !form.tem_intermediario;
                setForm(f => ({...f,
                  tem_intermediario: novo,
                  ...(!novo ? { intermediario_cnpj:"", intermediario_cpf:"", intermediario_razao_social:"" } : {})
                }));
              }}
              style={{
                display:"flex", alignItems:"center", gap:6,
                background: form.tem_intermediario ? "#a78bfa20" : T.btnSecBg,
                border:"1px solid "+(form.tem_intermediario ? "#a78bfa60" : T.border),
                borderRadius:6, padding:"5px 12px", fontSize:11, fontWeight:600,
                color: form.tem_intermediario ? "#a78bfa" : T.textMuted, cursor:"pointer"
              }}>
              <span style={{
                display:"inline-block", width:14, height:14, borderRadius:3,
                border:"1.5px solid "+(form.tem_intermediario ? "#a78bfa" : T.textFaint),
                background: form.tem_intermediario ? "#a78bfa" : "transparent",
                marginRight:2, flexShrink:0, position:"relative"
              }}>
                {form.tem_intermediario && <span style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",color:"#fff",display:"flex"}}><Check size={10} /></span>}
              </span>
              Há intermediário na operação
            </button>
          </div>

          {form.tem_intermediario && (
            <div style={{ background:"#a78bfa08", border:"1px solid #a78bfa30", borderRadius:8, padding:16, display:"flex", flexDirection:"column", gap:14 }}>
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16 }}>
                <div>
                  <label style={{...S.label, color:"#a78bfa"}}>
                    CNPJ DO INTERMEDIÁRIO
                    {!form.intermediario_cpf && <span style={{color:"#f97316",marginLeft:3}}>*</span>}
                    {intermediarioBuscando && <span style={{color:"#a78bfa",fontWeight:400,marginLeft:8,fontSize:11}}><Loader2 size={13} style={{verticalAlign:"middle",marginRight:4,animation:"spin 1s linear infinite"}} /> buscando...</span>}
                    {!intermediarioBuscando && form.intermediario_cnpj && form.intermediario_razao_social && !intermediarioErro && <span style={{color:"#4ade80",fontWeight:400,marginLeft:8,fontSize:11}}><Check size={11} style={{verticalAlign:"middle",marginRight:3}} /> encontrado</span>}
                  </label>
                  <input
                    style={{ ...S.input, borderColor: intermediarioErro ? "#ef4444" : undefined }}
                    placeholder="00.000.000/0000-00 (aceita alfanumérico)"
                    value={form.intermediario_cnpj}
                    onChange={e => {
                      const cnpj = limparCNPJ(e.target.value);
                      const fmt = fmtCNPJAlfa(cnpj);
                      set("intermediario_cnpj", fmt);
                      if (cnpj.length > 0) set("intermediario_cpf", "");
                      setIntermediarioErro("");
                      if (cnpj.length === 14) buscarCnpjIntermediario(cnpj);
                    }}
                  />
                  {intermediarioErro && <div style={{fontSize:11, color:"#f87171", marginTop:4}}>{intermediarioErro}</div>}
                </div>
                <div>
                  <label style={{...S.label, color:"#a78bfa"}}>
                    CPF DO INTERMEDIÁRIO
                    {!form.intermediario_cnpj && <span style={{color:"#f97316",marginLeft:3}}>*</span>}
                    <span style={{color:"#475569",fontWeight:400,fontSize:10,marginLeft:4}}>(se PF)</span>
                  </label>
                  <input
                    style={S.input}
                    placeholder="000.000.000-00"
                    value={form.intermediario_cpf}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g,"").slice(0,11);
                      const fmt = digits.length <= 3 ? digits
                        : digits.length <= 6 ? digits.replace(/^(\d{3})(\d+)/,"$1.$2")
                        : digits.length <= 9 ? digits.replace(/^(\d{3})(\d{3})(\d+)/,"$1.$2.$3")
                        : digits.replace(/^(\d{3})(\d{3})(\d{3})(\d+)/,"$1.$2.$3-$4");
                      set("intermediario_cpf", fmt);
                      if (digits.length > 0) { set("intermediario_cnpj", ""); setIntermediarioErro(""); }
                    }}
                  />
                </div>
              </div>
              <div>
                <label style={{...S.label, color:"#a78bfa"}}>RAZÃO SOCIAL DO INTERMEDIÁRIO <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                <input style={S.input} placeholder="Nome ou razão social do intermediário" value={form.intermediario_razao_social} onChange={e=>set("intermediario_razao_social",e.target.value)} />
              </div>
              {servicoSel?.iss_retido && (
                <div style={{ fontSize:11, color:"#facc15", background:"#fbbf2410", border:"1px solid #fbbf2440", borderRadius:6, padding:"8px 12px" }}>
                  <AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> O serviço tem ISS retido — como há intermediário, será emitido com <strong>tpRetISSQN = 3</strong> (Retido pelo Intermediário).
                </div>
              )}
            </div>
          )}

          {/* Valor + Quantidade + Data */}
          <div style={{ fontSize:11, fontWeight:700, color:"#38bdf8", textTransform:"uppercase", letterSpacing:1 }}>Valores</div>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap:16 }}>
            <div>
              <label style={S.label}>VALOR UNITÁRIO (R$) <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
              <input style={{...S.input, borderColor: (form.valor_servico!==""&&parseBR(form.valor_servico)<=0)?"#ef4444":undefined}} type="text" inputMode="numeric" placeholder="0,00" value={form.valor_servico}
                onChange={e=>set("valor_servico",fmtMoneyInput(e.target.value))} />
              {form.valor_servico!==""&&parseBR(form.valor_servico)<=0&&<span style={{color:"#ef4444",fontSize:12,marginTop:2,display:"block"}}>Valor deve ser maior que zero</span>}
            </div>
            <div>
              <label style={S.label}>QUANTIDADE <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
              <input style={{...S.input, borderColor: erroQtd?"#ef4444":undefined}} type="number" min="1" step="1" value={form.quantidade}
                onChange={e=>{
                  const v=parseInt(e.target.value);
                  set("quantidade", e.target.value);
                  if(e.target.value===""||isNaN(v)||v<1) setErroQtd("Quantidade mínima é 1");
                  else setErroQtd("");
                }}
                onBlur={e=>{
                  const v=parseInt(e.target.value);
                  if(isNaN(v)||v<1){ set("quantidade","1"); setErroQtd(""); }
                }} />
              {erroQtd && <span style={{color:"#ef4444",fontSize:12,marginTop:2,display:"block"}}>{erroQtd}</span>}
            </div>
            <div>
              <label style={S.label}>DATA DE COMPETÊNCIA <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
              <input style={S.input} type="date" value={form.data_emissao} onChange={e=>set("data_emissao",e.target.value)} />
            </div>
          </div>

          {/* Descontos */}
          <div style={{ fontSize:11, fontWeight:700, color:"#38bdf8", textTransform:"uppercase", letterSpacing:1 }}>
            Descontos <span style={{ fontWeight:400, color:T.textFaint, textTransform:"none", letterSpacing:0 }}>(se houver)</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16 }}>
            <div>
              <label style={S.label}>DESCONTO INCONDICIONADO (R$)</label>
              <input style={{...S.input, ...(errosCampos.desconto_incondicionado ? {borderColor:"#ef4444"} : {})}} type="text" inputMode="numeric" placeholder="0,00" value={form.desconto_incondicionado} onChange={e=>set("desconto_incondicionado",fmtMoneyInput(e.target.value))} />
              {errosCampos.desconto_incondicionado ? <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.desconto_incondicionado}</div> : <div style={{ fontSize:10, color:T.textFaint, marginTop:4 }}>Reduz a base de cálculo do ISS</div>}
            </div>
            <div>
              <label style={S.label}>DESCONTO CONDICIONADO (R$)</label>
              <input style={{...S.input, ...(errosCampos.desconto_condicionado ? {borderColor:"#ef4444"} : {})}} type="text" inputMode="numeric" placeholder="0,00" value={form.desconto_condicionado} onChange={e=>set("desconto_condicionado",fmtMoneyInput(e.target.value))} />
              {errosCampos.desconto_condicionado ? <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.desconto_condicionado}</div> : <div style={{ fontSize:10, color:T.textFaint, marginTop:4 }}>Registrado na nota, não reduz a base</div>}
            </div>
          </div>

          {/* Valor da Dedução — só aparece se o serviço permite dedução à BC */}
          {servicoSel?.tem_deducao_bc && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:"#fbbf24", textTransform:"uppercase", letterSpacing:1 }}>
                Dedução à Base de Cálculo do ISSQN
              </div>
              <div>
                <label style={S.label}>VALOR DA DEDUÇÃO (R$) <span style={{color:"#f97316"}}>*</span></label>
                <input style={{...S.input, ...(errosCampos.valor_deducao_bc ? {borderColor:"#ef4444"} : {})}} type="number" step="0.01" min="0" placeholder="0,00" value={form.valor_deducao_bc} onChange={e=>set("valor_deducao_bc",e.target.value)} />
                {errosCampos.valor_deducao_bc ? <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.valor_deducao_bc}</div> : <div style={{ fontSize:11, color:T.textFaint, marginTop:4 }}>Ex: subempreitada, materiais fornecidos. Reduz a base de cálculo do ISS.</div>}
              </div>
            </>
          )}

          {/* Tributos aproximados monetários — só aparece para modo 1 */}
          {servicoSel?.modo_tot_trib === 1 && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:"#38bdf8", textTransform:"uppercase", letterSpacing:1 }}>
                Tributos Aproximados <span style={{ fontWeight:400, color:T.textFaint, textTransform:"none", letterSpacing:0 }}>(valores monetários)</span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap:16 }}>
                <div>
                  <label style={S.label}>FEDERAL (R$)</label>
                  <input style={S.input} type="number" step="0.01" min="0" placeholder="0,00" value={form.v_trib_fed} onChange={e=>set("v_trib_fed",e.target.value)} />
                </div>
                <div>
                  <label style={S.label}>ESTADUAL (R$)</label>
                  <input style={S.input} type="number" step="0.01" min="0" placeholder="0,00" value={form.v_trib_est} onChange={e=>set("v_trib_est",e.target.value)} />
                </div>
                <div>
                  <label style={S.label}>MUNICIPAL (R$)</label>
                  <input style={S.input} type="number" step="0.01" min="0" placeholder="0,00" value={form.v_trib_mun} onChange={e=>set("v_trib_mun",e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* Retenções Federais */}
          <div style={{ fontSize:11, fontWeight:700, color:"#a78bfa", textTransform:"uppercase", letterSpacing:1 }}>
            Retenções Federais <span style={{ fontWeight:400, color:T.textFaint, textTransform:"none", letterSpacing:0 }}>(se houver)</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap:16 }}>
            <div>
              <label style={S.label}>IRRF (R$)</label>
              <input style={{...S.input, ...(errosCampos.v_ret_irrf ? {borderColor:"#ef4444"} : {})}} type="text" inputMode="numeric" placeholder="0,00" value={form.v_ret_irrf} onChange={e=>set("v_ret_irrf",fmtMoneyInput(e.target.value))} />
              {errosCampos.v_ret_irrf && <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.v_ret_irrf}</div>}
            </div>
            <div>
              <label style={S.label}>CSLL RETIDA (R$)</label>
              <input style={{...S.input, ...(errosCampos.v_ret_csll ? {borderColor:"#ef4444"} : {})}} type="text" inputMode="numeric" placeholder="0,00" value={form.v_ret_csll} onChange={e=>set("v_ret_csll",fmtMoneyInput(e.target.value))} />
              {errosCampos.v_ret_csll && <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.v_ret_csll}</div>}
            </div>
            <div>
              <label style={S.label}>CONTRIB. PREVIDENCIÁRIA (R$)</label>
              <input style={{...S.input, ...(errosCampos.v_ret_cp ? {borderColor:"#ef4444"} : {})}} type="text" inputMode="numeric" placeholder="0,00" value={form.v_ret_cp} onChange={e=>set("v_ret_cp",fmtMoneyInput(e.target.value))} />
              {errosCampos.v_ret_cp && <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCampos.v_ret_cp}</div>}
            </div>
          </div>

          {(() => {
            // Sem certificado a emissão falharia de qualquer jeito, no fim do processo
            const impedido = loading || !servicoId || !!problemaCertificado;
            return (
          <button onClick={emitir} disabled={impedido}
            title={problemaCertificado ? problemaCertificado.titulo : undefined}
            style={{ background: impedido ? T.btnSecBg : "linear-gradient(135deg,#0ea5e9,#0284c7)", color: impedido ? T.btnSecText : "#fff", border:"none", borderRadius:8, padding: isMobile ? "16px" : "13px", fontSize:14, fontWeight:600, cursor: impedido ? "not-allowed" : "pointer" }}>
            {loading ? <><Loader2 size={14} style={{verticalAlign:"middle",marginRight:5,animation:"spin 1s linear infinite"}} />Enviando...</>
             : problemaCertificado ? <><AlertTriangle size={14} style={{verticalAlign:"middle",marginRight:5}} />Sem certificado válido</>
             : <><Check size={14} style={{verticalAlign:"middle",marginRight:5}} />Emitir NFS-e</>}
          </button>
            );
          })()}
        </div>

        {/* Resumo lateral */}
        <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:12, padding:22, height:"fit-content", boxShadow:T.shadow, display: isMobile ? "none" : undefined }}>
          <div style={{ fontSize:13, fontWeight:600, color:T.textMuted, marginBottom:16 }}>Resumo</div>
          <div style={{ fontSize:28, fontWeight:800, color:"#4ade80", marginBottom:4 }}>{fmtMoeda(valorTotal)}</div>
          <div style={{ fontSize:11, color:T.textFaint, marginBottom:12 }}>Valor bruto</div>
          {qtd > 1 && <div style={{ fontSize:12, color:T.textFaint, marginBottom:8 }}>{qtd}× {fmtMoeda(valorUnit)}</div>}
          {descontoInc > 0 && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
              <span style={{ color:T.textFaint }}>Desc. Incondicionado</span>
              <span style={{ color:"#f87171" }}>– {fmtMoeda(descontoInc)}</span>
            </div>
          )}
          {descontoCond > 0 && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
              <span style={{ color:T.textFaint }}>Desc. Condicionado</span>
              <span style={{ color:T.textFaint }}>– {fmtMoeda(descontoCond)}</span>
            </div>
          )}
          {deducaoBC > 0 && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
              <span style={{ color:T.textFaint }}>Dedução BC ISSQN</span>
              <span style={{ color:"#f87171" }}>– {fmtMoeda(deducaoBC)}</span>
            </div>
          )}
          {(descontoInc > 0 || deducaoBC > 0) && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:8, color:T.textFaint }}>
              <span>Base ISS</span>
              <span>{fmtMoeda(baseISS)}</span>
            </div>
          )}
          {iss > 0 && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:8 }}>
              <span style={{ color:T.textFaint }}>
                {servicoSel?.nao_tributacao_iss ? "ISS (isento/imune)" : `ISS (${aliq}%)`}
                {!servicoSel?.iss_retido && iss > 0 && (
                  <span style={{ fontSize:10, color:T.textFaint, marginLeft:4 }}>(prestador recolhe)</span>
                )}
              </span>
              {servicoSel?.iss_retido
                ? <span style={{ color:"#fbbf24" }}>– {fmtMoeda(iss)}</span>
                : <span style={{ color:T.textFaint, fontStyle:"italic" }}>{fmtMoeda(iss)}</span>
              }
            </div>
          )}
          {retIRRF > 0 && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
              <span style={{ color:T.textFaint }}>IRRF</span>
              <span style={{ color:"#f87171" }}>– {fmtMoeda(retIRRF)}</span>
            </div>
          )}
          {retCSLL > 0 && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
              <span style={{ color:T.textFaint }}>CSLL</span>
              <span style={{ color:"#f87171" }}>– {fmtMoeda(retCSLL)}</span>
            </div>
          )}
          {retPIS > 0 && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
              <span style={{ color:T.textFaint }}>PIS ({parseFloat(servicoSel?.p_aliq_pis).toFixed(4)}%)</span>
              <span style={{ color:"#f87171" }}>– {fmtMoeda(retPIS)}</span>
            </div>
          )}
          {retCOFINS > 0 && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
              <span style={{ color:T.textFaint }}>COFINS ({parseFloat(servicoSel?.p_aliq_cofins).toFixed(4)}%)</span>
              <span style={{ color:"#f87171" }}>– {fmtMoeda(retCOFINS)}</span>
            </div>
          )}
          {retCP > 0 && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
              <span style={{ color:T.textFaint }}>Contrib. Prev.</span>
              <span style={{ color:"#f87171" }}>– {fmtMoeda(retCP)}</span>
            </div>
          )}
          <div style={{ height:1, background:T.border, marginBottom:8 }} />
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, fontWeight:600 }}>
            <span style={{ color:T.textFaint }}>Líquido</span>
            <span style={{ color:"#38bdf8" }}>{fmtMoeda(valorTotal - descontoInc - (servicoSel?.iss_retido ? iss : 0) - totalRetencoes)}</span>
          </div>
          {servicoSel && (
            <div style={{ marginTop:16, padding:"10px 12px", background:T.cardInner, borderRadius:8, border:"1px solid "+T.border }}>
              <div style={{ fontSize:11, color:T.textFaint, marginBottom:4 }}>SERVIÇO</div>
              <div style={{ fontSize:12, color:T.inputText, fontWeight:600 }}>{servicoSel.nome}</div>
            </div>
          )}
          <div style={{ marginTop:10, padding:"10px 12px", background:T.cardInner, borderRadius:8, border:"1px solid "+(form.tomador_nao_informado ? "#f9731640" : T.border) }}>
            <div style={{ fontSize:11, color:T.textFaint, marginBottom:4 }}>TOMADOR</div>
            {form.tomador_nao_informado
              ? <div style={{ fontSize:12, color:"#fb923c", fontStyle:"italic" }}>Não informado</div>
              : form.tomador_razao_social
                ? <>
                    <div style={{ fontSize:12, color:T.inputText, fontWeight:600 }}>{form.tomador_razao_social}</div>
                    {form.tomador_municipio && <div style={{ fontSize:11, color:T.textFaint }}>{form.tomador_municipio}{form.tomador_uf?" / "+form.tomador_uf:""}</div>}
                  </>
                : <div style={{ fontSize:12, color:T.textFaint, fontStyle:"italic" }}>—</div>
            }
          </div>
          {form.tem_intermediario && form.intermediario_razao_social && (
            <div style={{ marginTop:10, padding:"10px 12px", background:T.cardInner, borderRadius:8, border:"1px solid #a78bfa40" }}>
              <div style={{ fontSize:11, color:"#a78bfa", marginBottom:4 }}>INTERMEDIÁRIO</div>
              <div style={{ fontSize:12, color:T.inputText, fontWeight:600 }}>{form.intermediario_razao_social}</div>
              {(form.intermediario_cnpj||form.intermediario_cpf) && <div style={{ fontSize:11, color:T.textFaint }}>{form.intermediario_cnpj||form.intermediario_cpf}</div>}
            </div>
          )}
          {form.data_emissao && (
            <div style={{ marginTop:10, padding:"10px 12px", background:T.cardInner, borderRadius:8, border:"1px solid "+T.border }}>
              <div style={{ fontSize:11, color:T.textFaint, marginBottom:4 }}>COMPETÊNCIA</div>
              <div style={{ fontSize:12, color:"#38bdf8", fontWeight:600 }}>{form.data_emissao ? (() => { const [y,m]=form.data_emissao.slice(0,7).split("-"); const meses=["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"]; return `${meses[parseInt(m)-1]}/${y}`; })() : "—"}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// Nota que veio da SEFIN, não saiu daqui. Sem marca não dá para saber por que
// uma nota do histórico não tem rascunho, log de emissão nem RPS nosso.
function SeloImportada({ T }) {
  return (
    <span title="Emitida fora do sistema — baixada da SEFIN"
      style={{ marginLeft:6, padding:"1px 6px", borderRadius:4, fontSize:9,
               fontWeight:700, letterSpacing:0.3, verticalAlign:"middle",
               background:"#0ea5e922", color:"#38bdf8", border:"1px solid #38bdf844" }}>
      SEFIN
    </span>
  );
}

function HistoricoView({ token, onReemitir = null, isMobile = false, T = {isDark:true,bg:"#080c10",card:"#0c1117",border:"#1a2332",text:"#f1f5f9",textMuted:"#94a3b8",textFaint:"#475569",input:"#080c10",inputBorder:"#1a2332",inputText:"#e2e8f0",rowBorder:"#0f1923",tableBg:"#0c1117",dangerBg:"#2f0d0d",danger:"#f87171",logBg:"#0c1117",logBorder:"#1a2332",logText:"#94a3b8"} }) {
  const hoje = new Date();
  const mesAtualHist = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,"0")}`;
  const [notas, setNotas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  // Lembra a empresa escolhida entre visitas à tela
  const [empresaFiltro, setEmpresaFiltro] = useState(
    () => lerEmpresaAtual());
  const [search, setSearch] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todas");
  const [mesHist, setMesHist] = useState(mesAtualHist);
  const [cancelando, setCancelando] = useState(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState("Erro na emissão");
  const [loadingAcao, setLoadingAcao] = useState(null);
  const [logNota, setLogNota] = useState(null);
  // "Reemitir" abria o mesmo formulário que "Copiar nota" — duas opções para o
  // mesmo efeito. Agora ele reenvia a nota como está, depois de confirmar.
  const [reemissao, setReemissao] = useState(null);   // { nota, previa } | { nota, erro }
  const [reemitindo, setReemitindo] = useState(false);
  // Importação das notas emitidas fora do sistema, puxadas da SEFIN
  const [importando, setImportando] = useState(false);
  const [resImport, setResImport] = useState(null);
  // Identifica a busca em andamento. Trocar de empresa invalida a anterior —
  // sem isto a busca continuava rodando na empresa antiga, com o botão preso
  // em "Buscando..." e o contador mostrando números de outra empresa.
  const buscaAtual = useRef(0);
  const [verNota, setVerNota] = useState(null);
  const [verNotaServico, setVerNotaServico] = useState(null);

  // Município e IBS/CBS não têm coluna própria na tabela de notas — vêm do XML
  // enviado, extraídos sob demanda pelo backend. Funciona também para notas antigas.
  const [resumoFiscal, setResumoFiscal] = useState(null);
  useEffect(() => {
    if (!verNota?.id) { setResumoFiscal(null); return; }
    let cancelado = false;
    fetch(`${API}/nfse/${verNota.id}/resumo-fiscal`, { headers:{ Authorization:"Bearer "+token } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelado) setResumoFiscal(d); })
      .catch(() => { if (!cancelado) setResumoFiscal(null); });
    return () => { cancelado = true; };
  }, [verNota?.id]);
  const [menuAberto, setMenuAberto] = useState(null);
  const [menuPos, setMenuPos] = useState({ top:0, right:0 });
  useEffect(() => {
    const fechar = () => setMenuAberto(null);
    document.addEventListener("mousedown", fechar);
    return () => document.removeEventListener("mousedown", fechar);
  }, []);

  const abrirDetalhes = (n) => { setVerNota(n); };

  const copiarNota = async (n) => {
    localStorage.setItem("nfse_emitir_empresa", n.empresa_id);
    // A nota guarda só o código do município; o seletor da emissão precisa de
    // nome e UF. Sem isso o código copiado é ignorado e vale o padrão do serviço.
    if (n.municipio_incidencia) {
      try {
        const r = await fetch(`${API}/municipios/${n.municipio_incidencia}`, {
          headers: { Authorization: "Bearer " + token }
        });
        if (r.ok) localStorage.setItem("nfse_emitir_mun_incid", JSON.stringify(await r.json()));
      } catch { /* segue sem o município; a tela cai no padrão do serviço */ }
    }
    const isExterior = !!n.tomador_exterior;
    const isNaoInformado = n.tomador_razao_social === "NÃO INFORMADO";
    localStorage.setItem("nfse_emitir_rascunho", JSON.stringify({
      tomador_razao_social: isNaoInformado ? "" : (n.tomador_razao_social || ""),
      tomador_cnpj: n.tomador_cnpj || "",
      tomador_cpf: n.tomador_cpf || "",
      tomador_email: n.tomador_email || "",
      tomador_fone: n.tomador_fone || "",
      tomador_endereco: n.tomador_endereco || "",
      tomador_numero: n.tomador_numero || "",
      tomador_bairro: n.tomador_bairro || "",
      tomador_municipio: n.tomador_municipio || "",
      tomador_municipio_codigo: n.tomador_municipio_codigo || "",
      tomador_cep: n.tomador_cep || "",
      tomador_uf: n.tomador_uf || "",
      tomador_nao_informado: isNaoInformado,
      tomador_localizacao: isNaoInformado ? "nao_informado" : isExterior ? "exterior" : "brasil",
      tomador_exterior: isExterior,
      // Campos exterior
      tomador_nif: n.tomador_nif || "",
      tomador_c_nao_nif: n.tomador_c_nao_nif != null ? String(n.tomador_c_nao_nif) : "",
      tomador_pais_nome: n.tomador_pais_nome || "",
      tomador_codigo_pais: n.tomador_codigo_pais || "",
      tomador_municipio_exterior: n.tomador_municipio_exterior || "",
      tomador_cep_exterior: n.tomador_cep_exterior || "",
      tomador_estado_exterior: n.tomador_estado_exterior || "",
      // comExt
      com_ext_md_prestacao: n.com_ext_md_prestacao != null ? String(n.com_ext_md_prestacao) : "",
      com_ext_vinc_prest: n.com_ext_vinc_prest != null ? String(n.com_ext_vinc_prest) : "",
      com_ext_tp_moeda: n.com_ext_tp_moeda != null ? String(n.com_ext_tp_moeda) : "",
      com_ext_tp_moeda_display: (() => { const m = MOEDAS.find(x=>x.codigo===String(n.com_ext_tp_moeda)); return m ? `${m.sigla} – ${m.nome}` : ""; })(),
      com_ext_v_serv_moeda: n.com_ext_v_serv_moeda ? String(n.com_ext_v_serv_moeda) : "",
      com_ext_mec_af_comex_p: n.com_ext_mec_af_comex_p || "00",
      com_ext_mec_af_comex_t: n.com_ext_mec_af_comex_t || "00",
      com_ext_mov_temp_bens: n.com_ext_mov_temp_bens != null ? String(n.com_ext_mov_temp_bens) : "",
      com_ext_mdic: n.com_ext_mdic != null ? String(n.com_ext_mdic) : "",
      // Intermediário: o bloco só abre se houver documento gravado na nota
      tem_intermediario: !!(n.intermediario_cnpj || n.intermediario_cpf),
      intermediario_cnpj: n.intermediario_cnpj || "",
      intermediario_cpf: n.intermediario_cpf || "",
      intermediario_razao_social: n.intermediario_razao_social || "",
      // Obra (construção civil)
      obra_forma: n.obra_forma || "cno",
      cno: n.cno || "",
      obra_cib: n.obra_cib || "",
      obra_cep: n.obra_cep || "",
      obra_logradouro: n.obra_logradouro || "",
      obra_numero: n.obra_numero || "",
      obra_complemento: n.obra_complemento || "",
      obra_bairro: n.obra_bairro || "",
      obra_cod_postal: n.obra_cod_postal || "",
      obra_cidade_ext: n.obra_cidade_ext || "",
      obra_estado_ext: n.obra_estado_ext || "",
      inscricao_imobiliaria_fiscal: n.inscricao_imobiliaria_fiscal || "",
      // Município e alíquota escolhidos naquela nota (serviço sem município fixo)
      municipio_incidencia: n.municipio_incidencia || "",
      aliquota_iss_nota: n.aliquota_iss != null ? String(n.aliquota_iss) : "",
      // Tributos aproximados no modo monetário
      v_trib_fed: n.v_trib_fed ? String(n.v_trib_fed) : "",
      v_trib_est: n.v_trib_est ? String(n.v_trib_est) : "",
      v_trib_mun: n.v_trib_mun ? String(n.v_trib_mun) : "",
      valor_deducao_bc: n.valor_deducao_bc ? numToBR(n.valor_deducao_bc) : "",
      servico_descricao: n.servico_descricao || "",
      informacoes_complementares: n.informacoes_complementares || "",
      valor_servico: n.valor_servico ? numToBR(n.valor_servico) : "",
      quantidade: "1",
      data_emissao: new Date().toISOString().slice(0, 10),
      v_ret_irrf: n.v_ret_irrf ? numToBR(n.v_ret_irrf) : "",
      v_ret_csll: n.v_ret_csll ? numToBR(n.v_ret_csll) : "",
      v_ret_cp: n.v_ret_cp ? numToBR(n.v_ret_cp) : "",
      desconto_incondicionado: n.desconto_incondicionado ? numToBR(n.desconto_incondicionado) : "",
      desconto_condicionado: n.desconto_condicionado ? numToBR(n.desconto_condicionado) : "",
    }));
    if (n.servico_codigo) localStorage.setItem("nfse_emitir_servico_codigo", n.servico_codigo);
    if (n.servico_nome)  localStorage.setItem("nfse_emitir_servico_nome", n.servico_nome);
    // Salva documento separado para o CpfCnpjInput (input box)
    const doc = (n.tomador_cpf || n.tomador_cnpj || "").replace(/\D/g,"");
    if (doc) localStorage.setItem("nfse_emitir_doc_novo", doc);
    if (onReemitir) onReemitir();
  };

  // Busca o resumo do que será reenviado. Se o servidor não conseguir remontar
  // a nota (serviço apagado, por exemplo), mostra o motivo e oferece a cópia.
  const abrirReemissao = async (n) => {
    setReemissao({ nota: n, carregando: true });
    try {
      const r = await fetch(`${API}/nfse/${n.id}/reemitir`, { headers:{ Authorization:"Bearer "+token } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setReemissao({ nota: n, erro: d.detail || `Erro ${r.status}` }); return; }
      setReemissao({ nota: n, previa: d });
    } catch (e) {
      setReemissao({ nota: n, erro: "Não foi possível falar com o servidor: " + e.message });
    }
  };

  const confirmarReemissao = async () => {
    const n = reemissao?.nota;
    if (!n || reemitindo) return;
    setReemitindo(true);
    try {
      const r = await fetch(`${API}/nfse/${n.id}/reemitir`, {
        method:"POST", headers:{ Authorization:"Bearer "+token } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setReemissao(rm => ({ ...rm, erro: d.detail || `Erro ${r.status}` })); return; }
      setReemissao(null);
      carregar();
      alert("Nota reemitida com sucesso"
            + (d?.nota?.numero_nfse ? ` — NFS-e nº ${d.nota.numero_nfse}.` : ".")
            + "\n\nA tentativa anterior continua no histórico, marcada como erro.");
    } catch (e) {
      setReemissao(rm => ({ ...rm, erro: "Não foi possível falar com o servidor: " + e.message }));
    } finally { setReemitindo(false); }
  };

  const mesesOpcoesHist = Array.from({ length: 24 }, (_, i) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    const mesesNomes = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
    const label = `${mesesNomes[d.getMonth()]}/${d.getFullYear()}`;
    return { val, label };
  });

  const carregar = async () => {
    const d = await fetch(API + "/nfse", { headers:{ Authorization:"Bearer "+token } }).then(r=>r.json()).catch(()=>[]);
    const lista = Array.isArray(d) ? d : [];
    setNotas(lista);
    return lista;
  };

  useEffect(() => { carregar(); }, [token]);

  // A fila de DF-e da SEFIN é sequencial e não tem filtro por data: na primeira
  // busca é preciso percorrê-la inteira para chegar nas notas recentes. O
  // servidor devolve `concluido:false` quando parou no meio de propósito, para
  // a requisição não estourar o tempo — aqui a gente chama de novo, de onde
  // parou, até terminar. O ponteiro fica gravado, então nada se perde.
  const importarDaSefin = async () => {
    if (!empresaFiltro || importando) return;
    // Congela a empresa desta busca: o seletor pode mudar no meio do caminho.
    const empresa = empresaFiltro;
    const minhaBusca = ++buscaAtual.current;
    const atual = () => buscaAtual.current === minhaBusca;
    setImportando(true);
    setResImport(null);
    const soma = { importadas:0, ja_tinha:0, fora_do_periodo:0, de_outro_emitente:0 };
    try {
      for (let volta = 0; volta < 40; volta++) {
        const r = await fetch(API + "/empresas/" + empresa + "/importar-nfse?meses=6",
          { method:"POST", headers:{ Authorization:"Bearer "+token } });
        if (!atual()) return;
        if (!r.ok) {
          const e = await r.json().catch(()=>({}));
          setResImport({ erro: e.detail || `O servidor respondeu ${r.status}.` });
          return;
        }
        const d = await r.json();
        if (!atual()) return;
        Object.keys(soma).forEach(k => { soma[k] += d[k] || 0; });
        setResImport({ ...soma, parcial: !d.concluido });
        if (d.concluido) break;
      }
      await carregar();
    } catch (e) {
      if (atual()) setResImport({ erro: "Não foi possível falar com o servidor: " + e.message });
    } finally { if (atual()) setImportando(false); }
  };

  // Trocar de empresa abandona a busca anterior e limpa o resumo — ele é
  // daquela empresa, não desta.
  useEffect(() => {
    buscaAtual.current++;
    setImportando(false);
    setResImport(null);
  }, [empresaFiltro]);

  // Sem este filtro, quem tem mais de uma empresa via as notas de todas
  // misturadas na mesma lista, sem sequer saber de qual era cada uma.
  useEffect(() => {
    fetch(API + "/empresas", { headers:{ Authorization:"Bearer "+token } })
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        const lista = Array.isArray(d) ? d : [];
        setEmpresas(lista);
        // A empresa guardada em localStorage pode ser de OUTRA conta — é o que
        // acontece no acesso delegado — ou ter sido excluída. Sem reconciliar,
        // o filtro apontava para um id que nenhuma nota tem e o histórico vinha
        // vazio; com uma empresa só o seletor nem aparece, então não havia como
        // corrigir pela tela. O Dashboard já fazia isso; aqui faltava.
        setEmpresaFiltro(f => lista.some(e => e.id === f)
          ? f
          : (lista.length === 1 ? lista[0].id : ""));
      })
      .catch(() => setEmpresas([]));
  }, [token]);

  const nomeEmpresa = (id) => empresas.find(e => e.id === id)?.razao_social || "";

  // O recorte por empresa vem antes de tudo: os contadores de status e o
  // download em lote têm de refletir a empresa escolhida, não o total.
  const notasDaEmpresa = notas.filter(n => !empresaFiltro || n.empresa_id === empresaFiltro);

  const filtradas = notasDaEmpresa.filter(n => {
    const d = new Date(n.created_at);
    const nm = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    const matchMes = nm === mesHist;
    const matchStatus = filtroStatus === "todas" || n.status === filtroStatus;
    const matchSearch = n.tomador_razao_social?.toLowerCase().includes(search.toLowerCase()) || n.numero_rps?.includes(search);
    return matchMes && matchStatus && matchSearch;
  });

  const handleExcluir = async (nota) => {
    if (!window.confirm(`Excluir a NFS-e #${nota.numero_rps} do histórico local?\n\nIsso não cancela a nota no governo.`)) return;
    try {
      const r = await fetch(`${API}/nfse/${nota.id}`, { method:"DELETE", headers:{ Authorization:"Bearer "+token } });
      if (!r.ok) throw new Error("Erro ao excluir");
      setNotas(ns => ns.filter(n => n.id !== nota.id));
    } catch(e) { alert("Erro: " + e.message); }
  };

  const handleCancelar = async (nota) => {
    if (!window.confirm(`Cancelar a NFS-e #${nota.numero_rps} de ${nota.tomador_razao_social}?\n\nEsta ação não pode ser desfeita.`)) return;
    setLoadingAcao(nota.id + "_cancelar");
    try {
      const r = await fetch(API + "/nfse/" + nota.id + "/cancelar", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ motivo: motivoCancelamento })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Erro ao cancelar");
      alert("NFS-e cancelada com sucesso!");
      // Recarrega a lista e atualiza o logNota se estiver aberto para esta nota
      const lista = await carregar();
      if (logNota && logNota.id === nota.id) {
        const atualizada = lista.find(n => n.id === nota.id);
        if (atualizada) setLogNota(atualizada);
      }
    } catch(e) {
      alert("Erro: " + e.message);
    }
    setLoadingAcao(null);
    setCancelando(null);
  };

  const handleDownloadDanfse = async (nota) => {
    if (!nota.chave_acesso) { alert("Nota sem chave de acesso"); return; }
    setLoadingAcao(nota.id + "_danfse");
    try {
      const r = await fetch(API + "/nfse/" + nota.id + "/danfse", {
        headers: { Authorization: "Bearer " + token }
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        let msg = err.detail || String(r.status);
        // Remove mensagens repetidas
        msg = msg.replace(/Erro ao baixar DANFSe:\s*/gi, "").trim();
        if (r.status === 502 || msg.includes("502") || msg.toLowerCase().includes("configuring http"))
          msg = "O servidor da Receita Federal está temporariamente instável. Aguarde alguns minutos e tente novamente.";
        alert("Erro ao baixar PDF:\n\n" + msg);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "DANFSe_" + nota.numero_rps + ".pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Erro: " + e.message);
    } finally {
      setLoadingAcao(null);
    }
  };

  // Baixa em ZIP os XMLs do mês exibido, respeitando o filtro de status da tela
  // para que o arquivo tenha exatamente as notas que estão à vista.
  const handleDownloadXMLsLote = async () => {
    setLoadingAcao("lote_xml");
    try {
      const params = new URLSearchParams({ mes: mesHist });
      if (filtroStatus !== "todas") params.set("status", filtroStatus);
      // O ZIP tem de trazer o mesmo recorte que está na tela
      if (empresaFiltro) params.set("empresa_id", empresaFiltro);
      const r = await fetch(`${API}/nfse/xmls-lote?${params}`, {
        headers: { Authorization: "Bearer " + token }
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert("Não foi possível baixar os XMLs:\n\n" + (err.detail || String(r.status)));
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `xmls_nfse_${mesHist}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      const semXml = parseInt(r.headers.get("X-Notas-Sem-Xml") || "0", 10);
      if (semXml > 0)
        alert(`${semXml} nota(s) do período ficaram de fora do ZIP: não há XML guardado `
            + `para elas — nem a NFS-e do governo, nem a DPS enviada.`);
    } catch (e) {
      alert("Erro: " + e.message);
    } finally {
      setLoadingAcao(null);
    }
  };

  const handleDownloadXML = async (nota) => {
    setLoadingAcao(nota.id + "_xml");
    try {
      const r = await fetch(API + "/nfse/" + nota.id + "/xml", {
        headers: { Authorization: "Bearer " + token }
      });
      if (!r.ok) {
        // Tenta XML direto da SEFIN
        const r2 = await fetch(API + "/nfse/" + nota.id + "/xml-sefin", {
          headers: { Authorization: "Bearer " + token }
        });
        if (!r2.ok) throw new Error("XML não disponível");
        const blob = await r2.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `nfse_${nota.numero_rps}.xml`; a.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `nfse_${nota.numero_rps}.xml`; a.click();
        URL.revokeObjectURL(url);
      }
    } catch(e) {
      alert("Erro: " + e.message);
    }
    setLoadingAcao(null);
  };

  const handleCopiarChave = (chave) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(chave).then(() => alert("Chave de acesso copiada!")).catch(() => alert("Erro ao copiar."));
    } else {
      const el = document.createElement("textarea");
      el.value = chave;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      alert("Chave de acesso copiada!");
    }
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <h2 style={{ fontSize:20, fontWeight:700, color:T.text, margin:0 }}>Histórico de NFS-e</h2>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <button onClick={handleDownloadXMLsLote}
          disabled={loadingAcao === "lote_xml" || filtradas.length === 0}
          title={filtradas.length === 0 ? "Nenhuma nota neste filtro" : `Baixar os XMLs de ${filtradas.length} nota(s) em um ZIP`}
          style={{ display:"flex", alignItems:"center", gap:6, background: filtradas.length === 0 ? T.btnSecBg : "#0ea5e915",
                   border:"1px solid " + (filtradas.length === 0 ? T.border : "#0ea5e940"), borderRadius:10, padding:"8px 12px",
                   fontSize:12, fontWeight:600, whiteSpace:"nowrap",
                   color: filtradas.length === 0 ? T.textFaint : "#38bdf8",
                   cursor: filtradas.length === 0 ? "not-allowed" : (loadingAcao === "lote_xml" ? "wait" : "pointer") }}>
          {loadingAcao === "lote_xml"
            ? "Gerando ZIP..."
            : <><FileDown size={13}/> {isMobile ? "XMLs" : "Baixar XMLs do mês"}</>}
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:4, background:T.card, border:"1px solid "+T.border, borderRadius:12, padding:"4px 6px" }}>
          <button onClick={()=>{ const idx=mesesOpcoesHist.findIndex(m=>m.val===mesHist); if(idx<mesesOpcoesHist.length-1) setMesHist(mesesOpcoesHist[idx+1].val); }}
            style={{ background:"none", border:"none", color:T.textMuted, cursor:"pointer", fontSize:18, padding:"2px 8px", borderRadius:8, lineHeight:1 }}>‹</button>
          <span style={{ fontSize:14, fontWeight:700, color:T.text, minWidth:80, textAlign:"center" }}>
            {mesesOpcoesHist.find(m=>m.val===mesHist)?.label || mesHist}
          </span>
          <button onClick={()=>{ const idx=mesesOpcoesHist.findIndex(m=>m.val===mesHist); if(idx>0) setMesHist(mesesOpcoesHist[idx-1].val); }}
            disabled={mesesOpcoesHist.findIndex(m=>m.val===mesHist)===0}
            style={{ background:"none", border:"none", color: mesesOpcoesHist.findIndex(m=>m.val===mesHist)===0 ? T.textFaint : T.textMuted, cursor: mesesOpcoesHist.findIndex(m=>m.val===mesHist)===0 ? "default":"pointer", fontSize:18, padding:"2px 8px", borderRadius:8, lineHeight:1 }}>›</button>
        </div>
        </div>
      </div>

      {/* Filtros de status */}
      <div style={{ display:"flex", gap:8, marginBottom:16, overflowX: isMobile ? "auto" : undefined, whiteSpace: isMobile ? "nowrap" : undefined }}>
        {[
          { key:"todas",      label:"Todas" },
          { key:"autorizada", label:"Autorizadas" },
          { key:"cancelada",  label:"Canceladas" },
          { key:"erro",       label:"Com Erro" },
          { key:"processando",label:"Processando" },
        ].map(f => (
          <button key={f.key} onClick={()=>setFiltroStatus(f.key)}
            style={{
              padding:"6px 16px", borderRadius:20, fontSize:12, fontWeight:600, cursor:"pointer", border:"none",
              background: filtroStatus===f.key
                ? f.key==="autorizada" ? "#0d2f1a"
                : f.key==="cancelada"  ? "#2f0d0d"
                : f.key==="erro"       ? "#2f0d0d"
                : f.key==="processando"? "#1a1a0d"
                : T.navActive
                : T.btnSecBg,
              color: filtroStatus===f.key
                ? f.key==="autorizada" ? "#4ade80"
                : f.key==="cancelada"  ? "#f87171"
                : f.key==="erro"       ? "#f87171"
                : f.key==="processando"? "#facc15"
                : T.navActiveText
                : T.btnSecText,
            }}
          >
            {f.label}
            <span style={{ marginLeft:6, fontSize:11, opacity:0.8 }}>
              ({notasDaEmpresa.filter(n => {
                const d = new Date(n.created_at);
                const nm = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
                return nm === mesHist && (f.key==="todas" || n.status===f.key);
              }).length})
            </span>
          </button>
        ))}
      </div>

      {/* Só aparece para quem tem mais de uma empresa — com uma só não há o que separar */}
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        {empresas.length > 1 && (
          <select
            style={{ flex: isMobile ? "1 1 100%" : "0 0 300px", background:T.input,
                     border:"1px solid " + (empresaFiltro ? "#38bdf880" : T.inputBorder),
                     borderRadius:8, padding:"10px 14px", fontSize:13, color:T.inputText,
                     outline:"none", boxSizing:"border-box" }}
            value={empresaFiltro}
            onChange={e => {
              setEmpresaFiltro(e.target.value);
              // "Todas" e um modo de visualizacao desta tela; nao apaga a empresa atual
              gravarEmpresaAtual(e.target.value);
            }}>
            <option value="">Todas as empresas ({empresas.length})</option>
            {empresas.map(e => <option key={e.id} value={e.id}>{e.razao_social}</option>)}
          </select>
        )}
        <input style={{ flex:"1 1 220px", background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8, padding:"10px 14px", fontSize:13, color:T.inputText, outline:"none", boxSizing:"border-box" }} placeholder="Buscar por tomador ou número..." value={search} onChange={e=>setSearch(e.target.value)} />
        {/* Só com uma empresa escolhida: a busca usa o certificado dela. */}
        <button onClick={importarDaSefin} disabled={!empresaFiltro || importando}
          title={empresaFiltro
            ? "Baixa da SEFIN as notas desta empresa emitidas fora do sistema, dos últimos 6 meses"
            : "Escolha uma empresa para buscar as notas dela na SEFIN"}
          style={{ display:"flex", alignItems:"center", gap:6, background:"transparent",
                   border:"1px solid "+T.inputBorder, borderRadius:8, padding:"10px 14px",
                   fontSize:13, color: empresaFiltro ? T.text : T.textFaint,
                   cursor: (!empresaFiltro || importando) ? "default" : "pointer",
                   opacity: (!empresaFiltro || importando) ? 0.6 : 1, whiteSpace:"nowrap" }}>
          {importando
            ? <><Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} /> Buscando...</>
            : <><Download size={14} /> Buscar na SEFIN</>}
        </button>
      </div>
      {resImport && (
        <div style={{ marginBottom:16, padding:"12px 14px", borderRadius:8, fontSize:13,
                      background: resImport.erro ? T.dangerBg : T.card,
                      border:"1px solid "+(resImport.erro ? T.danger+"55" : T.border),
                      color: resImport.erro ? T.danger : T.textMuted,
                      display:"flex", alignItems:"flex-start", gap:8 }}>
          {resImport.erro
            ? <><AlertTriangle size={15} style={{ flexShrink:0, marginTop:1 }} /><span>{resImport.erro}</span></>
            : <>
                {resImport.parcial
                  ? <Loader2 size={15} style={{ flexShrink:0, marginTop:1, animation:"spin 1s linear infinite" }} />
                  : <CheckCircle size={15} style={{ flexShrink:0, marginTop:1, color:"#4ade80" }} />}
                <span>
                  {resImport.parcial ? (
                    // Na primeira busca a fila é percorrida inteira, e as notas
                    // recentes só aparecem no fim. Mostrar só "0 importadas"
                    // durante esse tempo parece travado — daí o total visto.
                    <>Percorrendo o histórico da SEFIN… <b style={{ color:T.text }}>
                      {resImport.importadas + resImport.ja_tinha
                       + resImport.fora_do_periodo + resImport.de_outro_emitente}
                    </b> documento(s) conferido(s), <b style={{ color:T.text }}>
                      {resImport.importadas}</b> nota(s) importada(s) até agora.</>
                  ) : (
                    <>
                      <b style={{ color:T.text }}>{resImport.importadas}</b> nota(s) importada(s).
                      {" "}{resImport.ja_tinha} já estavam aqui
                      {resImport.fora_do_periodo > 0 && `, ${resImport.fora_do_periodo} fora dos últimos 6 meses`}
                      {resImport.de_outro_emitente > 0 && `, ${resImport.de_outro_emitente} recebidas de terceiros`}.
                      {resImport.importadas === 0 && " Nada novo — o histórico já está completo."}
                    </>
                  )}
                </span>
              </>}
        </div>
      )}
      <div style={{ background:T.tableBg, border:"1px solid "+T.border, borderRadius:12, overflow:"hidden", boxShadow:T.shadow }}>
        {isMobile ? (
          <div>
            {filtradas.length === 0 && <div style={{ padding:"40px 20px", textAlign:"center", color:T.textFaint }}>Nenhuma nota encontrada.</div>}
            {filtradas.map(n => (
              <div key={n.id} style={{ padding:"14px 16px", borderBottom:"1px solid "+T.rowBorder }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                  <div style={{ flex:1, marginRight:12 }}>
                    <div style={{ fontSize:14, fontWeight:600, color:T.text }}>{n.tomador_razao_social || "Não informado"}</div>
                    <div style={{ fontSize:11, color:T.textFaint, marginTop:2 }}>{n.numero_nfse ? `NFS-e #${n.numero_nfse}` : `RPS #${n.numero_rps}`} · {fmtData(n.data_emissao || n.created_at)}{n.origem === "importada" && <SeloImportada T={T} />}</div>
                    {/* Sem empresa escolhida, é preciso dizer de quem é cada nota */}
                    {!empresaFiltro && empresas.length > 1 && nomeEmpresa(n.empresa_id) && (
                      <div style={{ fontSize:10.5, color:"#38bdf8", marginTop:2, display:"flex", alignItems:"center", gap:3 }}>
                        <Building2 size={10} />{nomeEmpresa(n.empresa_id)}
                      </div>
                    )}
                    {(n.servico_nome || n.servico_descricao) && <div style={{ fontSize:11, color:T.textMuted, marginTop:2 }}>{(n.servico_nome || n.servico_descricao?.slice(0,45))}</div>}
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontSize:15, fontWeight:700, color:"#4ade80" }}>{fmtMoeda(n.valor_servico)}</div>
                    <div style={{ marginTop:4 }}><StatusBadge status={n.status} dark={T.isDark} /></div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  <button onClick={()=>abrirDetalhes(n)} style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:4, background:"#10b98115", border:"1px solid #10b98140", borderRadius:6, padding:"8px 10px", fontSize:12, color:"#34d399", cursor:"pointer", fontWeight:600 }}><FileText size={13}/> Ver</button>
                  {n.chave_acesso && (() => {
                    const carregando = loadingAcao === n.id+"_danfse";
                    return <button onClick={()=>!carregando&&handleDownloadDanfse(n)} disabled={carregando} style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:4, background:"#7c3aed15", border:"1px solid #7c3aed40", borderRadius:6, padding:"8px 10px", fontSize:12, color:carregando?T.textFaint:"#a78bfa", cursor:carregando?"wait":"pointer", fontWeight:600 }}>{carregando?"...":<><FileDown size={13}/> PDF</>}</button>;
                  })()}
                  <div onMouseDown={e=>e.stopPropagation()}>
                    <button onClick={e=>{const r=e.currentTarget.getBoundingClientRect();setMenuPos({top:r.bottom+4,right:window.innerWidth-r.right});setMenuAberto(menuAberto===n.id?null:n.id);}} style={{ display:"flex", alignItems:"center", justifyContent:"center", background:T.btnSecBg, border:"1px solid "+T.border, borderRadius:6, padding:"8px 12px", color:T.textMuted, cursor:"pointer", fontSize:14, lineHeight:1 }}>···</button>
                    {menuAberto===n.id && (
                      <div style={{ position:"fixed", top:menuPos.top, right:menuPos.right, background:T.card, border:"1px solid "+T.border, borderRadius:8, boxShadow:"0 8px 24px #00000060", zIndex:9999, minWidth:170, overflow:"hidden" }}>
                        {onReemitir && <button onClick={()=>{setMenuAberto(null);copiarNota(n);}} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"11px 14px", background:"none", border:"none", fontSize:13, color:"#38bdf8", cursor:"pointer", textAlign:"left" }}><Copy size={13}/> Copiar nota</button>}
                        <button onClick={()=>{setMenuAberto(null);handleDownloadXML(n);}} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"11px 14px", background:"none", border:"none", fontSize:13, color:T.textMuted, cursor:"pointer", textAlign:"left" }}><Download size={13}/> Baixar XML</button>
                        {n.resposta_governo && <button onClick={()=>{setMenuAberto(null);setLogNota(n);}} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"11px 14px", background:"none", border:"none", fontSize:13, color:n.status==="erro"?"#f87171":T.textMuted, cursor:"pointer", textAlign:"left" }}>{n.status==="erro"?<AlertCircle size={13}/>:<ClipboardList size={13}/>} Ver log</button>}
                        {n.status==="erro" && <button onClick={()=>{setMenuAberto(null);abrirReemissao(n);}} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"11px 14px", background:"none", border:"none", fontSize:13, color:"#fb923c", cursor:"pointer", textAlign:"left" }}><RefreshCw size={13}/> Reemitir</button>}
                        {n.status==="autorizada" && n.chave_acesso && <button onClick={()=>{setMenuAberto(null);setCancelando(n);}} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"11px 14px", background:"none", border:"none", fontSize:13, color:"#f87171", cursor:"pointer", textAlign:"left", borderTop:"1px solid "+T.border }}><Ban size={13}/> Cancelar nota</button>}
                        <button onClick={()=>{setMenuAberto(null);handleExcluir(n);}} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"11px 14px", background:"none", border:"none", fontSize:13, color:"#f87171", cursor:"pointer", textAlign:"left", borderTop:"1px solid "+T.border }}><X size={13}/> Excluir</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr>{["Nº","Tomador","Valor","Status","Data","Ações"].map(h=><th key={h} style={{ padding:"12px 20px", fontSize:11, fontWeight:600, color:T.textFaint, textAlign:"left", borderBottom:"1px solid "+T.border, textTransform:"uppercase" }}>{h}</th>)}</tr></thead>
            <tbody>
              {filtradas.map(n=>(
                <tr key={n.id} style={{ borderBottom:"1px solid "+T.rowBorder }}>
                  <td style={{ padding:"14px 20px", fontSize:12, color:T.textFaint, fontFamily:"monospace" }}>
                    {n.numero_nfse ? <><span style={{color:T.text,fontWeight:600}}>#{n.numero_nfse}</span>{n.origem === "importada" && <SeloImportada T={T} />}<br/><span style={{fontSize:10,opacity:0.5}}>RPS {n.numero_rps}</span></> : <>{`#${n.numero_rps}`}{n.origem === "importada" && <SeloImportada T={T} />}</>}
                  </td>
                  <td style={{ padding:"14px 20px" }}>
                    <div style={{ fontSize:13, color:T.text, fontWeight:500 }}>{n.tomador_razao_social}</div>
                    <div style={{ fontSize:11, color:T.textFaint, marginTop:2 }}>{n.servico_nome || n.servico_descricao?.slice(0,40)}</div>
                    {/* Sem empresa escolhida, é preciso dizer de quem é cada nota */}
                    {!empresaFiltro && empresas.length > 1 && nomeEmpresa(n.empresa_id) && (
                      <div style={{ fontSize:10.5, color:"#38bdf8", marginTop:3, display:"flex", alignItems:"center", gap:3 }}>
                        <Building2 size={10} />{nomeEmpresa(n.empresa_id)}
                      </div>
                    )}
                    {n.chave_acesso && (
                      <div style={{ fontSize:10, color:T.textFaint, marginTop:3, fontFamily:"monospace", cursor:"pointer" }}
                        onClick={() => handleCopiarChave(n.chave_acesso)}
                        title="Clique para copiar a chave de acesso">
                        {n.chave_acesso?.slice(0,20)}... <span style={{color:"#0ea5e9"}}>copiar</span>
                      </div>
                    )}
                  </td>
                  <td style={{ padding:"14px 20px" }}>
                    <div style={{ fontSize:13, color:"#4ade80", fontWeight:600 }}>{fmtMoeda(n.valor_servico)}</div>
                    <div style={{ fontSize:11, color:T.textFaint }}>ISS {fmtMoeda(n.valor_iss)}</div>
                  </td>
                  <td style={{ padding:"14px 20px" }}><StatusBadge status={n.status} dark={T.isDark} /></td>
                  <td style={{ padding:"14px 20px", fontSize:12, color:T.textFaint }}>{fmtData(n.data_emissao || n.created_at)}</td>
                  <td style={{ padding:"14px 20px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      {/* Ver */}
                      <button onClick={() => abrirDetalhes(n)} title="Ver detalhes" style={{ display:"flex", alignItems:"center", gap:4, background:"#10b98115", border:"1px solid #10b98140", borderRadius:6, padding:"5px 10px", fontSize:11, color:"#34d399", cursor:"pointer", fontWeight:600, whiteSpace:"nowrap" }}>
                        <FileText size={12}/> Ver
                      </button>
                      {/* PDF */}
                      {n.chave_acesso && (() => {
                        const carregando = loadingAcao === n.id+"_danfse";
                        return (
                          <button onClick={() => !carregando && handleDownloadDanfse(n)} disabled={carregando}
                            title="Baixar DANFSe (PDF)"
                            style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:4, background:carregando?T.btnSecBg:"#7c3aed15", border:"1px solid #7c3aed40", borderRadius:6, padding:"5px 10px", fontSize:11, color:carregando?T.textFaint:"#a78bfa", cursor:carregando?"wait":"pointer", fontWeight:600, whiteSpace:"nowrap", minWidth:52 }}>
                            {carregando ? "..." : <><FileDown size={12}/> PDF</>}
                          </button>
                        );
                      })()}
                      {/* Menu ··· */}
                      <div onMouseDown={e => e.stopPropagation()}>
                        <button onClick={e => { const r=e.currentTarget.getBoundingClientRect(); setMenuPos({top:r.bottom+4, right:window.innerWidth-r.right}); setMenuAberto(menuAberto === n.id ? null : n.id); }}
                          title="Mais ações"
                          style={{ display:"flex", alignItems:"center", justifyContent:"center", background:T.btnSecBg, border:"1px solid "+T.border, borderRadius:6, padding:"5px 8px", color:T.textMuted, cursor:"pointer", fontSize:13, lineHeight:1 }}>
                          ···
                        </button>
                        {menuAberto === n.id && (
                          <div style={{ position:"fixed", top:menuPos.top, right:menuPos.right, background:T.card, border:"1px solid "+T.border, borderRadius:8, boxShadow:"0 8px 24px #00000040", zIndex:9999, minWidth:160, overflow:"hidden" }}>
                            {onReemitir && (
                              <button onClick={() => { setMenuAberto(null); copiarNota(n); }} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 14px", background:"none", border:"none", fontSize:12, color:"#38bdf8", cursor:"pointer", textAlign:"left" }}>
                                <Copy size={12}/> Copiar nota
                              </button>
                            )}
                            <button onClick={() => { setMenuAberto(null); handleDownloadXML(n); }} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 14px", background:"none", border:"none", fontSize:12, color:T.textMuted, cursor:"pointer", textAlign:"left" }}>
                              <Download size={12}/> Baixar XML
                            </button>
                            {n.resposta_governo && (
                              <button onClick={() => { setMenuAberto(null); setLogNota(n); }} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 14px", background:"none", border:"none", fontSize:12, color: n.status==="erro" ? "#f87171" : T.textMuted, cursor:"pointer", textAlign:"left" }}>
                                {n.status==="erro" ? <AlertCircle size={12}/> : <ClipboardList size={12}/>} Ver log
                              </button>
                            )}
                            {n.status === "erro" && (
                              <button onClick={() => { setMenuAberto(null); abrirReemissao(n); }} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 14px", background:"none", border:"none", fontSize:12, color:"#fb923c", cursor:"pointer", textAlign:"left" }}>
                                <RefreshCw size={12}/> Reemitir
                              </button>
                            )}
                            {n.status === "autorizada" && n.chave_acesso && (
                              <button onClick={() => { setMenuAberto(null); setCancelando(n); }} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 14px", background:"none", border:"none", fontSize:12, color:"#f87171", cursor:"pointer", textAlign:"left", borderTop:"1px solid "+T.border }}>
                                <Ban size={12}/> Cancelar nota
                              </button>
                            )}
                            <button onClick={() => { setMenuAberto(null); handleExcluir(n); }} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 14px", background:"none", border:"none", fontSize:12, color:"#f87171", cursor:"pointer", textAlign:"left", borderTop:"1px solid "+T.border }}>
                              <X size={12}/> Excluir do histórico
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
              {filtradas.length===0 && <tr><td colSpan={6} style={{ padding:"40px", textAlign:"center", color:T.textFaint }}>Nenhuma nota encontrada</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de cancelamento */}
      {cancelando && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom: isMobile ? 64 : 0, background:"rgba(0,0,0,0.75)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200 }}
          onClick={() => setCancelando(null)}>
          <div style={{ background:T.card, border:"1px solid #ef444440", borderRadius:16, width:"100%", maxWidth:460, padding:28, boxShadow:T.shadowHover }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ color:"#f87171", marginTop:0, marginBottom:8 }}>Cancelar NFS-e</h3>
            <p style={{ fontSize:13, color:T.textMuted, marginBottom:20 }}>
              Nota <strong style={{color:T.text}}>#{cancelando.numero_rps}</strong> — {cancelando.tomador_razao_social}<br/>
              Valor: <strong style={{color:"#4ade80"}}>{fmtMoeda(cancelando.valor_servico)}</strong>
            </p>
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:600, color:T.textFaint, display:"block", marginBottom:6 }}>MOTIVO DO CANCELAMENTO</label>
              <select value={motivoCancelamento} onChange={e=>setMotivoCancelamento(e.target.value)}
                style={{ width:"100%", background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8, padding:"10px 14px", fontSize:13, color:T.inputText, outline:"none" }}>
                <option value="Erro na emissão">Erro na emissão</option>
                <option value="Serviço não prestado">Serviço não prestado</option>
                <option value="Duplicidade de nota">Duplicidade de nota</option>
                <option value="Dados incorretos">Dados incorretos do tomador</option>
                <option value="Solicitação do tomador">Solicitação do tomador</option>
              </select>
            </div>
            <div style={{ background:T.dangerBg, border:"1px solid "+T.danger+"40", borderRadius:8, padding:12, fontSize:12, color:T.danger, marginBottom:20 }}>
             Esta ação não pode ser desfeita. A NFS-e será cancelada no Emissor Nacional da Receita Federal.
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setCancelando(null)}
                style={{ background:"transparent", border:"1px solid "+T.border, borderRadius:8, padding:"9px 18px", fontSize:13, color:T.textFaint, cursor:"pointer" }}>
                Voltar
              </button>
              <button onClick={() => handleCancelar(cancelando)}
                disabled={loadingAcao === cancelando?.id+"_cancelar"}
                style={{ background:"linear-gradient(135deg,#dc2626,#b91c1c)", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                {loadingAcao === cancelando?.id+"_cancelar" ? "Cancelando..." : "Confirmar Cancelamento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Log / Resposta do Governo */}
      {verNota && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom: isMobile ? 64 : 0, background:"#00000080", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={()=>setVerNota(null)}>
          <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:16, padding:28, maxWidth:620, width:"90%", maxHeight:"85vh", overflowY:"auto", boxShadow:"0 20px 60px #00000060" }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <div style={{ fontWeight:700, fontSize:16, color:T.text }}>Detalhes da NFS-e #{verNota.numero_rps}</div>
              <button onClick={()=>setVerNota(null)} style={{ background:"none", border:"none", cursor:"pointer", color:T.textMuted }}><X size={18}/></button>
            </div>
            {[
              { titulo:"EMISSÃO", campos:[
                ["Data de Emissão", new Date(verNota.created_at).toLocaleDateString("pt-BR")],
                ["Competência", verNota.data_emissao ? (() => { const [y,m]=(verNota.data_emissao||"").slice(0,7).split("-"); const ms=["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"]; return ms[parseInt(m)-1]+"/"+y; })() : "—"],
                ["Status", verNota.status],
                ["Empresa Emissora", verNota.empresa_razao_social || "—"],
                ["Chave de Acesso", verNota.chave_acesso || "—"],
              ]},
              { titulo:"TOMADOR", campos:[
                ["Nome / Razão Social", verNota.tomador_razao_social || "—"],
                ["CPF", verNota.tomador_cpf ? (() => { const n=verNota.tomador_cpf.replace(/\D/g,""); return n.length===11 ? n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/,"$1.$2.$3-$4") : verNota.tomador_cpf; })() : "—"],
                ["CNPJ", verNota.tomador_cnpj ? fmtCNPJAlfa(limparCNPJ(verNota.tomador_cnpj)) : "—"],
              ]},
              { titulo:"SERVIÇO", campos:[
                ["Nome", verNota.servico_nome || "—"],
                ["Código LC 116", verNota.servico_codigo ? `${verNota.servico_codigo}${descLC116(verNota.servico_codigo) ? " — "+descLC116(verNota.servico_codigo) : ""}` : "—"],
                ["Descrição", verNota.servico_descricao || "—"],
              ]},
              { titulo:"VALORES", campos:[
                ["Valor do Serviço", verNota.valor_servico != null ? fmtMoeda(verNota.valor_servico) : "—"],
                ["Valor Líquido", verNota.valor_liquido != null ? fmtMoeda(verNota.valor_liquido) : "—"],
                ["Alíquota ISS", verNota.aliquota_iss != null ? `${verNota.aliquota_iss}%` : "—"],
                ["Valor ISS", verNota.valor_iss != null ? fmtMoeda(verNota.valor_iss) : "—"],
                ["ISS Retido", verNota.iss_retido != null ? (verNota.iss_retido ? "Sim" : "Não") : "—"],
                ["Situação Tributária", verNota.situacao_tributaria != null ? ({1:"Normal",2:"Imune",3:"Isenta",4:"Exportação",5:"Suspensa",6:"Exig. Suspensa (Ação Judicial)",7:"Exig. Suspensa (Adm)"}[verNota.situacao_tributaria] || `${verNota.situacao_tributaria}`) : "—"],
              ]},
              { titulo:"TRIBUTAÇÃO FEDERAL", campos:[
                ["CST PIS/COFINS", verNota.cst_pis_cofins ? `${verNota.cst_pis_cofins} — ${{"00":"Tributável (cumulativo/não-cumulativo)","01":"Tributável – Alíq. Básica","02":"Tributável – Alíq. Diferenc.","03":"Tributável – Alíq. por Unid.","04":"Tributável – Monofásica","05":"Tributável – ST","06":"Tributável – Alíq. Zero","07":"Isenta da Contribuição","08":"Sem Incidência","09":"Suspensão"}[verNota.cst_pis_cofins] || verNota.cst_pis_cofins}` : "—"],
                ...(verNota.cst_pis_cofins === "01" || verNota.cst_pis_cofins === "02" ? [
                  ["Alíquota PIS", verNota.p_aliq_pis != null ? `${verNota.p_aliq_pis}%` : "—"],
                  ["Alíquota COFINS", verNota.p_aliq_cofins != null ? `${verNota.p_aliq_cofins}%` : "—"],
                  ["Tipo Retenção PIS/COFINS/CSLL", verNota.tp_ret_pis_cofins_csll != null ? `${verNota.tp_ret_pis_cofins_csll} — ${{"1":"PIS e COFINS Retidos","2":"COFINS Retido","3":"PIS Retido","4":"Sem Retenção","5":"PIS/COFINS/CSLL Retidos","6":"PIS Não Retido/COFINS e CSLL Ret.","7":"PIS/COFINS Não Ret./CSLL Ret.","8":"COFINS Não Ret./PIS e CSLL Ret."}[String(verNota.tp_ret_pis_cofins_csll)] || String(verNota.tp_ret_pis_cofins_csll)}` : "—"],
                ] : []),
                ["Modo Tributos Aprox.", verNota.modo_tot_trib != null ? ({1:"Monetário (por nota)",2:"Percentual",3:"Simples Nacional"}[verNota.modo_tot_trib] || `${verNota.modo_tot_trib}`) : "—"],
                ...(verNota.modo_tot_trib === 2 ? [
                  ["% Trib. Federal", verNota.perc_trib_fed != null ? `${verNota.perc_trib_fed}%` : "—"],
                  ["% Trib. Estadual", verNota.perc_trib_est != null ? `${verNota.perc_trib_est}%` : "—"],
                  ["% Trib. Municipal", verNota.perc_trib_mun != null ? `${verNota.perc_trib_mun}%` : "—"],
                ] : verNota.modo_tot_trib === 3 ? [
                  ["Alíquota Simples Nac.", verNota.aliquota_simples != null ? `${verNota.aliquota_simples}%` : "—"],
                ] : []),
              ]},
              // Local da prestação: define para qual município o ISS é devido
              ...(resumoFiscal?.municipio_prestacao_codigo ? [{ titulo:"LOCAL DA PRESTAÇÃO", campos:[
                ["Município de Incidência", resumoFiscal.municipio_prestacao_nome
                  ? `${resumoFiscal.municipio_prestacao_nome} (${resumoFiscal.municipio_prestacao_codigo})`
                  : resumoFiscal.municipio_prestacao_codigo],
                ...(resumoFiscal.codigo_tributacao_nacional ? [["Cód. Tributação Nacional", resumoFiscal.codigo_tributacao_nacional]] : []),
                ...(resumoFiscal.codigo_nbs ? [["Código NBS", resumoFiscal.codigo_nbs]] : []),
              ]}] : []),

              ...(resumoFiscal?.ibscbs ? [{ titulo:"TRIBUTAÇÃO IBS / CBS", campos:[
                ["Indicador de Operação (cIndOp)", resumoFiscal.ibscbs.c_ind_op || "—"],
                ["CST IBS/CBS", resumoFiscal.ibscbs.cst || "—"],
                ["Classificação Tributária (cClassTrib)", resumoFiscal.ibscbs.c_class_trib || "—"],
                ...(resumoFiscal.ibscbs.ind_dest ? [["Destinatário (indDest)", resumoFiscal.ibscbs.ind_dest === "0" ? "0 — o próprio tomador" : `${resumoFiscal.ibscbs.ind_dest} — diferente do tomador`]] : []),
                ...(resumoFiscal.ibscbs.c_cib ? [["CIB do Imóvel", resumoFiscal.ibscbs.c_cib]] : []),
                ...(resumoFiscal.ibscbs.tp_oper ? [["Tipo de Operação", resumoFiscal.ibscbs.tp_oper]] : []),
                ...(resumoFiscal.ibscbs.ind_final ? [["Uso/Consumo Pessoal", resumoFiscal.ibscbs.ind_final === "1" ? "Sim" : "Não"]] : []),
              ]}] : []),

              ...(resumoFiscal?.obra ? [{ titulo:"INFORMAÇÕES DA OBRA", campos:[
                ...(resumoFiscal.obra.c_obra ? [["Código da Obra (CNO/CEI)", resumoFiscal.obra.c_obra]] : []),
                ...(resumoFiscal.obra.c_cib ? [["Código CIB", resumoFiscal.obra.c_cib]] : []),
                ...(resumoFiscal.obra.cep ? [["CEP da Obra", resumoFiscal.obra.cep]] : []),
                ...(resumoFiscal.obra.logradouro ? [["Endereço", `${resumoFiscal.obra.logradouro}, ${resumoFiscal.obra.numero || "S/N"}${resumoFiscal.obra.bairro ? " — "+resumoFiscal.obra.bairro : ""}`]] : []),
                ...(resumoFiscal.obra.insc_imob ? [["Inscrição Imobiliária", resumoFiscal.obra.insc_imob]] : []),
              ]}] : []),

              ...((verNota.v_ret_irrf > 0 || verNota.v_ret_csll > 0 || verNota.v_ret_cp > 0) ? [{ titulo:"RETENÇÕES FEDERAIS", campos:[
                ...(verNota.v_ret_irrf > 0 ? [["IRRF Retido", fmtMoeda(verNota.v_ret_irrf)]] : []),
                ...(verNota.v_ret_csll > 0 ? [["Contrib. Sociais Retidas", fmtMoeda(verNota.v_ret_csll)]] : []),
                ...(verNota.v_ret_cp > 0 ? [["Contrib. Prev. Ret.", fmtMoeda(verNota.v_ret_cp)]] : []),
              ]}] : []),
            ].map(sec => (
              <div key={sec.titulo} style={{ marginBottom:18 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#38bdf8", letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>{sec.titulo}</div>
                <div style={{ background:T.bg, borderRadius:8, padding:"12px 16px", display:"flex", flexDirection:"column", gap:8 }}>
                  {sec.campos.map(([k,v]) => (
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", gap:16, fontSize:12 }}>
                      <span style={{ color:T.textMuted, flexShrink:0 }}>{k}</span>
                      <span style={{ color:T.text, textAlign:"right", wordBreak:"break-all" }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirmação da reemissão: mostra o que será reenviado sem abrir o
          formulário. Quem quiser mudar algo tem o atalho para a cópia. */}
      {reemissao && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom: isMobile ? 64 : 0, background:"rgba(0,0,0,0.75)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:16 }}
          onClick={() => { if (!reemitindo) setReemissao(null); }}>
          <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:16, width:"100%", maxWidth:480, boxShadow:T.shadowHover }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:"20px 24px 0" }}>
              <h3 style={{ margin:"0 0 4px", color:T.text, fontSize:16, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}>
                <RefreshCw size={16} color="#fb923c" /> Reemitir nota
              </h3>
              <p style={{ margin:"0 0 16px", fontSize:12.5, color:T.textMuted, lineHeight:1.5 }}>
                A nota será enviada de novo à SEFIN exatamente com estes dados, com uma numeração nova.
              </p>
            </div>

            {reemissao.carregando && (
              <div style={{ padding:"0 24px 20px", fontSize:13, color:T.textMuted }}>Carregando os dados da nota...</div>
            )}

            {reemissao.previa && (
              <div style={{ padding:"0 24px", display:"flex", flexDirection:"column", gap:9 }}>
                {[["Empresa", reemissao.previa.empresa],
                  ["Serviço", reemissao.previa.servico],
                  ["Tomador", reemissao.previa.tomador],
                  ["Valor", fmtMoeda(reemissao.previa.valor_servico)],
                  ["Competência", reemissao.previa.data_emissao ? fmtData(reemissao.previa.data_emissao) : "—"],
                ].map(([rotulo, valor]) => (
                  <div key={rotulo} style={{ display:"flex", gap:12, fontSize:13 }}>
                    <span style={{ color:T.textFaint, minWidth:96, flexShrink:0 }}>{rotulo}</span>
                    <span style={{ color:T.text, fontWeight:600, wordBreak:"break-word" }}>{valor || "—"}</span>
                  </div>
                ))}
                {reemissao.previa.erro_anterior && (
                  <div style={{ marginTop:4, padding:"10px 12px", background:T.dangerBg, border:"1px solid "+T.border, borderRadius:8 }}>
                    <div style={{ fontSize:10.5, color:T.textFaint, textTransform:"uppercase", fontWeight:600, marginBottom:3 }}>Recusa anterior</div>
                    <div style={{ fontSize:12, color:T.danger, lineHeight:1.45, wordBreak:"break-word" }}>{reemissao.previa.erro_anterior}</div>
                  </div>
                )}
              </div>
            )}

            {reemissao.erro && (
              <div style={{ margin:"0 24px", padding:"11px 13px", background:T.dangerBg, borderRadius:8, fontSize:12.5, color:T.danger, lineHeight:1.5 }}>
                {reemissao.erro}
              </div>
            )}

            <div style={{ padding:"18px 24px 20px", display:"flex", gap:9, justifyContent:"flex-end", flexWrap:"wrap" }}>
              <button onClick={() => setReemissao(null)} disabled={reemitindo}
                style={{ background:"transparent", border:"1px solid "+T.border, borderRadius:8, padding:"9px 16px", fontSize:12.5, color:T.textFaint, cursor: reemitindo ? "default" : "pointer" }}>
                Cancelar
              </button>
              {onReemitir && (
                <button onClick={() => { const n = reemissao.nota; setReemissao(null); copiarNota(n); }} disabled={reemitindo}
                  style={{ background:"transparent", border:"1px solid "+T.border, borderRadius:8, padding:"9px 16px", fontSize:12.5, color:"#38bdf8", cursor: reemitindo ? "default" : "pointer" }}>
                  Editar antes de emitir
                </button>
              )}
              {reemissao.previa && (
                <button onClick={confirmarReemissao} disabled={reemitindo}
                  style={{ background: reemitindo ? "#7c4a1d" : "linear-gradient(135deg,#fb923c,#ea580c)", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:12.5, fontWeight:600, cursor: reemitindo ? "default" : "pointer" }}>
                  {reemitindo ? "Enviando..." : "Reemitir agora"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {logNota && (() => {
        let parsed = null;
        let rawText = logNota.resposta_governo || "";
        try { parsed = JSON.parse(rawText); } catch { parsed = null; }

        const renderValor = (v, depth=0) => {
          if (v === null || v === undefined) return <span style={{color:T.textFaint}}>null</span>;
          if (typeof v === "boolean") return <span style={{color:"#f59e0b"}}>{v ? "true" : "false"}</span>;
          if (typeof v === "number") return <span style={{color:"#34d399"}}>{v}</span>;
          if (typeof v === "string") return <span style={{color:T.isDark?"#f1f5f9":"#0f172a", wordBreak:"break-word"}}>{v}</span>;
          if (Array.isArray(v)) return (
            <div style={{marginLeft: depth*12}}>
              {v.map((item, i) => <div key={i} style={{marginBottom:2}}>
                <span style={{color:T.textFaint}}>•</span> {renderValor(item, depth+1)}
              </div>)}
            </div>
          );
          if (typeof v === "object") return (
            <div style={{marginLeft: depth*12}}>
              {Object.entries(v).map(([k, val]) => (
                <div key={k} style={{marginBottom:4}}>
                  <span style={{color:"#0ea5e9", fontWeight:600}}>{k}:</span>{" "}
                  {renderValor(val, depth+1)}
                </div>
              ))}
            </div>
          );
          return <span>{String(v)}</span>;
        };

        return (
          <div style={{ position:"fixed", top:0, left:0, right:0, bottom: isMobile ? 64 : 0, background:"rgba(0,0,0,0.75)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200 }}
            onClick={() => setLogNota(null)}>
            <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:16, width:"100%", maxWidth:640, maxHeight:"80vh", display:"flex", flexDirection:"column", boxShadow:T.shadowHover }}
              onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ padding:"18px 24px", borderBottom:"1px solid "+T.border, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <h3 style={{ margin:0, color:T.text, fontSize:15, fontWeight:700 }}>
                    <span style={{display:"flex",alignItems:"center",gap:6}}>{logNota.status === "erro" ? <AlertCircle size={15} color="#f87171"/> : <ClipboardList size={15} color="#38bdf8"/>}{logNota.status === "erro" ? "Log de Erro" : "Resposta do Governo"} — NFS-e #{logNota.numero_rps}</span>
                  </h3>
                  <p style={{ margin:"4px 0 0", fontSize:12, color:T.textFaint }}>{logNota.tomador_razao_social}</p>
                </div>
                <button onClick={() => setLogNota(null)}
                  style={{ background:"transparent", border:"none", color:T.textFaint, cursor:"pointer", display:"flex", alignItems:"center" }}><X size={18}/></button>
              </div>
              {/* Status badge */}
              <div style={{ padding:"12px 24px 0", display:"flex", alignItems:"center", gap:10 }}>
                <StatusBadge status={logNota.status} dark={T.isDark} />
                <span style={{ fontSize:11, color:T.textFaint }}>{fmtData(logNota.created_at)}</span>
              </div>
              {/* Content */}
              <div style={{ flex:1, overflowY:"auto", padding:"16px 24px 24px" }}>
                {parsed ? (
                  <div style={{ background:T.logBg, border:"1px solid "+T.logBorder, borderRadius:8, padding:"14px 16px", fontSize:12, lineHeight:1.7, fontFamily:"'IBM Plex Mono', monospace" }}>
                    {renderValor(parsed)}
                  </div>
                ) : (
                  <pre style={{ background:T.logBg, border:"1px solid "+T.logBorder, borderRadius:8, padding:"14px 16px", fontSize:11, color:T.logText, margin:0, whiteSpace:"pre-wrap", wordBreak:"break-word", fontFamily:"'IBM Plex Mono', monospace", lineHeight:1.6 }}>
                    {rawText || "Sem informações de resposta."}
                  </pre>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}


function CertificadoModal({ token, empresa, onClose, T = {card:"#0c1117",border:"#1a2332",text:"#f1f5f9",textFaint:"#475569",textMuted:"#94a3b8",input:"#080c10",inputBorder:"#1a2332",inputText:"#e2e8f0"} }) {
  const isMobile = useIsMobile();
  const [arquivo, setArquivo] = useState(null);
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState(false);

  const enviar = async () => {
    if (!arquivo) { setErro("Selecione um arquivo .pfx ou .p12"); return; }
    setLoading(true); setErro("");
    try {
      const formData = new FormData();
      formData.append("arquivo", arquivo);
      formData.append("senha", senha);
      const r = await fetch(API + "/empresas/" + empresa.id + "/certificado", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: formData
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setSucesso(true);
      setTimeout(onClose, 2000);
    } catch(e) { setErro(e.message); }
    setLoading(false);
  };

  const S = {
    input: { width:"100%", background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8, padding:"10px 14px", fontSize:13, color:T.inputText, outline:"none", boxSizing:"border-box" },
    label: { fontSize:12, fontWeight:600, color:T.textFaint, display:"block", marginBottom:6 },
  };

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom: isMobile ? 64 : 0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100 }} onClick={onClose}>
      <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:16, width:"100%", maxWidth:480, padding:28, boxShadow:T.shadowHover }} onClick={e=>e.stopPropagation()}>
        <h3 style={{ color:T.text, marginTop:0, marginBottom:6 }}>Upload de Certificado A1</h3>
        <p style={{ fontSize:13, color:T.textFaint, marginBottom:24 }}>Empresa: <strong style={{color:T.textMuted}}>{empresa.razao_social}</strong></p>

        {sucesso ? (
          <div style={{ background:T.successBg, border:"1px solid "+T.successBorder, borderRadius:8, padding:20, textAlign:"center" }}>
            <div style={{ fontSize:32, marginBottom:8 }}> </div>
            <div style={{ color:T.successText, fontWeight:600 }}>Certificado enviado com sucesso!</div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            {erro && <div style={{ background:"#2f0d0d", border:"1px solid #ef444440", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#f87171" }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {erro}</div>}

            <div>
              <label style={S.label}>ARQUIVO DO CERTIFICADO (.pfx ou .p12)</label>
              <input type="file" accept=".pfx,.p12" onChange={e=>setArquivo(e.target.files[0])}
                style={{ ...S.input, padding:"8px 14px" }} />
              {arquivo && <div style={{ fontSize:11, color:"#4ade80", marginTop:6 }}><Check size={11} style={{verticalAlign:"middle",marginRight:3}} /> {arquivo.name} ({(arquivo.size/1024).toFixed(1)} KB)</div>}
            </div>

            <div>
              <label style={S.label}>SENHA DO CERTIFICADO</label>
              <CampoSenha estilo={S.input} valor={senha} placeholder="Digite a senha do certificado"
                autoComplete="off" onChange={e=>setSenha(e.target.value)} />
            </div>

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={onClose} style={{ background:"transparent", border:"1px solid "+T.border, borderRadius:8, padding:"9px 18px", fontSize:13, color:T.textFaint, cursor:"pointer" }}>Cancelar</button>
              <button onClick={enviar} disabled={loading} style={{ background:"linear-gradient(135deg,#0ea5e9,#0284c7)", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                {loading ? "Enviando..." : "Enviar Certificado"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EmpresasView({ token, podeExcluir = false, T = {bg:"#080c10",card:"#0c1117",border:"#1a2332",text:"#f1f5f9",textMuted:"#94a3b8",input:"#080c10",inputBorder:"#1a2332",inputText:"#e2e8f0",btnSecBg:"#1a2332",btnSecText:"#94a3b8"} }) {
  const isMobile = useIsMobile();
  const [empresas, setEmpresas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [certEmpresa, setCertEmpresa] = useState(null);
  const [form, setForm] = useState({ razao_social:"", cnpj:"", inscricao_municipal:"", email:"", telefone:"", endereco:"", municipio:"", uf:"", cep:"" });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false);
  // erro = impede salvar (CNPJ inválido); aviso = só informa (consulta fora do ar)
  const [erroCNPJ, setErroCNPJ] = useState("");
  const [avisoCNPJ, setAvisoCNPJ] = useState("");
  const [editandoId, setEditandoId] = useState(null);
  const vazio = { razao_social:"", cnpj:"", inscricao_municipal:"", email:"", telefone:"", endereco:"", numero:"", bairro:"", municipio:"", uf:"", cep:"", codigo_municipio:"" };
  const carregar = () => fetch(API+"/empresas",{headers:{Authorization:"Bearer "+token}}).then(r=>r.json()).then(d=>setEmpresas(Array.isArray(d)?d:[]));
  useEffect(()=>{ carregar(); },[token]);

  const abrirNovo = () => { setEditandoId(null); setForm(vazio); setErroCNPJ(""); setAvisoCNPJ(""); setShowModal(true); };
  const abrirEditar = (e) => { setEditandoId(e.id); setErroCNPJ(""); setAvisoCNPJ(""); setForm({ razao_social:e.razao_social||"", cnpj:e.cnpj||"", inscricao_municipal:e.inscricao_municipal||"", email:e.email||"", telefone:e.telefone||"", endereco:e.endereco||"", numero:e.numero||"", bairro:e.bairro||"", municipio:e.municipio||"", uf:e.uf||"", cep:e.cep||"", codigo_municipio:e.codigo_municipio||"" }); setShowModal(true); };

  const buscarIM = async (cnpjDigits, codMun) => {
    if (!cnpjDigits || !codMun) return;
    try {
      const r = await fetch(`${API}/consultar-im?cnpj=${cnpjDigits}&codigo_municipio=${codMun}`, { headers:{ Authorization:"Bearer "+token } });
      if (r.ok) { const d = await r.json(); set("inscricao_municipal", d.inscricao_municipal); }
    } catch { /* ignora */ }
  };

  const handleCNPJChange = async (v) => {
    const cnpj = limparCNPJ(v);
    const fmt = fmtCNPJAlfa(cnpj);
    set("cnpj", fmt);
    setErroCNPJ("");
    setAvisoCNPJ("");
    if (cnpj.length === 14) {
      // Antes de consultar: dígito errado não existe na Receita e a consulta
      // voltaria 404, dando a entender que o serviço está fora do ar.
      if (!validarCNPJ(cnpj)) {
        setErroCNPJ("CNPJ inválido — os dígitos verificadores não conferem. Revise o número no cartão CNPJ.");
        return;
      }
      setBuscandoCNPJ(true);
      try {
        // Tenta BrasilAPI e, se estiver fora, os provedores de reserva.
        const d = await consultarCNPJ(cnpj);
        const codMun = d.codigo_municipio_ibge;
        setForm(f => ({
          ...f, cnpj: fmt,
          razao_social: d.razao_social || f.razao_social,
          email: d.email || f.email,
          telefone: d.telefone || f.telefone,
          endereco: d.logradouro || f.endereco,
          numero: d.numero || f.numero,
          bairro: d.bairro || f.bairro,
          municipio: d.municipio || f.municipio,
          uf: d.uf || f.uf,
          cep: d.cep.replace(/(\d{5})(\d{3})/,"$1-$2") || f.cep,
          codigo_municipio: codMun || f.codigo_municipio,
        }));
        if (codMun) buscarIM(cnpj, codMun);
        // Sem o código do município a nota é rejeitada com E0084 lá na frente.
        // Nem todo provedor devolve o código IBGE, mas todos trazem o CEP — e o
        // CEP resolve o município por outro serviço. Só pede manual se os dois
        // falharem, em vez de empurrar o trabalho para o usuário na primeira.
        else {
          const resolvido = d.cep.length === 8 && await resolverMunicipioPorCep(d.cep);
          if (resolvido) buscarIM(cnpj, resolvido);
          else setAvisoCNPJ("Não veio o código do município na consulta do CNPJ. "
                          + "Selecione o município da empresa manualmente antes de salvar.");
        }
      } catch (ex) {
        setAvisoCNPJ(ex?.message === "nao_encontrado"
          ? "CNPJ não encontrado na base da Receita. Confira o número."
          : "A consulta automática de CNPJ está indisponível no momento (todas as fontes). "
            + "Preencha os dados manualmente — em especial o município — ou tente de novo em alguns minutos.");
      }
      finally { setBuscandoCNPJ(false); }
    }
  };

  // A consulta do CNPJ nem sempre devolve o código IBGE do município — e sem
  // ele a nota é rejeitada com E0084. O ViaCEP devolve esse código a partir do
  // CEP, e é outro serviço: funciona mesmo com a consulta de CNPJ fora do ar.
  // Devolve o código IBGE quando consegue resolver, senão "".
  const resolverMunicipioPorCep = async (cepNums) => {
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cepNums}/json/`);
      const d = await r.json();
      if (d.erro) { setAvisoCNPJ("CEP não encontrado. Selecione o município manualmente."); return ""; }
      setForm(f => ({
        ...f,
        // O que veio do CNPJ tem prioridade: o CEP só preenche o que está vazio.
        endereco: f.endereco || d.logradouro || "",
        bairro:   f.bairro   || d.bairro    || "",
        uf:        d.uf || f.uf,
        municipio: d.localidade || f.municipio,
        codigo_municipio: d.ibge || f.codigo_municipio,
      }));
      if (d.ibge) { setAvisoCNPJ(""); return String(d.ibge); }
      return "";
    } catch {
      setAvisoCNPJ("Não foi possível consultar o CEP. Selecione o município manualmente.");
      return "";
    }
  };

  const handleCepChange = async (v) => {
    const nums = (v || "").replace(/\D/g, "").slice(0, 8);
    set("cep", nums.length > 5 ? nums.replace(/(\d{5})(\d{1,3})/, "$1-$2") : nums);
    if (nums.length === 8) await resolverMunicipioPorCep(nums);
  };

  const handleExcluirEmpresa = async (e) => {
    if (!window.confirm(`Excluir a empresa ${e.razao_social}?\n\nTodas as notas e serviços dela serão apagados. Esta ação não pode ser desfeita.`)) return;
    const r = await fetch(API+"/empresas/"+e.id, { method:"DELETE", headers:{ Authorization:"Bearer "+token } });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      alert("Não foi possível excluir a empresa:\n\n" + (err.detail || String(r.status)));
      return;
    }
    carregar();
  };

  const salvar = async () => {
    if (!form.razao_social || !form.cnpj || !form.inscricao_municipal) {
      alert("Preencha os campos obrigatórios: CNPJ, Razão Social e Inscrição Municipal."); return;
    }
    // Um CNPJ com dígito errado só era descoberto na primeira emissão, com a
    // nota já montada e uma mensagem que não dizia o que havia de errado.
    if (!validarCNPJ(form.cnpj)) {
      setErroCNPJ("CNPJ inválido — os dígitos verificadores não conferem. Revise o número no cartão CNPJ.");
      alert("O CNPJ informado é inválido: os dígitos verificadores não conferem.\n\n"
          + "Confira o número no cartão CNPJ antes de salvar — uma empresa com CNPJ errado "
          + "não consegue emitir nenhuma nota."); return;
    }
    // O código do município vira o cLocEmi da nota e a SEFIN confere contra o
    // CNPJ. Salvar sem ele só adia o problema para a hora da emissão (E0084).
    if (!String(form.codigo_municipio || "").trim()) {
      alert("Selecione o município da empresa.\n\nEle define o município emissor da nota "
          + "e precisa ser o mesmo do cadastro do CNPJ na Receita."); return;
    }
    if (editandoId) {
      const r = await fetch(API+"/empresas/"+editandoId, { method:"PATCH", headers:{"Content-Type":"application/json","Authorization":"Bearer "+token}, body:JSON.stringify(form) });
      if (!r.ok) { alert("Erro ao salvar empresa: " + (await r.text())); return; }
    } else {
      const r = await fetch(API+"/empresas", { method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer "+token}, body:JSON.stringify(form) });
      if (!r.ok) { alert("Erro ao criar empresa: " + (await r.text())); return; }
    }
    setShowModal(false); carregar();
  };

  const S = { input: { width:"100%", background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8, padding:"10px 14px", fontSize:13, color:T.inputText, outline:"none", boxSizing:"border-box" }, label: { fontSize:12, fontWeight:600, color:T.textMuted, display:"block", marginBottom:6 } };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <h2 style={{ fontSize:20, fontWeight:700, color:T.text, margin:0 }}>Empresas</h2>
        <button onClick={abrirNovo} style={{ background:"linear-gradient(135deg,#0ea5e9,#0284c7)", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer" }}>+ Adicionar</button>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:16 }}>
        {empresas.map(e=>(
          <div key={e.id} style={{ background:T.card, border:"1px solid "+T.border, borderRadius:12, padding:22, position:"relative", boxShadow:T.shadow, transition:"box-shadow .2s" }}>
            <div style={{ position:"absolute", top:10, right:10, display:"flex", gap:4 }}>
              <button onClick={()=>abrirEditar(e)} title="Editar empresa" style={{ background:"transparent", border:"none", color:"#475569", fontSize:15, cursor:"pointer", padding:"2px 5px", borderRadius:4 }} onMouseOver={ev=>ev.target.style.color="#0ea5e9"} onMouseOut={ev=>ev.target.style.color="#475569"}><Settings size={14} /></button>
              {/* Só admin exclui: um clique errado apaga a empresa com todas as notas. */}
              {podeExcluir && <button onClick={()=>handleExcluirEmpresa(e)} title="Excluir empresa (somente admin)" style={{ background:"transparent", border:"none", color:"#475569", fontSize:15, cursor:"pointer", padding:"2px 5px", borderRadius:4 }} onMouseOver={ev=>ev.target.style.color="#f87171"} onMouseOut={ev=>ev.target.style.color="#475569"}><X size={13} /></button>}
            </div>
            <div style={{ fontSize:20, marginBottom:12 }}> </div>
            <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:4 }}>{e.razao_social}</div>
            <div style={{ fontSize:12, color:T.textFaint, fontFamily:"monospace" }}>{fmtCnpj(e.cnpj)}</div>
            <div style={{ fontSize:12, color:T.textFaint }}>{e.municipio} — {e.uf}</div>
            {e.inscricao_municipal && <div style={{ fontSize:11, color:T.textFaint, marginTop:2 }}>IM: <span style={{ color:T.text, fontWeight:600 }}>{e.inscricao_municipal}</span></div>}
            <div style={{ marginTop:12, display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
              {(() => {
                const hoje = new Date();
                const validade = e.certificado_validade ? new Date(e.certificado_validade) : null;
                const dias = validade ? Math.floor((validade - hoje) / (1000*60*60*24)) : null;
                const vencido = dias !== null && dias < 0;
                const proxVencer = dias !== null && dias >= 0 && dias <= 30;
                const fmtData = validade ? validade.toLocaleDateString("pt-BR") : null;
                let bg, cor, CertIcon, label;
                if (!e.certificado_valido || !validade) {
                  bg = T.certNoBg; cor = T.certNoText; CertIcon = AlertTriangle; label = "Sem certificado";
                } else if (vencido) {
                  bg = "#3b0a0a"; cor = "#f87171"; CertIcon = X; label = `Vencido em ${fmtData}`;
                } else if (proxVencer) {
                  bg = "#3b2000"; cor = "#fb923c"; CertIcon = AlertTriangle; label = `Vence em ${fmtData} (${dias}d)`;
                } else {
                  bg = T.certOkBg; cor = T.certOkText; CertIcon = Check; label = `Válido até ${fmtData}`;
                }
                return (
                  <span style={{ fontSize:11, padding:"3px 10px", borderRadius:20, background:bg, color:cor, fontWeight:600, display:"inline-flex", alignItems:"center", gap:4 }}>
                    <CertIcon size={11} /> {label}
                  </span>
                );
              })()}
              <button onClick={()=>setCertEmpresa(e)} style={{ fontSize:11, padding:"3px 10px", borderRadius:6, background:T.btnSecBg, border:"none", color:T.btnSecText, cursor:"pointer" }}>
                <Lock size={13} style={{verticalAlign:"middle",marginRight:4}} /> {e.certificado_valido ? "Atualizar cert." : "Upload cert."}
              </button>
            </div>
          </div>
        ))}
        {empresas.length===0 && <div style={{ color:T.textFaint, padding:40, textAlign:"center" }}>Nenhuma empresa cadastrada</div>}
      </div>
      {certEmpresa && <CertificadoModal token={token} empresa={certEmpresa} T={T} onClose={()=>{ setCertEmpresa(null); carregar(); }} />}
      {showModal && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom: isMobile ? 64 : 0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100 }} onClick={()=>setShowModal(false)}>
          <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:16, width:"100%", maxWidth:600, padding:28, boxShadow:T.shadowHover }} onClick={e=>e.stopPropagation()}>
            <h3 style={{ color:T.text, marginTop:0 }}>{editandoId ? "Editar Empresa" : "Nova Empresa"}</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div>
                <label style={S.label}>CNPJ <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                <div style={{ position:"relative" }}>
                  <input style={{...S.input, ...(erroCNPJ ? {borderColor:"#ef4444"} : {})}} value={form.cnpj} onChange={e=>handleCNPJChange(e.target.value)} placeholder="00.000.000/0000-00" maxLength={18} />
                  {buscandoCNPJ && <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", fontSize:11, color:"#0ea5e9" }}>Buscando...</span>}
                </div>
                {erroCNPJ && <div style={{ fontSize:11, color:"#f87171", marginTop:5, display:"flex", alignItems:"flex-start", gap:4 }}>
                  <AlertTriangle size={11} style={{ marginTop:1, flexShrink:0 }} />{erroCNPJ}
                </div>}
                {!erroCNPJ && avisoCNPJ && <div style={{ fontSize:11, color:"#fbbf24", marginTop:5, display:"flex", alignItems:"flex-start", gap:4 }}>
                  <AlertCircle size={11} style={{ marginTop:1, flexShrink:0 }} />{avisoCNPJ}
                </div>}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                <div><label style={S.label}>RAZÃO SOCIAL <span style={{color:"#f97316",marginLeft:3}}>*</span></label><input style={S.input} value={form.razao_social} onChange={e=>set("razao_social",e.target.value)} /></div>
                <div>
                  <label style={S.label}>INSCRIÇÃO MUNICIPAL <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                  <input style={{...S.input, borderColor: !form.inscricao_municipal ? "#f97316" : S.input.borderColor}} value={form.inscricao_municipal} onChange={e=>set("inscricao_municipal",e.target.value)} />
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                <div><label style={S.label}>E-MAIL</label><input style={S.input} value={form.email} onChange={e=>set("email",e.target.value)} /></div>
                <div><label style={S.label}>TELEFONE</label><input style={S.input} value={form.telefone||""} onChange={e=>set("telefone",e.target.value)} /></div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:14 }}>
                <div><label style={S.label}>ENDEREÇO</label><input style={S.input} value={form.endereco||""} onChange={e=>set("endereco",e.target.value)} /></div>
                <div><label style={S.label}>NÚMERO</label><input style={S.input} value={form.numero||""} onChange={e=>set("numero",e.target.value)} /></div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                <div><label style={S.label}>BAIRRO</label><input style={S.input} value={form.bairro||""} onChange={e=>set("bairro",e.target.value)} /></div>
                <div><label style={S.label}>CEP</label><input style={S.input} value={form.cep} onChange={e=>handleCepChange(e.target.value)} placeholder="00000-000" maxLength={9} /></div>
              </div>
              {/* Município emissor da nota (cLocEmi). Precisa bater com o cadastro
                  do CNPJ na Receita, senão a SEFIN rejeita com E0084. */}
              <div>
                <MunicipioInput
                  label="MUNICÍPIO DA EMPRESA *"
                  S={S} T={T}
                  value={form.codigo_municipio
                    ? { codigo:String(form.codigo_municipio), nome:form.municipio||"", uf:form.uf||"" }
                    : null}
                  onChange={v => setForm(f => ({
                    ...f,
                    codigo_municipio: v ? v.codigo : "",
                    municipio: v ? v.nome : "",
                    uf: v ? (v.uf || f.uf) : f.uf,
                  }))}
                />
                <div style={{ fontSize:11, color:T.textFaint, marginTop:4, lineHeight:1.5 }}>
                  É o município emissor da nota. Tem que ser o mesmo do cadastro do CNPJ
                  na Receita — se divergir, a nota é rejeitada com o erro E0084.
                </div>
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                <button onClick={()=>setShowModal(false)} style={{ background:"transparent", border:"1px solid "+T.border, borderRadius:8, padding:"9px 18px", fontSize:13, color:T.textFaint, cursor:"pointer" }}>Cancelar</button>
                <button onClick={salvar} style={{ background:"linear-gradient(135deg,#0ea5e9,#0284c7)", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer" }}>Salvar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PessoasView({ token, onVoltarEmitir, T = {bg:"#080c10",card:"#0c1117",border:"#1a2332",text:"#f1f5f9",textMuted:"#94a3b8",input:"#080c10",inputBorder:"#1a2332",inputText:"#e2e8f0",rowBorder:"#0f1923",tableBg:"#0c1117"} }) {
  const isMobile = useIsMobile();
  const [pessoas, setPessoas] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [munSel, setMunSel] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [empresaFiltro, setEmpresaFiltro] = useState("");
  const vazio = { empresa_id:"", cpf:"", nome:"", email:"", fone:"", endereco:"", numero:"", bairro:"", municipio:"", municipio_codigo:"", uf:"", cep:"", observacao:"" };
  const [form, setForm] = useState(vazio);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const S = {
    input: { width:"100%", background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8, padding:"10px 14px", fontSize:13, color:T.inputText, outline:"none", boxSizing:"border-box" },
    label: { fontSize:12, fontWeight:600, color:T.textMuted, display:"block", marginBottom:6 }
  };

  const soNum = v => v.replace(/\D/g, "");
  const fmtCPF = n => { const s = soNum(n).slice(0,11); return s.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2"); };

  // null = não digitado ainda; true = válido; false = inválido
  const [cpfStatus, setCpfStatus] = useState(null);
  const [cpfDuplicado, setCpfDuplicado] = useState("");
  const [errosCamposPessoa, setErrosCamposPessoa] = useState({});

  const handleCpfChange = async (e) => {
    const nums = soNum(e.target.value).slice(0, 11);
    const fmt = fmtCPF(nums);
    set("cpf", fmt);
    setCpfDuplicado("");
    if (nums.length === 11) {
      setCpfStatus(validarCPF(nums));
      if (!editando) {
        try {
          const eid = form.empresa_id || empresaFiltro;
          const url = `${API}/pessoas?q=${nums}${eid ? "&empresa_id="+eid : ""}`;
          const r = await fetch(url, { headers:{ Authorization:"Bearer "+token } });
          const lista = await r.json();
          if (Array.isArray(lista)) {
            const dup = lista.find(p => p.cpf.replace(/\D/g,"") === nums);
            if (dup) setCpfDuplicado(`CPF já cadastrado para: ${dup.nome}`);
          }
        } catch { /* ignora */ }
      }
    } else {
      setCpfStatus(null);
    }
  };

  const handleCepChange = async (e) => {
    const raw = e.target.value;
    const nums = soNum(raw).slice(0,8);
    const fmt = nums.length > 5 ? nums.replace(/(\d{5})(\d{1,3})/, "$1-$2") : nums;
    set("cep", fmt);
    if (nums.length === 8) {
      try {
        const r = await fetch(`https://viacep.com.br/ws/${nums}/json/`);
        const d = await r.json();
        if (!d.erro) {
          if (d.logradouro) set("endereco", d.logradouro);
          if (d.bairro) set("bairro", d.bairro);
          if (d.uf) set("uf", d.uf);
          if (d.localidade) set("municipio", d.localidade);
          if (d.ibge) {
            set("municipio_codigo", d.ibge);
            setMunSel({ codigo: d.ibge, nome: d.localidade, uf: d.uf });
          }
        }
      } catch { /* ignora erros de rede */ }
    }
  };

  const carregar = async (q, empId) => {
    const eid = empId !== undefined ? empId : empresaFiltro;
    let url = `${API}/pessoas`;
    const params = [];
    if (q) params.push(`q=${encodeURIComponent(q)}`);
    if (eid) params.push(`empresa_id=${eid}`);
    if (params.length) url += "?" + params.join("&");
    const r = await fetch(url, { headers:{ Authorization:"Bearer "+token } });
    const d = await r.json();
    setPessoas(Array.isArray(d) ? d : []);
  };

  useEffect(() => {
    fetch(`${API}/empresas`, { headers:{ Authorization:"Bearer "+token } })
      .then(r=>r.json()).then(d => {
        if (Array.isArray(d) && d.length > 0) {
          setEmpresas(d);
          setEmpresaFiltro(empresaInicial(d, lerEmpresaAtual()));
        }
      });
  }, [token]);

  useEffect(() => {
    const cpf = localStorage.getItem("nfse_precadastro_cpf");
    if (cpf) { setTimeout(() => abrirNovo(cpf), 300); }
  }, []);

  useEffect(() => { if (empresaFiltro) carregar("", empresaFiltro); }, [empresaFiltro]);

  useEffect(() => {
    const t = setTimeout(() => carregar(search, empresaFiltro), 300);
    return () => clearTimeout(t);
  }, [search, empresaFiltro]);

  const abrirNovo = (cpfInicial) => {
    setEditando(null);
    const cpf = cpfInicial || localStorage.getItem("nfse_precadastro_cpf") || "";
    if (cpf) localStorage.removeItem("nfse_precadastro_cpf");
    const nums = cpf.replace(/\D/g,"").slice(0,11);
    const fmt = nums.replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{3})(\d{1,2})$/,"$1-$2");
    const empresaParaCadastro = localStorage.getItem("nfse_emitir_empresa") || empresaFiltro;
    setForm({...vazio, empresa_id: empresaParaCadastro, cpf: fmt});
    setCpfStatus(nums.length === 11 ? validarCPF(nums) : null);
    setCpfDuplicado("");
    setMunSel(null);
    setShowModal(true);
  };

  const abrirEditar = (p) => {
    setEditando(p.id);
    const nums = p.cpf.replace(/\D/g,"");
    setForm({ empresa_id: p.empresa_id||empresaFiltro, cpf: fmtCPF(p.cpf), nome: p.nome, email: p.email||"", fone: p.fone||"", endereco: p.endereco||"", numero: p.numero||"", bairro: p.bairro||"", municipio: p.municipio||"", municipio_codigo: p.municipio_codigo||"", uf: p.uf||"", cep: p.cep ? p.cep.replace(/(\d{5})(\d{3})/, "$1-$2") : "", observacao: p.observacao||"" });
    setCpfStatus(nums.length === 11 ? validarCPF(nums) : null);
    setMunSel(p.municipio_codigo ? { codigo: p.municipio_codigo, nome: p.municipio, uf: p.uf } : null);
    setShowModal(true);
  };

  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState("");

  const salvar = async () => {
    const novosErros = {};
    const cepNums = soNum(form.cep);
    if (!cepNums) novosErros.cep = "CEP é obrigatório.";
    else if (cepNums.length !== 8) novosErros.cep = "CEP deve ter 8 dígitos.";
    if (Object.keys(novosErros).length > 0) { setErrosCamposPessoa(novosErros); return; }
    setErrosCamposPessoa({});
    setErroSalvar("");
    setSalvando(true);
    try {
      const payload = { ...form, cpf: soNum(form.cpf), cep: cepNums, municipio: munSel ? munSel.nome : form.municipio, municipio_codigo: munSel ? munSel.codigo : form.municipio_codigo, uf: munSel ? munSel.uf : form.uf };
      const url = editando ? `${API}/pessoas/${editando}` : `${API}/pessoas`;
      const method = editando ? "PUT" : "POST";
      const resp = await fetch(url, { method, headers:{"Content-Type":"application/json","Authorization":"Bearer "+token}, body:JSON.stringify(payload) });
      if (!resp.ok) {
        const err = await resp.json().catch(()=>({}));
        if (resp.status === 409) {
          setErroSalvar(`${err.detail || "CPF já cadastrado."}`);
        } else {
          setErroSalvar(err.detail || "Erro ao salvar. Tente novamente.");
        }
        return;
      }
      setShowModal(false); carregar(search);
      if (!editando && onVoltarEmitir) {
        localStorage.setItem("nfse_emitir_doc_novo", soNum(form.cpf));
        onVoltarEmitir();
      }
    } catch(e) {
      setErroSalvar("Erro de conexão. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (id) => {
    if(!window.confirm("Excluir esta pessoa?")) return;
    await fetch(`${API}/pessoas/${id}`, { method:"DELETE", headers:{ Authorization:"Bearer "+token } });
    carregar(search);
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <h2 style={{ fontSize:20, fontWeight:700, color:T.text, margin:0 }}>Cadastro de Pessoas (CPF)</h2>
        <button onClick={() => abrirNovo()} style={{ background:"linear-gradient(135deg,#0ea5e9,#0284c7)", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer" }}>+ Nova Pessoa</button>
      </div>

      {empresas.length > 1 && (
        <div style={{ marginBottom:14 }}>
          <label style={{ ...S.label, marginBottom:6 }}>EMPRESA</label>
          <select style={S.input} value={empresaFiltro} onChange={e => { setEmpresaFiltro(e.target.value); gravarEmpresaAtual(e.target.value); }}>
            {empresas.map(e => <option key={e.id} value={e.id}>{e.razao_social}</option>)}
          </select>
        </div>
      )}

      <input style={{ ...S.input, marginBottom:16 }} placeholder="Buscar por nome ou CPF..." value={search} onChange={e=>setSearch(e.target.value)} />

      <div style={{ background:T.tableBg, border:"1px solid "+T.border, borderRadius:12, overflow:"hidden", boxShadow:T.shadow }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr>{["Nome","CPF","Município","Telefone","E-mail",""].map(h=>(
              <th key={h} style={{ padding:"12px 20px", fontSize:11, fontWeight:600, color:T.textFaint, textAlign:"left", borderBottom:"1px solid "+T.border, textTransform:"uppercase" }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {pessoas.map(p => (
              <tr key={p.id} style={{ borderBottom:"1px solid "+T.rowBorder }}>
                <td style={{ padding:"14px 20px", fontSize:13, color:T.text, fontWeight:600 }}>{p.nome}</td>
                <td style={{ padding:"14px 20px", fontSize:13, color:T.textMuted, fontFamily:"monospace" }}>{fmtCPF(p.cpf)}</td>
                <td style={{ padding:"14px 20px", fontSize:13, color:T.textFaint }}>{p.municipio}{p.uf ? " / "+p.uf : ""}</td>
                <td style={{ padding:"14px 20px", fontSize:13, color:T.textFaint }}>{p.fone||"–"}</td>
                <td style={{ padding:"14px 20px", fontSize:13, color:T.textFaint }}>{p.email||"–"}</td>
                <td style={{ padding:"14px 20px" }}>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={()=>abrirEditar(p)} style={{ background:T.btnSecBg, border:"none", borderRadius:6, padding:"5px 10px", fontSize:12, color:T.btnSecText, cursor:"pointer" }}>Editar</button>
                    <button onClick={()=>excluir(p.id)} style={{ background:T.dangerBg, border:"none", borderRadius:6, padding:"5px 10px", fontSize:12, color:T.danger, cursor:"pointer" }}>Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
            {pessoas.length===0 && (
              <tr><td colSpan={6} style={{ padding:"50px", textAlign:"center", color:T.textFaint }}>
                <div style={{ marginBottom:10, color:T.textFaint }}><Users size={36} /></div>
                <div style={{ fontWeight:600, marginBottom:6, color:T.textMuted }}>Nenhuma pessoa cadastrada</div>
                <div style={{ fontSize:12 }}>Clique em "+ Nova Pessoa" para cadastrar</div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom: isMobile ? 64 : 0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100, padding:20 }} onMouseDown={()=>setShowModal(false)}>
          <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:16, width:"100%", maxWidth:640, padding:28, maxHeight:"90vh", overflowY:"auto", boxShadow:T.shadowHover }} onMouseDown={e=>e.stopPropagation()}>
            <h3 style={{ color:T.text, marginTop:0, marginBottom:24 }}>{editando ? "Editar Pessoa" : "Nova Pessoa"}</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                <div>
                  <label style={S.label}>
                    CPF <span style={{color:"#f97316",marginLeft:3}}>*</span>
                    {cpfStatus === true && <span style={{color:"#4ade80",fontWeight:400,marginLeft:8,fontSize:11}}><Check size={11} style={{verticalAlign:"middle",marginRight:3}} /> válido</span>}
                    {cpfStatus === false && <span style={{color:"#f87171",fontWeight:400,marginLeft:8,fontSize:11}}><X size={11} style={{verticalAlign:"middle",marginRight:3}} /> inválido</span>}
                  </label>
                  <input
                    style={{ ...S.input, borderColor: cpfStatus === true ? "#22c55e" : cpfStatus === false ? "#ef4444" : T.inputBorder }}
                    placeholder="000.000.000-00"
                    value={form.cpf}
                    onChange={handleCpfChange}
                  />
                  {cpfStatus === false && (
                    <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}>
                      CPF inválido — verifique os dígitos digitados.
                    </div>
                  )}
                  {cpfDuplicado && soNum(form.cpf).length === 11 && (
                    <div style={{ fontSize:11, color:"#f97316", background:"#f9731610", border:"1px solid #f9731640", borderRadius:6, padding:"6px 10px", marginTop:6 }}>
                      {cpfDuplicado}
                    </div>
                  )}
                </div>
                <div>
                  <label style={S.label}>NOME COMPLETO <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                  <input style={S.input} placeholder="Nome da pessoa" value={form.nome} onChange={e=>set("nome",e.target.value)} />
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                <div><label style={S.label}>E-MAIL</label><input style={S.input} type="email" value={form.email} onChange={e=>set("email",e.target.value)} /></div>
                <div><label style={S.label}>TELEFONE</label><input style={S.input} placeholder="(00) 00000-0000" value={form.fone} onChange={e=>set("fone",e.target.value)} /></div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16 }}>
                <div><label style={S.label}>ENDEREÇO</label><input style={S.input} value={form.endereco} onChange={e=>set("endereco",e.target.value)} /></div>
                <div><label style={S.label}>NÚMERO</label><input style={S.input} value={form.numero} onChange={e=>set("numero",e.target.value)} /></div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                <div><label style={S.label}>BAIRRO</label><input style={S.input} value={form.bairro} onChange={e=>set("bairro",e.target.value)} /></div>
                <div>
                  <label style={S.label}>CEP <span style={{color:"#f97316"}}>*</span> <span style={{fontWeight:400,color:"#475569",fontSize:11}}>(ou selecione o município abaixo)</span></label>
                  <input style={{ ...S.input, ...(errosCamposPessoa.cep ? {borderColor:"#ef4444"} : {}) }} placeholder="00000-000" value={form.cep} onChange={e => { setErrosCamposPessoa(er=>({...er,cep:""})); handleCepChange(e); }} />
                  {errosCamposPessoa.cep && <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {errosCamposPessoa.cep}</div>}
                </div>
              </div>

              <MunicipioInput value={munSel} onChange={async v => {
                setMunSel(v);
                if (v) {
                  set("municipio", v.nome);
                  set("municipio_codigo", v.codigo);
                  set("uf", v.uf || "");
                  // Busca CEP da cidade via ViaCEP (só se o campo estiver vazio)
                  if (!soNum(form.cep)) {
                    const termos = ["Rua", "Avenida", v.nome.split(" ")[0]];
                    for (const termo of termos) {
                      try {
                        const r = await fetch(`https://viacep.com.br/ws/${v.uf}/${encodeURIComponent(v.nome)}/${encodeURIComponent(termo)}/json/`);
                        const d = await r.json();
                        if (Array.isArray(d) && d.length > 0 && d[0].cep) {
                          // Usa os 5 primeiros dígitos + "000" para obter o CEP geral da cidade
                          const prefixo = d[0].cep.replace(/\D/g, "").slice(0, 5);
                          set("cep", `${prefixo}-000`);
                          setErrosCamposPessoa(er => ({...er, cep: ""}));
                          break;
                        }
                      } catch { /* ignora */ }
                    }
                  }
                }
              }} label="MUNICÍPIO *" S={S} T={T} />

              <div>
                <label style={S.label}>OBSERVAÇÃO</label>
                <textarea style={{...S.input, resize:"vertical", minHeight:60}} value={form.observacao} onChange={e=>set("observacao",e.target.value)} />
              </div>

              {erroSalvar && <div style={{ fontSize:12, color:"#f87171", padding:"8px 12px", background:"#f871711a", borderRadius:6 }}><AlertTriangle size={11} style={{verticalAlign:"middle",marginRight:3}} /> {erroSalvar}</div>}
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                <button onClick={()=>setShowModal(false)} style={{ background:"transparent", border:"1px solid "+T.border, borderRadius:8, padding:"9px 18px", fontSize:13, color:T.textFaint, cursor:"pointer" }}>Cancelar</button>
                <button onClick={salvar} disabled={salvando||!form.cpf||!form.nome||cpfStatus===false||!soNum(form.cep)} style={{ background: (salvando||!form.cpf||!form.nome||cpfStatus===false||!soNum(form.cep))?T.btnSecBg:"linear-gradient(135deg,#0ea5e9,#0284c7)", color:(salvando||!form.cpf||!form.nome||cpfStatus===false||!soNum(form.cep))?T.btnSecText:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor: (salvando||!form.cpf||!form.nome||cpfStatus===false||!soNum(form.cep))?"not-allowed":"pointer" }}>
                  {salvando ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAINEL ADMIN
// ═══════════════════════════════════════════════════════════════════════════
// Contas de clientes que o contador pode acessar. A lista vem do vínculo criado
// no painel admin; sem vínculo nenhum, a aba nem aparece no menu.
function ClientesView({ contas, onAcessar, T, isMobile }) {
  const [busca, setBusca] = useState("");
  const norm = s => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const lista = contas.filter(c => norm(`${c.nome} ${c.email}`).includes(norm(busca)));

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:20, fontWeight:700, color:T.text, margin:"0 0 4px" }}>Clientes</h2>
        <p style={{ fontSize:13, color:T.textMuted, margin:0 }}>
          Contas que você pode acessar. Ao entrar, você opera como o cliente —
          emite, cancela e edita cadastros no nome dele.
        </p>
      </div>

      {contas.length > 4 && (
        <input
          style={{ width:"100%", background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8,
                   padding:"10px 14px", fontSize:13, color:T.inputText, outline:"none",
                   boxSizing:"border-box", marginBottom:16 }}
          placeholder="Buscar por nome ou e-mail..."
          value={busca} onChange={e=>setBusca(e.target.value)} />
      )}

      {lista.length === 0 ? (
        <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:12,
                      padding:"40px 20px", textAlign:"center", color:T.textFaint, fontSize:13 }}>
          {contas.length === 0
            ? "Nenhuma conta vinculada à sua. Peça ao administrador para criar o vínculo."
            : "Nenhum cliente encontrado com esse termo."}
        </div>
      ) : (
        <div style={{ display:"grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill,minmax(300px,1fr))",
                      gap:16 }}>
          {lista.map(c => (
            <div key={c.id} style={{ background:T.card, border:"1px solid "+T.border, borderRadius:12,
                                     padding:"18px 20px", boxShadow:T.shadow }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
                <Building2 size={16} style={{ color:"#8b5cf6", flexShrink:0 }} />
                <span style={{ fontSize:15, fontWeight:700, color:T.text }}>{c.nome}</span>
              </div>
              <div style={{ fontSize:12, color:T.textFaint, marginBottom:14, wordBreak:"break-all" }}>
                {c.email}
              </div>
              <button onClick={()=>onAcessar(c)}
                style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                         background:"linear-gradient(135deg,#0ea5e9,#0284c7)", color:"#fff", border:"none",
                         borderRadius:8, padding:"9px 16px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                <Eye size={14} /> Acessar conta
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminView({ token, T, onEntrarComo }) {
  const isMobile = useIsMobile();
  const h = { Authorization: "Bearer " + token, "Content-Type": "application/json" };
  const [stats, setStats] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aba, setAba] = useState("usuarios");
  const [busca, setBusca] = useState("");
  const [modal, setModal] = useState(null); // { tipo, usuario }
  const [modalSenha, setModalSenha] = useState("");
  const [modalEmail, setModalEmail] = useState("");
  // Erro exibido DENTRO do modal: o aviso do topo da tela fica atrás do overlay
  // e o usuário não vê nada acontecer ao clicar em Salvar.
  const [modalErro, setModalErro] = useState("");
  const [modalPlano, setModalPlano] = useState("");
  const [msg, setMsg] = useState({ tipo:"", texto:"" });
  // Reportes
  const [reportes, setReportes] = useState([]);
  const [reporteAberto, setReporteAberto] = useState(null); // id do reporte expandido
  const [respostaTxt, setRespostaTxt] = useState({});      // { [id]: texto }
  const [enviandoResp, setEnviandoResp] = useState(null);  // id em andamento
  const [abaReportes, setAbaReportes] = useState("aberto"); // "aberto" | "resolvido"

  // Vínculos contador → cliente
  const [acessos, setAcessos] = useState([]);
  const [novoAcesso, setNovoAcesso] = useState({ contador_id:"", cliente_id:"" });
  const [contadorAberto, setContadorAberto] = useState(null); // id do contador expandido

  // Contador é quem trabalha na Inatec. A lista de usuários mistura contadores e
  // clientes, e escolher um cliente por engano daria a ele acesso à conta de
  // outro — por isso o seletor só oferece os e-mails do domínio da empresa.
  const DOMINIO_CONTADOR = "@inateccontabilidade.com.br";
  const contadoresDisponiveis = usuarios.filter(
    u => (u.email || "").toLowerCase().endsWith(DOMINIO_CONTADOR));

  // Um contador costuma ter vários clientes; a lista plana repetia o nome dele
  // em cada linha. Agrupado, cada contador é uma linha que abre com os seus.
  // Vínculos antigos de quem não é do domínio continuam aparecendo — esconder
  // um acesso existente seria pior do que mostrá-lo.
  const acessosPorContador = (() => {
    const mapa = new Map();
    for (const a of acessos) {
      if (!mapa.has(a.contador_id)) {
        mapa.set(a.contador_id, { id: a.contador_id, nome: a.contador_nome, email: a.contador_email, clientes: [] });
      }
      mapa.get(a.contador_id).clientes.push(a);
    }
    return [...mapa.values()].sort((x, y) => (x.nome || "").localeCompare(y.nome || "", "pt-BR"));
  })();

  const carregarAcessos = async () => {
    try {
      const r = await fetch(`${API}/admin/acessos`, { headers:h });
      setAcessos(r.ok ? await r.json() : []);
    } catch { setAcessos([]); }
  };

  const criarAcesso = async () => {
    try {
      const r = await fetch(`${API}/admin/acessos`, {
        method:"POST", headers:h, body:JSON.stringify(novoAcesso)
      });
      if (!r.ok) {
        const err = await r.json().catch(()=>({}));
        setMsg({ tipo:"erro", texto: err.detail || "Erro ao criar o vínculo." });
        return;
      }
      setNovoAcesso({ contador_id:"", cliente_id:"" });
      setMsg({ tipo:"ok", texto:"Vínculo criado." });
      carregarAcessos();
    } catch (e) { setMsg({ tipo:"erro", texto:e.message }); }
  };

  const revogarAcesso = async (a) => {
    if (!window.confirm(`Remover o acesso de ${a.contador_nome} à conta de ${a.cliente_nome}?`)) return;
    try {
      const r = await fetch(`${API}/admin/acessos/${a.id}`, { method:"DELETE", headers:h });
      if (!r.ok) { setMsg({ tipo:"erro", texto:"Erro ao remover o vínculo." }); return; }
      setMsg({ tipo:"ok", texto:"Vínculo removido." });
      carregarAcessos();
    } catch (e) { setMsg({ tipo:"erro", texto:e.message }); }
  };

  // Log de atividades
  const [logs, setLogs] = useState([]);
  const [logCategoria, setLogCategoria] = useState("");   // "" = todas
  const [logUsuario, setLogUsuario] = useState("");       // id selecionado ("" = todos)
  const [logUsuarioTxt, setLogUsuarioTxt] = useState(""); // texto digitado na busca
  const [logSugestoesAbertas, setLogSugestoesAbertas] = useState(false);
  const [logDias, setLogDias] = useState(30);             // atalho; ignorado se houver datas
  const [logDe, setLogDe] = useState("");                 // AAAA-MM-DD
  const [logAte, setLogAte] = useState("");
  const [logCarregando, setLogCarregando] = useState(false);

  // Monta os parâmetros uma única vez para a listagem e o download não divergirem
  const paramsLog = (o = {}) => {
    const cat   = o.cat   !== undefined ? o.cat   : logCategoria;
    const ator  = o.ator  !== undefined ? o.ator  : logUsuario;
    const busca = o.busca !== undefined ? o.busca : logUsuarioTxt;
    const de    = o.de    !== undefined ? o.de    : logDe;
    const ate   = o.ate   !== undefined ? o.ate   : logAte;
    const dias  = o.dias  !== undefined ? o.dias  : logDias;

    const p = new URLSearchParams();
    if (cat) p.set("categoria", cat);
    if (ator) p.set("ator_id", ator);
    else if (busca && busca.trim()) p.set("ator_busca", busca.trim());
    if (de)  p.set("data_inicio", de);
    if (ate) p.set("data_fim", ate);
    if (!de && !ate) p.set("dias", String(dias));
    return p;
  };

  const carregarLogs = async (o = {}) => {
    setLogCarregando(true);
    const p = paramsLog(o);
    p.set("limite", "300");
    try {
      const r = await fetch(`${API}/admin/auditoria?${p}`, { headers: h });
      const d = await r.json();
      setLogs(Array.isArray(d) ? d : []);
    } catch { setLogs([]); }
    setLogCarregando(false);
  };

  const limparFiltrosLog = () => {
    setLogCategoria(""); setLogUsuario(""); setLogUsuarioTxt("");
    setLogDe(""); setLogAte(""); setLogDias(30);
    carregarLogs({ cat:"", ator:"", busca:"", de:"", ate:"", dias:30 });
  };

  const baixarLogs = async () => {
    const p = paramsLog();
    try {
      // Vai via fetch (e não link direto) porque o endpoint exige o token no header.
      const r = await fetch(`${API}/admin/auditoria/exportar?${p}`, { headers: h });
      if (!r.ok) return mostrarMsg("erro", "Falha ao gerar o arquivo.");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `log-atividades-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { mostrarMsg("erro", "Falha ao baixar o log."); }
  };

  const carregar = async () => {
    setLoading(true);
    const [s, u, rep] = await Promise.all([
      fetch(API + "/admin/stats",         { headers: h }).then(r => r.json()),
      fetch(API + "/admin/usuarios",      { headers: h }).then(r => r.json()),
      fetch(API + "/reportar-erro/todos?resolvido=false", { headers: h }).then(r => r.json()).catch(()=>[]),
    ]);
    setStats(s);
    setUsuarios(Array.isArray(u) ? u : []);
    setReportes(Array.isArray(rep) ? rep : []);
    setLoading(false);
  };

  useEffect(() => { carregar(); carregarLogs(); carregarAcessos(); }, []);

  const mostrarMsg = (tipo, texto) => { setMsg({ tipo, texto }); setTimeout(() => setMsg({ tipo:"", texto:"" }), 4000); };

  const redefinirSenha = async () => {
    if (modalSenha.length < 8) return mostrarMsg("erro", "Mínimo 8 caracteres.");
    const r = await fetch(API + `/admin/usuarios/${modal.usuario.id}/redefinir-senha`, { method:"POST", headers:h, body: JSON.stringify({ nova_senha: modalSenha }) });
    const d = await r.json();
    if (!r.ok) return mostrarMsg("erro", d.detail);
    mostrarMsg("ok", d.mensagem); setModal(null); setModalSenha("");
  };

  const alterarEmail = async () => {
    const novo = modalEmail.trim().toLowerCase();
    setModalErro("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(novo)) return setModalErro("E-mail inválido.");
    // Comparar com o valor exato gravado, e não em minúsculas: corrigir só a
    // caixa das letras (Tecla@ -> tecla@) é justamente o caso mais comum.
    if (novo === modal.usuario.email) return setModalErro("O e-mail é igual ao atual.");
    try {
      const r = await fetch(API + `/admin/usuarios/${modal.usuario.id}/email`, { method:"PUT", headers:h, body: JSON.stringify({ email: novo }) });
      const d = await r.json();
      if (!r.ok) return setModalErro(typeof d.detail === "string" ? d.detail : "Não foi possível alterar o e-mail.");
      mostrarMsg("ok", d.mensagem); setModal(null); setModalEmail(""); carregar();
    } catch { setModalErro("Erro de conexão com o servidor."); }
  };

  const alterarPlano = async () => {
    const r = await fetch(API + `/admin/usuarios/${modal.usuario.id}/plano`, { method:"PUT", headers:h, body: JSON.stringify({ plano: modalPlano }) });
    const d = await r.json();
    if (!r.ok) return mostrarMsg("erro", d.detail);
    mostrarMsg("ok", d.mensagem); setModal(null); carregar();
  };

  const excluirUsuario = async () => {
    const r = await fetch(API + `/admin/usuarios/${modal.usuario.id}`, { method:"DELETE", headers:h });
    const d = await r.json();
    if (!r.ok) return mostrarMsg("erro", d.detail);
    mostrarMsg("ok", d.mensagem); setModal(null); carregar();
  };

  const acessarConta = async (u) => {
    try {
      const r = await fetch(API + `/admin/usuarios/${u.id}/acessar`, { method:"POST", headers:h });
      const d = await r.json();
      if (!r.ok) return mostrarMsg("erro", d.detail || "Erro ao acessar conta");
      if (onEntrarComo) onEntrarComo(d.access_token, d.usuario);
    } catch { mostrarMsg("erro", "Erro de conexão"); }
  };

  const toggleAdmin = async () => {
    const novoStatus = !modal.usuario.is_admin;
    const r = await fetch(API + `/admin/usuarios/${modal.usuario.id}/admin`, {
      method:"PATCH", headers:{ ...h, "Content-Type":"application/json" },
      body: JSON.stringify({ is_admin: novoStatus })
    });
    const d = await r.json();
    if (!r.ok) return mostrarMsg("erro", d.detail || "Erro ao alterar permissão");
    mostrarMsg("ok", novoStatus ? "Admin concedido com sucesso!" : "Admin revogado com sucesso!");
    setModal(null); carregar();
  };

  const carregarReportesPorAba = async (aba) => {
    const resolvido = aba === "resolvido" ? "true" : "false";
    const rep = await fetch(`${API}/reportar-erro/todos?resolvido=${resolvido}`, { headers: h }).then(r => r.json()).catch(()=>[]);
    setReportes(Array.isArray(rep) ? rep : []);
    setReporteAberto(null);
  };

  const resolverReporte = async (reporteId) => {
    if (!window.confirm("Marcar este reporte como resolvido?")) return;
    await fetch(`${API}/reportar-erro/${reporteId}/resolver`, { method: "PUT", headers: h });
    carregarReportesPorAba(abaReportes);
  };

  const enviarResposta = async (reporteId) => {
    const txt = (respostaTxt[reporteId] || "").trim();
    if (!txt) return;
    setEnviandoResp(reporteId);
    try {
      const r = await fetch(`${API}/reportar-erro/${reporteId}/responder`, {
        method: "PUT", headers: h,
        body: JSON.stringify({ resposta: txt }),
      });
      if (!r.ok) { const d = await r.json(); mostrarMsg("erro", d.detail || "Erro ao responder"); return; }
      mostrarMsg("ok", "Resposta enviada!");
      setRespostaTxt(prev => ({ ...prev, [reporteId]: "" }));
      setReporteAberto(null);
      carregar();
    } catch { mostrarMsg("erro", "Erro de conexão"); }
    setEnviandoResp(null);
  };

  const fmtMoeda = v => new Intl.NumberFormat("pt-BR", { style:"currency", currency:"BRL" }).format(v || 0);
  const fmtData  = d => d ? new Date(d).toLocaleDateString("pt-BR") : "—";
  const fmtDataHora = d => d ? new Date(d).toLocaleString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";
  const cor = darkMode => darkMode ? "#080c10" : "#f0f4f8";

  const corPlano = p => ({
    gratuito:    { bg:"#1a1a0d", color:"#facc15" },
    basico:      { bg:"#0d1a2f", color:"#60a5fa" },
    profissional:{ bg:"#0d2f1a", color:"#4ade80" },
    enterprise:  { bg:"#2f0d2f", color:"#c084fc" },
  }[p] || { bg:"#1a1a2f", color:"#94a3b8" });

  const usuariosFiltrados = usuarios.filter(u =>
    u.nome?.toLowerCase().includes(busca.toLowerCase()) ||
    u.email?.toLowerCase().includes(busca.toLowerCase())
  );

  const S = {
    card:   { background:T.card, border:"1px solid "+T.border, borderRadius:12, padding:"22px 24px" },
    input:  { background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8, padding:"9px 13px", fontSize:13, color:T.inputText, outline:"none", width:"100%", boxSizing:"border-box" },
    btn:    { background:"linear-gradient(135deg,#0ea5e9,#0284c7)", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer" },
    btnSec: { background:T.btnSecBg, color:T.btnSecText, border:"1px solid "+T.border, borderRadius:8, padding:"7px 14px", fontSize:12, cursor:"pointer" },
    btnRed: { background:"#2f0d0d", color:"#f87171", border:"1px solid #ef444430", borderRadius:8, padding:"7px 14px", fontSize:12, cursor:"pointer" },
    label:  { fontSize:11, fontWeight:700, color:T.textFaint, letterSpacing:1, display:"block", marginBottom:5 },
  };

  const statsCards = [
    { label:"Usuários",          value: stats.total_usuarios || 0,       icon:Users,        cor:"#0ea5e9" },
    { label:"Empresas",          value: stats.total_empresas || 0,       icon:Building2,    cor:"#8b5cf6" },
    { label:"NFS-e Emitidas",    value: stats.total_notas || 0,          icon:FileText,     cor:"#f59e0b" },
    { label:"Autorizadas",       value: stats.notas_autorizadas || 0,    icon:CheckCircle,  cor:"#22c55e" },
    { label:"Faturamento Total", value: fmtMoeda(stats.total_faturado),  icon:DollarSign,   cor:"#0ea5e9" },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:800, color:T.text, margin:"0 0 3px" }}>Painel Admin</h2>
          <p style={{ fontSize:13, color:T.textMuted, margin:0 }}>Gerencie todos os usuários e dados do sistema</p>
        </div>
        <button style={{ ...S.btnSec, display:"flex", alignItems:"center", gap:6 }} onClick={carregar}><RefreshCw size={14}/> Atualizar</button>
      </div>

      {/* Mensagem feedback */}
      {msg.texto && (
        <div style={{ marginBottom:16, padding:"10px 16px", borderRadius:8, fontSize:13,
          background: msg.tipo==="ok" ? "#0d2f1a" : "#2f0d0d",
          color:      msg.tipo==="ok" ? "#4ade80" : "#f87171",
          border:     `1px solid ${msg.tipo==="ok" ? "#22c55e40" : "#ef444440"}` }}>
          {msg.tipo==="ok" ? <CheckCircle size={14} style={{marginRight:6}}/> : <AlertTriangle size={14} style={{marginRight:6}}/>} {msg.texto}
        </div>
      )}

      {/* Cards de stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:14, marginBottom:28 }}>
        {statsCards.map((c,i) => (
          <div key={i} style={S.card}>
            <c.icon size={22} color={c.cor} style={{ marginBottom:10 }} />
            <div style={{ fontSize:11, color:T.textFaint, marginBottom:4 }}>{c.label}</div>
            <div style={{ fontSize:20, fontWeight:800, color:c.cor }}>{loading ? "..." : c.value}</div>
          </div>
        ))}
      </div>

      {/* Tabela de usuários */}
      <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:12, overflow:"hidden" }}>
        <div style={{ padding:"16px 20px", borderBottom:"1px solid "+T.border, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:14, fontWeight:700, color:T.text }}><Users size={14} style={{verticalAlign:"middle",marginRight:5}} /> Usuários cadastrados ({usuariosFiltrados.length})</span>
          <input style={{ ...S.input, width:240 }} placeholder="Buscar por nome ou e-mail..." value={busca} onChange={e=>setBusca(e.target.value)} />
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:T.cardInner }}>
                {["Nome","E-mail","Empresas","NFS-e","Cadastro","Ações"].map(col => (
                  <th key={col} style={{ padding:"10px 16px", textAlign:"left", fontSize:11, fontWeight:700, color:T.textFaint, letterSpacing:0.5, whiteSpace:"nowrap" }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding:32, textAlign:"center", color:T.textMuted }}>Carregando...</td></tr>
              ) : usuariosFiltrados.length === 0 ? (
                <tr><td colSpan={6} style={{ padding:32, textAlign:"center", color:T.textMuted }}>Nenhum usuário encontrado.</td></tr>
              ) : usuariosFiltrados.map(u => {
                const plano = corPlano(u.plano);
                return (
                  <tr key={u.id} style={{ borderTop:"1px solid "+T.rowBorder }}>
                    <td style={{ padding:"12px 16px", color:T.text, fontWeight:500 }}>
                      {u.nome}
                      {u.is_admin && <span style={{ marginLeft:6, fontSize:10, background:"#2f0d2f", color:"#c084fc", borderRadius:4, padding:"2px 6px", fontWeight:700 }}>ADMIN</span>}
                    </td>
                    <td style={{ padding:"12px 16px", color:T.textMuted }}>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
                        {u.email}
                        {/* Sempre visível: a lista não distingue o admin vindo de
                            ADMIN_EMAILS do admin marcado no banco, e só o primeiro
                            é intocável. Quem decide é o backend. */}
                        <button title="Alterar e-mail"
                          onClick={()=>{ setModal({ tipo:"email", usuario:u }); setModalEmail(u.email || ""); setModalErro(""); }}
                          style={{ background:"none", border:"none", padding:2, cursor:"pointer",
                                   color:T.textMuted, display:"inline-flex", lineHeight:0 }}>
                          <Pencil size={12} />
                        </button>
                      </span>
                    </td>
                    <td style={{ padding:"12px 16px", color:T.textMuted, textAlign:"center" }}>{u.total_empresas}</td>
                    <td style={{ padding:"12px 16px", color:T.textMuted, textAlign:"center" }}>{u.total_notas}</td>
                    <td style={{ padding:"12px 16px", color:T.textMuted, whiteSpace:"nowrap" }}>{fmtData(u.created_at)}</td>
                    <td style={{ padding:"12px 16px" }}>
                      <div style={{ display:"flex", gap:6 }}>
                        <button style={{ ...S.btnSec, color:"#4ade80" }} onClick={()=>acessarConta(u)} title="Entrar na conta deste usuário"><Eye size={13} style={{verticalAlign:"middle",marginRight:4}} /> Acessar</button>
                        <button style={S.btnSec} onClick={()=>{ setModal({ tipo:"senha", usuario:u }); setModalSenha(""); }}><Key size={13} style={{verticalAlign:"middle",marginRight:4}} /> Senha</button>
                        <button style={{ ...S.btnSec, color: u.is_admin ? "#f97316" : "#a78bfa" }} onClick={()=>setModal({ tipo:"admin", usuario:u })}>
                          {u.is_admin ? "Revogar Admin" : "Tornar Admin"}
                        </button>
                        {!u.is_admin && <button style={S.btnRed} onClick={()=>setModal({ tipo:"excluir", usuario:u })}><X size={13} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Vínculos contador → cliente ── */}
      <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:12, overflow:"hidden", marginTop:28 }}>
        <div style={{ padding:"16px 20px", borderBottom:"1px solid "+T.border }}>
          <span style={{ fontSize:14, fontWeight:700, color:T.text, display:"flex", alignItems:"center", gap:8 }}>
            <Users size={15} style={{ color:"#0ea5e9" }} />
            Acessos de contadores
            <span style={{ fontSize:11, fontWeight:500, color:T.textFaint }}>
              ({acessosPorContador.length} contador{acessosPorContador.length===1?"":"es"} · {acessos.length} vínculo{acessos.length===1?"":"s"})
            </span>
          </span>
          <div style={{ fontSize:11, color:T.textFaint, marginTop:6, lineHeight:1.5 }}>
            Quem tem vínculo entra na conta do cliente e faz tudo que o cliente faria.
            Não recebe painel de admin, e cada entrada fica registrada no log.
            Clique num contador para ver os clientes dele.
          </div>
        </div>

        <div style={{ padding:"14px 20px", borderBottom:"1px solid "+T.border, display:"flex", gap:10, flexWrap:"wrap", alignItems:"flex-end" }}>
          <div style={{ flex:"1 1 220px" }}>
            <label style={{ fontSize:11, fontWeight:600, color:T.textMuted, display:"block", marginBottom:5 }}>CONTADOR</label>
            <select value={novoAcesso.contador_id} onChange={e=>setNovoAcesso(v=>({...v, contador_id:e.target.value}))}
              style={{ width:"100%", background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8, padding:"9px 12px", fontSize:13, color:T.inputText, boxSizing:"border-box" }}>
              <option value="">Selecione...</option>
              {contadoresDisponiveis.map(u => <option key={u.id} value={u.id}>{u.nome} — {u.email}</option>)}
            </select>
            {contadoresDisponiveis.length === 0 && (
              <div style={{ fontSize:10.5, color:T.textFaint, marginTop:4 }}>
                Nenhum usuário com e-mail {DOMINIO_CONTADOR}.
              </div>
            )}
          </div>
          <div style={{ flex:"1 1 220px" }}>
            <label style={{ fontSize:11, fontWeight:600, color:T.textMuted, display:"block", marginBottom:5 }}>PODE ACESSAR A CONTA DE</label>
            <select value={novoAcesso.cliente_id} onChange={e=>setNovoAcesso(v=>({...v, cliente_id:e.target.value}))}
              style={{ width:"100%", background:T.input, border:"1px solid "+T.inputBorder, borderRadius:8, padding:"9px 12px", fontSize:13, color:T.inputText, boxSizing:"border-box" }}>
              <option value="">Selecione...</option>
              {usuarios.filter(u => u.id !== novoAcesso.contador_id).map(u => <option key={u.id} value={u.id}>{u.nome} — {u.email}</option>)}
            </select>
          </div>
          <button onClick={criarAcesso} disabled={!novoAcesso.contador_id || !novoAcesso.cliente_id}
            style={{ background: (!novoAcesso.contador_id || !novoAcesso.cliente_id) ? T.btnSecBg : "linear-gradient(135deg,#0ea5e9,#0284c7)",
                     color: (!novoAcesso.contador_id || !novoAcesso.cliente_id) ? T.textFaint : "#fff",
                     border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:600, whiteSpace:"nowrap",
                     cursor: (!novoAcesso.contador_id || !novoAcesso.cliente_id) ? "not-allowed" : "pointer" }}>
            Vincular
          </button>
        </div>

        {acessos.length === 0 ? (
          <div style={{ padding:"24px 20px", textAlign:"center", color:T.textFaint, fontSize:13 }}>
            Nenhum vínculo criado ainda.
          </div>
        ) : acessosPorContador.map(c => {
          const aberto = contadorAberto === c.id;
          return (
            <div key={c.id} style={{ borderBottom:"1px solid "+T.rowBorder }}>
              <div onClick={() => setContadorAberto(aberto ? null : c.id)}
                style={{ padding:"12px 20px", display:"flex", alignItems:"center", gap:10, cursor:"pointer",
                         background: aberto ? T.cardInner : "transparent", userSelect:"none" }}>
                <span style={{ color:T.textFaint, fontSize:11, width:10, flexShrink:0,
                               transform: aberto ? "rotate(90deg)" : "none", transition:"transform .15s" }}>▶</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, color:T.text, fontWeight:700 }}>{c.nome}</div>
                  <div style={{ fontSize:11, color:T.textFaint, marginTop:2 }}>{c.email}</div>
                </div>
                <span style={{ fontSize:11, fontWeight:600, color:"#38bdf8", background:"#38bdf815",
                               border:"1px solid #38bdf830", borderRadius:20, padding:"3px 10px", whiteSpace:"nowrap" }}>
                  {c.clientes.length} cliente{c.clientes.length === 1 ? "" : "s"}
                </span>
              </div>

              {aberto && c.clientes.map(a => (
                <div key={a.id} style={{ padding:"10px 20px 10px 40px", borderTop:"1px solid "+T.rowBorder,
                                         display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, color:T.text }}>{a.cliente_nome}</div>
                    <div style={{ fontSize:11, color:T.textFaint, marginTop:2, wordBreak:"break-all" }}>{a.cliente_email}</div>
                  </div>
                  <button onClick={()=>revogarAcesso(a)}
                    style={{ background:"#7f1d1d20", border:"1px solid #f8717140", borderRadius:6, padding:"5px 12px", fontSize:12, color:"#f87171", cursor:"pointer", fontWeight:600, whiteSpace:"nowrap" }}>
                    Remover
                  </button>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* ── Log de atividades ── */}
      {(() => {
        const ROTULOS = {
          login:"Entrou no sistema", logout:"Saiu do sistema", login_falha:"Tentativa de login falhou",
          nota_emitida:"Emitiu NFS-e", nota_cancelada:"Cancelou NFS-e", nota_excluida:"Excluiu NFS-e do sistema",
          xmls_exportados:"Baixou XMLs em lote",
          empresa_criada:"Cadastrou empresa", empresa_alterada:"Alterou empresa", empresa_excluida:"Excluiu empresa",
          servico_criado:"Cadastrou serviço", servico_alterado:"Alterou serviço", servico_excluido:"Excluiu serviço",
          pessoa_criada:"Cadastrou pessoa", pessoa_alterada:"Alterou pessoa", pessoa_excluida:"Excluiu pessoa",
          certificado_enviado:"Enviou certificado digital",
          acesso_conta_usuario:"Acessou conta de usuário", redefinicao_senha:"Redefiniu senha de usuário",
          alteracao_email:"Alterou e-mail de usuário",
          exclusao_usuario:"Excluiu usuário", concessao_admin:"Concedeu admin", revogacao_admin:"Revogou admin",
          acesso_delegado_criado:"Vinculou contador a uma conta",
          acesso_delegado_revogado:"Removeu vínculo de contador",
          acesso_conta_delegada:"Contador acessou conta vinculada",
        };
        const COR = a =>
          a === "login_falha" ? "#ef4444"
          : a.startsWith("nota_") ? "#0ea5e9"
          : a.startsWith("certificado") ? "#f59e0b"
          : ["acesso_conta_usuario","exclusao_usuario","redefinicao_senha","alteracao_email","concessao_admin","revogacao_admin",
             "acesso_delegado_criado","acesso_delegado_revogado","acesso_conta_delegada"].includes(a) ? "#a78bfa"
          : a.includes("excluid") || a.includes("excluiu") ? "#f87171"
          : "#22c55e";
        const CATEGORIAS = [["","Todas"],["acesso","Acessos"],["nota","Notas"],
                            ["cadastro","Cadastros"],["certificado","Certificado"],["admin","Admin"]];
        return (
      <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:12, overflow:"hidden", marginTop:28 }}>
        <div style={{ padding:"16px 20px", borderBottom:"1px solid "+T.border }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10, marginBottom:12 }}>
            <span style={{ fontSize:14, fontWeight:700, color:T.text, display:"flex", alignItems:"center", gap:8 }}>
              <ClipboardList size={15} style={{ color:"#a78bfa" }} />
              Log de Atividades
              <span style={{ fontSize:11, fontWeight:500, color:T.textFaint }}>
                ({logs.length} registro{logs.length===1?"":"s"})
              </span>
            </span>
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              {/* Busca de usuário por nome/e-mail — substitui a lista suspensa,
                  que fica inviável com dezenas ou centenas de usuários. */}
              <div style={{ position:"relative" }}>
                <input
                  value={logUsuarioTxt}
                  placeholder="Buscar usuário..."
                  onChange={e=>{ setLogUsuarioTxt(e.target.value); setLogUsuario(""); setLogSugestoesAbertas(true); }}
                  onFocus={()=>setLogSugestoesAbertas(true)}
                  onBlur={()=>setTimeout(()=>setLogSugestoesAbertas(false), 150)}
                  onKeyDown={e=>{ if(e.key==="Enter"){ setLogSugestoesAbertas(false); carregarLogs({ ator:"", busca:logUsuarioTxt }); } }}
                  style={{ background:T.input, border:"1px solid "+T.inputBorder, borderRadius:7,
                           padding:"5px 26px 5px 9px", fontSize:12, color:T.inputText, width:172,
                           fontFamily:"'IBM Plex Sans',sans-serif", outline:"none" }} />
                {(logUsuarioTxt || logUsuario) && (
                  <button onClick={()=>{ setLogUsuarioTxt(""); setLogUsuario(""); carregarLogs({ ator:"", busca:"" }); }}
                    title="Limpar" style={{ position:"absolute", right:3, top:"50%", transform:"translateY(-50%)",
                      background:"transparent", border:"none", cursor:"pointer", color:T.textFaint, padding:4, display:"flex", lineHeight:0 }}>
                    <X size={12} />
                  </button>
                )}
                {logSugestoesAbertas && logUsuarioTxt.trim() && (() => {
                  const q = logUsuarioTxt.trim().toLowerCase();
                  const achados = usuarios.filter(us =>
                    (us.nome||"").toLowerCase().includes(q) || (us.email||"").toLowerCase().includes(q)
                  ).slice(0, 8);
                  if (!achados.length) return null;
                  return (
                    <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:50, width:250,
                                  background:T.card, border:"1px solid "+T.border, borderRadius:8,
                                  boxShadow:"0 6px 20px rgba(0,0,0,0.25)", overflow:"hidden" }}>
                      {achados.map(us => (
                        <div key={us.id}
                          onMouseDown={()=>{ setLogUsuario(us.id); setLogUsuarioTxt(us.nome); setLogSugestoesAbertas(false); carregarLogs({ ator:us.id, busca:"" }); }}
                          style={{ padding:"7px 11px", cursor:"pointer", borderBottom:"1px solid "+T.rowBorder }}>
                          <div style={{ fontSize:12.5, color:T.text, fontWeight:600 }}>{us.nome}</div>
                          <div style={{ fontSize:11, color:T.textFaint }}>{us.email}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              <span style={{ fontSize:11.5, color:T.textFaint }}>de</span>
              <input type="date" value={logDe}
                onChange={e=>{ setLogDe(e.target.value); carregarLogs({ de:e.target.value }); }}
                style={{ background:T.input, border:"1px solid "+T.inputBorder, borderRadius:7,
                         padding:"4px 8px", fontSize:12, color:T.inputText, colorScheme:T.isDark?"dark":"light" }} />
              <span style={{ fontSize:11.5, color:T.textFaint }}>até</span>
              <input type="date" value={logAte}
                onChange={e=>{ setLogAte(e.target.value); carregarLogs({ ate:e.target.value }); }}
                style={{ background:T.input, border:"1px solid "+T.inputBorder, borderRadius:7,
                         padding:"4px 8px", fontSize:12, color:T.inputText, colorScheme:T.isDark?"dark":"light" }} />

              {(logDe || logAte) ? (
                <button onClick={limparFiltrosLog} title="Limpar todos os filtros"
                  style={{ fontSize:11.5, fontWeight:600, background:"transparent", border:"none",
                           color:"#0ea5e9", cursor:"pointer", padding:"4px 2px" }}>
                  Limpar
                </button>
              ) : (
                <select value={logDias} onChange={e=>{ const d=Number(e.target.value); setLogDias(d); carregarLogs({ dias:d }); }}
                  style={{ background:T.input, border:"1px solid "+T.inputBorder, borderRadius:7, padding:"5px 9px", fontSize:12, color:T.inputText }}>
                  <option value={7}>7 dias</option><option value={30}>30 dias</option>
                  <option value={90}>90 dias</option><option value={365}>1 ano</option>
                </select>
              )}
              <button onClick={()=>carregarLogs()} title="Atualizar"
                style={{ background:T.btnSecBg, border:"1px solid "+T.border, borderRadius:7, padding:"5px 9px", cursor:"pointer", color:T.textMuted, display:"flex", alignItems:"center" }}>
                <RefreshCw size={13} />
              </button>
              <button onClick={baixarLogs} title="Baixar em CSV (respeita os filtros aplicados)"
                disabled={logs.length === 0}
                style={{ display:"flex", alignItems:"center", gap:6, background:T.btnSecBg,
                         border:"1px solid "+T.border, borderRadius:7, padding:"5px 11px",
                         fontSize:12, fontWeight:600, color: logs.length ? T.textMuted : T.textFaint,
                         cursor: logs.length ? "pointer" : "not-allowed" }}>
                <Download size={13} /> Baixar CSV
              </button>
            </div>
          </div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {CATEGORIAS.map(([val,label]) => (
              <button key={val} onClick={()=>{ setLogCategoria(val); carregarLogs({ cat:val }); }}
                style={{ fontSize:11.5, fontWeight:700, padding:"4px 12px", borderRadius:20, cursor:"pointer",
                         border:"1px solid "+(logCategoria===val?"#a78bfa":T.border),
                         background: logCategoria===val?"#a78bfa":T.btnSecBg,
                         color: logCategoria===val?"#fff":T.textMuted }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {logCarregando ? (
          <div style={{ padding:24, textAlign:"center", color:T.textMuted, fontSize:13 }}>Carregando...</div>
        ) : logs.length === 0 ? (
          <div style={{ padding:24, textAlign:"center", color:T.textMuted, fontSize:13 }}>
            Nenhuma atividade registrada no período.
          </div>
        ) : (
          <div style={{ maxHeight:460, overflowY:"auto" }}>
            {logs.map(l => (
              <div key={l.id} style={{ padding:"10px 20px", borderTop:"1px solid "+T.rowBorder, display:"flex", alignItems:"flex-start", gap:11 }}>
                <span style={{ width:7, height:7, borderRadius:"50%", background:COR(l.acao), marginTop:6, flexShrink:0 }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, color:T.text, fontWeight:500 }}>
                    {ROTULOS[l.acao] || l.acao}
                    {l.ator_email && <span style={{ color:T.textMuted, fontWeight:400 }}> — {l.ator_email}</span>}
                  </div>
                  {(l.detalhe || l.alvo_email) && (
                    <div style={{ fontSize:12, color:T.textFaint, marginTop:2, wordBreak:"break-word" }}>
                      {l.alvo_email ? `Alvo: ${l.alvo_email}` : ""}{l.alvo_email && l.detalhe ? " · " : ""}{l.detalhe || ""}
                    </div>
                  )}
                </div>
                <div style={{ textAlign:"right", flexShrink:0 }}>
                  <div style={{ fontSize:11.5, color:T.textFaint, whiteSpace:"nowrap" }}>{fmtDataHora(l.criado_em)}</div>
                  {l.ip && <div style={{ fontSize:10.5, color:T.textFaint, opacity:0.7, whiteSpace:"nowrap" }}>{l.ip}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        );
      })()}

      {/* ── Reportes de erro ── */}
      <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:12, overflow:"hidden", marginTop:28 }}>
        <div style={{ padding:"16px 20px", borderBottom:"1px solid "+T.border, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <span style={{ fontSize:14, fontWeight:700, color:T.text, display:"flex", alignItems:"center", gap:8 }}>
            <MessageSquare size={15} style={{ color:"#f59e0b" }} />
            Reportes de Erro
          </span>
          {/* Abas Em Aberto / Resolvidos */}
          <div style={{ display:"flex", gap:6 }}>
            {[["aberto","Em Aberto"],["resolvido","Resolvidos"]].map(([val,label]) => (
              <button key={val} onClick={() => { setAbaReportes(val); carregarReportesPorAba(val); }}
                style={{ fontSize:12, fontWeight:700, padding:"5px 14px", borderRadius:20, border:"1px solid "+(abaReportes===val?"#0ea5e9":T.border), background:abaReportes===val?"#0ea5e9":T.btnSecBg, color:abaReportes===val?"#fff":T.textMuted, cursor:"pointer" }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div style={{ padding:24, textAlign:"center", color:T.textMuted, fontSize:13 }}>Carregando...</div>
        ) : reportes.length === 0 ? (
          <div style={{ padding:24, textAlign:"center", color:T.textMuted, fontSize:13 }}>
            {abaReportes === "resolvido" ? "Nenhum reporte resolvido." : "Nenhum reporte em aberto."}
          </div>
        ) : reportes.map(rep => {
          const aberto    = reporteAberto === rep.id;
          const respondido = !!rep.resposta;
          const resolvido  = !!rep.resolvido;
          return (
            <div key={rep.id} style={{ borderTop:"1px solid "+T.rowBorder }}>
              {/* Linha resumo */}
              <div onClick={() => setReporteAberto(aberto ? null : rep.id)}
                style={{ padding:"12px 20px", cursor:"pointer", display:"flex", alignItems:"center", gap:12, background: aberto ? T.cardInner : "transparent" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:T.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                    {rep.usuario_nome} <span style={{ color:T.textMuted, fontWeight:400 }}>({rep.usuario_email})</span>
                  </div>
                  <div style={{ fontSize:12, color:T.textFaint, marginTop:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                    {rep.descricao?.slice(0, 90)}{rep.descricao?.length > 90 ? "…" : ""}
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                  <span style={{ fontSize:11, color:T.textFaint }}>{fmtDataHora(rep.criado_em)}</span>
                  {resolvido ? (
                    <span style={{ background:T.isDark?"#0d2030":"#eff6ff", color:T.isDark?"#60a5fa":"#1d4ed8", fontSize:10, fontWeight:700, borderRadius:20, padding:"2px 10px" }}>RESOLVIDO</span>
                  ) : respondido ? (
                    <span style={{ background:T.isDark?"#0d2f1a":"#f0fdf4", color:T.isDark?"#4ade80":"#16a34a", fontSize:10, fontWeight:700, borderRadius:20, padding:"2px 10px" }}>RESPONDIDO</span>
                  ) : (
                    <span style={{ background:T.isDark?"#2d1515":"#fef2f2", color:"#ef4444", fontSize:10, fontWeight:700, borderRadius:20, padding:"2px 10px" }}>PENDENTE</span>
                  )}
                  {!resolvido && (
                    <button onClick={e => { e.stopPropagation(); resolverReporte(rep.id); }}
                      title="Marcar como resolvido" style={{ background:"transparent", border:"none", cursor:"pointer", color:"#22c55e", padding:4, display:"flex", alignItems:"center" }}>
                      <Check size={14}/>
                    </button>
                  )}
                  <button onClick={e => { e.stopPropagation(); if(window.confirm("Excluir este reporte?")) fetch(`${API}/reportar-erro/${rep.id}`,{method:"DELETE",headers:h}).then(()=>carregarReportesPorAba(abaReportes)); }}
                    title="Excluir" style={{ background:"transparent", border:"none", cursor:"pointer", color:"#f87171", padding:4, display:"flex", alignItems:"center" }}>
                    <Trash2 size={13}/>
                  </button>
                </div>
              </div>

              {/* Detalhe expandido */}
              {aberto && (
                <div style={{ padding:"0 20px 20px", background:T.cardInner, borderTop:"1px solid "+T.border }}>
                  {resolvido && (
                    <div style={{ marginTop:14, display:"flex", alignItems:"center", gap:8, background:T.isDark?"#0d1f3a":"#eff6ff", border:"1px solid "+(T.isDark?"#3b82f620":"#bfdbfe"), borderRadius:8, padding:"10px 14px" }}>
                      <Check size={14} style={{ color:"#60a5fa" }} />
                      <span style={{ fontSize:12, color:T.isDark?"#93c5fd":"#1d4ed8", fontWeight:600 }}>
                        Resolvido em {fmtDataHora(rep.resolvido_em)}
                      </span>
                    </div>
                  )}
                  <div style={{ marginTop:14 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:T.textFaint, marginBottom:6, letterSpacing:0.5 }}>DESCRIÇÃO DO USUÁRIO</div>
                    <pre style={{ margin:0, fontSize:13, color:T.text, whiteSpace:"pre-wrap", fontFamily:"'IBM Plex Sans',sans-serif", lineHeight:1.6, background:T.card, border:"1px solid "+T.border, borderRadius:8, padding:"12px 14px" }}>{rep.descricao}</pre>
                  </div>

                  {respondido && (
                    <div style={{ marginTop:14 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:T.isDark?"#4ade80":"#16a34a", marginBottom:6, letterSpacing:0.5 }}>SUA RESPOSTA · {fmtDataHora(rep.respondido_em)}</div>
                      <pre style={{ margin:0, fontSize:13, color:T.isDark?"#d1fae5":"#065f46", whiteSpace:"pre-wrap", fontFamily:"'IBM Plex Sans',sans-serif", lineHeight:1.6, background:T.isDark?"#0d2f1a":"#f0fdf4", border:"1px solid "+(T.isDark?"#22c55e30":"#bbf7d0"), borderRadius:8, padding:"12px 14px" }}>{rep.resposta}</pre>
                    </div>
                  )}

                  {/* Campo de resposta/nova resposta — oculto se já resolvido */}
                  {!resolvido && (
                    <div style={{ marginTop:16 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:T.textFaint, marginBottom:6, letterSpacing:0.5 }}>
                        {respondido ? "ENVIAR NOVA RESPOSTA" : "RESPONDER"}
                      </div>
                      <textarea
                        value={respostaTxt[rep.id] || ""}
                        onChange={e => setRespostaTxt(prev => ({ ...prev, [rep.id]: e.target.value }))}
                        rows={4}
                        placeholder="Digite sua resposta aqui..."
                        style={{ width:"100%", boxSizing:"border-box", resize:"vertical", background:T.card, border:"1px solid "+T.inputBorder, borderRadius:8, padding:"10px 14px", fontSize:13, color:T.text, fontFamily:"'IBM Plex Sans',sans-serif", lineHeight:1.6, outline:"none" }}
                      />
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:10 }}>
                        <button onClick={() => resolverReporte(rep.id)}
                          style={{ display:"flex", alignItems:"center", gap:6, background:T.isDark?"#052e16":"#f0fdf4", border:"1px solid "+(T.isDark?"#22c55e40":"#bbf7d0"), borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:700, color:T.isDark?"#4ade80":"#15803d", cursor:"pointer" }}>
                          <Check size={13} /> Marcar como Resolvido
                        </button>
                        <button
                          onClick={() => enviarResposta(rep.id)}
                          disabled={!respostaTxt[rep.id]?.trim() || enviandoResp === rep.id}
                          style={{ display:"flex", alignItems:"center", gap:7, background: !respostaTxt[rep.id]?.trim() || enviandoResp===rep.id ? T.btnSecBg : "linear-gradient(135deg,#0ea5e9,#0284c7)", border:"none", borderRadius:8, padding:"9px 20px", fontSize:13, fontWeight:600, color: !respostaTxt[rep.id]?.trim() || enviandoResp===rep.id ? T.textMuted : "#fff", cursor: !respostaTxt[rep.id]?.trim() || enviandoResp===rep.id ? "not-allowed" : "pointer" }}>
                          <Send size={13} />
                          {enviandoResp === rep.id ? "Enviando..." : "Enviar Resposta"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modal && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom: isMobile ? 64 : 0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:T.card, border:"1px solid "+T.border, borderRadius:16, padding:32, width:"100%", maxWidth:420 }}>
            {/* Modal: Redefinir Senha */}
            {modal.tipo === "senha" && (
              <>
                <h3 style={{ margin:"0 0 6px", color:T.text }}>Redefinir senha</h3>
                <p style={{ margin:"0 0 20px", fontSize:13, color:T.textMuted }}>Usuário: <strong style={{ color:T.text }}>{modal.usuario.nome}</strong></p>
                <label style={S.label}>NOVA SENHA <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                <div style={{ marginBottom:20 }}>
                  <CampoSenha estilo={S.input} valor={modalSenha} autoComplete="new-password"
                    placeholder="Mínimo 8 caracteres" onChange={e=>setModalSenha(e.target.value)} />
                </div>
                <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                  <button style={S.btnSec} onClick={()=>setModal(null)}>Cancelar</button>
                  <button style={S.btn} onClick={redefinirSenha}>Salvar</button>
                </div>
              </>
            )}
            {/* Modal: Alterar E-mail */}
            {modal.tipo === "email" && (
              <>
                <h3 style={{ margin:"0 0 6px", color:T.text }}>Alterar e-mail</h3>
                <p style={{ margin:"0 0 20px", fontSize:13, color:T.textMuted }}>
                  Usuário: <strong style={{ color:T.text }}>{modal.usuario.nome}</strong>
                </p>
                <label style={S.label}>E-MAIL ATUAL</label>
                <div style={{ ...S.input, marginBottom:16, opacity:0.6, cursor:"not-allowed" }}>{modal.usuario.email}</div>
                <label style={S.label}>NOVO E-MAIL <span style={{color:"#f97316",marginLeft:3}}>*</span></label>
                <input style={{ ...S.input, marginBottom:14 }} type="email" autoComplete="off"
                  value={modalEmail} placeholder="nome@empresa.com.br"
                  onChange={e=>{ setModalEmail(e.target.value); setModalErro(""); }}
                  onKeyDown={e=>{ if (e.key === "Enter") alterarEmail(); }} />
                {modalErro && (
                  <div style={{ background:"#2f0d0d", border:"1px solid #ef444440", borderRadius:8,
                                padding:"10px 12px", marginBottom:14, fontSize:12.5, color:"#f87171" }}>
                    <AlertTriangle size={13} style={{ verticalAlign:"middle", marginRight:5 }} />
                    {modalErro}
                  </div>
                )}
                <div style={{ background:"#78350f22", border:"1px solid #f59e0b55", borderRadius:8,
                              padding:"10px 12px", marginBottom:20, fontSize:12, color:T.textMuted, lineHeight:1.5 }}>
                  <AlertTriangle size={13} style={{ verticalAlign:"middle", marginRight:5, color:"#f59e0b" }} />
                  O e-mail é o login. O usuário será <strong style={{color:T.text}}>desconectado</strong> e passará
                  a entrar com o novo endereço — a senha continua a mesma. O e-mail é salvo em minúsculas.
                </div>
                <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                  <button style={S.btnSec} onClick={()=>setModal(null)}>Cancelar</button>
                  <button style={S.btn} onClick={alterarEmail}>Salvar</button>
                </div>
              </>
            )}
            {/* Modal: Toggle Admin */}
            {modal.tipo === "admin" && (
              <>
                <h3 style={{ margin:"0 0 6px", color: modal.usuario.is_admin ? "#f97316" : "#a78bfa" }}>
                  {modal.usuario.is_admin ? "Revogar acesso Admin" : "Conceder acesso Admin"}
                </h3>
                <p style={{ margin:"0 0 20px", fontSize:13, color:T.textMuted }}>
                  Usuário: <strong style={{ color:T.text }}>{modal.usuario.nome}</strong> ({modal.usuario.email})
                </p>
                <p style={{ margin:"0 0 20px", fontSize:13, color:T.textMuted }}>
                  {modal.usuario.is_admin
                    ? "Este usuário perderá acesso ao Painel Admin."
                    : "Este usuário terá acesso completo ao Painel Admin."}
                </p>
                <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                  <button style={S.btnSec} onClick={()=>setModal(null)}>Cancelar</button>
                  <button style={{ ...S.btn, background: modal.usuario.is_admin ? "linear-gradient(135deg,#f97316,#ea580c)" : "linear-gradient(135deg,#7c3aed,#6d28d9)" }} onClick={toggleAdmin}>
                    {modal.usuario.is_admin ? "Revogar Admin" : "Conceder Admin"}
                  </button>
                </div>
              </>
            )}
            {/* Modal: Excluir */}
            {modal.tipo === "excluir" && (
              <>
                <h3 style={{ margin:"0 0 6px", color:"#f87171" }}>Excluir conta</h3>
                <p style={{ margin:"0 0 4px", fontSize:13, color:T.textMuted }}>Esta ação é <strong style={{ color:"#f87171" }}>irreversível</strong>. Serão removidos todos os dados do usuário:</p>
                <p style={{ margin:"0 0 20px", fontSize:14, fontWeight:700, color:T.text }}>{modal.usuario.nome} ({modal.usuario.email})</p>
                <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                  <button style={S.btnSec} onClick={()=>setModal(null)}>Cancelar</button>
                  <button style={{ ...S.btn, background:"linear-gradient(135deg,#ef4444,#dc2626)" }} onClick={excluirUsuario}>Confirmar exclusão</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reportar Erro
// ─────────────────────────────────────────────────────────────────────────────
function ReportarErroView({ token, T, isMobile, onReporteSent }) {
  const h = { Authorization: "Bearer " + token };
  const [descricao, setDescricao]   = useState("");
  const [imagens,   setImagens]     = useState([]);
  const [enviando,  setEnviando]    = useState(false);
  const [status,    setStatus]      = useState(null); // "ok" | "erro" | null
  const [errMsg,    setErrMsg]      = useState("");
  const [historico, setHistorico]   = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [excluindo,  setExcluindo]  = useState(null); // id sendo excluído
  const inputRef = useRef();

  const fmtData = d => d ? new Date(d).toLocaleString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

  const carregarHistorico = async () => {
    setCarregando(true);
    try {
      const r = await fetch(`${API}/reportar-erro/meus`, { headers: h });
      const d = await r.json();
      setHistorico(Array.isArray(d) ? d : []);
    } catch { setHistorico([]); }
    setCarregando(false);
  };

  // Marca respostas não lidas como lidas
  const marcarLidos = async (lista) => {
    const naoLidos = lista.filter(r => r.resposta && !r.lida_usuario);
    await Promise.all(naoLidos.map(r =>
      fetch(`${API}/reportar-erro/${r.id}/marcar-lido`, { method: "PUT", headers: h })
    ));
    if (naoLidos.length > 0 && onReporteSent) onReporteSent(); // atualiza badge
  };

  useEffect(() => {
    carregarHistorico().then(() => {});
  }, []);

  // Quando histórico carrega, marca lidos automaticamente
  useEffect(() => {
    if (historico.length > 0) marcarLidos(historico);
  }, [historico]);

  const excluirReporte = async (id) => {
    if (!window.confirm("Excluir este reporte?")) return;
    setExcluindo(id);
    await fetch(`${API}/reportar-erro/${id}`, { method: "DELETE", headers: h });
    setExcluindo(null);
    carregarHistorico();
    if (onReporteSent) onReporteSent();
  };

  const handleFiles = (e) => {
    const novos = Array.from(e.target.files || []);
    setImagens(prev => {
      const existentes = prev.map(f => f.name + f.size);
      const filtrados  = novos.filter(f => !existentes.includes(f.name + f.size));
      return [...prev, ...filtrados].slice(0, 5);
    });
    e.target.value = "";
  };

  const removerImagem = (idx) =>
    setImagens(prev => prev.filter((_, i) => i !== idx));

  const enviar = async () => {
    if (!descricao.trim()) return;
    setEnviando(true);
    setStatus(null);
    try {
      const fd = new FormData();
      fd.append("descricao", descricao.trim());
      imagens.forEach(img => fd.append("imagens", img, img.name));
      const res = await fetch(`${API}/reportar-erro`, {
        method: "POST",
        headers: h,
        body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `Erro ${res.status}`);
      }
      setStatus("ok");
      setDescricao("");
      setImagens([]);
      carregarHistorico();
    } catch (ex) {
      setErrMsg(ex.message);
      setStatus("erro");
    } finally {
      setEnviando(false);
    }
  };

  const card = {
    background: T.card, border: "1px solid " + T.border,
    borderRadius: 12, padding: isMobile ? "20px 16px" : "24px 28px",
    boxShadow: T.shadow,
  };

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
          <MessageSquare size={20} style={{ color: "#f59e0b" }} />
          Reportar um Erro
        </h2>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: T.textMuted }}>
          Descreva o problema. Você pode anexar capturas de tela para ajudar no diagnóstico.
        </p>
      </div>

      {/* ── Formulário novo reporte ── */}
      <div style={card}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 6 }}>
          Descrição do problema <span style={{ color: "#ef4444" }}>*</span>
        </label>
        <textarea
          value={descricao}
          onChange={e => setDescricao(e.target.value)}
          rows={6}
          placeholder="Descreva o que aconteceu, qual tela estava usando, o que esperava e o que ocorreu de fato..."
          style={{
            width: "100%", boxSizing: "border-box", resize: "vertical",
            background: T.input, border: "1px solid " + T.inputBorder,
            borderRadius: 8, padding: "10px 14px", fontSize: 13,
            color: T.text, lineHeight: 1.6, outline: "none",
            fontFamily: "'IBM Plex Sans', sans-serif", minHeight: 120,
          }}
        />
        <div style={{ textAlign: "right", fontSize: 11, color: T.textFaint, marginTop: 4 }}>
          {descricao.length} caracteres
        </div>

        {/* Upload */}
        <div style={{ marginTop: 16 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 }}>
            Capturas de tela <span style={{ fontSize: 12, fontWeight: 400, color: T.textMuted }}>(opcional · máx. 5)</span>
          </label>
          {imagens.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
              {imagens.map((img, idx) => {
                const url = URL.createObjectURL(img);
                return (
                  <div key={idx} style={{ position: "relative", width: 80, height: 64, borderRadius: 8, overflow: "hidden", border: "1px solid " + T.border, background: T.input }}>
                    <img src={url} alt={img.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} onLoad={() => URL.revokeObjectURL(url)} />
                    <button onClick={() => removerImagem(idx)}
                      style={{ position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,0.65)", border: "none", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", padding: 0 }}>
                      <X size={10} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {imagens.length < 5 && (
            <>
              <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleFiles} />
              <button onClick={() => inputRef.current?.click()}
                style={{ display: "flex", alignItems: "center", gap: 7, background: T.btnSecBg, border: "1px dashed " + T.border, borderRadius: 8, padding: "8px 16px", fontSize: 13, color: T.btnSecText, cursor: "pointer", width: "100%", justifyContent: "center" }}>
                <Paperclip size={14} />
                {imagens.length === 0 ? "Adicionar imagens" : "Adicionar mais"}
              </button>
            </>
          )}
        </div>

        {status === "ok" && (
          <div style={{ marginTop: 14, background: T.successBg, border: "1px solid " + T.successBorder, borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: T.successText }}>
            <CheckCircle size={15} /><span>Reporte enviado! Nossa equipe irá analisar em breve.</span>
          </div>
        )}
        {status === "erro" && (
          <div style={{ marginTop: 14, background: T.isDark ? "#2d1515" : "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#ef4444" }}>
            <AlertTriangle size={15} /><span>{errMsg}</span>
          </div>
        )}

        <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          <button onClick={enviar} disabled={!descricao.trim() || enviando}
            style={{ display: "flex", alignItems: "center", gap: 8, background: !descricao.trim() || enviando ? T.btnSecBg : "#f59e0b", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 13, fontWeight: 600, color: !descricao.trim() || enviando ? T.textMuted : "#fff", cursor: !descricao.trim() || enviando ? "not-allowed" : "pointer", transition: "background .2s" }}>
            <Send size={14} />
            {enviando ? "Enviando..." : "Enviar Reporte"}
          </button>
        </div>
      </div>

      {/* ── Histórico de reportes ── */}
      {(carregando || historico.length > 0) && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: "0 0 14px", display: "flex", alignItems: "center", gap: 6 }}>
            <ClipboardList size={15} style={{ color: T.textMuted }} /> Seus reportes anteriores
          </h3>
          {carregando ? (
            <div style={{ textAlign: "center", color: T.textMuted, padding: 24, fontSize: 13 }}>Carregando...</div>
          ) : historico.map(rep => {
            const temResposta = !!rep.resposta;
            const naoLida     = temResposta && !rep.lida_usuario;
            return (
              <div key={rep.id} style={{ ...card, marginBottom: 14, borderLeft: naoLida ? "3px solid #22c55e" : "3px solid " + T.border }}>
                {/* Cabeçalho */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: T.textFaint }}>{fmtData(rep.criado_em)}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {naoLida && (
                      <span style={{ background: "#22c55e", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 10px" }}>NOVA RESPOSTA</span>
                    )}
                    {temResposta && !naoLida && (
                      <span style={{ background: T.isDark ? "#0d2f1a" : "#f0fdf4", color: T.isDark ? "#4ade80" : "#16a34a", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 10px" }}>RESPONDIDO</span>
                    )}
                    {!temResposta && (
                      <span style={{ background: T.isDark ? "#1a1a0d" : "#fffbeb", color: T.isDark ? "#facc15" : "#b45309", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 10px" }}>AGUARDANDO</span>
                    )}
                    <button onClick={() => excluirReporte(rep.id)} disabled={excluindo === rep.id}
                      title="Excluir reporte"
                      style={{ background: "transparent", border: "none", cursor: "pointer", color: T.textFaint, padding: 2, display: "flex", alignItems: "center", opacity: excluindo === rep.id ? 0.4 : 1 }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                {/* Descrição do usuário */}
                <div style={{ background: T.cardInner, borderRadius: 8, padding: "10px 14px", marginBottom: temResposta ? 12 : 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.textFaint, marginBottom: 5, letterSpacing: 0.5 }}>SEU REPORTE</div>
                  <pre style={{ margin: 0, fontSize: 13, color: T.text, whiteSpace: "pre-wrap", fontFamily: "'IBM Plex Sans', sans-serif", lineHeight: 1.6 }}>{rep.descricao}</pre>
                </div>

                {/* Resposta do admin */}
                {temResposta && (
                  <div style={{ background: T.isDark ? "#0d2f1a" : "#f0fdf4", border: "1px solid " + (T.isDark ? "#22c55e30" : "#bbf7d0"), borderRadius: 8, padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.isDark ? "#4ade80" : "#16a34a", marginBottom: 5, letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 5 }}>
                      <CheckCircle size={11} /> RESPOSTA DA EQUIPE · {fmtData(rep.respondido_em)}
                    </div>
                    <pre style={{ margin: 0, fontSize: 13, color: T.isDark ? "#d1fae5" : "#065f46", whiteSpace: "pre-wrap", fontFamily: "'IBM Plex Sans', sans-serif", lineHeight: 1.6 }}>{rep.resposta}</pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem("nfse_token"));
  const [sessaoExpirada, setSessaoExpirada] = useState(false);
  const [usuario, setUsuario] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nfse_usuario") || "null"); } catch { return null; }
  });
  const [pagina, setPagina] = useState("dashboard");
  const [toast, setToast] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("nfse_theme") === "dark");
  const toggleTheme = () => setDarkMode(d => {
    const next = !d;
    localStorage.setItem("nfse_theme", next ? "dark" : "light");
    return next;
  });

  const [maisAberto, setMaisAberto] = useState(false);
  const [reportesPendentes, setReportesPendentes] = useState(0);
  const [certAlerta, setCertAlerta] = useState(0);

  const atualizarBadgeReportes = async (tk) => {
    try {
      const r = await fetch(`${API}/reportar-erro/pendente`, { headers: { Authorization: "Bearer " + tk } });
      const d = await r.json();
      setReportesPendentes(d.pendente || 0);
    } catch { /* silencioso */ }
  };

  const atualizarBadgeCert = async (tk) => {
    try {
      const r = await fetch(`${API}/empresas`, { headers: { Authorization: "Bearer " + tk } });
      const lista = await r.json();
      if (!Array.isArray(lista)) return;
      const hoje = new Date();
      const alertas = lista.filter(e => {
        if (!e.certificado_validade) return false;
        const dias = Math.floor((new Date(e.certificado_validade) - hoje) / (1000*60*60*24));
        return dias <= 30;
      }).length;
      setCertAlerta(alertas);
    } catch { /* silencioso */ }
  };

  // Impersonação: guarda token/usuario do admin para poder voltar
  // Pilha de sessões de acesso. É pilha, e não um valor só, porque o encadeamento
  // é real: o admin entra na conta do contador, e de lá o contador entra na conta
  // do cliente. Com um valor único, o caminho de volta ao admin se perdia.
  const [sessoesAcesso, setSessoesAcesso] = useState(() => {
    try {
      const bruto = JSON.parse(localStorage.getItem("nfse_admin_sessao") || "null");
      if (!bruto) return [];
      return Array.isArray(bruto) ? bruto : [bruto];  // formato antigo: objeto
    } catch { return []; }
  });
  const adminSessao = sessoesAcesso.length ? sessoesAcesso[sessoesAcesso.length - 1] : null;
  // Quem está no teclado é admin? Vale para a própria conta e para a conta de
  // um usuário em que o admin entrou pelo painel (não pelo vínculo de contador).
  // Espelha o `operador_admin` do servidor, que é quem de fato barra a ação.
  const operadorAdmin = !!usuario?.is_admin
    || sessoesAcesso.some(s => !s.comoContador && s.usuario?.is_admin);

  // Contas de clientes que este usuário pode acessar por vínculo. Fica vazio
  // para quem não é contador, e o seletor nem aparece.
  const [contasVinculadas, setContasVinculadas] = useState([]);

  // ── Instalação como app ────────────────────────────────────────────────────
  // O Chrome avisa que dá para instalar pelo evento beforeinstallprompt e deixa
  // guardar esse evento para disparar depois. O Safari do iPhone não tem nada
  // disso: lá a instalação é manual, pelo menu Compartilhar.
  const promptInstalarRef = useRef(null);
  const [podeInstalar, setPodeInstalar] = useState(false);

  useEffect(() => {
    const jaInstalado = window.matchMedia("(display-mode: standalone)").matches
                     || window.navigator.standalone === true;
    if (jaInstalado) return;

    // O evento pode ter chegado antes do React montar — o index.html o guarda.
    const capturar = () => {
      if (window.__promptInstalar) promptInstalarRef.current = window.__promptInstalar;
      setPodeInstalar(true);
    };
    capturar();

    const aoInstalar = () => { promptInstalarRef.current = null; setPodeInstalar(false); };
    window.addEventListener("nfpro:instalavel", capturar);
    window.addEventListener("nfpro:instalado", aoInstalar);
    return () => {
      window.removeEventListener("nfpro:instalavel", capturar);
      window.removeEventListener("nfpro:instalado", aoInstalar);
    };
  }, []);

  const instalarApp = async () => {
    const evt = promptInstalarRef.current || window.__promptInstalar;
    if (evt) {
      promptInstalarRef.current = null;
      window.__promptInstalar = null;
      evt.prompt();
      const escolha = await evt.userChoice.catch(() => null);
      if (escolha?.outcome === "accepted") setPodeInstalar(false);
      return;
    }

    // Sem prompt disponível: iPhone nunca oferece, e no desktop o Chrome às
    // vezes só libera depois de alguns acessos. Nesses casos, explicamos.
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      alert("Para instalar no iPhone:\n\n"
          + "1. Toque no botão Compartilhar (o quadrado com a seta para cima)\n"
          + "2. Escolha \"Adicionar à Tela de Início\"\n"
          + "3. Toque em Adicionar");
    } else if (/Firefox/.test(navigator.userAgent)) {
      alert("O Firefox não instala aplicativos web no computador.\n\n"
          + "Abra o sistema no Chrome ou no Edge para instalar.");
    } else {
      alert("Para instalar no computador:\n\n"
          + "1. Clique no ícone de instalar na barra de endereço, à direita\n"
          + "   (um monitor com uma seta para baixo)\n"
          + "2. Ou abra o menu do navegador e escolha \"Instalar NFPro\"");
    }
  };

  // Rota /privacidade — o nginx faz try_files cair no index.html, então a
  // navegação é resolvida aqui pelo pathname.
  const [rotaPrivacidade, setRotaPrivacidade] = useState(
    () => window.location.pathname.replace(/\/+$/, "") === "/privacidade"
  );
  useEffect(() => {
    const aoNavegar = () =>
      setRotaPrivacidade(window.location.pathname.replace(/\/+$/, "") === "/privacidade");
    window.addEventListener("popstate", aoNavegar);
    return () => window.removeEventListener("popstate", aoNavegar);
  }, []);

  const abrirPrivacidade = (e) => {
    if (e) e.preventDefault();
    window.history.pushState({}, "", "/privacidade");
    setRotaPrivacidade(true);
  };

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(null),4000); };

  // Nunca persiste hash de senha nem campos de sessão vindos do servidor.
  const limparUsuario = (u) => {
    if (!u || typeof u !== "object") return u;
    const { senha_hash, tokens_validos_apos, ...seguro } = u;
    return seguro;
  };

  const onLogin = (t, u) => {
    const limpo = limparUsuario(u);
    localStorage.setItem("nfse_token", t);
    localStorage.setItem("nfse_usuario", JSON.stringify(limpo));
    setToken(t); setUsuario(limpo);
  };

  const limparSessaoLocal = () => {
    // Remove TUDO do sistema, preservando só a preferência de tema. Antes ficavam
    // para trás o rascunho de emissão e o CPF de pré-cadastro — que contêm dados
    // pessoais de tomadores e não devem sobreviver ao logout (LGPD).
    Object.keys(localStorage)
      .filter(k => k.startsWith("nfse_") && k !== "nfse_theme")
      .forEach(k => localStorage.removeItem(k));
    setToken(null); setUsuario(null); setSessoesAcesso([]); setReportesPendentes(0);
  };

  // ── Sessão expirada ─────────────────────────────────────────────────────────
  // O token vale 24h. Sem este tratamento, quando ele vencia toda chamada
  // devolvia 401, e como a maioria dos `fetch` faz `Array.isArray(d) ? d : []`,
  // a tela ficava vazia ou em "Carregando..." eterno — sem erro visível. O
  // usuário só se recuperava saindo e entrando de novo.
  //
  // Interceptamos o fetch globalmente em vez de tratar as 62 chamadas uma a uma:
  // qualquer 401 vindo da nossa API encerra a sessão e leva ao login.
  const expirandoRef = useRef(false);
  useEffect(() => {
    const fetchOriginal = window.fetch;
    window.fetch = async (...args) => {
      const resp = await fetchOriginal(...args);
      try {
        const url = typeof args[0] === "string" ? args[0] : (args[0]?.url || "");
        const nossa = url.startsWith(API);
        // /auth/login responde 401 para senha errada — não é sessão expirada
        const ehLogin = url.includes("/auth/login");
        if (resp.status === 401 && nossa && !ehLogin && !expirandoRef.current) {
          expirandoRef.current = true;
          setSessaoExpirada(true);
          limparSessaoLocal();
        }
      } catch { /* nunca deixa o interceptador quebrar a requisição */ }
      return resp;
    };
    return () => { window.fetch = fetchOriginal; };
  }, []);

  const sair = async () => {
    const tokenAtual = token;
    // Se estiver impersonando, encerrar a sessão invalidaria os tokens do usuário
    // acessado — nesse caso só limpa o estado local.
    const impersonando = !!adminSessao;
    limparSessaoLocal();
    if (tokenAtual && !impersonando) {
      try {
        await fetch(API + "/auth/logout", {
          method: "POST",
          headers: { Authorization: "Bearer " + tokenAtual },
        });
      } catch { /* logout local já feito */ }
    }
  };

  // Poll badge de reportes pendentes (a cada 60s)
  useEffect(() => {
    if (!token) return;
    atualizarBadgeReportes(token);
    const id = setInterval(() => atualizarBadgeReportes(token), 60000);
    return () => clearInterval(id);
  }, [token]);

  // Verifica validade dos certificados ao logar e ao navegar para empresas
  useEffect(() => {
    if (!token) return;
    atualizarBadgeCert(token);
  }, [token, pagina]);

  // Contas vinculadas. Não busca durante um acesso em andamento: ali o token é
  // do cliente, e o que vale são os vínculos de quem entrou.
  useEffect(() => {
    // Vale também durante um acesso: ao entrar na conta de um contador, o token
    // passa a ser dele, e são os clientes dele que devem aparecer.
    if (!token) { setContasVinculadas([]); return; }
    let cancelado = false;
    fetch(`${API}/contas-vinculadas`, { headers:{ Authorization:"Bearer "+token } })
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (!cancelado) setContasVinculadas(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelado) setContasVinculadas([]); });
    return () => { cancelado = true; };
  }, [token, adminSessao]);

  const acessarContaVinculada = async (conta) => {
    try {
      const r = await fetch(`${API}/contas/${conta.id}/acessar`, {
        method: "POST", headers: { Authorization: "Bearer " + token }
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert("Não foi possível acessar a conta:\n\n" + (err.detail || String(r.status)));
        return;
      }
      const d = await r.json();
      entrarComo(d.access_token, d.usuario, true);
    } catch (e) { alert("Erro: " + e.message); }
  };

  // `comoContador` distingue o acesso por vínculo do acesso de admin: muda o
  // texto do banner e para onde o botão de voltar leva.
  const entrarComo = (tokenUser, usuarioUser, comoContador = false) => {
    // Empilha a sessão atual antes de trocar
    const pilha = [...sessoesAcesso, { token, usuario, comoContador }];
    const alvo = limparUsuario(usuarioUser);
    localStorage.setItem("nfse_admin_sessao", JSON.stringify(pilha));
    localStorage.setItem("nfse_token", tokenUser);
    localStorage.setItem("nfse_usuario", JSON.stringify(alvo));
    setSessoesAcesso(pilha);
    setToken(tokenUser);
    setUsuario(alvo);
    setPagina("dashboard");
  };

  const voltarAdmin = () => {
    if (!sessoesAcesso.length) return;
    const pilha = [...sessoesAcesso];
    const anterior = pilha.pop();

    localStorage.setItem("nfse_token", anterior.token);
    localStorage.setItem("nfse_usuario", JSON.stringify(anterior.usuario));
    if (pilha.length) localStorage.setItem("nfse_admin_sessao", JSON.stringify(pilha));
    else localStorage.removeItem("nfse_admin_sessao");

    setToken(anterior.token);
    setUsuario(anterior.usuario);
    setSessoesAcesso(pilha);
    // Contador não tem painel admin para onde voltar
    setPagina(anterior.comoContador ? "clientes" : "admin");
  };

  const T = darkMode ? {
    isDark: true,
    bg:"#080c10", sidebar:"#0c1117", border:"#1a2332",
    card:"#0c1117", cardInner:"#080c10", header:"#0c1117",
    text:"#f1f5f9", textMuted:"#94a3b8", textFaint:"#475569",
    input:"#080c10", inputBorder:"#1a2332", inputText:"#e2e8f0",
    navActive:"#0ea5e915", navActiveBorder:"#0ea5e9", navActiveText:"#38bdf8",
    navText:"#94a3b8", btnSecBg:"#1a2332", btnSecText:"#94a3b8",
    rowBorder:"#0f1923", tableBg:"#0c1117",
    shadow:"0 2px 12px rgba(0,0,0,0.45)", shadowHover:"0 4px 24px rgba(0,0,0,0.6)",
    dropdownBg:"#0c1117", dropdownBorder:"#1a2332", dropdownText:"#e2e8f0", dropdownRowBorder:"#0f172a",
    dangerBg:"#2f0d0d", danger:"#f87171",
    logBg:"#0c1117", logBorder:"#1a2332", logText:"#94a3b8",
    lockedBg:"#0a1a0a", lockedBorder:"#22c55e40", lockedText:"#4ade80",
    successBg:"#0d2f1a", successBorder:"#22c55e40", successText:"#4ade80",
    certOkBg:"#0d2f1a", certOkText:"#4ade80", certNoBg:"#2f1a0d", certNoText:"#fbbf24",
  } : {
    isDark: false,
    bg:"#f0f4f8", sidebar:"#ffffff", border:"#e2e8f0",
    card:"#ffffff", cardInner:"#f8fafc", header:"#ffffff",
    text:"#0f172a", textMuted:"#475569", textFaint:"#94a3b8",
    input:"#ffffff", inputBorder:"#cbd5e1", inputText:"#0f172a",
    navActive:"#eff6ff", navActiveBorder:"#0ea5e9", navActiveText:"#0284c7",
    navText:"#475569", btnSecBg:"#f1f5f9", btnSecText:"#475569",
    rowBorder:"#f1f5f9", tableBg:"#ffffff",
    shadow:"0 2px 12px rgba(0,0,0,0.07)", shadowHover:"0 6px 24px rgba(0,0,0,0.12)",
    dropdownBg:"#ffffff", dropdownBorder:"#e2e8f0", dropdownText:"#0f172a", dropdownRowBorder:"#f1f5f9",
    dangerBg:"#fef2f2", danger:"#ef4444",
    logBg:"#f8fafc", logBorder:"#e2e8f0", logText:"#475569",
    lockedBg:"#f0fdf4", lockedBorder:"#22c55e40", lockedText:"#16a34a",
    successBg:"#f0fdf4", successBorder:"#22c55e40", successText:"#16a34a",
    certOkBg:"#f0fdf4", certOkText:"#16a34a", certNoBg:"#fffbeb", certNoText:"#d97706",
  };

  const isMobile = useIsMobile();

  const navItems = [
    { id:"dashboard", label:"Dashboard",     icon:LayoutDashboard },
    { id:"emitir",    label:"Emitir NFS-e",  icon:FilePlus },
    { id:"servicos",  label:"Serviços",      icon:Briefcase },
    { id:"pessoas",   label:"Pessoas (CPF)", icon:Users },
    { id:"historico", label:"Histórico",     icon:Clock },
    { id:"empresas",  label:"Empresas",      icon:Building2 },
    // Só aparece para quem tem vínculo com contas de clientes. Ícone diferente
    // do de Pessoas (CPF), que também usa Users.
    ...(contasVinculadas.length > 0
        ? [{ id:"clientes", label:"Clientes", icon:Handshake, destaque:true }] : []),
    ...(usuario?.is_admin ? [{ id:"admin", label:"Painel Admin", icon:ShieldCheck, admin:true }] : []),
  ];

  // Política de privacidade: acessível em /privacidade SEM login, porque os
  // titulares (inclusive tomadores, que nunca criam conta) precisam poder ler.
  // Esta verificação vem ANTES da checagem de token de propósito.
  if (rotaPrivacidade) {
    return <PoliticaPrivacidade T={T} onVoltar={() => {
      window.history.pushState({}, "", "/");
      setRotaPrivacidade(false);
    }} />;
  }

  if (!token) return (
    <LoginView
      onLogin={(t, u) => { setSessaoExpirada(false); expirandoRef.current = false; onLogin(t, u); }}
      avisoSessao={sessaoExpirada ? "Sua sessão expirou por inatividade. Entre novamente para continuar." : ""}
    />
  );

  const bottomNavItems = [
    { id:"dashboard", label:"Dashboard", icon:LayoutDashboard },
    { id:"emitir",    label:"Emitir",    icon:FilePlus },
    { id:"historico", label:"Histórico", icon:Clock },
    { id:"empresas",  label:"Empresas",  icon:Building2 },
    { id:"__mais__",  label:"Mais",      icon:MoreHorizontal },
  ];

  const maisMenuItems = [
    { id:"servicos",      label:"Serviços",        icon:Briefcase },
    { id:"pessoas",       label:"Pessoas (CPF)",   icon:Users },
    { id:"reportar-erro", label:"Reportar Erro",   icon:MessageSquare },
    ...(usuario?.is_admin ? [{ id:"admin", label:"Painel Admin", icon:ShieldCheck }] : []),
    { id:"__sair__",      label:"Sair",            icon:LogOut, danger:true },
  ];

  return (
    <div style={{ display:"flex", height:"100vh", background:T.bg, fontFamily:"'IBM Plex Sans',sans-serif", color:T.text, overflow:"hidden" }}>
      {/* Fonte hospedada localmente. Antes vinha de fonts.googleapis.com, o que
          enviava o IP de cada visitante ao Google a cada acesso (LGPD). */}
      <link href="/fonts/ibm-plex-sans.css" rel="stylesheet" />

      {/* ── Desktop sidebar ── */}
      {!isMobile && (
        <div style={{ width:220, background:T.sidebar, borderRight:"1px solid "+T.border, display:"flex", flexDirection:"column", boxShadow:T.shadow }}>
          <div style={{ padding:"18px 16px", borderBottom:"1px solid "+T.border, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <a href="https://inateccontabilidade.com.br/" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none" }}>
              <img src="/logo-inatec.png" alt="INATEC" style={{ width:"100%", maxWidth:180, objectFit:"contain", cursor:"pointer" }} />
            </a>
          </div>
          <nav style={{ padding:"16px 12px", flex:1 }}>
            {navItems.map(item=>{
              // Itens de destaque (Painel Admin em roxo, Clientes em azul) mantêm
              // a cor mesmo quando não estão selecionados.
              const acento = item.admin ? "#a78bfa" : item.destaque ? "#38bdf8" : null;
              const acentoForte = item.admin ? "#c084fc" : item.destaque ? "#7dd3fc" : null;
              const fundoAcento = item.admin ? (T.isDark ? "#1a0d2f" : "#f5f3ff")
                                : item.destaque ? (T.isDark ? "#0b2434" : "#eff6ff") : null;
              return (
              <div key={item.id} onClick={()=>setPagina(item.id)} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:8, cursor:"pointer", fontSize:13.5, fontWeight: acento ? 600 : 500, marginBottom:2,
                background: acento && pagina!==item.id ? fundoAcento : pagina===item.id ? T.navActive : "transparent",
                color: acento ? (pagina===item.id ? acentoForte : acento) : pagina===item.id ? T.navActiveText : T.navText,
                borderLeft: pagina===item.id ? "2px solid " + (acento || T.navActiveBorder) : "2px solid transparent" }}>
                <item.icon size={16} /><span>{item.label}</span>
                {item.id === "empresas" && certAlerta > 0 && (
                  <span style={{ marginLeft:"auto", background:"#ef4444", color:"#fff", borderRadius:"50%", fontSize:10, fontWeight:700, width:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    !
                  </span>
                )}
              </div>
              );
            })}
          </nav>
          <div style={{ padding:"16px 12px", borderTop:"1px solid "+T.border }}>
            {/* Reportar Erro — acima do Sair */}
            <div onClick={() => setPagina("reportar-erro")}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:8, cursor:"pointer", fontSize:13.5, fontWeight:500, marginBottom:2, position:"relative",
                background: pagina==="reportar-erro" ? T.navActive : "transparent",
                color:      pagina==="reportar-erro" ? T.navActiveText : T.navText,
                borderLeft: pagina==="reportar-erro" ? "2px solid "+T.navActiveBorder : "2px solid transparent" }}>
              <MessageSquare size={16} />
              <span>Reportar Erro</span>
              {reportesPendentes > 0 && (
                <span style={{ marginLeft:"auto", background:"#ef4444", color:"#fff", borderRadius:20, fontSize:10, fontWeight:700, padding:"1px 8px", minWidth:18, textAlign:"center" }}>
                  {reportesPendentes > 9 ? "9+" : reportesPendentes}
                </span>
              )}
            </div>
            {podeInstalar && (
              <div onClick={instalarApp}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:8, cursor:"pointer", fontSize:13.5, fontWeight:600, color:"#38bdf8" }}>
                <Download size={16} /><span>Instalar app</span>
              </div>
            )}
            <div onClick={sair} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:8, cursor:"pointer", fontSize:13.5, color:"#94a3b8" }}>
              <LogOut size={16} /><span>Sair</span>
            </div>
            <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid "+T.border, textAlign:"center", fontSize:10, color:T.textFaint, lineHeight:1.6 }}>
              <a href="/privacidade" onClick={abrirPrivacidade}
                style={{ color:T.textFaint, textDecoration:"none", fontSize:11 }}>
                Política de Privacidade
              </a><br/>
              © 2026 Inatec Contabilidade<br/>
              <span style={{ opacity:0.7 }}>Desenvolvido por Jean Egewarth</span>
            </div>
          </div>
        </div>
      )}

      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        {/* ── Header ── */}
        {isMobile ? (
          <div style={{ height:52, borderBottom:"1px solid "+T.border, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 16px", background:T.header, boxShadow:T.shadow, flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <a href="https://inateccontabilidade.com.br/" target="_blank" rel="noopener noreferrer">
                <img src="/logo-inatec.png" alt="INATEC" style={{ height:32, objectFit:"contain", cursor:"pointer" }} />
              </a>
            </div>
            <button onClick={toggleTheme} title={darkMode ? "Modo claro" : "Modo escuro"}
              style={{ background:T.btnSecBg, border:"1px solid "+T.border, borderRadius:8, padding:"6px 10px", fontSize:12, color:T.btnSecText, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
              {darkMode ? <Sun size={13}/> : <Moon size={13}/>}
            </button>
          </div>
        ) : (
          <div style={{ height:60, borderBottom:"1px solid "+T.border, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 28px", background:T.header, boxShadow:T.shadow }}>
            <span style={{ fontSize:15, fontWeight:600, color:T.text, display:"flex", alignItems:"center", gap:8 }}>
              {(() => {
                const allItems = [...navItems, { id:"reportar-erro", label:"Reportar Erro", icon:MessageSquare }];
                const I = allItems.find(i=>i.id===pagina)?.icon;
                return I ? <I size={16}/> : null;
              })()}
              {[...navItems, { id:"reportar-erro", label:"Reportar Erro" }].find(i=>i.id===pagina)?.label}
            </span>
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              <button onClick={toggleTheme} title={darkMode ? "Modo claro" : "Modo escuro"}
                style={{ background:T.btnSecBg, border:"1px solid "+T.border, borderRadius:8, padding:"6px 12px", fontSize:13, color:T.btnSecText, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
                {darkMode ? <><Sun size={14} style={{marginRight:4}}/> Claro</> : <><Moon size={14} style={{marginRight:4}}/> Escuro</>}
              </button>
              <div style={{ fontSize:13, color:T.textMuted }}>Olá, <span style={{ color:T.text }}>{usuario?.nome || "Usuário"}</span></div>
            </div>
          </div>
        )}

        {/* ── Banner de impersonação ── */}
        {adminSessao && (
          <div style={{ background:"linear-gradient(135deg,#f97316,#ea580c)", padding:"10px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexShrink:0 }}>
            <div style={{ fontSize:13, color:"#fff", fontWeight:500 }}>
              <Eye size={13} style={{verticalAlign:"middle",marginRight:4}} /> Acessando conta de <strong>{usuario?.nome}</strong> ({usuario?.email}){adminSessao.comoContador ? " como contador vinculado" : " como administrador"}
            </div>
            <button onClick={voltarAdmin} style={{ background:"rgba(255,255,255,0.2)", border:"1px solid rgba(255,255,255,0.4)", borderRadius:6, padding:"5px 14px", fontSize:12, color:"#fff", cursor:"pointer", fontWeight:600, whiteSpace:"nowrap" }}>
              ← Voltar à minha conta
            </button>
          </div>
        )}

        {/* ── Main content ── */}
        <div style={{ flex:1, overflow:"auto", padding: isMobile ? "16px 16px 80px" : 28 }}>
          {pagina==="dashboard" && <Dashboard token={token} usuario={usuario} onNavigate={setPagina} isMobile={isMobile} T={T} />}
          {pagina==="emitir"    && <EmitirView token={token} T={T} isMobile={isMobile} onIrParaEmpresas={()=>setPagina("empresas")} onSuccess={(dest)=>{ if(dest==="pessoas"){ setPagina("pessoas"); } else { showToast("NFS-e emitida!"); setPagina("historico"); } }} />}
          {pagina==="servicos"  && <ServicosView token={token} T={T} isMobile={isMobile} />}
          {pagina==="historico" && <HistoricoView token={token} T={T} isMobile={isMobile} onReemitir={() => setPagina("emitir")} />}
          {pagina==="empresas"  && <EmpresasView token={token} T={T} podeExcluir={operadorAdmin} />}
          {pagina==="pessoas"       && <PessoasView token={token} T={T} onVoltarEmitir={localStorage.getItem("nfse_emitir_rascunho") ? () => setPagina("emitir") : null} />}
          {pagina==="reportar-erro" && <ReportarErroView token={token} T={T} isMobile={isMobile} onReporteSent={() => atualizarBadgeReportes(token)} />}
          {pagina==="admin"         && usuario?.is_admin && <AdminView token={token} T={T} onEntrarComo={entrarComo} />}
          {pagina==="clientes"      && <ClientesView contas={contasVinculadas} onAcessar={acessarContaVinculada} T={T} isMobile={isMobile} />}
        </div>
      </div>

      {/* ── Mobile bottom nav ── */}
      {isMobile && (
        <>
          {/* Backdrop para fechar o menu "Mais" */}
          {maisAberto && (
            <div onClick={() => setMaisAberto(false)}
              style={{ position:"fixed", inset:0, zIndex:98, background:"rgba(0,0,0,0.45)" }} />
          )}

          {/* Popup "Mais" — sobe da bottom nav */}
          {maisAberto && (
            <div style={{ position:"fixed", bottom:64, left:0, right:0, zIndex:99, background:T.sidebar, borderTop:"1px solid "+T.border, borderRadius:"16px 16px 0 0", boxShadow:"0 -4px 24px rgba(0,0,0,0.35)", paddingBottom:8 }}>
              {/* Alça */}
              <div style={{ display:"flex", justifyContent:"center", padding:"10px 0 4px" }}>
                <div style={{ width:36, height:4, borderRadius:2, background:T.border }} />
              </div>
              {/* No celular não existe a barra lateral, então o botão de
                  instalar precisa morar aqui — é justamente onde ele importa. */}
              {podeInstalar && (
                <div onClick={() => { setMaisAberto(false); instalarApp(); }}
                  style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 24px", cursor:"pointer",
                           color:"#38bdf8", fontSize:15, fontWeight:600 }}>
                  <Download size={20} />
                  <span>Instalar app</span>
                </div>
              )}
              {maisMenuItems.map(item => (
                <div key={item.id}
                  onClick={() => {
                    setMaisAberto(false);
                    if (item.id === "__sair__") sair();
                    else setPagina(item.id);
                  }}
                  style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 24px", cursor:"pointer",
                    color: item.danger ? "#f87171" : pagina === item.id ? T.navActiveText : T.text,
                    background: !item.danger && pagina === item.id ? T.navActive : "transparent",
                    fontSize:15, fontWeight:500 }}>
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Barra de navegação */}
          <div style={{ position:"fixed", bottom:0, left:0, right:0, height:64, background:T.sidebar, borderTop:"1px solid "+T.border, display:"flex", zIndex:100, boxShadow:"0 -2px 12px rgba(0,0,0,0.2)" }}>
            {bottomNavItems.map(item => {
              const ativo = item.id !== "__mais__" && pagina === item.id;
              const maisSel = item.id === "__mais__" && (maisAberto || ["servicos","pessoas","admin"].includes(pagina));
              return (
                <div key={item.id}
                  onClick={() => {
                    if (item.id === "__mais__") { setMaisAberto(v => !v); }
                    else { setMaisAberto(false); setPagina(item.id); }
                  }}
                  style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3, cursor:"pointer", position:"relative",
                    color: (ativo || maisSel) ? T.navActiveText : T.navText,
                    background: (ativo || maisSel) ? T.navActive : "transparent" }}>
                  <item.icon size={20} />
                  <span style={{ fontSize:10, fontWeight:600 }}>{item.label}</span>
                  {item.id === "empresas" && certAlerta > 0 && (
                    <span style={{ position:"absolute", top:6, right:"calc(50% - 18px)", background:"#ef4444", color:"#fff", borderRadius:"50%", fontSize:9, fontWeight:700, width:15, height:15, display:"flex", alignItems:"center", justifyContent:"center" }}>!</span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {toast && <div style={{ position:"fixed", bottom: isMobile ? 80 : 24, right:24, background:T.successBg, border:"1px solid "+T.successBorder, color:T.successText, padding:"12px 20px", borderRadius:10, fontSize:13, fontWeight:500, boxShadow:T.shadowHover }}><Check size={11} style={{verticalAlign:"middle",marginRight:3}} /> {toast}</div>}
    </div>
  );
}

export default App;